import {
  alternarTodosOsFilhos,
  guardarBandasFechadas,
  guardarRolagem as guardarRolagemNoDisco,
  lerBandasFechadas,
  lerRolagem,
} from './kanban-memoria.ts'
import { CatalogIcon } from '../components/IconPicker.tsx'
import { useModuleGates } from '../lib/module-gate.tsx'
import { SelosDoCard } from '../features/plugin/SelosDoCard.tsx'
import { useSelosDePlugin } from '../features/plugin/useSelos.ts'
import { CompartilharQuadro, type PessoaDoQuadro } from '../features/kanban/CompartilharQuadro.tsx'
import { MODOS_DO_QUADRO, evaluateFilter, type ModoDaVisao, type SavedFilter, type Visao } from '@orbind/core'
import { useMutacoesDeVisao, useVisoes } from '../features/visao/api.ts'
import { EditorDeVisao } from '../features/visao/EditorDeVisao.tsx'
import { filtroDoQuadro, filtroParaGuardar, opcoesDoCombo, ordenarVisoes } from '../features/visao/visao.ts'
import {
  Alert,
  Button,
  Checkbox,
  DateInput,
  FieldBlock,
  FilterCombo,
  IconButton,
  Input,
  LoadingState,
  Modal,
  FilterEditor,
  Select,
  TagSelect,
  Textarea,
  emptyFilter,
  type FilterField,
  useToast,
} from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useParams } from 'react-router'
import { FormRow, FormScreen, FormSection } from '../components/FormScreen.tsx'
import { api, apiBase } from '../lib/api.ts'
import { urlDoFundo } from '../features/kanban/fundo-de-quadro.ts'
import { NovaListaRapida } from '../features/kanban/NovaListaRapida.tsx'
import { moverCartaoNaVisao } from '../features/kanban/quadro-em-cache.ts'
import { CHAVE_ULTIMO_QUADRO, primeiroQuadroSem } from '../features/kanban/proximo-quadro.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useT } from '../lib/i18n.tsx'
import { useSession } from '../lib/session.tsx'
import { TONE_COLOR, TONE_SOFT, initialsOf, useConfirm } from '../features/work-item/bits.tsx'
import { useSetBoardBackground, useSetBoardTelaCheia } from '../shell/board-background.tsx'
import { useTemaEfetivo } from '../shell/theme.tsx'
import { ehAnexoDeCampo, uploadWorkItemImage } from '../features/work-item/api.ts'
import { ScopePicker, type ScopeNode } from './ScopePicker.tsx'
import { useTemplates } from '../features/admin/template-data.ts'
import { useCustomFields } from '../features/admin/custom-field-data.ts'
import { rotuloCampo } from '../features/work-item/campos-shared.tsx'
import { EscolherTemplateModal } from '../features/work-item/TemplatesUI.tsx'
import '../features/work-item/wi.css'
import './kanban.css'
import { WorkItemsPage } from '../features/work-item/WorkItemsPage.tsx'
import { useCatalogoDeColunas } from '../features/work-item/catalogo-de-colunas.ts'
import { adotarTabela, padraoDaTabela, type EstadoTabela } from '../features/work-item/colunas-da-tabela.ts'
import { colunasLigadas } from '../features/work-item/colunas-do-filtro.ts'
import { iniciarArrasteDeColuna } from '../lib/arrastar-coluna.ts'
import { ItensNaoMapeados } from '../features/kanban/ItensNaoMapeados.tsx'
import {
  CAMPOS_DE_FILTRO,
  QUADROS_TODOS,
  diasDoItem,
  tipoDoCampoComplementar,
  valorDeQuadros,
} from '../features/work-item/filter-fields.ts'
import { SomenteNesteQuadro, VisibilidadeDosNovosItens } from '../features/kanban/VisibilidadeDosNovosItens.tsx'
import { usePreferenciaEstruturada } from '../lib/layout-prefs.ts'

/**
 * Quadro Kanban — fiel a regiao `showBoard` do prototipo v2 (SPEC-20260807-1610).
 *
 * Regras do produto:
 *  - cada lane tem UM status (o que o item assume ao ser solto nela), exibido
 *    sob o titulo no modo de edicao;
 *  - o mesmo status pode estar em varias lanes;
 *  - a configuracao acontece NO PROPRIO QUADRO (modo "Em edicao"): renomear
 *    inline, engrenagem com chips de status/WIP/Aplicar, excluir, arrastar a
 *    lane pelo grip com marcador de destino, Adicionar lane; Salvar aplica
 *    tudo de uma vez e fecha; Cancelar descarta.
 */

/** Cores solidas translucidas para o fundo da area de trabalho do quadro. */
export const KB_BG_COLORS = [
  'rgba(27, 77, 245, 0.10)',
  'rgba(14, 159, 110, 0.12)',
  'rgba(245, 158, 11, 0.14)',
  'rgba(239, 68, 68, 0.10)',
  'rgba(139, 92, 246, 0.12)',
  'rgba(20, 184, 166, 0.12)',
  'rgba(236, 72, 153, 0.10)',
  'rgba(100, 116, 139, 0.12)',
]

/** Gradientes do seletor "Cores" (guardados em backgroundImageUrl). */
export const KB_BG_GRADIENTS = [
  'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
  'linear-gradient(135deg, #1B4DF5 0%, #14B8A6 100%)',
  'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
  'linear-gradient(135deg, #0E9F6E 0%, #1B4DF5 100%)',
]


/**
 * URLs da API (/public/kanban-bg/...) viram absolutas; os fundos prontos da
 * biblioteca (/fundos/...) são do próprio site (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro).
 */
const resolveBgUrl = urlDoFundo

/**
 * Barra de rolagem HORIZONTAL do quadro, desenhada por nós e FIXA no pé da
 * tela (2026-09-01). A nativa fica no fim do elemento — se o fim das lanes
 * passa do fim da tela (zoom, lane alta), ela some da vista. Esta vive num
 * portal com position fixed: está sempre embaixo, como a vertical está sempre
 * na direita, e sincroniza nos dois sentidos com o scroll do quadro.
 */
function BarraHorizontalFixa({ alvoRef }: { alvoRef: React.RefObject<HTMLDivElement | null> }) {
  const [st, setSt] = useState<null | { left: number; width: number; thumbLeft: number; thumbWidth: number }>(null)
  const arraste = useRef<null | { x: number; scroll: number }>(null)

  useEffect(() => {
    const medir = () => {
      const el = alvoRef.current
      if (!el || el.scrollWidth <= el.clientWidth + 1) {
        setSt((atual) => (atual === null ? atual : null))
        return
      }
      const r = el.getBoundingClientRect()
      const thumbWidth = Math.max(48, (el.clientWidth / el.scrollWidth) * r.width)
      const thumbLeft = (el.scrollLeft / (el.scrollWidth - el.clientWidth)) * (r.width - thumbWidth)
      setSt({ left: r.left, width: r.width, thumbLeft, thumbWidth })
    }
    medir()
    const el = alvoRef.current
    el?.addEventListener('scroll', medir)
    window.addEventListener('resize', medir)
    // conteudo muda sem evento (cards chegam, lane cresce): revalida no tempo
    const timer = window.setInterval(medir, 400)
    return () => {
      el?.removeEventListener('scroll', medir)
      window.removeEventListener('resize', medir)
      window.clearInterval(timer)
    }
  }, [alvoRef])

  if (!st) return null
  return createPortal(
    <div
      className="kb__hbar"
      style={{ left: st.left, width: st.width }}
      role="scrollbar"
      aria-orientation="horizontal"
      onPointerDown={(e) => {
        // clique no trilho: pula direto para o ponto
        const el = alvoRef.current
        if (!el || (e.target as HTMLElement).classList.contains('kb__hbar-thumb')) return
        const rel = (e.clientX - st.left - st.thumbWidth / 2) / Math.max(1, st.width - st.thumbWidth)
        el.scrollLeft = Math.max(0, Math.min(1, rel)) * (el.scrollWidth - el.clientWidth)
      }}
    >
      <div
        className="kb__hbar-thumb"
        style={{ left: st.thumbLeft, width: st.thumbWidth }}
        onPointerDown={(e) => {
          e.stopPropagation()
          try {
            ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          } catch {
            /* evento sintetico */
          }
          arraste.current = { x: e.clientX, scroll: alvoRef.current?.scrollLeft ?? 0 }
        }}
        onPointerMove={(e) => {
          const d = arraste.current
          const el = alvoRef.current
          if (!d || !el) return
          const escala = (el.scrollWidth - el.clientWidth) / Math.max(1, st.width - st.thumbWidth)
          el.scrollLeft = d.scroll + (e.clientX - d.x) * escala
        }}
        onPointerUp={(e) => {
          arraste.current = null
          try {
            ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
          } catch {
            /* pointer ja solto */
          }
        }}
      />
    </div>,
    document.body,
  )
}

/** Luminancia relativa (0..1) de um #RRGGBB. */
const hexLuminance = (hex: string) => {
  const n = Number.parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

/** Gradiente e "escuro" quando a media dos hex que o compoem e escura. */
const gradientIsDark = (gradient: string) => {
  const hexes = gradient.match(/#[0-9a-fA-F]{6}/g) ?? []
  if (hexes.length === 0) return false
  return hexes.reduce((sum, hex) => sum + hexLuminance(hex), 0) / hexes.length < 0.55
}

/**
 * Mede se uma imagem e escura ou clara: desenha numa miniatura de canvas e
 * tira a media da luminancia dos pixels (transparencia composta sobre claro).
 */
const measureImageDarkness = (url: string) =>
  new Promise<boolean>((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const size = 32
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(false)
        ctx.drawImage(img, 0, 0, size, size)
        const data = ctx.getImageData(0, 0, size, size).data
        let sum = 0
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3]! / 255
          const r = data[i]! * alpha + 255 * (1 - alpha)
          const g = data[i + 1]! * alpha + 255 * (1 - alpha)
          const b = data[i + 2]! * alpha + 255 * (1 - alpha)
          sum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
        }
        resolve(sum / (data.length / 4) < 0.55)
      } catch {
        resolve(false)
      }
    }
    img.onerror = () => resolve(false)
    img.src = url
  })

/**
 * Campos filtraveis do CARTAO — a MESMA lista da lista de Itens: todos os
 * campos da barra de propriedades (SPEC-20261008-1745-todos-os-campos-no-filtro).
 */
const CARD_FILTER_FIELDS: readonly FilterField[] = CAMPOS_DE_FILTRO

// ------------------------------------------------- período do cabeçalho ---
// (SPEC-20261104-1638-filtro-por-periodo-de-conclusao-e-criacao)

type PresetDePeriodo = '' | 'hoje' | 'semana' | 'mes' | '7d' | '30d' | 'custom'
/** Qual data o período recorta (pedido do Cristian: trocar no cabeçalho). */
type CampoDePeriodo = 'completedAt' | 'createdAt' | 'dueAt' | 'startAt'
const CAMPOS_DE_PERIODO: { value: CampoDePeriodo; label: string }[] = [
  { value: 'completedAt', label: 'Data de conclusão' },
  { value: 'createdAt', label: 'Data de criação' },
  { value: 'dueAt', label: 'Prazo' },
  { value: 'startAt', label: 'Início' },
]

const PRESETS_DE_PERIODO: { value: PresetDePeriodo; label: string }[] = [
  { value: '', label: 'Período: todos' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mês' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Personalizado…' },
]

/** Data LOCAL como AAAA-MM-DD (sem o salto de fuso do toISOString). */
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** AAAA-MM-DD → Date local (meia-noite), ou null quando vazio. */
const dataLocal = (texto: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** Início e fim (AAAA-MM-DD) de um atalho, a partir de hoje; vazio = sem o lado. */
function limitesDoPreset(preset: PresetDePeriodo, de: string, ate: string, hoje = new Date()): [string, string] | null {
  const dia = (n: number) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + n)
    return ymd(d)
  }
  switch (preset) {
    case 'hoje':
      return [dia(0), dia(0)]
    case 'semana': {
      // semana de segunda a domingo
      const dow = (hoje.getDay() + 6) % 7
      return [dia(-dow), dia(6 - dow)]
    }
    case 'mes':
      return [ymd(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), ymd(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0))]
    case '7d':
      return [dia(-6), dia(0)]
    case '30d':
      return [dia(-29), dia(0)]
    case 'custom':
      return de || ate ? [de, ate] : null
    default:
      return null
  }
}

// --------------------------------------- filtro rápido: campos padrão ---
// (SPEC-20261104-1646-filtro-rapido-com-campos-padrao)

const PREFIXO_RAPIDO = 'rapido:'

/** Quais campos viram seletor direto, na ordem pedida; Aplicação é o campo complementar com esse nome. */
function camposRapidosDe(fields: readonly FilterField[]): FilterField[] {
  const aplicacao = fields.find((f) => f.key.startsWith('cf:') && f.label.trim().toLowerCase() === 'aplicação')
  const ordem = ['status', 'etapa', 'assignee', 'members', aplicacao?.key, 'area']
  return ordem
    .filter((k): k is string => Boolean(k))
    .map((k) => fields.find((f) => f.key === k))
    .filter((f): f is FilterField => Boolean(f))
}

/** Separa um filtro salvo nos seletores diretos e nos critérios avançados. */
function separarFiltroRapido(f: SavedFilter | null): { rapidos: Record<string, string[]>; avancado: SavedFilter['criteria'] } {
  const rapidos: Record<string, string[]> = {}
  const avancado: SavedFilter['criteria'] = []
  for (const c of f?.criteria ?? []) {
    if (c.id.startsWith(PREFIXO_RAPIDO) && Array.isArray(c.value)) rapidos[c.field] = c.value
    else avancado.push(c)
  }
  return { rapidos, avancado }
}

/** Junta de volta: um `in` por seletor com valor + os critérios avançados. */
function juntarFiltroRapido(base: SavedFilter, rapidos: Record<string, string[]>, avancado: SavedFilter['criteria']): SavedFilter {
  const diretos = Object.entries(rapidos)
    .filter(([, vals]) => vals.length > 0)
    .map(([field, vals]) => ({ id: `${PREFIXO_RAPIDO}${field}`, field, operator: 'in' as const, value: vals }))
  return { ...base, criteria: [...diretos, ...avancado] }
}

/** O período do cabeçalho como filtro de um critério "entre" (lados opcionais). */
function filtroDoPeriodo(p: { campo: CampoDePeriodo; preset: PresetDePeriodo; de: string; ate: string }): SavedFilter | null {
  const limites = limitesDoPreset(p.preset, p.de, p.ate)
  if (!limites) return null
  return {
    id: 'periodo',
    name: 'Período',
    join: 'AND',
    criteria: [{ id: 'periodo', field: p.campo, operator: 'between', value: limites }],
  }
}

const cardFiltersKey = (boardId: string) => `orbind.kanban.${boardId}.filters`

/**
 * Campos exibiveis no CARD (config por quadro, max 8 — o titulo e sempre
 * mostrado e nao conta). Cada um tem uma forma COMPACTA e reconhecivel.
 */
const CARD_CAMPOS = [
  { key: 'type', rotulo: 'Tipo do item' },
  { key: 'code', rotulo: 'Código' },
  { key: 'description', rotulo: 'Descrição' },
  { key: 'priority', rotulo: 'Prioridade' },
  { key: 'assignee', rotulo: 'Responsável' },
  { key: 'members', rotulo: 'Membros' },
  { key: 'status', rotulo: 'Status' },
  { key: 'etapa', rotulo: 'Etapa do fluxo' },
  { key: 'due', rotulo: 'Prazo' },
  { key: 'start', rotulo: 'Início' },
  { key: 'completed', rotulo: 'Concluído em' },
  { key: 'area', rotulo: 'Área' },
  { key: 'produto', rotulo: 'Produto/Serviço' },
  { key: 'company', rotulo: 'Empresa' },
  { key: 'expectedResult', rotulo: 'Tem resultado esperado' },
  { key: 'criteria', rotulo: 'Critérios de aceite' },
  { key: 'children', rotulo: 'Itens filhos' },
  { key: 'parent', rotulo: 'É subitem' },
  { key: 'recurrence', rotulo: 'Recorrência' },
  { key: 'reminder', rotulo: 'Lembrete' },
] as const
type CardCampo = (typeof CARD_CAMPOS)[number]['key']

/**
 * Colunas do MODO LISTA (SPEC-20260914-2210).
 *
 * Fixas de proposito: sao os campos que todo cartao carrega e que servem para
 * comparar itens entre si (o que vence, de quem e, em que pe esta). A escolha
 * de campos do CARTAO e outra coisa — ela desenha o cartao no quadro.
 */
const COLUNAS_LISTA = [
  // largura em PIXELS: a coluna e redimensionavel e a tabela rola na horizontal
  // quando a soma nao cabe. Sem preferencia salva a tabela ainda estica ate
  // 100% da area, entao esses numeros sao proporcao inicial, nao teto.
  // a primeira célula também abriga a alça de arrastar (18px): o código precisa
  // dessa folga para não truncar
  { key: 'code', rotulo: 'Código', largura: 112 },
  { key: 'title', rotulo: 'Item', largura: 320 },
  { key: 'type', rotulo: 'Tipo', largura: 130 },
  { key: 'status', rotulo: 'Status', largura: 130 },
  { key: 'etapa', rotulo: 'Etapa', largura: 140 },
  { key: 'assignee', rotulo: 'Responsável', largura: 170 },
  { key: 'due', rotulo: 'Prazo', largura: 96 },
  { key: 'priority', rotulo: 'Prioridade', largura: 120 },
  // textos formatados (SPEC-20260915-2250): fora do padrão inicial — ligam em
  // Colunas; prévia em texto simples, edição com o editor rico na célula
  { key: 'description', rotulo: 'Descrição', largura: 260 },
  { key: 'expectedResult', rotulo: 'Resultado esperado', largura: 260 },
] as const
type ColunaLista = (typeof COLUNAS_LISTA)[number]['key']
/** Colunas que NÃO entram no conjunto inicial (a pessoa liga em Colunas). */
const COLUNAS_FORA_DO_PADRAO: readonly ColunaLista[] = ['description', 'expectedResult']
/** Colunas fixas de texto formatado (HTML). */
const COLUNAS_RICAS: readonly ColunaLista[] = ['description', 'expectedResult']

/** HTML → texto de uma linha para prévia, filtro e ordenação (SPEC-20260915-2250). */
const tiraHtml = (html: string | null | undefined): string => {
  if (!html) return ''
  const semMarcador = html.startsWith('<!--rte:doc-->') ? html.slice('<!--rte:doc-->'.length) : html
  const el = document.createElement('div')
  el.innerHTML = semMarcador
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/** Faixa de agrupamento da lista, com os niveis abaixo dela. */
type BandaDaLista<C> = {
  key: string
  label: string
  icon?: string
  count: number
  filter: (c: C) => boolean
  filhas: BandaDaLista<C>[]
}

/**
 * O que a GRADE precisa para editar na celula (SPEC-20260915-2048): os
 * catalogos ja resolvidos em opcoes de Select e os gravadores. Status vai por
 * `mudarStatus`, que no quadro e mover para a lane do status — nunca PATCH.
 */
type EdicaoDaGrade = {
  /** Avisa enquanto uma célula de texto formatado está em edição (barra do Sheets liga a formatação). */
  onEdicaoRica?: (ativa: boolean) => void
  tipos: { value: string; label: string; icon?: string }[]
  prioridades: { value: string; label: string }[]
  /** So status que tem LANE ativa no quadro: escolher outro faria o item sumir. */
  statusComLane: { value: string; label: string }[]
  membros: { value: string; label: string }[]
  /** Definicoes dos campos complementares filtraveis, por chave `cf:<key>`. */
  camposCompl: Map<string, { key: string; type: string; config: { options?: { value: string; label: string }[] } }>
  /**
   * ETAPA (SPEC-20260916-0120): opções para um item NOVO (etapas iniciais dos
   * fluxos do tipo) e para um item existente (todas as etapas dos fluxos do
   * tipo; o servidor valida a sequência); trocar a etapa move o item para a
   * lane daquela etapa.
   */
  etapasParaNovo: (typeId: string | null) => { value: string; label: string }[]
  etapasParaItem: (typeId: string | null) => { value: string; label: string }[]
  mudarEtapa: (cardId: string, flowStepId: string) => Promise<void>
  /** PATCH generico do item (titulo, tipo, prioridade, responsavel, prazo). */
  gravar: (cardId: string, patch: Record<string, unknown>) => Promise<unknown>
  /** Complementar: grava o mapa inteiro de customValues do item. */
  gravarCompl: (cardId: string, customValues: Record<string, unknown>) => Promise<unknown>
  mudarStatus: (cardId: string, statusId: string) => Promise<unknown>
}

/** Abaixo disto a coluna deixa de ser legivel — o arraste para aqui. */
const LARGURA_MIN_COLUNA = 56

/** Movimento minimo para o arraste na linha virar rolagem, e nao clique. */
const LIMIAR_PAN = 5

/** Coluna ja resolvida para desenho: fixa (sem ) ou complementar. */
type ColunaDaLista = {
  key: string
  rotulo: string
  /** Largura em px: a do catalogo, ou a que a pessoa arrastou. */
  largura: number
  compl?: { key: string; type: string }
}

/**
 * O que a LISTA precisa de um cartao. Tipo estrutural de proposito: o quadro
 * infere BoardCard do payload dentro do componente, e a lista nao pode
 * depender dessa inferencia para existir fora dele.
 */
type KbListaCard = {
  id: string
  code: string
  title: string
  type: { icon: string | null; nameKey: string }
  statusId: string | null
  status?: { isTerminal?: boolean; tone?: string } | null
  assignee?: { name: string } | null
  dueAt: string | null
  priority: { nameKey: string; tone?: string }
  flowStepId?: string | null
  /** ids crus para a grade editar sem mapear pelo catalogo (SPEC-20260915-2048) */
  workItemTypeId?: string | null
  workItemPriorityId?: string | null
  assigneeMemberId?: string | null
  /** JSONB cru do servidor: a grade so o le como objeto depois de checar */
  customValues?: unknown
  /**
   * textos formatados (HTML) — colunas Descrição / Resultado esperado
   * (SPEC-20260915-2250). `null` quando o HTML é pesado demais para o view
   * (SPEC-20260916-1948): fica a flag `…Pesada` e o trecho `…Excerpt`.
   */
  description?: string | null
  expectedResult?: string | null
  descriptionPesada?: boolean
  expectedResultPesada?: boolean
  descriptionExcerpt?: string
  expectedResultExcerpt?: string
}

/** HTML da célula rica, ou o trecho quando o HTML inteiro não veio no view (SPEC-20260916-1948). */
const htmlDaCelula = (card: KbListaCard, campo: 'description' | 'expectedResult'): string =>
  card[campo] ?? (campo === 'description' ? card.descriptionExcerpt : card.expectedResultExcerpt) ?? ''
/** Célula rica cujo HTML não veio inteiro: não edita na linha (gravaria vazio por cima) — abre o item. */
const celulaPesada = (card: KbListaCard, col: { key: string }): boolean =>
  col.key === 'description' ? Boolean(card.descriptionPesada) : col.key === 'expectedResult' ? Boolean(card.expectedResultPesada) : false

/** `customValues` como objeto — o JSONB pode vir null/qualquer coisa. */
const complDoCartao = (card: { customValues?: unknown }): Record<string, unknown> =>
  card.customValues && typeof card.customValues === 'object' && !Array.isArray(card.customValues)
    ? (card.customValues as Record<string, unknown>)
    : {}
const CARD_CAMPOS_PADRAO: CardCampo[] = ['type', 'code', 'priority', 'assignee', 'due', 'description', 'criteria', 'children']

/** Le o filtro fixo do quadro (JSON gravado em kanban_board_filter). */
const parseBoardFilter = (raw: string | null): SavedFilter | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as SavedFilter
  } catch {
    return null
  }
}

type DraftLane = {
  key: string
  id?: string
  name: string
  statusId: string | null
  wipLimit: number | null
  /** As ETAPAS em que a lane atua — N:N, pode ser vazia (2026-08-24). */
  flowStepIds: string[]
  gearOpen?: boolean
}

export function KanbanBoardPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const t = useT()
  const toast = useToast()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const bootstrap = useBootstrap()

  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState<DraftLane[]>([])

  // Ao abrir o DESENHO de um fluxo a partir da configuracao, o rascunho em
  // andamento e guardado; na volta, a configuracao reabre NO MESMO ESTADO
  // (pedido do usuario 2026-08-24). sessionStorage: morre com a aba.
  const chaveRascunho = `orbind.kb.editdraft.${id}`
  useEffect(() => {
    try {
      const salvo = sessionStorage.getItem(chaveRascunho)
      if (salvo) {
        sessionStorage.removeItem(chaveRascunho)
        setDraft(JSON.parse(salvo) as DraftLane[])
        setEditMode(true)
      }
    } catch {
      /* rascunho corrompido: ignora */
    }
  }, [chaveRascunho])
  /**
   * Altura das lanes POR USUARIO (2026-08-31): cada um redimensiona as suas e
   * a escolha fica no navegador, por quadro. Sem redimensionar, a lane cresce
   * ate 6 cards — mesmo que passe da area da tela.
   */
  const [laneAlturas, setLaneAlturas] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem(`orbind.kb.alturas.${id}`) ?? '{}') as Record<string, number>
    } catch {
      return {}
    }
  })
  const salvarLaneAltura = (laneKey: string, px: number | null) => {
    setLaneAlturas((atual) => {
      const novo = { ...atual }
      if (px === null) delete novo[laneKey]
      else novo[laneKey] = px
      localStorage.setItem(`orbind.kb.alturas.${id}`, JSON.stringify(novo))
      return novo
    })
  }
  const laneResize = useRef<{ laneKey: string; y: number; alt: number; atual: number; rolado: number } | null>(null)
  /** Laço do auto-scroll do resize: roda enquanto o ponteiro está no pé da tela. */
  const laneResizeTimer = useRef<number | null>(null)
  const laneResizeUltimoY = useRef(0)

  const [selectedCard, setSelectedCard] = useState<string | null>(null)
  const [draggingCard, setDraggingCard] = useState<string | null>(null)
  const [cardDropLane, setCardDropLane] = useState<string | null>(null)
  // posicao de insercao do cartao na lane de destino: 0 = antes do primeiro,
  // cards.length = depois do ultimo
  const [cardDropIndex, setCardDropIndex] = useState<number | null>(null)
  const [draggingLane, setDraggingLane] = useState<string | null>(null)
  /**
   * AUTO-SCROLL no arrasto (2026-09-01): levando um card (ou uma lane) ate a
   * borda direita/inferior do quadro, ele rola sozinho para alcancar colunas
   * fora da tela; esquerda/topo rolam de volta. O dragover do documento e a
   * unica fonte confiavel de posicao durante um arraste HTML5 — os alvos de
   * dentro cortam a propagacao, entao o listener vai na fase de captura.
   */
  const arrastandoAlgoParaScroll = draggingCard !== null || draggingLane !== null
  useEffect(() => {
    if (!arrastandoAlgoParaScroll) return
    const aoArrastar = (e: DragEvent) => {
      const rolagem = lanesRef.current
      if (!rolagem) return
      const borda = rolagem.getBoundingClientRect()
      const MARGEM = 56
      const PASSO = 18
      if (e.clientX > borda.right - MARGEM) rolagem.scrollLeft += PASSO
      else if (e.clientX < borda.left + MARGEM) rolagem.scrollLeft -= PASSO
      if (e.clientY > borda.bottom - MARGEM) rolagem.scrollTop += PASSO
      else if (e.clientY < borda.top + MARGEM) rolagem.scrollTop -= PASSO
    }
    document.addEventListener('dragover', aoArrastar, true)
    return () => document.removeEventListener('dragover', aoArrastar, true)
  }, [arrastandoAlgoParaScroll])
  const [laneDropIndex, setLaneDropIndex] = useState<number | null>(null)
  // ficha de ETAPA arrastada entre lanes direto no quadro (modo edicao)
  const [draggingEtapa, setDraggingEtapa] = useState<{ stepId: string; deKey: string } | null>(null)
  const [etapaDropLane, setEtapaDropLane] = useState<string | null>(null)
  /** Posicao de insercao da ficha na lane de destino (marcador visual). */
  const [etapaDropIndex, setEtapaDropIndex] = useState<number | null>(null)
  // Lane "armada" pelo mousedown no grip: a SECAO inteira vira o alvo do drag
  // (o desenho arrastado e a lane toda, com sombra forte), sem atrapalhar a
  // edicao do nome no input.
  const [armedLane, setArmedLane] = useState<string | null>(null)
  // composer de inclusao de item: um "cartao em branco" na propria lane com
  // tipo, titulo, descricao e a demanda que da elegibilidade ao quadro
  const [composerLane, setComposerLane] = useState<string | null>(null)
  // `isPrivate`: item privado so aparece para responsavel, membros e admin
  // (pedido 2026-08-30). Padrao publico.
  const [composer, setComposer] = useState({ typeId: '', title: '', description: '', flowStepId: '', isPrivate: false })

  // modal "Alterar tela de fundo" (estilo Trello: cores + personalizar)
  const [bgOpen, setBgOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  // fundo escuro -> letras claras no cabecalho; fundo claro -> letras pretas
  const [bgDark, setBgDark] = useState(false)

  // menu da engrenagem (editar quadro / alterar fundo / editar lanes) e o
  // modal de edicao dos metadados do quadro
  const [gearMenuOpen, setGearMenuOpen] = useState(false)
  // Toda a configuracao das etapas acontece NA AREA DE DESENHO (pedido do
  // usuario 2026-08-24): no modo de edicao, a primeira coluna do quadro e o
  // painel "Etapas nao mapeadas" (acordeao por fluxo) — arrasta de la para
  // as lanes e de volta.
  /** Fluxos RECOLHIDOS no acordeao de etapas (comecam todos abertos). */
  const [fluxosFechados, setFluxosFechados] = useState<Set<string>>(new Set())
  // edicao inline do titulo do quadro (clique no titulo; salva no blur/check)
  const [titleEditing, setTitleEditing] = useState(false)
  // modal da DESCRICAO do quadro (botao ao lado do titulo na configuracao);
  // a descricao aparece num "i" ao lado do nome
  const [descricaoOpen, setDescricaoOpen] = useState(false)
  const [descricaoDraft, setDescricaoDraft] = useState('')
  const [titleDraft, setTitleDraft] = useState('')
  // menu de troca do DONO do quadro (chip ao lado do nome)
  // popover "Dono e membros" (estilo Trello, no canto direito do cabecalho)
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false)
  /** Diálogo COMPARTILHAR no estilo do Google (SPEC-20260916-1527) — substitui a popover de membros. */
  const [compartilharAberto, setCompartilharAberto] = useState(false)
  const [membroBusca, setMembroBusca] = useState('')
  /** Lista de escolha aberta pelo "+": adiciona donos ou membros (2026-08-31). */
  const [membroPicker, setMembroPicker] = useState<null | 'dono' | 'membro'>(null)
  const membroBuscaRef = useRef<HTMLInputElement>(null)
  // Agrupamento (swimlanes, estilo Jira/Plane): 'none' | 'member' | 'area' |
  // 'company' | 'type:<code>'. Preferencia POR USUARIO, guardada por quadro
  // (2026-08-27). As bandas recolhidas ficam em bandasFechadas.
  const [agruparPor, setAgruparPor] = useState<string>(() => {
    try {
      return localStorage.getItem(`orbind.kb.groupby.${id}`) ?? 'none'
    } catch {
      return 'none'
    }
  })
  /**
   * BANDAS RECOLHIDAS, lembradas (pedido 2026-09-21).
   *
   * Vale por quadro E por agrupamento: as chaves das bandas são os valores do
   * atributo agrupado, então "Vendas" recolhido em "Agrupar por área" não diz
   * nada sobre o mesmo quadro agrupado por pessoa. Uma chave só misturaria os
   * dois e reabriria banda errada.
   *
   * Fica no navegador, ao lado do agrupamento escolhido e da altura do
   * cabeçalho, que já moram lá desde 2026-08-27.
   */
  const [bandasFechadas, setBandasFechadas] = useState<Set<string>>(() => lerBandasFechadas(id, agruparPor))
  /* Trocar de agrupamento troca o conjunto inteiro — cada um tem o seu. */
  useEffect(() => {
    setBandasFechadas(lerBandasFechadas(id, agruparPor))
  }, [id, agruparPor])
  /**
   * Grava junto com o estado, e não por efeito: um efeito que observasse
   * `bandasFechadas` e `agruparPor` dispararia na TROCA de agrupamento e
   * escreveria o conjunto antigo na chave do novo.
   */
  const definirBandasFechadas = (proximo: Set<string>) => {
    setBandasFechadas(proximo)
    guardarBandasFechadas(id, agruparPor, proximo)
  }
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.groupby.${id}`, agruparPor)
    } catch {
      /* localStorage indisponivel: agrupamento so nao persiste */
    }
  }, [agruparPor, id])

  /**
   * MODO DE VISUALIZACAO (SPEC-20260914-2210): o mesmo quadro visto como
   * colunas ou como tabela. Preferencia por QUADRO, igual ao agrupamento —
   * quem usa um quadro de acompanhamento em lista costuma querer os outros
   * em colunas.
   */
  const [modoVisao, setModoVisao] = useState<'quadro' | 'lista' | 'planilha' | 'tempo'>(() => {
    try {
      const guardado = localStorage.getItem(`orbind.kb.view.${id}`)
      /*
       * O que estava gravado como "lista" era a PLANILHA: ela se chamou Lista
       * ate 2026-10-03, quando a Lista de verdade entrou no meio
       * (SPEC-20261003-1059). Quem deixou o quadro na planilha tem de
       * reencontrar a planilha, e nao uma tela que nunca viu.
       */
      if (guardado === 'lista' || guardado === 'planilha') return 'planilha'
      if (guardado === 'listaitens') return 'lista'
      // a LINHA DO TEMPO dos itens do quadro (SPEC-20261009-2112-linha-do-tempo-no-quadro)
      if (guardado === 'tempo') return 'tempo'
      return 'quadro'
    } catch {
      return 'quadro'
    }
  })
  useEffect(() => {
    try {
      // 'lista' nao pode ser gravado: na releitura ele significa PLANILHA, pelo
      // historico do nome. A Lista nova grava com outro nome.
      localStorage.setItem(`orbind.kb.view.${id}`, modoVisao === 'lista' ? 'listaitens' : modoVisao)
    } catch {
      /* sem storage: o modo vale ate recarregar */
    }
  }, [modoVisao, id])
  /** Coluna e sentido da ordenacao da lista (so em memoria). */
  /**
   * Ordenação da lista. `manual` = a ordem do próprio quadro (lane, depois
   * sortOrder) — é nela que arrastar a linha faz sentido, e arrastar liga ela
   * sozinha (decisão do usuário, 2026-09-14 23:10).
   */
  const [ordemLista, setOrdemLista] = useState<{ col: ColunaLista | 'manual'; desc: boolean }>(() => {
    try {
      const bruto = localStorage.getItem(`orbind.kb.sort.${id}`)
      const lido = bruto ? (JSON.parse(bruto) as unknown) : null
      if (lido && typeof lido === 'object' && typeof (lido as { col?: unknown }).col === 'string') {
        return { col: (lido as { col: ColunaLista | 'manual' }).col, desc: Boolean((lido as { desc?: unknown }).desc) }
      }
    } catch {
      /* preferencia ilegivel: cai no padrao */
    }
    return { col: 'code', desc: false }
  })
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.sort.${id}`, JSON.stringify(ordemLista))
    } catch {
      /* sem storage: vale ate recarregar */
    }
  }, [ordemLista, id])
  /**
   * COLUNAS DA LISTA (SPEC-20260914-2231): quais aparecem e em que ordem.
   * Guarda só as chaves escolhidas — coluna nova (campo complementar criado
   * depois) entra no fim sem invalidar o que já estava salvo.
   */
  /**
   * COLUNAS DA VISÃO (SPEC-20261103-1217, SPEC-20261103-1418): o editor de cada
   * visão do quadro escolhe as colunas da LISTA (a mesma tela da lista de Itens) —
   * no mesmo catálogo dela, partindo das colunas que a pessoa vê agora.
   */
  const { catalogo: catalogoDaLista } = useCatalogoDeColunas()
  const padraoDaLista = useMemo(() => padraoDaTabela(catalogoDaLista), [catalogoDaLista])
  const [tabelaDaLista] = usePreferenciaEstruturada<EstadoTabela>('wi.colunas', padraoDaLista, (bruto) =>
    adotarTabela(bruto, catalogoDaLista, true),
  )
  const colunasAtuaisDaLista = colunasLigadas(adotarTabela(tabelaDaLista, catalogoDaLista, true)?.cols ?? tabelaDaLista.cols)

  const [colunasLista, setColunasLista] = useState<string[]>(() => {
    try {
      const bruto = localStorage.getItem(`orbind.kb.cols.${id}`)
      const lido = bruto ? (JSON.parse(bruto) as unknown) : null
      if (Array.isArray(lido) && lido.every((x) => typeof x === 'string') && lido.length > 0) return lido as string[]
    } catch {
      /* preferencia ilegivel: cai no padrao */
    }
    return COLUNAS_LISTA.map((c) => c.key).filter((k) => !COLUNAS_FORA_DO_PADRAO.includes(k))
  })
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.cols.${id}`, JSON.stringify(colunasLista))
    } catch {
      /* sem storage: vale ate recarregar */
    }
  }, [colunasLista, id])
  const [seletorColunas, setSeletorColunas] = useState(false)
  /**
   * TELA CHEIA (SPEC-20260915-2116): o shell esconde rail/explorer e o quadro
   * ocupa a janela; o cabeçalho vira barra de ferramentas e o seletor de
   * colunas da lista é portalado para o slot dela. Não é lembrada: recarregar
   * volta ao normal.
   */
  // Lembrada na SESSÃO do navegador, por quadro (SPEC-20260915-2231): abrir um
  // item e voltar devolve a planilha como estava; fechar a aba esquece.
  const [telaCheia, setTelaCheia] = useState(() => {
    try {
      return sessionStorage.getItem(`orbind.kb.cheia.${id}`) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      if (telaCheia) sessionStorage.setItem(`orbind.kb.cheia.${id}`, '1')
      else sessionStorage.removeItem(`orbind.kb.cheia.${id}`)
    } catch {
      /* sem storage: vale até sair da tela */
    }
  }, [telaCheia, id])
  const [slotDaBarra, setSlotDaBarra] = useState<HTMLElement | null>(null)
  /**
   * CARA DE PLANILHA (SPEC-20260915-2149): em tela cheia + Lista a tela se
   * veste de planilha do Google — barra de ícones, barra de fórmula, abas.
   * Zoom e tamanho da letra da grade são reais (variáveis CSS); não persistem.
   */
  const [zoomPlanilha, setZoomPlanilha] = useState(100)
  const [fontePlanilha, setFontePlanilha] = useState(13)
  /**
   * FORMATAÇÃO PRIVADA × PÚBLICA (SPEC-20260916-0102). A formatação da lista
   * (colunas, larguras, alturas, ordenação, agrupamento, zoom, letra) é de quem
   * olha (localStorage). O dono pode PUBLICAR a dele no quadro; quem está no
   * modo "pública" recebe; mudar qualquer coisa vira "privada" sozinho.
   */
  const [fmtModo, setFmtModo] = useState<'privada' | 'publica'>(() => {
    try {
      return localStorage.getItem(`orbind.kb.fmtmodo.${id}`) === 'privada' ? 'privada' : 'publica'
    } catch {
      return 'publica'
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.fmtmodo.${id}`, fmtModo)
    } catch {
      /* sem storage */
    }
  }, [fmtModo, id])
  const [fmtMenu, setFmtMenu] = useState(false)
  const [publicando, setPublicando] = useState(false)
  /**
   * ESTILO DE TABELA (SPEC-20260916-0131): a galeria "Formatar como Tabela" do
   * Excel — Clara/Média/Escura × cor. Faz parte da formatação (privada/pública).
   */
  const [estiloPlanilha, setEstiloPlanilha] = useState<string>(() => {
    try {
      return localStorage.getItem(`orbind.kb.estilo.${id}`) ?? ''
    } catch {
      return ''
    }
  })
  useEffect(() => {
    try {
      if (estiloPlanilha) localStorage.setItem(`orbind.kb.estilo.${id}`, estiloPlanilha)
      else localStorage.removeItem(`orbind.kb.estilo.${id}`)
    } catch {
      /* sem storage */
    }
  }, [estiloPlanilha, id])
  const [galeriaMenu, setGaleriaMenu] = useState(false)
  type FormatoDaLista = {
    colunas: string[]
    larguras: Record<string, number>
    alturas: Record<string, number>
    ordem: { col: string; desc: boolean }
    agruparPor: string
    subAgruparPor: string
    sub2AgruparPor: string
    zoom: number
    fonte: number
    estilo: string
    /** altura do cabeçalho da planilha em px; 0 = padrão (SPEC-20260916-0943) */
    alturaCab: number
  }
  const formatoAtual = (): FormatoDaLista => ({
    colunas: colunasLista,
    larguras: largurasLista,
    alturas: alturasLinha,
    ordem: ordemLista,
    agruparPor,
    subAgruparPor,
    sub2AgruparPor,
    zoom: zoomPlanilha,
    fonte: fontePlanilha,
    estilo: estiloPlanilha,
    alturaCab: alturaCabecalho,
  })
  const assinaturaDoFormato = (f: Partial<FormatoDaLista> | null | undefined) =>
    f
      ? JSON.stringify([f.colunas ?? [], f.larguras ?? {}, f.alturas ?? {}, f.ordem ?? null, f.agruparPor ?? 'none', f.subAgruparPor ?? 'none', f.sub2AgruparPor ?? 'none', f.zoom ?? 100, f.fonte ?? 13, f.estilo ?? '', f.alturaCab ?? 0])
      : ''
  const aplicarFormato = (f: Partial<FormatoDaLista>) => {
    setEstiloPlanilha(f.estilo ?? '')
    setAlturaCabecalho(f.alturaCab ?? 0)
    setColunasLista(f.colunas ?? [])
    setLargurasLista(f.larguras ?? {})
    setAlturasLinha(f.alturas ?? {})
    setOrdemLista((f.ordem as { col: ColunaLista | 'manual'; desc: boolean } | undefined) ?? { col: 'code', desc: false })
    setAgruparPor(f.agruparPor ?? 'none')
    setSubAgruparPor(f.subAgruparPor ?? 'none')
    setSub2AgruparPor(f.sub2AgruparPor ?? 'none')
    setZoomPlanilha(f.zoom ?? 100)
    setFontePlanilha(f.fonte ?? 13)
  }
  const [zoomMenu, setZoomMenu] = useState(false)
  /**
   * FILTROS POR COLUNA (estilo Excel) vivem aqui, não na lista, para o menu do
   * funil da barra do Sheets enxergar e remover (SPEC-20260915-2245).
   * `filtrosLigados` = as setas de filtro aparecem nos cabeçalhos.
   */
  const [filtrosColuna, setFiltrosColuna] = useState<Map<string, Set<string>>>(() => new Map())
  const [filtrosLigados, setFiltrosLigados] = useState(true)
  const [funilMenu, setFunilMenu] = useState(false)
  /**
   * TEXTO FORMATADO (SPEC-20260915-2250): enquanto uma célula rica está em
   * edição, os botões de formatação da barra ficam vivos e agem no editor.
   */
  const [edicaoRica, setEdicaoRica] = useState(false)
  const [corMenu, setCorMenu] = useState(false)
  const [fundoMenu, setFundoMenu] = useState(false)
  const inputImagemRef = useRef<HTMLInputElement | null>(null)
  const editorDaImagemRef = useRef<HTMLElement | null>(null)
  /** EXPORTAR PARA O EXCEL (SPEC-20260916-0035): a lista preenche; a barra chama. */
  const exportarRef = useRef<((nomeDoArquivo: string) => Promise<{ linhas: number; colunas: string[] }>) | null>(null)
  const [exportando, setExportando] = useState(false)
  /** Menu "visualizações de tabela" da barra do Sheets: agrupamento em 3 níveis (SPEC-20260915-2223). */
  const [tabelaMenu, setTabelaMenu] = useState<null | 'root' | 'n1' | 'n1tipo' | 'n2' | 'n3'>(null)
  const publicarTelaCheia = useSetBoardTelaCheia()
  useEffect(() => {
    publicarTelaCheia(telaCheia)
    // sair da tela do quadro devolve o shell ao normal
    return () => publicarTelaCheia(false)
  }, [telaCheia, publicarTelaCheia])
  // PLANILHA DENTRO DA LISTA (SPEC-20260916-0938): o modo Lista É a planilha
  // (barra do Sheets, cabeçalho de linha, abas) mesmo sem tela cheia — a tela
  // cheia só tira o resto do Orbind da frente (pedido 2026-09-16 09:38:
  // "colocar a mesma planilha quando expandida mas só aqui dentro da lista")
  const planilha = modoVisao === 'planilha' && !editMode
  useEffect(() => {
    if (!telaCheia) return
    // Esc volta ao normal — só quando ninguém por cima consumiu (editor da
    // célula, menus e modais param a propagação)
    const sair = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      const alvo = e.target as HTMLElement | null
      if (alvo?.closest('input, textarea, [contenteditable], [role="dialog"], .kbl__cel--edit, .kb__ownermenu, .kbl__colmenu, .kbl__filtromenu')) return
      setTelaCheia(false)
    }
    window.addEventListener('keydown', sair)
    return () => window.removeEventListener('keydown', sair)
  }, [telaCheia])
  /**
   * LARGURA DAS COLUNAS (SPEC-20260914-2256): mapa chave→px, separado da ordem
   * para que mover a coluna não jogue fora a largura que a pessoa ajustou.
   */
  const [largurasLista, setLargurasLista] = useState<Record<string, number>>(() => {
    try {
      const bruto = localStorage.getItem(`orbind.kb.colw.${id}`)
      const lido = bruto ? (JSON.parse(bruto) as unknown) : null
      if (lido && typeof lido === 'object' && !Array.isArray(lido)) {
        const so: Record<string, number> = {}
        for (const [k, v] of Object.entries(lido as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v) && v >= LARGURA_MIN_COLUNA) so[k] = v
        }
        return so
      }
    } catch {
      /* preferencia ilegivel: cai nas larguras do catalogo */
    }
    return {}
  })
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.colw.${id}`, JSON.stringify(largurasLista))
    } catch {
      /* sem storage: vale ate recarregar */
    }
  }, [largurasLista, id])
  /**
   * ALTURA DAS LINHAS (SPEC-20260915-2214): mapa item→px, por quadro, como a
   * largura das colunas. Linha sem entrada usa a altura padrão da grade.
   */
  const [alturasLinha, setAlturasLinha] = useState<Record<string, number>>(() => {
    try {
      const bruto = localStorage.getItem(`orbind.kb.rowh.${id}`)
      const lido = bruto ? (JSON.parse(bruto) as unknown) : null
      if (lido && typeof lido === 'object' && !Array.isArray(lido)) {
        const so: Record<string, number> = {}
        for (const [k, v] of Object.entries(lido as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v) && v >= ALTURA_MIN_LINHA) so[k] = v
        }
        return so
      }
    } catch {
      /* preferencia ilegivel: alturas padrao */
    }
    return {}
  })
  useEffect(() => {
    try {
      localStorage.setItem(`orbind.kb.rowh.${id}`, JSON.stringify(alturasLinha))
    } catch {
      /* sem storage: vale ate recarregar */
    }
  }, [alturasLinha, id])
  /**
   * ALTURA DO CABEÇALHO da planilha (SPEC-20260916-0943): px ou 0 = padrão;
   * por quadro, como as alturas de linha. Arrastar a borda inferior do canto grava.
   */
  const [alturaCabecalho, setAlturaCabecalho] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(`orbind.kb.headh.${id}`) ?? 0)
      return Number.isFinite(v) && v >= ALTURA_MIN_LINHA ? v : 0
    } catch {
      return 0
    }
  })
  useEffect(() => {
    try {
      if (alturaCabecalho) localStorage.setItem(`orbind.kb.headh.${id}`, String(alturaCabecalho))
      else localStorage.removeItem(`orbind.kb.headh.${id}`)
    } catch {
      /* sem storage */
    }
  }, [alturaCabecalho, id])
  const alternarBanda = (key: string) => {
    const proximo = new Set(bandasFechadas)
    if (proximo.has(key)) proximo.delete(key)
    else proximo.add(key)
    definirBandasFechadas(proximo)
  }
  /**
   * Recolhe (ou expande) TODOS os filhos de um grupo de uma vez — pedido do
   * usuário em 2026-09-22.
   *
   * A setinha de cada subgrupo já existia, mas um grupo com dez subgrupos
   * exigia dez cliques para chegar à visão geral, que é justamente o motivo de
   * alguém recolher.
   *
   * Só expande quando TODOS estão recolhidos: com a maioria aberta, o gesto
   * esperado é fechar o que sobrou, não abrir tudo de novo.
   */
  const alternarFilhos = (chavesDosFilhos: string[]) =>
    definirBandasFechadas(alternarTodosOsFilhos(bandasFechadas, chavesDosFilhos))
  // menu do "Agrupar por": 'root' (opcoes) ou 'tipo' (sub-lista de tipos)
  const [agruparMenu, setAgruparMenu] = useState<null | 'root' | 'tipo'>(null)
  /**
   * SUB-AGRUPAMENTO dentro da banda do item (2026-08-30). Vale só quando o
   * quadro está agrupado por Tipo de Item — é lá que existe uma banda por item,
   * e portanto algo para subdividir. Lembrado por quadro, como o agrupamento.
   */
  const [subAgruparPor, setSubAgruparPor] = useState<string>(
    () => localStorage.getItem(`orbind.kb.subgroupby.${id}`) ?? 'checklist',
  )
  useEffect(() => {
    localStorage.setItem(`orbind.kb.subgroupby.${id}`, subAgruparPor)
  }, [subAgruparPor, id])
  const [subMenuAberto, setSubMenuAberto] = useState(false)
  /**
   * TERCEIRO nível (2026-08-31): dentro da sub-faixa (normalmente o checklist),
   * ainda dá para separar por membro, responsável, área ou empresa. Os três
   * níveis se empilham — um não substitui o outro.
   */
  const [sub2AgruparPor, setSub2AgruparPor] = useState<string>(
    () => localStorage.getItem(`orbind.kb.subgroupby2.${id}`) ?? 'none',
  )
  useEffect(() => {
    localStorage.setItem(`orbind.kb.subgroupby2.${id}`, sub2AgruparPor)
  }, [sub2AgruparPor, id])
  const [sub2MenuAberto, setSub2MenuAberto] = useState(false)
  /**
   * Critério repetido de um nível acima não separa nada — a faixa filha seria
   * uma só, com o mesmo conteúdo do pai. Vale como "sem agrupamento" aqui, em
   * vez de eu ter de lembrar do caso em cada ponto de uso (2026-09-15).
   */
  const subEfetivo = subAgruparPor === agruparPor ? 'none' : subAgruparPor
  const sub2Efetivo =
    sub2AgruparPor === agruparPor || sub2AgruparPor === subEfetivo || subEfetivo === 'none'
      ? 'none'
      : sub2AgruparPor
  const session = useSession()
  // "Adicionar filtro" no desenho (modo de configuracao): menu raiz com as
  // mesmas opcoes do Editar quadro; listas simples viram submenu, as
  // grandes (demanda, produto, filtro fixo) abrem modal
  const [filtroMenu, setFiltroMenu] = useState<null | 'root' | 'area' | 'tipo' | 'membro' | 'fluxo' | 'empresa'>(null)
  const [filtroModal, setFiltroModal] = useState<null | 'produto' | 'filtro'>(null)
  /** Rascunho das listas suspensas: so aplica no botao Aplicar. */
  const [listaDraft, setListaDraft] = useState<string[]>([])
  /**
   * Menu de escolha dos CAMPOS do card (no titulo "Modelo do card"). Guarda o
   * BOTAO da lane que abriu o menu: um menu so por vez, ancorado nele. Antes
   * era um booleano unico e cada lane renderizava o proprio menu — com ele
   * ligado, abriam os menus de TODAS as lanes ao mesmo tempo, empilhados.
   */
  const [camposAnchor, setCamposAnchor] = useState<HTMLElement | null>(null)
  /** Posicao FIXA do menu de campos (portal no body: fora da lane, sem cortes). */
  const [camposPos, setCamposPos] = useState<{ left: number; top?: number; bottom?: number; maxH: number } | null>(null)
  const camposPopRef = useRef<HTMLDivElement | null>(null)
  /** Rascunho local dos campos: cliques seguidos nao se atropelam. */
  const [camposDraft, setCamposDraft] = useState<CardCampo[] | null>(null)
  /** Espelho sincrono do rascunho (cliques na MESMA renderizacao). */
  const camposDraftRef = useRef<CardCampo[] | null>(null)
  // sair da edicao do quadro fecha o menu de campos
  useEffect(() => {
    if (!editMode) setCamposAnchor(null)
  }, [editMode])
  // menu de campos aberto: posiciona logo abaixo do botao (acima se nao
  // couber), sempre dentro da tela; acompanha rolagem/redimensionamento e
  // fecha com Esc ou clique fora
  useLayoutEffect(() => {
    if (!camposAnchor) {
      setCamposPos(null)
      return
    }
    const LARGURA = 260
    const MARGEM = 8
    const posicionar = () => {
      if (!camposAnchor.isConnected) {
        setCamposAnchor(null)
        return
      }
      const r = camposAnchor.getBoundingClientRect()
      // alinhado pela direita do botao (o icone fica no canto direito da lane)
      const left = Math.max(MARGEM, Math.min(r.right - LARGURA, window.innerWidth - LARGURA - MARGEM))
      const abaixo = window.innerHeight - r.bottom - 6 - MARGEM
      const acima = r.top - 6 - MARGEM
      if (abaixo >= 240 || abaixo >= acima) {
        setCamposPos({ left, top: r.bottom + 6, maxH: Math.max(120, Math.min(440, abaixo)) })
      } else {
        setCamposPos({ left, bottom: window.innerHeight - r.top + 6, maxH: Math.max(120, Math.min(440, acima)) })
      }
    }
    posicionar()
    const fechar = () => setCamposAnchor(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar()
    }
    const onPointerDown = (e: PointerEvent) => {
      const alvo = e.target as Node | null
      if (!alvo) return
      // o proprio botao alterna no onClick; dentro do menu nao fecha
      if (camposPopRef.current?.contains(alvo) || camposAnchor.contains(alvo)) return
      fechar()
    }
    window.addEventListener('resize', posicionar)
    window.addEventListener('scroll', posicionar, true)
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('resize', posicionar)
      window.removeEventListener('scroll', posicionar, true)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [camposAnchor])
  const [produtoDraft, setProdutoDraft] = useState<string[]>([])
  const [filtroDraft, setFiltroDraft] = useState<SavedFilter | null>(null)
  /**
   * FILTRO RÁPIDO (SPEC-20261104-1557-filtro-rapido-do-quadro, pedido do Cristian): o funil
   * voltou ao cabeçalho para montar e aplicar um filtro na hora, SEM salvar —
   * vale só nesta sessão, por cima da visão escolhida. Para guardar, existe a
   * visão.
   */
  const [filtroRapido, setFiltroRapido] = useState<SavedFilter | null>(null)
  const [filtroRapidoDraft, setFiltroRapidoDraft] = useState<SavedFilter | null>(null)
  const [filtroRapidoAberto, setFiltroRapidoAberto] = useState(false)
  /**
   * Campos PADRÃO do filtro rápido (SPEC-20261104-1646-filtro-rapido-com-campos-padrao): Status,
   * Etapa, Responsável, Membros, Aplicação e Área como seletores diretos;
   * o montador de critérios fica em "Avançado". Cada seletor vira um
   * critério `in` com id `rapido:<campo>`.
   */
  const [rapidosDraft, setRapidosDraft] = useState<Record<string, string[]>>({})
  const [avancadoAberto, setAvancadoAberto] = useState(false)
  /**
   * PERÍODO no cabeçalho (SPEC-20261104-1638-filtro-por-periodo-de-conclusao-e-criacao, pedido do
   * Cristian): combo "por fora" do editor — Hoje, Esta semana, Este mês,
   * Últimos 7/30 dias ou Personalizado (início e fim opcionais) — sobre a
   * Data de conclusão ou a Data de criação. Só nesta sessão, como o filtro rápido.
   */
  const [periodo, setPeriodo] = useState<{ campo: CampoDePeriodo; preset: PresetDePeriodo; de: string; ate: string }>({
    campo: 'completedAt',
    preset: '',
    de: '',
    ate: '',
  })

  /**
   * O filtro aplicado FICA guardado, agora na CONTA da pessoa
   * (SPEC-20261006-1027-filtro-escolhido-fica) e nao mais no navegador: reabrir o quadro de outro
   * computador volta com o mesmo filtro, e dois colegas na mesma maquina nao
   * herdam o filtro um do outro.
   *
   * Um mapa por quadro numa chave so: cada quadro tem o seu, e o campo de
   * preferencias tem teto de chaves.
   */
  const [filtroPorQuadro, setFiltroPorQuadro] = usePreferenciaEstruturada<{
    v: 1
    porQuadro: Record<string, string>
  }>('kb.filtro', { v: 1, porQuadro: {} }, (bruto) => {
    if (typeof bruto !== 'object' || bruto === null) return null
    const v = bruto as { v?: unknown; porQuadro?: unknown }
    if (v.v !== 1 || typeof v.porQuadro !== 'object' || v.porQuadro === null) return null
    const porQuadro: Record<string, string> = {}
    for (const [quadro, filtro] of Object.entries(v.porQuadro as Record<string, unknown>)) {
      if (typeof filtro === 'string') porQuadro[quadro] = filtro
    }
    return { v: 1, porQuadro }
  })
  const activeCardFilterId = filtroPorQuadro.porQuadro[id] ?? null
  const setActiveCardFilterId = (filtro: string | null) => {
    const porQuadro = { ...filtroPorQuadro.porQuadro }
    if (filtro) porQuadro[id] = filtro
    else delete porQuadro[id]
    setFiltroPorQuadro({ v: 1, porQuadro })
  }
  /**
   * VISÕES do quadro (SPEC-20261103-1418): filtro + colunas e ordenação da
   * lista + tipo de visualização, privadas, compartilhadas ou públicas. Os
   * filtros (do navegador e os padrão do dono) viraram visões com o mesmo id,
   * então a escolhida continua guardada em `kb.filtro`.
   */
  const visoesDoQuadro = useVisoes(id)
  const { criar: criarVisao, alterar: alterarVisao, apagar: apagarVisao } = useMutacoesDeVisao(id)
  const listaDeVisoes = visoesDoQuadro.data ?? []
  const visaoAtiva = listaDeVisoes.find((v) => v.id === activeCardFilterId) ?? null
  const [editandoVisao, setEditandoVisao] = useState<Visao | 'nova' | null>(null)
  /**
   * Busca DENTRO do quadro (2026-09-02; passou a FILTRAR em 2026-10-02).
   *
   * Realcava quem casava e apagava o resto, sem tirar nada do lugar. Cristian
   * pediu o contrario: quem nao casa SAI, para sobrar so o que interessa.
   */
  const [buscaCards, setBuscaCards] = useState('')
  /**
   * Os filtros que ainda moram NO NAVEGADOR sobem como visões privadas, com o
   * mesmo id, e a chave local é apagada (SPEC-20261103-1418). Sem isto quem
   * tinha filtros os veria sumir. Só apaga se todos subiram.
   */
  const subiuFiltrosLocais = useRef<string | null>(null)
  useEffect(() => {
    if (!visoesDoQuadro.data || subiuFiltrosLocais.current === id) return
    subiuFiltrosLocais.current = id
    let locais: unknown = []
    try {
      locais = JSON.parse(localStorage.getItem(cardFiltersKey(id)) ?? '[]')
    } catch {
      locais = []
    }
    if (!Array.isArray(locais) || locais.length === 0) return
    void (async () => {
      let todos = true
      for (const f of locais as SavedFilter[]) {
        if (!f || typeof f.name !== 'string' || !Array.isArray(f.criteria)) continue
        const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(f.id) ? f.id : undefined
        await criarVisao
          .mutateAsync({
            ...(uuid ? { id: uuid } : {}),
            name: f.name.trim().slice(0, 80) || 'Sem nome',
            visibility: 'private',
            publicRole: 'viewer',
            shares: [],
            config: { filtro: filtroParaGuardar(f), ...(f.columns ? { colunas: f.columns } : {}) },
          })
          .catch(() => {
            todos = false
          })
      }
      if (todos) localStorage.removeItem(cardFiltersKey(id))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visoesDoQuadro.data, id])

  /**
   * O que estava guardado no navegador sobe uma vez e a chave local e apagada.
   * Sem isto quem tinha um filtro aplicado o veria sumir sozinho.
   */
  const migrouFiltro = useRef(false)
  useEffect(() => {
    if (migrouFiltro.current) return
    migrouFiltro.current = true
    const legado = localStorage.getItem(`orbind.kanban.${id}.filtroAtivo`)
    if (!legado) return
    localStorage.removeItem(`orbind.kanban.${id}.filtroAtivo`)
    if (!filtroPorQuadro.porQuadro[id]) setActiveCardFilterId(legado)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  const [editBoardOpen, setEditBoardOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    areaIds: [] as string[],
    ownerMemberId: '',
    /** Restricoes opcionais do quadro: vazias = mostra todos os itens. */
    workItemTypeIds: [] as string[],
    memberIds: [] as string[],
    portfolioNodeIds: [] as string[],
    companyIds: [] as string[],
    /** Fluxos que o quadro utiliza (SPEC-20260815-1204). */
    flowIds: [] as string[],
    filter: null as SavedFilter | null,
    /** Itens criados no quadro nascem privados (SPEC-20261007-0934-visibilidade-padrao-do-quadro). */
    newItemsPrivate: false,
    /** Itens criados no quadro aparecem só nele (SPEC-20261007-1538-quadros-do-item). */
    newItemsExclusive: false,
  })
  /** Snapshot do formulario ao abrir a edicao — base do dirty check. */
  const [editBase, setEditBase] = useState<typeof editForm | null>(null)
  // fluxos do workspace, para a secao "Fluxos do quadro" da edicao

  const fluxos = useQuery({
    queryKey: ['flows'],
    queryFn: async () => (await api.flows.$get()).json(),
    // tambem no modo de configuracao do desenho (chips/Adicionar filtro)
    enabled: editBoardOpen || editMode,
    retry: false,
  })
  // arvore do portfolio: edicao do quadro E resolucao do campo Produto no card
  const portfolio = useQuery({
    queryKey: ['portfolio', 'tree'],
    queryFn: async () => (await api.portfolio.tree.$get()).json(),
  })
  /** Nome de cada nó do portfólio — o filtro por "Item do portfólio". */
  const nomeDoNoDoPortfolio = useMemo(() => {
    const mapa = new Map<string, string>()
    const andar = (nos: readonly { id: string; name: string; children?: unknown }[]) => {
      for (const no of nos) {
        mapa.set(no.id, no.name)
        andar((no.children ?? []) as never)
      }
    }
    andar((portfolio.data?.nodes ?? []) as never)
    return mapa
  }, [portfolio.data])

  // Pan do quadro: mousedown no FUNDO (fora de lane/botao) + arrastar move o
  // scroll horizontal das lanes, como em ferramentas de quadro.
  const lanesRef = useRef<HTMLDivElement>(null)
  const panRef = useRef<{ startX: number; scrollLeft: number } | null>(null)

  // Fantasma do arraste de lane: clone translucido da lane INTEIRA que segue
  // o cursor (o desenho nativo do navegador e suprimido no dragstart).
  const dragGhostRef = useRef<HTMLElement | null>(null)
  const dragGhostOffset = useRef({ x: 0, y: 0 })

  // Um quadro reflete mudancas feitas em OUTROS quadros (etapa comum entre
  // fluxos, pino sobreposto pela etapa). Por isso a view se atualiza ao focar a
  // aba e num intervalo leve; o BroadcastChannel abaixo cobre o caso instantaneo
  // entre abas. (pedido 2026-08-26)
  const board = useQuery({
    queryKey: ['kanban', id, 'view'],
    queryFn: async () => (await api.kanbans[':id'].view.$get({ param: { id }, query: {} })).json(),
    refetchOnWindowFocus: true,
    refetchInterval: 20_000,
  })
  // campos complementares do item — viram campos do construtor de filtros
  const camposCompl = useCustomFields('work_item')
  // etiquetas do workspace — opcoes do filtro rapido por etiqueta (2026-09-02)
  const etiquetas = useQuery({
    queryKey: ['labels-all'],
    queryFn: async () => (await api['work-items'].labels.all.$get()).json(),
  })
  const unmapped = useQuery({
    queryKey: ['kanban', id, 'unmapped'],
    queryFn: async () => (await api.kanbans[':id'].unmapped.$get({ param: { id } })).json(),
    refetchOnWindowFocus: true,
    refetchInterval: 20_000,
  })
  /*
    "Só os não mapeados" na Lista (SPEC-20261006-2326-nao-mapeados-busca-e-filtro):
    o ícone de filtro da lista de não mapeados liga, e o X do aviso no
    cabeçalho desliga. Vale para este quadro: trocar de quadro desliga, e,
    sem nenhum não mapeado, não há o que recortar. Fica ANTES do retorno de
    carregamento do quadro: hook depois dele mudaria a contagem de hooks.
  */
  const [soNaoMapeados, setSoNaoMapeados] = useState(false)
  useEffect(() => setSoNaoMapeados(false), [id])
  const idsNaoMapeados = useMemo(
    () => (soNaoMapeados && (unmapped.data?.items.length ?? 0) > 0 ? new Set((unmapped.data?.items ?? []).map((i) => i.id)) : null),
    [soNaoMapeados, unmapped.data],
  )
  // lista de quadros: traz o estado de favorito do usuario para este quadro
  const boardList = useQuery({
    queryKey: ['kanbans'],
    queryFn: async () => (await api.kanbans.$get()).json(),
  })
  const config = useQuery({
    queryKey: ['kanban', id, 'config'],
    queryFn: async () => (await api.kanbans[':id'].config.$get({ param: { id } })).json(),
  })
  // imagens "Personalizar" enviadas para este quadro
  const backgrounds = useQuery({
    queryKey: ['kanban', id, 'backgrounds'],
    queryFn: async () => (await api.kanbans[':id'].backgrounds.$get({ param: { id } })).json(),
    enabled: bgOpen,
  })

  const uploadBackground = async (file: File) => {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const response = await fetch(`${apiBase()}/kanbans/${id}/backgrounds`, {
        method: 'POST',
        headers: { authorization: `Bearer ${localStorage.getItem('orbind.token') ?? ''}` },
        body: form,
      })
      if (!response.ok) throw new Error('Falha no upload da imagem')
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanban', id, 'backgrounds'] })
    } catch (error) {
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha no upload da imagem.' })
    } finally {
      setUploading(false)
    }
  }

  // Canal entre abas: mover um card num quadro pode afetar OUTROS quadros
  // (etapa comum / pino sobreposto). Avisamos as demais abas para atualizarem
  // na hora, sem esperar o polling.
  const canalRef = useRef<BroadcastChannel | null>(null)
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const canal = new BroadcastChannel('orbind-kanban')
    canalRef.current = canal
    canal.onmessage = (ev) => {
      if (ev.data?.tipo === 'mudou') queryClient.invalidateQueries({ queryKey: ['kanban'] })
    }
    return () => {
      canal.close()
      canalRef.current = null
    }
  }, [queryClient])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban', id] })
    queryClient.invalidateQueries({ queryKey: ['work-items'] })
    // avisa as outras abas (o proprio quadro ja invalidou acima)
    canalRef.current?.postMessage({ tipo: 'mudou' })
  }
  /**
   * Depois de mexer em CARTÕES (mover, criar, trocar de grupo) só a visão do
   * quadro e os "não mapeados" mudam — configuração, fundos e validação não.
   * Recarregar tudo isso a cada cartão deixava o quadro lento
   * (SPEC-20261027-1941-quadro-mais-rapido).
   */
  const invalidateCartoes = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban', id, 'view'] })
    queryClient.invalidateQueries({ queryKey: ['kanban', id, 'unmapped'] })
    queryClient.invalidateQueries({ queryKey: ['work-items'] })
    canalRef.current?.postMessage({ tipo: 'mudou' })
  }

  const move = useMutation({
    mutationFn: async (input: { workItemId: string; targetColumnId: string; beforeWorkItemId: string | null }) =>
      (
        await api.kanbans[':id'].cards[':workItemId'].move.$post({
          param: { id, workItemId: input.workItemId },
          json: { targetColumnId: input.targetColumnId, beforeWorkItemId: input.beforeWorkItemId },
        })
      ).json(),
    /*
      O cartão muda de lane NA HORA (SPEC-20261027-1941-quadro-mais-rapido); o servidor
      confirma por trás. Recusado (sequência do fluxo, WIP), volta ao lugar.
    */
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['kanban', id, 'view'] })
      const antes = queryClient.getQueryData(['kanban', id, 'view'])
      queryClient.setQueryData(['kanban', id, 'view'], (visao: typeof board.data) =>
        visao ? moverCartaoNaVisao(visao, input.workItemId, input.targetColumnId, input.beforeWorkItemId) : visao,
      )
      return { antes }
    },
    onError: (error, _input, contexto) => {
      if (contexto?.antes) queryClient.setQueryData(['kanban', id, 'view'], contexto.antes)
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao mover o cartão.' })
    },
    onSettled: invalidateCartoes,
  })

  /**
   * Campo que o AGRUPAMENTO da lista representa, quando ele é um campo único do
   * item. Soltar a linha dentro de outro grupo grava esse campo (decisão do
   * usuário, 2026-09-14 23:10). "Membro" e "Tipo de Item" ficam de fora: um é
   * lista de membros e o outro é a hierarquia — mudá-los por arraste seria
   * adivinhação.
   */
  const campoDoAgrupamento = (): 'assigneeMemberId' | 'companyAreaId' | 'companyId' | null => {
    if (agruparPor === 'responsavel') return 'assigneeMemberId'
    if (agruparPor === 'area') return 'companyAreaId'
    if (agruparPor === 'company') return 'companyId'
    return null
  }

  const mudarCampo = useMutation({
    mutationFn: async (input: { workItemId: string; campo: string; valor: string | null }) =>
      (
        await api['work-items'][':id'].$patch({
          param: { id: input.workItemId },
          query: {},
          json: { [input.campo]: input.valor } as never,
        })
      ).json(),
    onSuccess: invalidateCartoes,
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao mover o item de grupo.' }),
  })

  /**
   * Soltar uma linha da lista sobre outra. A lane de destino vem do ITEM DE
   * DESTINO (não do grupo): assim o gesto vale em qualquer agrupamento, e quem
   * decide se houve mudança de lane é onde o item foi solto.
   */
  const moverLinha = (arrastadoId: string, alvoId: string | null, bandaAlvo: string | null) => {
    if (arrastadoId === alvoId) return

    const campo = campoDoAgrupamento()
    if (campo && bandaAlvo !== null) {
      const cardArrastado = cardsVisiveis.find((c) => c.id === arrastadoId)
      const grupoAtual =
        campo === 'assigneeMemberId'
          ? (cardArrastado?.assignee?.id ?? null)
          : campo === 'companyAreaId'
            ? (cardArrastado?.companyAreaId ?? null)
            : (cardArrastado?.company?.id ?? null)
      const grupoDestino = bandaAlvo === '__sem__' ? null : bandaAlvo
      if (grupoAtual !== grupoDestino) mudarCampo.mutate({ workItemId: arrastadoId, campo, valor: grupoDestino })
    }

    const laneDestino = (alvoId ? laneDoCard.get(alvoId) : null) ?? laneDoCard.get(arrastadoId)
    if (!laneDestino) return
    move.mutate(
      { workItemId: arrastadoId, targetColumnId: laneDestino, beforeWorkItemId: alvoId },
      {
        // a ordenação por coluna só sai quando o servidor ACEITA o movimento:
        // um movimento recusado não pode reordenar a lista do nada
        onSuccess: () => setOrdemLista({ col: 'manual', desc: false }),
      },
    )
  }

  const saveLanes = useMutation({
    mutationFn: async (lanes: DraftLane[]) =>
      (
        await api.kanbans[':id'].lanes.$put({
          param: { id },
          json: {
            lanes: lanes.map(({ id: laneId, name, statusId, wipLimit, flowStepIds }) => ({
              id: laneId,
              name,
              statusId,
              wipLimit,
              flowStepIds,
            })),
          },
        })
      ).json(),
    onSuccess: () => {
      invalidate()
      setEditMode(false)
      toast.push({ tone: 'success', message: 'Quadro atualizado.' })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao salvar o quadro.' }),
  })

  const saveAppearance = useMutation({
    mutationFn: async (input: { backgroundColor: string | null; backgroundImageUrl: string | null }) =>
      (await api.kanbans[':id'].$put({ param: { id }, json: input })).json(),
    onSuccess: invalidate,
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao salvar o fundo.' }),
  })

  const saveBoardMeta = useMutation({
    mutationFn: async () =>
      (
        await api.kanbans[':id'].$put({
          param: { id },
          json: {
            name: editForm.name.trim(),
            description: editForm.description.trim() === '' ? null : editForm.description.trim(),
            areaIds: editForm.areaIds,
            ownerMemberId: editForm.ownerMemberId,
            workItemTypeIds: editForm.workItemTypeIds,
            memberIds: editForm.memberIds,
            portfolioNodeIds: editForm.portfolioNodeIds,
            companyIds: editForm.companyIds,
            flowIds: editForm.flowIds,
            filter: editForm.filter ? JSON.stringify(editForm.filter) : null,
            newItemsPrivate: editForm.newItemsPrivate,
            newItemsExclusive: editForm.newItemsExclusive,
          },
        })
      ).json(),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
      setEditBoardOpen(false)
      toast.push({ tone: 'success', message: 'Quadro atualizado.' })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao salvar o quadro.' }),
  })

  const toggleFavorite = useMutation({
    mutationFn: async (isFavorite: boolean) =>
      (await api.kanbans[':id'].favorite.$put({ param: { id }, json: { isFavorite } })).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kanbans'] }),
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao favoritar o quadro.' }),
  })

  const saveTitle = useMutation({
    mutationFn: async (name: string) => (await api.kanbans[':id'].$put({ param: { id }, json: { name } })).json(),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao renomear o quadro.' }),
  })

  /**
   * O quadro saiu da lista (excluído ou abandonado): tira da lista na hora e
   * vai para o PRIMEIRO quadro que sobrou (SPEC-20261027-1958-excluir-quadro-vai-para-o-primeiro).
   * Antes ia para /kanbans, e a lista ainda em cache — com o quadro removido —
   * fazia a tela reabrir justamente ele.
   */
  const irParaOPrimeiroQuadro = () => {
    const lista = queryClient.getQueryData<{ boards: { id: string; isFavorite?: boolean }[] }>(['kanbans'])
    const proximo = primeiroQuadroSem(lista?.boards ?? [], id)
    queryClient.setQueryData<{ boards: { id: string }[] }>(['kanbans'], (atual) =>
      atual ? { ...atual, boards: atual.boards.filter((b) => b.id !== id) } : atual,
    )
    try {
      if (proximo) localStorage.setItem(CHAVE_ULTIMO_QUADRO, proximo)
      else localStorage.removeItem(CHAVE_ULTIMO_QUADRO)
    } catch {
      // sem localStorage: só não lembra o último quadro
    }
    queryClient.removeQueries({ queryKey: ['kanban', id] })
    queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    navigate(proximo ? `/kanbans/${proximo}` : '/kanbans', { replace: true })
  }

  /** Excluir o quadro (a partir das configuracoes, 2026-08-26). */
  const excluirQuadro = useMutation({
    mutationFn: async () => (await api.kanbans[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Quadro excluído.' })
      irParaOPrimeiroQuadro()
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Não foi possível excluir o quadro.' }),
  })

  /**
   * Sair do quadro (2026-08-27): o membro se remove e perde a visibilidade.
   * Some da lista e volta pro explorer. O dono nao usa isto (usa Excluir).
   */
  const sairDoQuadro = useMutation({
    mutationFn: async () => (await api.kanbans[':id'].sair.$post({ param: { id } })).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Você saiu do quadro.' })
      irParaOPrimeiroQuadro()
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Não foi possível sair do quadro.' }),
  })

  // escopo do quadro trocado direto no desenho (chips + Adicionar filtro)
  const saveEscopo = useMutation({
    mutationFn: async (json: {
      areaIds?: string[]
      workItemTypeIds?: string[]
      memberIds?: string[]
      portfolioNodeIds?: string[]
      companyIds?: string[]
      flowIds?: string[]
      description?: string | null
      filter?: string | null
      quickFilters?: string | null
      cardFields?: string | null
    }) => (await api.kanbans[':id'].$put({ param: { id }, json })).json(),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao alterar o filtro.' }),
  })

  // MEMBROS do quadro por endpoint dedicado: por padrao qualquer membro pode
  // mexer; se a politica for 'owners', a API recusa quem nao e dono (2026-08-27)
  const saveMembros = useMutation({
    mutationFn: async (memberIds: string[]) =>
      (await api.kanbans[':id'].members.$put({ param: { id }, json: { memberIds } })).json(),
    // o certinho da lista responde na hora: a nova lista entra no cache antes
    // da resposta (sem isso, dois cliques rapidos se atropelavam)
    onMutate: (memberIds) => {
      queryClient.setQueryData(['kanban', id, 'config'], (atual: { memberIds?: string[] } | undefined) =>
        atual ? { ...atual, memberIds } : atual,
      )
    },
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao alterar os membros.' }),
  })

  // itens visiveis por lane (altura da lane); so o dono, na configuracao
  const saveVisiveis = useMutation({
    mutationFn: async (n: number) =>
      (await api.kanbans[':id'].$put({ param: { id }, json: { visibleCardCount: n } })).json(),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao alterar itens visíveis.' }),
  })

  // troca a POLITICA de quem gerencia membros (so o dono; PUT /:id)
  const saveGestaoMembros = useMutation({
    mutationFn: async (mode: 'members' | 'owners') =>
      (await api.kanbans[':id'].$put({ param: { id }, json: { memberManagement: mode } })).json(),
    onSuccess: () => {
      invalidate()
      toast.push({ tone: 'success', message: 'Regra de membros atualizada.' })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao alterar a regra.' }),
  })

  // DONOS do quadro (varios): a API garante minimo 1 e que um dono nao se
  // remove — a mensagem de erro dela aparece no toast
  const saveDonos = useMutation({
    mutationFn: async (ownerIds: string[]) =>
      (await api.kanbans[':id'].$put({ param: { id }, json: { ownerIds } })).json(),
    // idem saveMembros: o certinho e a fila de bolinhas respondem na hora
    onMutate: (ownerIds) => {
      queryClient.setQueryData(['kanban', id, 'config'], (atual: { ownerIds?: string[] } | undefined) =>
        atual ? { ...atual, ownerIds } : atual,
      )
    },
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
      toast.push({ tone: 'success', message: 'Donos do quadro atualizados.' })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao alterar os donos.' }),
  })

  // COMPARTILHAR (SPEC-20260916-1527): pessoas com papel/acesso + acesso geral
  const saveShare = useMutation({
    mutationFn: async (input: { people: PessoaDoQuadro[]; generalAccess?: 'restricted' | 'workspace'; generalRole?: 'editor' | 'viewer' }) =>
      (await api.kanbans[':id'].share.$put({ param: { id }, json: input })).json(),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
    },
  })

  const openEditBoard = () => {
    if (!boardData) return
    const inicial = {
      name: labelOf(boardData.board.nameTranslationKey),
      description: boardData.board.descriptionTranslationKey ? labelOf(boardData.board.descriptionTranslationKey) : '',
      areaIds: config.data?.areaIds ?? [],
      ownerMemberId: boardData.board.ownerMemberId ?? '',
      workItemTypeIds: config.data?.workItemTypeIds ?? [],
      memberIds: config.data?.memberIds ?? [],
      portfolioNodeIds: config.data?.portfolioNodeIds ?? [],
      companyIds: config.data?.companyIds ?? [],
      flowIds: config.data?.flowIds ?? [],
      filter: parseBoardFilter(boardData.board.filter),
      newItemsPrivate: novosItensPrivados,
      newItemsExclusive: novosItensExclusivos,
    }
    setEditForm(inicial)
    setEditBase(inicial)
    setEditBoardOpen(true)
  }

  const createItem = useMutation({
    mutationFn: async (input: { statusId: string; laneId?: string; abrirDetalhe?: boolean }) =>
      (
        await api['work-items'].$post({
          json: {
            // expandir sem titulo digitado: nasce com um titulo padrao e o
            // usuario completa na tela cheia do item
            title: composer.title.trim() || 'Novo Item de Trabalho',
            description: composer.description.trim(),
            workItemTypeId: composer.typeId,
            workItemStatusId: input.statusId,
            workItemPriorityId: bootstrap.data!.priorities.find((p) => p.code === 'MEDIUM')?.id ?? bootstrap.data!.priorities[0]!.id,
            // filtros do quadro viram VALORES INICIAIS do item (editaveis
            // depois no detalhe)
            companyAreaId: config.data?.areaIds?.[0] ?? null,
            companyId: config.data?.companyIds?.[0] ?? null,
            portfolioNodeId: config.data?.portfolioNodeIds?.[0] ?? null,
            // sem responsável automático: os membros do quadro dizem quem tem
            // ACESSO a ele, não recortam os itens (correção 2026-08-31)
            assigneeMemberId: null,
            // etapa escolhida no composer (lane sem etapa ou com varias);
            // lane com UMA etapa deixa null — o pino aplica a etapa da lane
            flowStepId: composer.flowStepId || null,
            // a visibilidade é a do QUADRO, e o servidor aplica (SPEC-20261007-0934-visibilidade-padrao-do-quadro)
            kanbanBoardId: id,
            expectedResult: '',
            recurrence: 'never',
            reminder: 'none',
          },
        })
      ).json(),
    onSuccess: async (data, vars) => {
      const itemId = data.item?.id
      if (itemId) await fixarNaLane(itemId, vars.laneId)
      setComposerLane(null)
      // "inclusao completa": expande para a TELA do item dentro do quadro
      if (itemId && vars.abrirDetalhe) navigate(`/kanbans/${id}/items/${itemId}`)
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao criar o item.' }),
  })

  /**
   * Pos-criacao a partir de uma lane (composer rapido OU inclusao completa):
   * PINA o item na lane (o card aparece SO ali e ganha a etapa da lane) e
   * adiciona os MEMBROS do quadro ao item, para todos verem o card.
   */
  /**
   * Lane que está com a escolha "em branco x template" aberta. Guarda a KEY da
   * lane (a mesma que o composer usa), não o id da coluna.
   */
  const [escolhendoParaLane, setEscolhendoParaLane] = useState<string | null>(null)
  const templatesDeItem = useTemplates('work_item')
  const temTemplatesDeItem = (templatesDeItem.data?.templates.length ?? 0) > 0

  /*
    INCLUSÃO RÁPIDA NA LANE (SPEC-20261007-1633-inclusao-rapida-na-lane): "Adicionar item" abre um
    cartão só com o título, dentro da lane — Enter cria, e o cartão continua
    aberto para o próximo. O ícone do cartão leva à inclusão completa (em
    branco ou template). Tudo o que não é título vem dos PADRÕES do quadro.
  */
  const [rapido, setRapido] = useState<{ laneKey: string; titulo: string } | null>(null)
  const criarRapido = useMutation({
    mutationFn: async (input: { laneKey: string; titulo: string; abrirDetalhe: boolean }) => {
      const padroes = padroesDaLane(input.laneKey)
      const lane = padroes.lane
      if (!lane) throw new Error('A lane não existe mais.')
      // lane sem status: o item nasce no status inicial do catálogo
      const statusId = lane.statusId ?? bootstrap.data?.workItemStatuses.find((s) => s.isActive)?.id
      if (!statusId || !padroes.typeId) throw new Error('Este quadro não tem um tipo de item disponível para esta lane.')
      const prioridades = bootstrap.data?.priorities ?? []
      const resposta = await api['work-items'].$post({
        json: {
          title: input.titulo.trim() || 'Novo Item de Trabalho',
          description: '',
          workItemTypeId: padroes.typeId,
          workItemStatusId: statusId,
          workItemPriorityId: prioridades.find((x) => x.code === 'MEDIUM')?.id ?? prioridades[0]!.id,
          // o escopo do quadro vira o valor inicial, como no composer
          companyAreaId: config.data?.areaIds?.[0] ?? null,
          companyId: config.data?.companyIds?.[0] ?? null,
          portfolioNodeId: config.data?.portfolioNodeIds?.[0] ?? null,
          assigneeMemberId: null,
          flowStepId: padroes.flowStepId || null,
          // visibilidade e "somente neste quadro" vêm do quadro, no servidor
          kanbanBoardId: id,
          expectedResult: '',
          recurrence: 'never',
          reminder: 'none',
        },
      })
      const corpo = (await resposta.json()) as { item?: { id: string }; error?: { message?: string } }
      if (!resposta.ok || !corpo.item) throw new Error(corpo.error?.message ?? 'Falha ao criar o item.')
      return { itemId: corpo.item.id, laneId: lane.id, abrirDetalhe: input.abrirDetalhe }
    },
    onSuccess: async (r) => {
      await fixarNaLane(r.itemId, r.laneId)
      if (r.abrirDetalhe) {
        setRapido(null)
        navigate(`/kanbans/${id}/items/${r.itemId}`)
      }
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao criar o item.' }),
  })
  /** A lane cabe mais um cartão? Avisa e devolve false quando o WIP está cheio. */
  const laneAceita = (laneKey: string) => {
    const lane = lanes.find((l) => l.key === laneKey)
    const naLane = lane ? (boardData?.columns.find((c) => c.id === lane.id)?.cards.length ?? 0) : 0
    if (lane?.wipLimit != null && naLane >= lane.wipLimit) {
      toast.push({ tone: 'danger', message: 'Limite de WIP excedido nesta lane.' })
      return false
    }
    return true
  }
  const salvarRapido = () => {
    if (!rapido || rapido.titulo.trim() === '' || criarRapido.isPending || !laneAceita(rapido.laneKey)) return
    const atual = rapido
    criarRapido.mutate(
      { laneKey: atual.laneKey, titulo: atual.titulo, abrirDetalhe: false },
      // criou: o cartão fica aberto e vazio, pronto para o próximo
      { onSuccess: () => setRapido((r) => (r && r.laneKey === atual.laneKey ? { ...r, titulo: '' } : r)) },
    )
  }
  /** Ícone do cartão: em branco ou template, e a tela completa do item. */
  const abrirInclusaoCompleta = (laneKey: string) => {
    if (!laneAceita(laneKey)) return
    if (temTemplatesDeItem) setEscolhendoParaLane(laneKey)
    else criarRapido.mutate({ laneKey, titulo: rapido?.titulo ?? '', abrirDetalhe: true })
  }

  const fixarNaLane = async (itemId: string, laneId: string | undefined) => {
    if (laneId) {
      // item novo entra no TOPO da lane (pedido 2026-09-01): pina antes do
      // primeiro card atual; lane vazia cai no null (final = topo)
      const primeiro = boardData?.columns.find((c) => c.id === laneId)?.cards[0]?.id ?? null
      await api.kanbans[':id'].cards[':workItemId'].move.$post({
        param: { id, workItemId: itemId },
        json: { targetColumnId: laneId, beforeWorkItemId: primeiro === itemId ? null : primeiro },
      })
    }
    // o item nasce SEM membros: quem entra nele é escolha de quem trabalha o
    // item, não consequência de quem tem acesso ao quadro (2026-08-31)
    invalidateCartoes()
  }

  /**
   * Tipos permitidos ao criar item no quadro: passa pelo escopo de tipos do
   * quadro E, quando o quadro tem FLUXO, so tipos que PERTENCEM a um desses
   * fluxos (2026-08-27). Sem fluxo no quadro, vale o escopo (ou todos ativos).
   */
  const flowIdsDoQuadro = new Set(config.data?.flowIds ?? [])
  const tiposDoQuadro = (bootstrap.data?.workItemTypes ?? []).filter((x) => {
    if (!x.isActive) return false
    const noEscopo =
      (config.data?.workItemTypeIds?.length ?? 0) === 0 || (config.data?.workItemTypeIds ?? []).includes(x.id)
    if (!noEscopo) return false
    // tipo SEM vinculo de fluxo e neutro: entra sempre. Sem isso, um workspace
    // que nao liga tipo<->fluxo fica com a lista de tipos VAZIA no composer.
    if (flowIdsDoQuadro.size > 0 && (x.flowIds ?? []).length > 0) {
      return (x.flowIds ?? []).some((f) => flowIdsDoQuadro.has(f))
    }
    return true
  })

  // PAPEL e MODO DE ACESSO de quem está olhando (SPEC-20260916-1527): leitor
  // não cria, não move cartão nem edita na planilha; "só o Kanban"/"só a
  // Lista" forçam a visão e escondem o alternador.
  const meuPapel = (config.data as { meuPapel?: 'owner' | 'editor' | 'viewer' } | undefined)?.meuPapel ?? 'editor'
  const meuAcesso = (config.data as { meuAcesso?: 'kanban' | 'lista' | 'ambos' } | undefined)?.meuAcesso ?? 'ambos'
  /** Os tipos que a pessoa tem neste quadro: só Kanban, só Planilha, ou todos. */
  const modosDoAcesso: readonly ModoDaVisao[] =
    meuAcesso === 'kanban' ? ['quadro'] : meuAcesso === 'lista' ? ['planilha'] : MODOS_DO_QUADRO
  /** Escolher a visão (ou trocar o tipo dela) leva ao tipo dela. */
  const modoDaVisaoAtiva = visaoAtiva?.config.modo
  useEffect(() => {
    if (modoDaVisaoAtiva && modosDoAcesso.includes(modoDaVisaoAtiva)) setModoVisao(modoDaVisaoAtiva)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visaoAtiva?.id, modoDaVisaoAtiva, meuAcesso])
  const souLeitor = meuPapel === 'viewer'
  useEffect(() => {
    if (meuAcesso === 'kanban' && modoVisao !== 'quadro') setModoVisao('quadro')
    // quem só tem acesso de "lista" cai na PLANILHA, que é o que esse acesso
    // sempre significou (ela se chamava Lista até 2026-10-03)
    if (meuAcesso === 'lista' && modoVisao !== 'planilha') setModoVisao('planilha')
  }, [meuAcesso, modoVisao])
  /**
   * Padrões do quadro para um item novo numa lane: o tipo (TASK, ou o
   * primeiro aceito pela lane/quadro) e a etapa — a inicial do fluxo do tipo,
   * ou a primeira da lane quando ela atua em várias. É o que a inclusão rápida
   * usa para tudo que não é o título (SPEC-20261007-1633-inclusao-rapida-na-lane).
   */
  const padroesDaLane = (laneKey: string) => {
    const laneObj = lanes.find((l) => l.key === laneKey)
    const fluxosDaLane = new Set(
      (laneObj?.flowStepIds ?? [])
        .map((sid) => etapasDoQuadro.find((s) => s.flowStepId === sid)?.flowId)
        .filter((f): f is string => Boolean(f)),
    )
    const tiposLane = tiposDoQuadro.filter(
      (t) =>
        fluxosDaLane.size === 0 ||
        (t.flowIds ?? []).length === 0 ||
        (t.flowIds ?? []).some((f) => fluxosDaLane.has(f)),
    )
    const tipoPadrao = tiposLane.find((x) => x.code === 'TASK') ?? tiposLane[0]
    const fluxosDeFiltro = fluxosDaLane.size > 0 ? fluxosDaLane : flowIdsDoQuadro
    const fluxosDoTipo = ((tipoPadrao?.flowIds as string[] | undefined) ?? []).filter((f) => fluxosDeFiltro.has(f))
    const iniciaisDoTipo = fluxosDoTipo.flatMap((f) => {
      const doFluxo = etapasDoQuadro.filter((s) => s.flowId === f)
      const marcadas = doFluxo.filter((s) => (s as { isInitial?: boolean }).isInitial)
      return marcadas.length > 0 ? marcadas : doFluxo.slice(0, 1)
    })
    const daLane = laneObj ? etapasAPerguntar(laneObj) : null
    return {
      lane: laneObj,
      typeId: tipoPadrao?.id ?? '',
      flowStepId: iniciaisDoTipo[0]?.flowStepId ?? daLane?.[0]?.flowStepId ?? '',
    }
  }

  const openComposer = (laneKey: string) => {
    if (souLeitor) {
      toast.push({ tone: 'warning', message: 'Você tem acesso de leitura a este quadro: não dá para criar itens.' })
      return
    }
    // fluxos da lane (pelas etapas mapeadas) — o tipo padrao respeita esse fluxo
    const laneObj = lanes.find((l) => l.key === laneKey)
    const fluxosDaLane = new Set(
      (laneObj?.flowStepIds ?? [])
        .map((sid) => etapasDoQuadro.find((s) => s.flowStepId === sid)?.flowId)
        .filter((f): f is string => Boolean(f)),
    )
    const tiposLane = tiposDoQuadro.filter(
      (t) => fluxosDaLane.size === 0 || (t.flowIds ?? []).some((f) => fluxosDaLane.has(f)),
    )
    const tipoPadrao = tiposLane.find((x) => x.code === 'TASK') ?? tiposLane[0]
    const fluxosDeFiltro = fluxosDaLane.size > 0 ? fluxosDaLane : flowIdsDoQuadro
    // se o tipo padrao tem UM fluxo, ja abre com a etapa inicial dele setada
    const fluxosPadrao = ((tipoPadrao?.flowIds as string[] | undefined) ?? []).filter((f) => fluxosDeFiltro.has(f))
    const iniciaisPadrao = fluxosPadrao
      .map((f) => (config.data?.flowSteps ?? []).find((s) => s.flowId === f))
      .filter((s): s is NonNullable<typeof s> => Boolean(s))
    setComposer({
      typeId: tipoPadrao?.id ?? '',
      title: '',
      description: '',
      flowStepId: iniciaisPadrao.length === 1 ? iniciaisPadrao[0]!.flowStepId : '',
      isPrivate: false,
    })
    setComposerLane(laneKey)
  }

  const boardData = board.data
  // Dono do quadro (base do gate de CONFIGURACAO, que e so dos donos) e a
  // politica de gestao de membros do quadro (2026-08-27).
  const ownerIdsQuadro =
    (config.data?.ownerIds?.length ?? 0) > 0
      ? (config.data?.ownerIds ?? [])
      : boardData?.board.ownerMemberId
        ? [boardData.board.ownerMemberId]
        : []
  const souDonoQuadro = ownerIdsQuadro.includes(session.id)
  const gestaoMembros: 'members' | 'owners' =
    (config.data?.board?.memberManagement as 'members' | 'owners' | undefined) ?? 'members'
  const podeGerenciarMembros = souDonoQuadro || gestaoMembros === 'members'
  // Itens visiveis por lane (altura da lane); 0 = automatico (2026-08-27).
  const itensVisiveis =
    (config.data?.board?.visibleCardCount as number | undefined) ?? boardData?.board.visibleCardCount ?? 0
  // entra no modo edicao com um draft copiado do quadro atual
  const startEdit = () => {
    if (!boardData) return
    // configuracao do quadro e exclusiva dos donos (2026-08-27)
    if (!souDonoQuadro) {
      toast.push({ tone: 'warning', message: 'Só um dono do quadro pode acessar a configuração.' })
      return
    }
    setDraft(
      boardData.columns.map((c) => ({
        key: c.id,
        id: c.id,
        name: labelOf(c.name),
        statusId: c.statusId,
        wipLimit: c.wipLimit,
        flowStepIds: c.flowStepIds ?? [],
      })),
    )
    setEditMode(true)
  }

  // ---- formatação pública (depende de boardData e souDonoQuadro, declarados acima)
  const formatoPublico = ((boardData?.board as { listFormat?: unknown } | undefined)?.listFormat ?? null) as Partial<FormatoDaLista> | null
  /** Assinatura da pública que já foi aplicada nesta sessão — divergir dela é a pessoa mexendo: vira privada. */
  const publicaAplicadaRef = useRef('')
  const assinaturaAtual = assinaturaDoFormato(formatoAtual())
  const assinaturaPublica = assinaturaDoFormato(formatoPublico)
  useEffect(() => {
    if (fmtModo !== 'publica' || !formatoPublico) return
    if (assinaturaAtual === assinaturaPublica) {
      publicaAplicadaRef.current = assinaturaPublica
      return
    }
    if (publicaAplicadaRef.current === assinaturaPublica) {
      // a pessoa mudou algo depois de receber a pública: agora é a dela
      setFmtModo('privada')
      return
    }
    aplicarFormato(formatoPublico)
    publicaAplicadaRef.current = assinaturaPublica
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fmtModo, assinaturaAtual, assinaturaPublica])
  const publicarFormato = async (formato: FormatoDaLista | null) => {
    setPublicando(true)
    try {
      const r = await api.kanbans[':id']['list-format'].$put({ param: { id }, json: { format: formato } as never })
      if (!r.ok) throw new Error(((await r.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ?? 'Não foi possível publicar.')
      queryClient.invalidateQueries({ queryKey: ['kanban', id] })
      if (formato) {
        publicaAplicadaRef.current = assinaturaDoFormato(formato)
        setFmtModo('publica')
        toast.push({ tone: 'success', message: 'Formatação publicada para todos do quadro.' })
      } else {
        setFmtModo('privada')
        toast.push({ tone: 'success', message: 'Formatação pública removida.' })
      }
    } catch (e) {
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível publicar.' })
    } finally {
      setPublicando(false)
    }
  }
  /** Botão + menu "formatação privada/pública" — usado na barra do Sheets e no cabeçalho da lista. */
  const botaoFormato = (
    <span className="kb__sdrop-wrap kb__fmt">
      <button
        type="button"
        className={`kb__sbtn kb__fmtbtn${fmtModo === 'publica' && formatoPublico ? ' kb__fmtbtn--publica' : ''}`}
        title={fmtModo === 'publica' && formatoPublico ? 'Formatação pública do quadro (clique para opções)' : 'Minha formatação (privada) — clique para opções'}
        aria-label="Formatação da planilha"
        aria-haspopup="menu"
        aria-expanded={fmtMenu}
        onClick={() => setFmtMenu((v) => !v)}
      >
        <i className={fmtModo === 'publica' && formatoPublico ? 'fas fa-globe-americas' : 'fas fa-lock'} aria-hidden />
      </button>
      {fmtMenu ? (
        <>
          <div className="wi-menu__backdrop" onClick={() => setFmtMenu(false)} />
          <div className="kb__smenu-tabela kb__smenu-tabela--dir" role="menu" aria-label="Formatação da planilha">
            <div className="kb__smenu-titulo">Formatação da planilha</div>
            <button type="button" className={`kb__smi${fmtModo === 'privada' || !formatoPublico ? ' kb__smi--on' : ''}`} onClick={() => { setFmtModo('privada'); setFmtMenu(false) }}>
              <span className="kb__smi-pre" aria-hidden><i className={fmtModo === 'privada' || !formatoPublico ? 'fas fa-check' : 'fas fa-lock'} /></span>
              <span className="kb__smi-txt">Minha formatação (privada)<small>colunas, larguras, agrupamento… só no meu navegador</small></span>
            </button>
            <button
              type="button"
              className={`kb__smi${fmtModo === 'publica' && formatoPublico ? ' kb__smi--on' : ''}`}
              disabled={!formatoPublico}
              title={formatoPublico ? undefined : 'O quadro ainda não tem formatação pública'}
              onClick={() => {
                // escolher a pública de novo = quero recebê-la: zera a marca de
                // "já aplicada", senão o efeito lê a diferença como mexida minha
                publicaAplicadaRef.current = ''
                setFmtModo('publica')
                setFmtMenu(false)
              }}
            >
              <span className="kb__smi-pre" aria-hidden><i className={fmtModo === 'publica' && formatoPublico ? 'fas fa-check' : 'fas fa-globe-americas'} /></span>
              <span className="kb__smi-txt">Formatação pública do quadro<small>{formatoPublico ? 'a que o dono publicou para todos' : 'nenhuma publicada ainda'}</small></span>
            </button>
            {souDonoQuadro ? (
              <>
                <div className="kb__smenu-sep" />
                <button type="button" className="kb__smi" disabled={publicando} onClick={() => { setFmtMenu(false); void publicarFormato(formatoAtual()) }}>
                  <span className="kb__smi-pre" aria-hidden><i className="fas fa-upload" /></span>
                  <span className="kb__smi-txt">Publicar a minha formatação para todos<small>só donos; quem está na pública recebe na hora</small></span>
                </button>
                {formatoPublico ? (
                  <button type="button" className="kb__smi" disabled={publicando} onClick={() => { setFmtMenu(false); void publicarFormato(null) }}>
                    <span className="kb__smi-pre" aria-hidden><i className="fas fa-times-circle" /></span>
                    <span className="kb__smi-txt">Remover a formatação pública</span>
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </>
      ) : null}
    </span>
  )

  // rotulo: traducao quando a chave existe; senao o proprio texto (nome livre)
  const labelOf = (key: string) => {
    const label = t(key)
    return label === key ? key : label
  }

  const statusOf = (statusId: string | null) => bootstrap.data?.workItemStatuses.find((s) => s.id === statusId)
  const statusLabel = (statusId: string | null) => {
    if (statusId === null) return 'Sem status'
    const status = statusOf(statusId)
    if (!status) return '—'
    const label = t(status.nameTranslationKey)
    return label === status.nameTranslationKey ? status.code : label
  }

  // ao voltar do detalhe (?card=...), o cartao volta selecionado no quadro
  useEffect(() => {
    const cardFromUrl = new URLSearchParams(location.search).get('card')
    setSelectedCard(cardFromUrl)
    if (cardFromUrl) {
      requestAnimationFrame(() =>
        document.querySelector('.kb__card--selected')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }),
      )
    }
  }, [id, location.search])

  // analisa o fundo aplicado: gradiente pelos hex do CSS, imagem pelo canvas
  const bgImageValue = board.data?.board.backgroundImageUrl ?? null
  const bgColorValue = board.data?.board.backgroundColor ?? null
  useEffect(() => {
    let cancelled = false
    if (!bgImageValue) {
      // cor sólida também define claro/escuro — antes só imagem contava, e um
      // fundo escuro por cor ficava com texto preto em cima
      setBgDark(bgColorValue ? hexLuminance(bgColorValue) < 0.55 : false)
      return
    }
    if (bgImageValue.startsWith('linear-gradient')) {
      setBgDark(gradientIsDark(bgImageValue))
      return
    }
    measureImageDarkness(resolveBgUrl(bgImageValue)).then((dark) => {
      if (!cancelled) setBgDark(dark)
    })
    return () => {
      cancelled = true
    }
  }, [bgImageValue])

  // publica o fundo para o shell: a imagem se estende pela area toda —
  // explorer, barra superior (com pelicula escura) e quadro
  const setBoardBg = useSetBoardBackground()
  // No Black o fundo não é pintado (SPEC-20261104-1631): o shell ignora o que
  // publicamos e o quadro não entra no modo "fundo escuro".
  const temaBlack = useTemaEfetivo() === 'black'
  useEffect(() => {
    if (!bgImageValue && !bgColorValue) {
      setBoardBg(null)
      return
    }
    const style = bgImageValue
      ? bgImageValue.startsWith('linear-gradient')
        ? { backgroundImage: bgImageValue }
        : {
            // cobre a AREA TODA (barra, painel e quadro) mantendo a proporcao
            backgroundImage: `url("${resolveBgUrl(bgImageValue)}")`,
            backgroundSize: 'cover' as const,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat' as const,
          }
      : { background: bgColorValue! }
    setBoardBg({ style, dark: bgDark })
  }, [bgImageValue, bgColorValue, bgDark, setBoardBg])

  // Nao limpa o fundo no unmount: trocar de rota dentro de Quadros
  // (/kanbans -> /kanbans/:id) e qualquer remontagem fariam a imagem piscar
  // ou sumir. Quem limpa e o shell, ao sair da area de Quadros.

  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!panRef.current || !lanesRef.current) return
      lanesRef.current.scrollLeft = panRef.current.scrollLeft - (event.clientX - panRef.current.startX)
    }
    const onUp = () => {
      if (!panRef.current) return
      panRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    // o fantasma acompanha o cursor durante o drag (dragover e o unico evento
    // com coordenadas confiaveis no HTML5 drag)
    const onDragOver = (event: DragEvent) => {
      const ghost = dragGhostRef.current
      if (!ghost) return
      ghost.style.left = `${event.clientX - dragGhostOffset.current.x}px`
      ghost.style.top = `${event.clientY - dragGhostOffset.current.y}px`
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('dragover', onDragOver)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('dragover', onDragOver)
    }
  }, [])

  /**
   * Devolve o QUADRO ao ponto onde a pessoa parou (2026-09-21).
   *
   * ESTE HOOK FICA AQUI, ACIMA DOS `return` ABAIXO, e é o motivo de estar
   * separado do resto da memória do quadro: na primeira versão ele morava
   * junto com o cálculo das bandas, depois do `if (board.isPending) return`.
   * Enquanto o quadro carregava, o componente rodava com menos hooks; quando
   * os dados chegavam, com mais — e o React derrubava a tela inteira com o
   * erro #310. Quebrou a produção em 2026-09-21 23:17.
   *
   * A condição de "já tem coluna desenhada" mora DENTRO do efeito, nunca em
   * volta dele: num container ainda vazio o navegador limita o `scrollLeft`
   * ao que existe e o valor guardado viraria zero.
   */
  const rolagemDoQuadroFeita = useRef(false)
  useLayoutEffect(() => {
    const colunas = board.data?.columns.length ?? 0
    if (rolagemDoQuadroFeita.current || colunas === 0) return
    const el = lanesRef.current
    if (!el) return
    rolagemDoQuadroFeita.current = true
    const lido = lerRolagem(`orbind.kb.scrollquadro.${id}`)
    if (lido) {
      el.scrollTop = lido.top
      el.scrollLeft = lido.left
    }
  }, [id, board.data?.columns.length])

  /**
   * Selos que plugins põem nos cards (SPEC-...-plugin-acao-e-selo).
   *
   * UMA chamada por extensão com todos os ids do quadro — nunca uma por card.
   * O card desenha sem selo e preenche quando a resposta chega; nada aqui
   * atrasa o quadro, e plugin fora do ar devolve vazio.
   *
   * Fica ANTES dos returns condicionais abaixo, e os ids saem direto de
   * `board.data`: hook depois de `return` é o erro #310 que já derrubou a
   * produção uma vez, e o teste `ordem-dos-hooks` existe por causa dele.
   */
  const pluginsLigados = useModuleGates().moduleEnabled('plugins')
  const idsDoQuadro = useMemo(
    () => (board.data?.columns ?? []).flatMap((col) => col.cards.map((c) => c.id)),
    [board.data],
  )
  const selosDePlugin = useSelosDePlugin(idsDoQuadro, pluginsLigados)

  if (board.isPending) return <LoadingState variant="board" />
  if (!boardData) {
    return (
      <div style={{ padding: 28 }}>
        <Alert tone="danger">Quadro não encontrado.</Alert>
      </div>
    )
  }

  const boardName = labelOf(boardData.board.nameTranslationKey)
  /** Campos do card configurados no quadro (nulo = conjunto padrao). */
  const camposDoCard: CardCampo[] = (() => {
    try {
      return boardData.board.cardFields ? (JSON.parse(boardData.board.cardFields) as CardCampo[]) : CARD_CAMPOS_PADRAO
    } catch {
      return CARD_CAMPOS_PADRAO
    }
  })()
  /** Com o menu aberto, o rascunho local manda (resposta imediata). */
  const camposEfetivos = camposDraft ?? camposDoCard
  const temCampo = (k: CardCampo) => camposEfetivos.includes(k)

  /**
   * CAMPOS COMPLEMENTARES no construtor de filtros do quadro (2026-09-02):
   * todo campo visivel entra com a chave "cf:<key>". Nos selects o VALOR do
   * criterio e o value da opcao (o TagSelect mostra o rotulo).
   */
  const tipoComplParaFiltro = tipoDoCampoComplementar
  const camposComplFiltraveis = (camposCompl.data?.fields ?? []).filter((f) => f.defaultVisible)

  /**
   * Catálogo de colunas da lista (SPEC-20260914-2231): as fixas mais um
   * `cf:<key>` por campo complementar visível. É daqui que sai tanto o
   * seletor quanto o desenho da tabela — uma lista só, sem dois lugares para
   * manter em sincronia.
   */
  const colunasDisponiveis: { key: string; rotulo: string; largura: number; compl?: (typeof camposComplFiltraveis)[number] }[] = [
    ...COLUNAS_LISTA.map((c) => ({
      key: c.key as string,
      rotulo: c.rotulo,
      largura: largurasLista[c.key] ?? (c.largura as number),
    })),
    ...camposComplFiltraveis.map((f) => ({
      key: `cf:${f.key}`,
      rotulo: rotuloCampo(f.labels, f.key),
      largura: largurasLista[`cf:${f.key}`] ?? 130,
      compl: f,
    })),
  ]
  /** As escolhidas, na ordem escolhida; chave órfã (campo apagado) some. */
  const colunasAtivas = colunasLista
    .map((k) => colunasDisponiveis.find((c) => c.key === k))
    .filter((c): c is (typeof colunasDisponiveis)[number] => Boolean(c))
  const filterFields: FilterField[] = [
    ...CARD_FILTER_FIELDS,
    ...camposComplFiltraveis.map((f) => ({
      key: `cf:${f.key}`,
      label: rotuloCampo(f.labels, f.key),
      type: tipoComplParaFiltro(f.type),
      placeholder: 'Valor…',
    })),
  ]

  const unmappedCount = unmapped.data?.items.length ?? 0
  /** Itens criados aqui nascem privados? Regra do quadro (SPEC-20261007-0934-visibilidade-padrao-do-quadro). */
  const novosItensPrivados = (boardData?.board as { newItemsPrivate?: boolean } | undefined)?.newItemsPrivate ?? false
  /** Itens criados aqui aparecem só neste quadro? (SPEC-20261007-1538-quadros-do-item) */
  const novosItensExclusivos = (boardData?.board as { newItemsExclusive?: boolean } | undefined)?.newItemsExclusive ?? false

  const isFavorite = boardList.data?.boards.find((b) => b.id === id)?.isFavorite ?? false

  // ------------------------------------------------------------ lanes UI ---

  const lanes: DraftLane[] = editMode
    ? draft
    : boardData.columns.map((c) => ({
        key: c.id,
        id: c.id,
        name: labelOf(c.name),
        statusId: c.statusId,
        wipLimit: c.wipLimit,
        flowStepIds: c.flowStepIds ?? [],
      }))

  // painel "Etapas nao mapeadas" (primeira coluna no modo de edicao): tudo
  // que nao esta em nenhuma lane do rascunho, agrupado por fluxo
  const etapasDoQuadro = config.data?.flowSteps ?? []

  // Etapas que o composer PRECISA perguntar (regra 2026-08-24): SO as etapas
  // DA LANE sao opcao. Lane com 2+ etapas pergunta em qual o item nasce;
  // lane com exatamente 1 nao pergunta (usa a dela); lane sem etapa (ou
  // quadro sem fluxo) nao pergunta — o item nasce sem etapa.
  const etapasAPerguntar = (lane: DraftLane): typeof etapasDoQuadro | null => {
    if (lane.flowStepIds.length < 2) return null
    const opcoes = etapasDoQuadro.filter((s) => lane.flowStepIds.includes(s.flowStepId))
    return opcoes.length > 0 ? opcoes : null
  }
  const naoMapeadas = editMode
    ? etapasDoQuadro.filter((s) => !draft.some((l) => l.flowStepIds.includes(s.flowStepId)))
    : []
  // TODOS os fluxos do quadro aparecem no painel — mesmo sem etapa sobrando
  const fluxosDoPool = [...new Set(etapasDoQuadro.map((e) => e.flowName))]
  const alternarFluxo = (nome: string) =>
    setFluxosFechados((atual) => {
      const novo = new Set(atual)
      if (novo.has(nome)) novo.delete(nome)
      else novo.add(nome)
      return novo
    })

  /** O filtro da visão escolhida — o que recorta os cartões. */
  const activeCardFilter = filtroDoQuadro(visaoAtiva)
  /** Filtro rápido com critérios de verdade (vazio = nada aplicado). */
  const filtroRapidoAtivo = filtroRapido && filtroRapido.criteria.length > 0 ? filtroRapido : null
  /** O período do cabeçalho como filtro ("entre" com lados opcionais). */
  const filtroDePeriodo = filtroDoPeriodo(periodo)
  /** Visão escolhida, filtro rápido E período: o cartão precisa passar em todos. */
  const passaNosFiltrosDaBarra = (values: ReturnType<typeof cardValues>) =>
    (!activeCardFilter || evaluateFilter(values, activeCardFilter)) &&
    (!filtroRapidoAtivo || evaluateFilter(values, filtroRapidoAtivo)) &&
    (!filtroDePeriodo || evaluateFilter(values, filtroDePeriodo))
  /** Trocar o tipo com a visão em uso grava nela, se a pessoa pode editá-la. */
  const trocarModo = (m: ModoDaVisao) => {
    setModoVisao(m)
    if (visaoAtiva?.canEdit && visaoAtiva.config.modo !== m) {
      alterarVisao.mutate(
        { id: visaoAtiva.id, config: { modo: m } },
        { onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar na visão.' }) },
      )
    }
  }
  const confirmarApagarVisao = (v: Visao) => {
    if (!window.confirm(`A visão “${v.name}” será apagada para todos que a veem.`)) return
    apagarVisao.mutate(v.id, {
      onSuccess: () => {
        if (activeCardFilterId === v.id) setActiveCardFilterId(null)
        toast.push({ tone: 'success', message: 'Visão apagada.' })
      },
      onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível apagar.' }),
    })
  }
  type BoardCard = (typeof boardData.columns)[number]['cards'][number]
  /** Achata o cartao nos campos declarados em CARD_FILTER_FIELDS. */
  const cardValues = (card: BoardCard) => ({
    title: card.title,
    code: card.code,
    type: labelOf(card.type.nameKey),
    status: statusLabel(card.statusId),
    priority: labelOf(card.priority.nameKey),
    assignee: card.assignee?.name ?? null,
    /** Membros do item (multi-valor): o filtro 'in' casa por qualquer um. */
    members: (card.members ?? []).map((m) => m.name),
    /** Etiquetas do item (multi-valor, 2026-09-02). */
    labels: (card.labels ?? []).map((l) => l.name),
    company: card.company?.name ?? null,
    /** Quadros escolhidos no item, ou "Todos" — as mesmas regras da lista de Itens. */
    boards: valorDeQuadros((card as { boards?: { name: string }[] }).boards, labelOf),
    dueAt: card.dueAt,
    // demais campos da barra de propriedades (SPEC-20261008-1745-todos-os-campos-no-filtro)
    startAt: card.startAt ?? null,
    dias: diasDoItem(card as { startAt?: string | null; dueAt?: string | null; durationDays?: number | null }),
    createdAt: (card as { createdAt?: string | null }).createdAt ?? null,
    // data de conclusão por período (SPEC-20261104-1638)
    completedAt: (card as { completedAt?: string | null }).completedAt ?? null,
    // busca direta: `nomeDaArea` só é declarada mais abaixo, e a busca do
    // cabeçalho chama esta função antes dela (TDZ)
    area: card.companyAreaId
      ? labelOf((bootstrap.data?.areas ?? []).find((a) => a.id === card.companyAreaId)?.nameTranslationKey ?? '') || null
      : null,
    etapa: card.flowStepId ? ((config.data?.flowSteps ?? []).find((s) => s.flowStepId === card.flowStepId)?.stepName ?? null) : null,
    portfolio: card.portfolioNodeId ? (nomeDoNoDoPortfolio.get(card.portfolioNodeId) ?? null) : null,
    // campos complementares: valor bruto (value das opcoes nos selects)
    ...Object.fromEntries(
      camposComplFiltraveis.map((f) => {
        const v = ((card as { customValues?: Record<string, unknown> }).customValues ?? {})[f.key]
        return [
          `cf:${f.key}`,
          Array.isArray(v) ? v.map(String) : v == null || v === '' ? null : typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v),
        ]
      }),
    ),
  })

  /** Busca sem acento e sem caixa: casa codigo, titulo, pessoas e empresa. */
  const semAcento = (t: string) => t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const buscaAtiva = buscaCards.trim() !== ''
  const casaBusca = (card: BoardCard) => {
    if (!buscaAtiva) return true
    const alvo = semAcento(
      [
        card.code,
        card.title,
        card.assignee?.name ?? '',
        ...(card.members ?? []).map((m) => m.name),
        card.company?.name ?? '',
      ].join(' '),
    )
    return alvo.includes(semAcento(buscaCards.trim()))
  }

  const cardsOf = (lane: { id?: string; statusId: string | null }) => {
    const cards = editMode
      ? (boardData.columns.find((c) => c.statusId === lane.statusId)?.cards ?? [])
      : (boardData.columns.find((c) => c.id === lane.id)?.cards ?? [])
    // filtro FIXO do quadro (configuracao) + filtro rapido escolhido na barra
    const boardFilter = parseBoardFilter(boardData.board.filter)
    return cards.filter(
      (card) =>
        (!boardFilter || evaluateFilter(cardValues(card), boardFilter)) &&
        passaNosFiltrosDaBarra(cardValues(card)) &&
        // a busca FILTRA (pedido do Cristian, 2026-10-02): antes ela so realcava
        // quem casava e apagava o resto, e os cartoes que nao interessavam
        // continuavam ocupando a coluna
        casaBusca(card),
    )
  }

  const totalAchados = (() => {
    if (!buscaAtiva) return 0
    const boardFilter = parseBoardFilter(boardData.board.filter)
    const vistos = new Set<string>()
    let total = 0
    for (const col of boardData.columns) {
      for (const card of col.cards) {
        if (vistos.has(card.id)) continue
        vistos.add(card.id)
        if (boardFilter && !evaluateFilter(cardValues(card), boardFilter)) continue
        if (!passaNosFiltrosDaBarra(cardValues(card))) continue
        if (casaBusca(card)) total++
      }
    }
    return total
  })()

  // -------------------------------------------------- agrupamento (bandas) ---
  /** Todos os cartoes visiveis (uma vez cada), ja passados pelos filtros. */
  /** Lane onde cada cartão está — a lista precisa dela para mover o item. */
  const laneDoCard = new Map<string, string>()
  const cardsVisiveis = (() => {
    const boardFilter = parseBoardFilter(boardData.board.filter)
    const vistos = new Set<string>()
    const out: BoardCard[] = []
    for (const col of boardData.columns) {
      for (const card of col.cards) {
        if (vistos.has(card.id)) continue
        if (boardFilter && !evaluateFilter(cardValues(card), boardFilter)) continue
        if (!passaNosFiltrosDaBarra(cardValues(card))) continue
        vistos.add(card.id)
        laneDoCard.set(card.id, col.id)
        out.push(card)
      }
    }
    return out
  })()

  type Banda = {
    key: string
    label: string
    icon?: string
    count: number
    filter: (c: BoardCard) => boolean
    /**
     * Item DONO do checklist, quando não é o item do agrupamento (2026-09-23).
     *
     * Dentro da banda de um item aparecem checklists dele e dos descendentes, e
     * os nomes repetem: três "Bugs" lado a lado, sem dizer de quem são. Quando
     * o dono é outro item, o cabeçalho mostra o código dele, clicável.
     */
    dono?: { id: string; code: string }
  }
  const nomeDaArea = (areaId: string) =>
    labelOf((bootstrap.data?.areas ?? []).find((a) => a.id === areaId)?.nameTranslationKey ?? '') || 'Área'
  /**
   * SUB-BANDAS por checklist (pedido 2026-08-30).
   *
   * Agrupando por item, os filhos dele que nasceram de uma linha de checklist
   * ("Converter em item") entram sob o nome do checklist de origem — assim as
   * atividades de cada lista ficam juntas em vez de num monte só. Quem não veio
   * de checklist fica em "Sem checklist".
   *
   * Sem nenhum filho vindo de checklist não há sub-banda: um cabeçalho "Sem
   * checklist" sozinho só faria barulho.
   */
  /**
   * : o id do item da banda de NIVEL 1 (quando se agrupa por
   * tipo, a banda é um item). Serve só para decidir se o código do dono do
   * checklist precisa aparecer — no terceiro nível a banda pai já não é o
   * item, então ele não dá para ser deduzido daqui de dentro.
   */
  /**
   * `itemDoGrupo` é o id do item da banda de NÍVEL 1 — agrupando por tipo, a
   * banda é um item. Serve só para decidir se o código do dono do checklist
   * precisa aparecer: no terceiro nível a banda pai já não é o item, então
   * ele não dá para ser deduzido daqui de dentro.
   */
  const subBandasDe = (banda: Banda, criterio: string, itemDoGrupo?: string): Banda[] => {
    if (criterio === 'none') return []
    // a faixa "Sem X" também subdivide (2026-09-15): ela deixou de ser um resto
    // desde que se pode agrupar por status — "Sem responsável" com 15 itens é
    // um grupo legítimo, e não separá-lo destoava de todas as faixas vizinhas.
    // Exceção: sob "Item pai", onde "Sem grupo" é de fato o que sobrou.
    if (banda.key === '__sem__' && agruparPor.startsWith('type:')) return []
    // "checklist" só faz sentido sob a banda de um item pai — é de lá que vêm
    // os filhos convertidos de linha de checklist
    if (criterio === 'checklist' && !agruparPor.startsWith('type:')) return []
    const doGrupo = cardsVisiveis.filter(banda.filter)

    /** Sub-bandas por um atributo do cartão (área, empresa, responsável). */
    const porAtributo = (
      chave: (c: BoardCard) => string | null,
      rotulo: (c: BoardCard) => string,
      icon: string,
      semLabel: string,
      /** Ordem própria do atributo (status no catálogo, etapa no fluxo). */
      ordemDaChave?: (k: string) => number,
      iconeDaChave?: (c: BoardCard) => string,
    ): Banda[] => {
      const grupos = new Map<string, { label: string; count: number; icon: string }>()
      let sem = 0
      for (const c of doGrupo) {
        const k = chave(c)
        if (k === null) {
          sem += 1
          continue
        }
        const g = grupos.get(k) ?? { label: rotulo(c), count: 0, icon: iconeDaChave?.(c) ?? icon }
        g.count += 1
        grupos.set(k, g)
      }
      const lista: Banda[] = [...grupos.entries()]
        .sort((a, b) =>
          ordemDaChave ? ordemDaChave(a[0]) - ordemDaChave(b[0]) : a[1].label.localeCompare(b[1].label, 'pt-BR'),
        )
        .map(([k, g]) => ({
          key: k,
          label: g.label,
          icon: g.icon,
          count: g.count,
          filter: (c: BoardCard) => banda.filter(c) && chave(c) === k,
        }))
      if (sem > 0) {
        lista.push({
          key: '__sem__',
          label: semLabel,
          icon,
          count: sem,
          filter: (c) => banda.filter(c) && chave(c) === null,
        })
      }
      return lista.length > 1 || (lista.length === 1 && lista[0]!.key !== '__sem__') ? lista : []
    }

    if (criterio === 'area') {
      return porAtributo((c) => c.companyAreaId ?? null, (c) => nomeDaArea(c.companyAreaId!), 'fas fa-sitemap', 'Sem área')
    }
    if (criterio === 'company') {
      return porAtributo((c) => c.company?.id ?? null, (c) => c.company?.name ?? '', 'fas fa-building', 'Sem empresa')
    }
    // os mesmos critérios de andamento do primeiro nível, com a ordem de negócio
    if (criterio === 'status') {
      const ordem = new Map((bootstrap.data?.workItemStatuses ?? []).map((s, i) => [s.id, i]))
      return porAtributo(
        (c) => c.statusId ?? null,
        (c) => statusLabel(c.statusId),
        'fas fa-adjust',
        'Sem status',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
      )
    }
    if (criterio === 'etapa') {
      const passos = config.data?.flowSteps ?? []
      const ordem = new Map(passos.map((s, i) => [s.flowStepId, i]))
      return porAtributo(
        (c) => c.flowStepId ?? null,
        (c) => passos.find((s) => s.flowStepId === c.flowStepId)?.stepName ?? '—',
        'fas fa-project-diagram',
        'Sem etapa',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
      )
    }
    if (criterio === 'tipoitem') {
      const ordem = new Map((bootstrap.data?.workItemTypes ?? []).map((t, i) => [t.code, i]))
      return porAtributo(
        (c) => c.type?.code ?? null,
        (c) => labelOf(c.type.nameKey),
        'fas fa-tasks',
        'Sem tipo',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
        (c) => c.type?.icon ?? 'fas fa-tasks',
      )
    }
    if (criterio === 'responsavel') {
      return porAtributo(
        (c) => c.assignee?.id ?? null,
        (c) => c.assignee?.name ?? '',
        'fas fa-user-check',
        'Sem responsável',
      )
    }
    if (criterio === 'member') {
      // um cartão com vários membros aparece na sub-banda de CADA um
      const grupos = new Map<string, { label: string; count: number }>()
      let sem = 0
      for (const c of doGrupo) {
        const ms = c.members ?? []
        if (ms.length === 0) {
          sem += 1
          continue
        }
        for (const m of ms) {
          const g = grupos.get(m.id) ?? { label: m.name, count: 0 }
          g.count += 1
          grupos.set(m.id, g)
        }
      }
      const lista: Banda[] = [...grupos.entries()]
        .sort((a, b) => a[1].label.localeCompare(b[1].label, 'pt-BR'))
        .map(([mid, g]) => ({
          key: mid,
          label: g.label,
          icon: 'fas fa-user',
          count: g.count,
          filter: (c: BoardCard) => banda.filter(c) && (c.members ?? []).some((m) => m.id === mid),
        }))
      if (sem > 0) {
        lista.push({
          key: '__sem__',
          label: 'Sem membro',
          icon: 'fas fa-user',
          count: sem,
          filter: (c) => banda.filter(c) && (c.members ?? []).length === 0,
        })
      }
      return lista.length > 1 || (lista.length === 1 && lista[0]!.key !== '__sem__') ? lista : []
    }

    // CHECKLIST (padrão): as atividades entram sob a lista de onde saíram
    type ChecklistDoCard = {
      id: string
      name: string
      sortOrder?: number
      dono?: { id: string; code: string }
    }
    const listas = new Map<
      string,
      { label: string; count: number; ordem: number; dono?: { id: string; code: string } }
    >()
    let semLista = 0
    for (const c of doGrupo) {
      const cl = (c as { checklist?: ChecklistDoCard | null }).checklist
      if (!cl) {
        semLista += 1
        continue
      }
      /*
       * O código do dono só entra quando ele NÃO é o item do agrupamento: ali
       * em cima já está escrito, e repetir em cada sublista seria ruído. O
       * caso que o usuário relatou é o contrário — checklist de um neto, cujo
       * dono não aparece em lugar nenhum da tela.
       */
      const donoOutro = cl.dono && cl.dono.id !== (itemDoGrupo ?? banda.key) ? cl.dono : undefined
      const g = listas.get(cl.id) ?? { label: cl.name, count: 0, ordem: cl.sortOrder ?? 0, dono: donoOutro }
      g.count += 1
      listas.set(cl.id, g)
    }
    if (listas.size === 0) return []
    // na ORDEM em que os checklists estao no Item de Trabalho (2026-08-31),
    // nao alfabetica; o nome desempata checklists na mesma posicao
    const subs: Banda[] = [...listas.entries()]
      .sort((a, b) => a[1].ordem - b[1].ordem || a[1].label.localeCompare(b[1].label, 'pt-BR'))
      .map(([clId, g]) => ({
        key: clId,
        label: g.label,
        icon: 'fas fa-list-ul',
        count: g.count,
        dono: g.dono,
        filter: (c: BoardCard) =>
          banda.filter(c) && (c as { checklist?: { id: string } | null }).checklist?.id === clId,
      }))
    if (semLista > 0) {
      subs.push({
        key: '__semlista__',
        label: 'Sem checklist',
        icon: 'fas fa-minus',
        count: semLista,
        filter: (c) => banda.filter(c) && !(c as { checklist?: unknown }).checklist,
      })
    }
    return subs
  }

  /** Monta as bandas conforme o "Agrupar por" escolhido. */
  const bandas: Banda[] = (() => {
    if (agruparPor === 'none' || editMode) return []

    // Membro / Área / Empresa: particiona os cartoes; banda "Sem ..." no fim.
    const porAtributo = (
      chave: (c: BoardCard) => string | null,
      rotulo: (c: BoardCard) => string,
      icon: string,
      semLabel: string,
      /**
       * Ordem das bandas quando o atributo TEM ordem própria (status no
       * catálogo, etapa no fluxo): devolve a posição da chave. Sem isto as
       * bandas sairiam em ordem alfabética — "Concluído" antes de "Pendente".
       */
      ordemDaChave?: (k: string) => number,
      /** Ícone por banda, quando o próprio atributo traz um (tipo de item). */
      iconeDaChave?: (c: BoardCard) => string,
    ): Banda[] => {
      const grupos = new Map<string, { label: string; count: number; icon: string }>()
      let semCount = 0
      for (const c of cardsVisiveis) {
        const k = chave(c)
        if (k === null) {
          semCount += 1
          continue
        }
        const g = grupos.get(k) ?? { label: rotulo(c), count: 0, icon: iconeDaChave?.(c) ?? icon }
        g.count += 1
        grupos.set(k, g)
      }
      const lista = [...grupos.entries()]
        .sort((a, b) =>
          ordemDaChave
            ? ordemDaChave(a[0]) - ordemDaChave(b[0])
            : a[1].label.localeCompare(b[1].label, 'pt-BR'),
        )
        .map(([k, g]) => ({ key: k, label: g.label, icon: g.icon, count: g.count, filter: (c: BoardCard) => chave(c) === k }))
      if (semCount > 0) {
        lista.push({ key: '__sem__', label: semLabel, icon, count: semCount, filter: (c) => chave(c) === null })
      }
      return lista
    }

    // MEMBRO: agrupa pelos MEMBROS adicionados ao item (varios por item — o
    // card aparece na banda de CADA membro); "Sem membro" no fim.
    if (agruparPor === 'member') {
      const grupos = new Map<string, { label: string; count: number }>()
      let semCount = 0
      for (const c of cardsVisiveis) {
        const ms = c.members ?? []
        if (ms.length === 0) {
          semCount += 1
          continue
        }
        for (const m of ms) {
          const g = grupos.get(m.id) ?? { label: m.name, count: 0 }
          g.count += 1
          grupos.set(m.id, g)
        }
      }
      const lista: Banda[] = [...grupos.entries()]
        .sort((a, b) => a[1].label.localeCompare(b[1].label, 'pt-BR'))
        .map(([id2, g]) => ({
          key: id2,
          label: g.label,
          icon: 'fas fa-user',
          count: g.count,
          filter: (c: BoardCard) => (c.members ?? []).some((m) => m.id === id2),
        }))
      if (semCount > 0) {
        lista.push({
          key: '__sem__',
          label: 'Sem membro',
          icon: 'fas fa-user',
          count: semCount,
          filter: (c) => (c.members ?? []).length === 0,
        })
      }
      return lista
    }
    // RESPONSAVEL: agrupa somente pelo responsavel (assignee) do item.
    if (agruparPor === 'responsavel') {
      return porAtributo((c) => c.assignee?.id ?? null, (c) => c.assignee?.name ?? '', 'fas fa-user-check', 'Sem responsável')
    }
    if (agruparPor === 'area') {
      return porAtributo((c) => c.companyAreaId ?? null, (c) => nomeDaArea(c.companyAreaId!), 'fas fa-sitemap', 'Sem área')
    }
    if (agruparPor === 'company') {
      return porAtributo((c) => c.company?.id ?? null, (c) => c.company?.name ?? '', 'fas fa-building', 'Sem empresa')
    }

    // STATUS: na ordem do CATÁLOGO, não alfabética — "Pendente" antes de
    // "Concluído" é o que se espera ler (SPEC-20260915-0136).
    if (agruparPor === 'status') {
      const ordem = new Map((bootstrap.data?.workItemStatuses ?? []).map((s, i) => [s.id, i]))
      return porAtributo(
        (c) => c.statusId ?? null,
        (c) => statusLabel(c.statusId),
        'fas fa-adjust',
        'Sem status',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
      )
    }

    // ETAPA do fluxo: na ordem em que o servidor já entrega os passos (ele
    // caminha as transições), que é a sequência real do fluxo.
    if (agruparPor === 'etapa') {
      const passos = config.data?.flowSteps ?? []
      const ordem = new Map(passos.map((s, i) => [s.flowStepId, i]))
      return porAtributo(
        (c) => c.flowStepId ?? null,
        (c) => passos.find((s) => s.flowStepId === c.flowStepId)?.stepName ?? '—',
        'fas fa-project-diagram',
        'Sem etapa',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
      )
    }

    // TIPO DO ITEM (Tarefa, Atividade…) — diferente de `type:<code>`, que
    // agrupa pelos ITENS de um tipo e põe os filhos deles dentro.
    if (agruparPor === 'tipoitem') {
      // o cartão traz o tipo por CODE (não por id) — é essa a chave da banda
      const ordem = new Map((bootstrap.data?.workItemTypes ?? []).map((t, i) => [t.code, i]))
      return porAtributo(
        (c) => c.type?.code ?? null,
        (c) => labelOf(c.type.nameKey),
        'fas fa-tasks',
        'Sem tipo',
        (k) => ordem.get(k) ?? Number.MAX_SAFE_INTEGER,
        (c) => c.type?.icon ?? 'fas fa-tasks',
      )
    }

    // Por TIPO: cada item do tipo escolhido vira uma banda; dentro ficam os
    // FILHOS desse item. Os itens do tipo nao aparecem como cartao (sao cabeca
    // da banda). O que nao e filho de nenhum item do tipo vai pra "Sem grupo".
    if (agruparPor.startsWith('type:')) {
      const code = agruparPor.slice('type:'.length)
      const iconeDoTipo = (bootstrap.data?.workItemTypes ?? []).find((t) => t.code === code)?.icon ?? 'fas fa-cube'
      // PAIS do tipo escolhido — vindos do campo `parent` de cada card, entao o
      // pai entra como banda MESMO nao sendo um card do quadro (fluxo diferente,
      // nao mapeado).
      const paisPorId = new Map<string, { id: string; code: string; title: string }>()
      for (const c of cardsVisiveis) {
        if (c.parent && c.parent.typeCode === code) paisPorId.set(c.parent.id, c.parent)
      }
      const idsPais = new Set(paisPorId.keys())
      /**
       * O pai puxa TODOS os descendentes (2026-08-31): cada card sobe a
       * cadeia de pais ate o ANCESTRAL MAIS ALTO do tipo escolhido, e e essa
       * a unica banda dele. Antes so o filho direto agrupava — neto caia em
       * "Sem grupo" — e um pai aninhado ganhava banda propria alem de
       * aparecer dentro do avo, repetindo o card em duas bandas.
       */
      const cardPorId = new Map(cardsVisiveis.map((c) => [c.id, c]))
      /*
        Sobe pelo pai EFETIVO que o servidor manda em `parent`: o "Item pai"
        e, sem ele, o dono do checklist em que o item está — como na árvore de
        Itens de Trabalho (SPEC-20261010-1814-agrupar-por-tipo-com-vinculo-de-checklist).
      */
      const paiDe = (c: BoardCard) => c.parent?.id ?? c.parentWorkItemId ?? null
      const bandaDoCard = new Map<string, string | null>()
      for (const c of cardsVisiveis) {
        let topo: string | null = null
        let atual: BoardCard | undefined = c
        const vistos = new Set<string>()
        while (atual && paiDe(atual) && !vistos.has(atual.id)) {
          vistos.add(atual.id)
          const pai = paiDe(atual)!
          if (idsPais.has(pai)) topo = pai
          atual = cardPorId.get(pai)
        }
        bandaDoCard.set(c.id, topo)
      }
      const chavesComCard = new Set([...bandaDoCard.values()].filter((v): v is string => v !== null))
      // "Sem grupo": sem ancestral do tipo E nao e cabeca de banda
      const naoAgrupado = (c: BoardCard) => bandaDoCard.get(c.id) === null && !chavesComCard.has(c.id)
      const bandasTipo: Banda[] = [...paisPorId.values()]
        .filter((pai) => chavesComCard.has(pai.id))
        .sort((a, b) => a.code.localeCompare(b.code, 'pt-BR', { numeric: true }))
        .map((pai) => ({
          key: pai.id,
          label: `${pai.code} · ${pai.title}`,
          icon: iconeDoTipo,
          count: cardsVisiveis.filter((c) => bandaDoCard.get(c.id) === pai.id).length,
          filter: (c: BoardCard) => bandaDoCard.get(c.id) === pai.id,
        }))
      const semCount = cardsVisiveis.filter(naoAgrupado).length
      if (semCount > 0) {
        bandasTipo.push({
          key: '__sem__',
          label: 'Sem grupo',
          icon: 'fas fa-layer-group',
          count: semCount,
          filter: naoAgrupado,
        })
      }
      return bandasTipo
    }
    return []
  })()
  const agrupado = bandas.length > 0

  /** Opcoes existentes na base/no quadro para o campo do construtor. */
  const loadCardValueOptions = (fieldKey: string, query: string) => {
    const match = (label: string) => label.toLowerCase().includes(query.trim().toLowerCase())
    const uniq = (values: (string | null | undefined)[]) =>
      [...new Set(values.filter((v): v is string => Boolean(v)))].filter(match).map((v) => ({ value: v, label: v }))
    const allCards = boardData.columns.flatMap((c) => c.cards)

    // campos complementares: opcoes do proprio cadastro do campo
    if (fieldKey.startsWith('cf:')) {
      const f = camposComplFiltraveis.find((x) => `cf:${x.key}` === fieldKey)
      if (!f) return []
      if (f.type === 'boolean') return [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }].filter((o) => match(o.label))
      return (f.config.options ?? []).filter((o) => match(o.label))
    }
    switch (fieldKey) {
      case 'type':
        return uniq((bootstrap.data?.workItemTypes ?? []).filter((x) => x.isActive).map((x) => labelOf(x.nameTranslationKey)))
      case 'status':
        return uniq((bootstrap.data?.workItemStatuses ?? []).filter((s) => s.isActive).map((s) => statusLabel(s.id)))
      case 'priority':
        return uniq((bootstrap.data?.priorities ?? []).map((p) => labelOf(p.nameTranslationKey)))
      case 'assignee':
        return uniq((bootstrap.data?.members ?? []).filter((c) => c.isActive).map((c) => c.name))
      case 'members':
        // filtro por MEMBROS: escolhe entre os membros ATIVOS
        return uniq((bootstrap.data?.members ?? []).filter((c) => c.isActive).map((c) => c.name))
      case 'labels':
        // etiquetas do WORKSPACE (nao so as dos cards visiveis)
        return uniq((etiquetas.data?.labels ?? []).map((l) => l.name))
      case 'company':
        return uniq((bootstrap.data?.companies ?? []).map((c) => c.name))
      case 'area':
        return uniq((bootstrap.data?.areas ?? []).map((a) => labelOf(a.nameTranslationKey)))
      case 'etapa':
        return uniq((config.data?.flowSteps ?? []).map((s) => s.stepName))
      case 'portfolio':
        return uniq([...nomeDoNoDoPortfolio.values()])
      case 'boards':
        return uniq([QUADROS_TODOS, ...(boardList.data?.boards ?? []).map((b) => labelOf(b.nameTranslationKey))])
      case 'title':
        return uniq(allCards.map((c) => c.title))
      case 'code':
        return uniq(allCards.map((c) => c.code))
      default:
        return []
    }
  }

  const reorderLane = (fromKey: string, toIndex: number) => {
    setDraft((current) => {
      const fromIndex = current.findIndex((l) => l.key === fromKey)
      if (fromIndex < 0) return current
      const next = [...current]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex > fromIndex ? toIndex - 1 : toIndex, 0, moved!)
      return next
    })
  }

  const patchLane = (key: string, patch: Partial<DraftLane>) =>
    setDraft((current) => current.map((lane) => (lane.key === key ? { ...lane, ...patch } : lane)))

  // ------------------------------------------- tela "Editar quadro" ---
  // Padrao FormScreen (referencia Conta Azul): ocupa a area de trabalho no
  // lugar do quadro (sem modal), secoes recolhiveis por assunto e rodape com
  // Cancelar/Salvar + dirty check. Secoes de escopo ja preenchidas abrem
  // expandidas; vazias abrem recolhidas.
  if (editBoardOpen) {
    const alteradoQuadro = editBase !== null && JSON.stringify(editForm) !== JSON.stringify(editBase)
    const salvarQuadroValido =
      alteradoQuadro && editForm.name.trim() !== '' && editForm.ownerMemberId !== '' && !saveBoardMeta.isPending
    const conta = (n: number) => (n > 0 ? ` (${n})` : '')

    return (
      <FormScreen
        title="Editar quadro"
        subtitle={boardName}
        onClose={() => setEditBoardOpen(false)}
        dirty={alteradoQuadro}
        saveLabel="Salvar"
        saveDisabled={!salvarQuadroValido}
        saving={saveBoardMeta.isPending}
        onSubmit={() => salvarQuadroValido && saveBoardMeta.mutate()}
      >
        {/* -------------------------------------------------------- geral */}
        <FormSection title="Informações do quadro">
          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock
              label="Título do quadro"
              required
              error={editForm.name.trim() === '' ? 'O título do quadro é obrigatório' : null}
            >
              {({ id: fieldId, invalid }) => (
                <Input
                  id={fieldId}
                  invalid={invalid}
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição">
              {({ id: fieldId }) => (
                <Input
                  id={fieldId}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Dono do quadro" required>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  value={editForm.ownerMemberId === '' ? null : editForm.ownerMemberId}
                  placeholder="Selecione o dono"
                  options={(bootstrap.data?.members ?? [])
                    .filter((col) => col.isActive)
                    .map((col) => ({ value: col.id, label: col.name }))}
                  onChange={(v) => setEditForm((f) => ({ ...f, ownerMemberId: v }))}
                />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        {/* ------------------------------------------------- novos itens */}
        <FormSection title="Novos itens">
          <FieldBlock
            label="Itens criados neste quadro nascem"
            hint="Vale para todo item criado pelo quadro — na lane, na planilha, na lista ou por template."
          >
            {() => (
              <VisibilidadeDosNovosItens
                privados={editForm.newItemsPrivate}
                onChange={(privados) => setEditForm((f) => ({ ...f, newItemsPrivate: privados }))}
              />
            )}
          </FieldBlock>
          <FieldBlock
            label="Itens criados somente para este quadro"
            hint="Ligado, o campo Quadros de cada item criado aqui já vem com este quadro, e o item não aparece nos outros. Dá para mudar item a item na barra de propriedades."
          >
            {() => (
              <SomenteNesteQuadro
                exclusivos={editForm.newItemsExclusive}
                onChange={(exclusivos) => setEditForm((f) => ({ ...f, newItemsExclusive: exclusivos }))}
              />
            )}
          </FieldBlock>
        </FormSection>

        {/* -------------------------------------------------------- areas */}
        <FormSection title={`Áreas${conta(editForm.areaIds.length)}`} defaultOpen={editForm.areaIds.length > 0}>
          <FieldBlock label="Áreas do quadro" hint="Opcional. Sem área escolhida, o quadro mostra itens de todas as áreas.">
            {({ id: fieldId }) => (
              <ScopePicker
                id={fieldId}
                placeholder="Buscar área…"
                nodes={(bootstrap.data?.areas ?? [])
                  .filter((a) => a.isActive)
                  .map((a) => ({
                    id: a.id,
                    label: labelOf(a.nameTranslationKey),
                    icon: 'fas fa-sitemap',
                    parentId: null,
                    depth: 0,
                    selectable: true,
                  }))}
                value={editForm.areaIds}
                onChange={(areaIds) => setEditForm((f) => ({ ...f, areaIds }))}
                emptyMessage="Nenhuma área escolhida: o quadro mostra itens de todas as áreas."
              />
            )}
          </FieldBlock>
        </FormSection>

        {/* ----------------------------------------------- tipos de item */}
        <FormSection
          title={`Tipos de itens${conta(editForm.workItemTypeIds.length)}`}
          defaultOpen={editForm.workItemTypeIds.length > 0}
        >
          <FieldBlock
            label="Tipos de Item de Trabalho que participam"
            hint="Opcional. Sem nenhum tipo escolhido, o quadro mostra itens de todos os tipos."
          >
            {({ id: fieldId }) => (
              <ScopePicker
                id={fieldId}
                placeholder="Buscar tipo de item…"
                nodes={(bootstrap.data?.workItemTypes ?? [])
                  .filter((x) => x.isActive)
                  .map((x) => ({
                    id: x.id,
                    label: labelOf(x.nameTranslationKey),
                    icon: x.icon ?? 'fas fa-tasks',
                    parentId: null,
                    depth: 0,
                    selectable: true,
                  }))}
                value={editForm.workItemTypeIds}
                onChange={(workItemTypeIds) => setEditForm((f) => ({ ...f, workItemTypeIds }))}
                emptyMessage="Nenhum tipo escolhido: o quadro mostra itens de todos os tipos."
              />
            )}
          </FieldBlock>
        </FormSection>

        {/* -------------------------------------------- produtos/servico */}
        <FormSection
          title={`Produtos/Serviço${conta(editForm.portfolioNodeIds.length)}`}
          defaultOpen={editForm.portfolioNodeIds.length > 0}
        >
          <FieldBlock
            label="Produtos e serviços do portfólio"
            hint="Opcional. Clique na busca para abrir a árvore; um clique seleciona (e habilita Adicionar), dois cliques adicionam ou removem."
          >
            {({ id: fieldId }) => (
              <ScopePicker
                id={fieldId}
                placeholder="Buscar produto ou serviço…"
                nodes={((portfolio.data?.nodes ?? []) as {
                  id: string
                  parentId: string | null
                  name: string
                  level: string
                  depth: number
                }[]).map((node) => ({
                  id: node.id,
                  label: node.name,
                  meta: labelOf(`portfolio_level.${node.level.toLowerCase()}`),
                  icon: 'fas fa-cube',
                  parentId: node.parentId,
                  depth: node.depth,
                  selectable: true,
                }))}
                value={editForm.portfolioNodeIds}
                onChange={(portfolioNodeIds) => setEditForm((f) => ({ ...f, portfolioNodeIds }))}
                emptyMessage="Nenhum produto/serviço escolhido: o quadro considera itens de todo o portfólio."
              />
            )}
          </FieldBlock>
        </FormSection>

        {/* ----------------------------------------------------- empresas */}
        <FormSection title={`Empresas${conta(editForm.companyIds.length)}`} defaultOpen={editForm.companyIds.length > 0}>
          <FieldBlock label="Empresas do quadro" hint="Opcional. Sem empresas, o quadro mostra itens de qualquer empresa.">
            {({ id: fieldId }) => (
              <ScopePicker
                id={fieldId}
                placeholder="Buscar empresa…"
                nodes={(bootstrap.data?.companies ?? []).map((client) => ({
                  id: client.id,
                  label: client.name,
                  icon: 'fas fa-building',
                  parentId: null,
                  depth: 0,
                  selectable: true,
                }))}
                value={editForm.companyIds}
                onChange={(companyIds) => setEditForm((f) => ({ ...f, companyIds }))}
                emptyMessage="Nenhuma empresa escolhido: o quadro considera itens de qualquer empresa."
              />
            )}
          </FieldBlock>
        </FormSection>

        {/* ------------------------------------------------------ membros */}
        <FormSection title={`Membros${conta(editForm.memberIds.length)}`} defaultOpen={editForm.memberIds.length > 0}>
          <FieldBlock
            label="Membros do quadro"
            hint="Quem enxerga este quadro além dos donos. Não filtra os cartões — para ver os itens de uma pessoa, use o filtro da barra. A árvore é por área; um clique seleciona, dois adicionam ou removem."
          >
            {({ id: fieldId }) => {
              const people = (bootstrap.data?.members ?? []).filter((c) => c.isActive)
              const areasDoPicker = [
                ...(bootstrap.data?.areas ?? [])
                  .filter((a) => a.isActive)
                  .map((a) => ({ id: a.id as string | null, name: labelOf(a.nameTranslationKey) })),
                ...(people.some((c) => !c.companyAreaId) ? [{ id: null, name: 'Sem área' }] : []),
              ]
              const nodes: ScopeNode[] = []
              for (const area of areasDoPicker) {
                const areaKey = `a:${area.id ?? 'none'}`
                const rows = people.filter((c) => (c.companyAreaId ?? null) === area.id)
                if (rows.length === 0) continue
                nodes.push({
                  id: areaKey,
                  label: area.name,
                  meta: String(rows.length),
                  icon: 'fas fa-sitemap',
                  parentId: null,
                  depth: 0,
                  selectable: false,
                })
                for (const person of rows) {
                  nodes.push({
                    id: person.id,
                    label: person.name,
                    icon: 'far fa-user',
                    parentId: areaKey,
                    depth: 1,
                    selectable: true,
                  })
                }
              }
              return (
                <ScopePicker
                  id={fieldId}
                  placeholder="Buscar pessoa…"
                  nodes={nodes}
                  value={editForm.memberIds}
                  onChange={(memberIds) => setEditForm((f) => ({ ...f, memberIds }))}
                  emptyMessage="Nenhum membro escolhido: o quadro considera itens de qualquer responsável."
                />
              )
            }}
          </FieldBlock>
        </FormSection>

        {/* ------------------------------------------- fluxos do quadro */}
        <FormSection
          title={`Fluxos do quadro${conta(editForm.flowIds.length)}`}
          defaultOpen={editForm.flowIds.length > 0}
        >
          <p className="adm-hint">
            Com fluxos vinculados, cada lane pode atuar numa etapa deles (escolhida na engrenagem da lane, no modo de
            edição do quadro — opcional).
          </p>
          {fluxos.isError ? (
            <p className="adm-hint">Fluxos indisponíveis neste plano — a tela Fluxos de trabalho não está contratada.</p>
          ) : (fluxos.data?.flows ?? []).length === 0 ? (
            <p className="adm-hint">Nenhum fluxo cadastrado ainda — crie em Administração → Fluxos de trabalho.</p>
          ) : (
            <div className="adm-permgrid" role="group" aria-label="Fluxos do quadro">
              {(fluxos.data?.flows ?? [])
                .filter((f) => f.isActive || editForm.flowIds.includes(f.id))
                .map((f) => (
                  <Checkbox
                    key={f.id}
                    label={`${f.name}${f.stepCount > 0 ? ` (${f.stepCount} ${f.stepCount === 1 ? 'etapa' : 'etapas'})` : ' (sem etapas)'}`}
                    checked={editForm.flowIds.includes(f.id)}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        flowIds: e.target.checked ? [...prev.flowIds, f.id] : prev.flowIds.filter((x) => x !== f.id),
                      }))
                    }
                  />
                ))}
            </div>
          )}
        </FormSection>

        {/* -------------------------------------------------- filtro fixo */}
        <FormSection
          title="Filtro fixo"
          defaultOpen={editForm.filter !== null}
          right={
            editForm.filter ? (
              <Button variant="ghost" onClick={() => setEditForm((f) => ({ ...f, filter: null }))}>
                Remover filtro
              </Button>
            ) : (
              <Button onClick={() => setEditForm((f) => ({ ...f, filter: emptyFilter(filterFields, 'Filtro do quadro') }))}>
                Criar filtro
              </Button>
            )
          }
        >
          <p className="kbform__filter-desc">Filtro avançado do quadro — sempre aplicado. Sem critérios, todos os itens aparecem.</p>
          {editForm.filter ? (
            <FilterEditor
              fields={CARD_FILTER_FIELDS}
              value={editForm.filter}
              onChange={(filter) => setEditForm((f) => ({ ...f, filter }))}
              loadValueOptions={loadCardValueOptions}
            />
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  /**
   * MENU DE AGRUPAMENTO (SPEC-20260915-2223/2234): "+ Agrupar por", "+ Subagrupar",
   * "+ Terceiro nível" em submenus, Colunas e Remover — no estilo do menu de
   * visualizações do Sheets. Usado pela barra do Sheets (planilha) e pelo
   * botão de agrupar do cabeçalho (quadro e lista normal).
   */
  const menuAgrupamento = tabelaMenu
                    ? (() => {
                        const CRITERIOS = [
                          { v: 'status', r: 'Status', i: 'fas fa-adjust' },
                          { v: 'etapa', r: 'Etapa do fluxo', i: 'fas fa-project-diagram' },
                          { v: 'tipoitem', r: 'Tipo do Item', i: 'fas fa-tasks' },
                          { v: 'member', r: 'Membro', i: 'fas fa-user' },
                          { v: 'responsavel', r: 'Responsável', i: 'fas fa-user-check' },
                          { v: 'area', r: 'Área', i: 'fas fa-sitemap' },
                          { v: 'company', r: 'Empresa', i: 'fas fa-building' },
                        ] as const
                        const tipos = (bootstrap.data?.workItemTypes ?? []).filter((t) => t.isActive)
                        const rotuloDe = (v: string) =>
                          v === 'none'
                            ? '—'
                            : v.startsWith('type:')
                              ? `Item pai: ${labelOf(tipos.find((t) => `type:${t.code}` === v)?.nameTranslationKey ?? '')}`
                              : (CRITERIOS.find((c) => c.v === v)?.r ?? (v === 'checklist' ? 'Checklist' : v))
                        const fechar = () => setTabelaMenu(null)
                        const item = (rotulo: string, onClick: () => void, opts?: { mais?: boolean; seta?: boolean; on?: boolean; icone?: string; desc?: string }) => (
                          <button key={rotulo} type="button" className={`kb__smi${opts?.on ? ' kb__smi--on' : ''}`} onClick={onClick}>
                            <span className="kb__smi-pre" aria-hidden>
                              {opts?.mais ? <i className="fas fa-plus" /> : opts?.icone ? <i className={opts.icone} /> : opts?.on ? <i className="fas fa-check" /> : null}
                            </span>
                            <span className="kb__smi-txt">
                              {rotulo}
                              {opts?.desc ? <small>{opts.desc}</small> : null}
                            </span>
                            {opts?.seta ? <i className="fas fa-caret-right kb__smi-seta" aria-hidden /> : null}
                          </button>
                        )
                        const voltar = (rotulo: string, para: typeof tabelaMenu) => (
                          <button type="button" className="kb__smi kb__smi--volta" onClick={() => setTabelaMenu(para)}>
                            <span className="kb__smi-pre" aria-hidden><i className="fas fa-chevron-left" /></span>
                            <span className="kb__smi-txt">{rotulo}</span>
                          </button>
                        )
                        const opcoesSub = (acima: string[]) =>
                          [
                            ...(agruparPor.startsWith('type:') ? ([{ v: 'checklist', r: 'Checklist', i: 'fas fa-list-ul' }] as const) : []),
                            ...CRITERIOS,
                          ].filter((o) => !acima.includes(o.v))
                        return (
                          <>
                            <div className="wi-menu__backdrop" onClick={fechar} />
                            <div className="kb__smenu-tabela" role="menu" aria-label="Visualizações de tabela">
                              {tabelaMenu === 'root' ? (
                                <>
                                  {item('Agrupar por', () => setTabelaMenu('n1'), { mais: true, seta: true, desc: agrupado ? rotuloDe(agruparPor) : undefined })}
                                  {agrupado ? item('Subagrupar por', () => setTabelaMenu('n2'), { mais: true, seta: true, desc: subEfetivo !== 'none' ? rotuloDe(subEfetivo) : undefined }) : null}
                                  {agrupado && subEfetivo !== 'none'
                                    ? item('Terceiro nível', () => setTabelaMenu('n3'), { mais: true, seta: true, desc: sub2Efetivo !== 'none' ? rotuloDe(sub2Efetivo) : undefined })
                                    : null}
                                  <div className="kb__smenu-sep" />
                                  {item('Colunas da tabela', () => { setSeletorColunas(true); fechar() }, { icone: 'fas fa-columns' })}
                                  {agrupado ? item('Remover agrupamento', () => { setAgruparPor('none'); fechar() }, { icone: 'fas fa-ban' }) : null}
                                </>
                              ) : null}
                              {tabelaMenu === 'n1' ? (
                                <>
                                  {voltar('Agrupar por', 'root')}
                                  {item('Sem agrupamento', () => { setAgruparPor('none'); fechar() }, { on: agruparPor === 'none' })}
                                  {CRITERIOS.map((c) => item(c.r, () => { setAgruparPor(c.v); fechar() }, { on: agruparPor === c.v, icone: agruparPor === c.v ? undefined : c.i }))}
                                  {item('Item pai (por tipo)', () => setTabelaMenu('n1tipo'), { seta: true, on: agruparPor.startsWith('type:'), icone: agruparPor.startsWith('type:') ? undefined : 'fas fa-sitemap' })}
                                </>
                              ) : null}
                              {tabelaMenu === 'n1tipo' ? (
                                <>
                                  {voltar('Item pai (por tipo)', 'n1')}
                                  {tipos.map((t) => item(labelOf(t.nameTranslationKey), () => { setAgruparPor(`type:${t.code}`); fechar() }, { on: agruparPor === `type:${t.code}` }))}
                                </>
                              ) : null}
                              {tabelaMenu === 'n2' ? (
                                <>
                                  {voltar('Subagrupar por', 'root')}
                                  {item('Sem subagrupamento', () => { setSubAgruparPor('none'); fechar() }, { on: subEfetivo === 'none' })}
                                  {opcoesSub([agruparPor]).map((c) => item(c.r, () => { setSubAgruparPor(c.v); fechar() }, { on: subEfetivo === c.v, icone: subEfetivo === c.v ? undefined : c.i }))}
                                </>
                              ) : null}
                              {tabelaMenu === 'n3' ? (
                                <>
                                  {voltar('Terceiro nível', 'root')}
                                  {item('Sem terceiro nível', () => { setSub2AgruparPor('none'); fechar() }, { on: sub2Efetivo === 'none' })}
                                  {opcoesSub([agruparPor, subEfetivo]).map((c) => item(c.r, () => { setSub2AgruparPor(c.v); fechar() }, { on: sub2Efetivo === c.v, icone: sub2Efetivo === c.v ? undefined : c.i }))}
                                </>
                              ) : null}
                            </div>
                          </>
                        )
                      })()
    : null

  return (
    // fundo forte (imagem ou gradiente) pede texto branco no cabecalho,
    // independentemente de a foto ser clara ou escura
    <div
      className={`kb${editMode ? ' kb--edit' : ''}${bgDark && !temaBlack ? ' kb--bgdark' : ''}${
        boardData.board.backgroundImageUrl ? ' kb--bgimage' : ''
      }${boardData.board.backgroundImageUrl || boardData.board.backgroundColor ? ' kb--bgcustom' : ''}${
        telaCheia ? ' kb--cheia' : ''
      }${planilha ? ' kb--planilha' : ''}`}
      data-estilo={planilha && estiloPlanilha ? estiloPlanilha : undefined}
      style={
        planilha
          ? ({ '--kbl-zoom': String(zoomPlanilha / 100), '--kbl-fonte': `${fontePlanilha}px`, ...varsDoEstilo(estiloPlanilha) } as React.CSSProperties)
          : undefined
      }
    >
      {planilha ? (
        // ------------------------------------------------ BARRA DO SHEETS ---
        // A mesma barra de ícones da planilha do Google (SPEC-20260915-2149).
        // O que tem equivalente aqui é real; o resto fica cinza (disabled),
        // como o próprio Sheets faz com "desfazer" quando não há o que desfazer.
        <div className="kb__sheetsbar" role="toolbar" aria-label="Barra de ferramentas da planilha">
          {(() => {
            const morto = (icone: string, rotulo: string, extra?: string) => (
              <button type="button" className={`kb__sbtn${extra ? ` ${extra}` : ''}`} disabled title={`${rotulo} — não se aplica aqui`} aria-label={rotulo}>
                <i className={icone} aria-hidden />
              </button>
            )
            const sep = <span className="kb__ssep" aria-hidden />
            return (
              <>
                {/* BUSCA embutida como primeiro campo da barra (pedido 2026-09-15
                    22:50): o mesmo estado da busca do quadro; a da barra de
                    fórmula some na planilha */}
                <div className={`kb__buscar kb__buscar--sheets${buscaAtiva ? ' kb__buscar--ativa' : ''}`}>
                  <i className="fas fa-search" aria-hidden />
                  <input
                    value={buscaCards}
                    placeholder="Localizar item…"
                    aria-label="Localizar item na planilha"
                    onChange={(e) => setBuscaCards(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation()
                        setBuscaCards('')
                      }
                    }}
                  />
                  {buscaAtiva ? (
                    <>
                      <span className="kb__buscar-n" title="Itens encontrados">{totalAchados}</span>
                      <button type="button" className="kb__buscar-x" aria-label="Limpar a busca" title="Limpar a busca (Esc)" onClick={() => setBuscaCards('')}>
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </>
                  ) : null}
                </div>
                {/* desfazer/refazer saíram a pedido do usuário (2026-09-16 00:35) */}
                <button type="button" className="kb__sbtn" title="Imprimir" aria-label="Imprimir" onClick={() => window.print()}>
                  <i className="fas fa-print" aria-hidden />
                </button>
                {/* EXPORTAR PARA O EXCEL (SPEC-20260916-0035): exatamente o que
                    está visível — colunas, filtros, grupos recolhidos */}
                <button
                  type="button"
                  className="kb__sbtn kb__sbtn--excel"
                  title="Exportar para o Excel o que está visível"
                  aria-label="Exportar para o Excel"
                  disabled={exportando}
                  onClick={async () => {
                    if (!exportarRef.current) return
                    setExportando(true)
                    try {
                      const data = new Date().toISOString().slice(0, 10)
                      const nome = `${boardName.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Quadro'} - ${data}.xlsx`
                      const r = await exportarRef.current(nome)
                      toast.push({ tone: 'success', message: `Exportado: ${r.linhas} linha(s), ${r.colunas.length} coluna(s).` })
                    } catch (e) {
                      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível exportar.' })
                    } finally {
                      setExportando(false)
                    }
                  }}
                >
                  <i className={exportando ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'} aria-hidden />
                </button>
                {/* "copiar formatação" saiu a pedido do usuário (2026-09-16 00:33) */}
                <span className="kb__sdrop-wrap">
                  <button type="button" className="kb__sdrop" title="Zoom" aria-haspopup="menu" aria-expanded={zoomMenu} onClick={() => setZoomMenu((v) => !v)}>
                    {zoomPlanilha}% <i className="fas fa-caret-down" aria-hidden />
                  </button>
                  {zoomMenu ? (
                    <>
                      <div className="wi-menu__backdrop" onClick={() => setZoomMenu(false)} />
                      <div className="kb__ownermenu kb__smenu" role="menu">
                        {[50, 75, 90, 100, 125, 150, 200].map((z) => (
                          <button key={z} type="button" className={`kb__menuitem${z === zoomPlanilha ? ' kb__menuitem--on' : ''}`} onClick={() => { setZoomPlanilha(z); setZoomMenu(false) }}>
                            {z}%
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                </span>
                {/* formato numérico (R$ % .0 .00 123) e a fonte "Calibri" saíram
                    a pedido do usuário (2026-09-16 00:05): não se aplicam */}
                {sep}
                <button type="button" className="kb__sbtn" title="Diminuir o tamanho da letra" aria-label="Diminuir a fonte" disabled={fontePlanilha <= 10} onClick={() => setFontePlanilha((f) => Math.max(10, f - 1))}>
                  <i className="fas fa-minus" aria-hidden />
                </button>
                {/* só o número, editável ao clicar (pedido 2026-09-16 02:20) */}
                <input
                  className="kb__stam"
                  type="text"
                  inputMode="numeric"
                  title="Tamanho da letra da grade (10 a 18)"
                  aria-label="Tamanho da letra"
                  value={fontePlanilha}
                  onChange={(e) => {
                    const n = Number(e.target.value.replace(/\D/g, ''))
                    if (Number.isFinite(n)) setFontePlanilha(n)
                  }}
                  onBlur={() => setFontePlanilha((f) => Math.min(18, Math.max(10, f || 13)))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur()
                    e.stopPropagation()
                  }}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button type="button" className="kb__sbtn" title="Aumentar o tamanho da letra" aria-label="Aumentar a fonte" disabled={fontePlanilha >= 18} onClick={() => setFontePlanilha((f) => Math.min(18, f + 1))}>
                  <i className="fas fa-plus" aria-hidden />
                </button>
                {sep}
                {/* FORMATAÇÃO (SPEC-20260915-2250): vivos só com uma célula de
                    texto formatado em edição; mousedown sem foco para a seleção
                    do editor não se perder */}
                {(() => {
                  const fmt = (icone: string, rotulo: string, cmd: string, val?: string) => (
                    <button
                      key={cmd + (val ?? '')}
                      type="button"
                      className="kb__sbtn"
                      disabled={!edicaoRica}
                      title={edicaoRica ? rotulo : `${rotulo} — edite uma célula de texto formatado (Descrição, Resultado esperado, parágrafo)`}
                      aria-label={rotulo}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        document.execCommand('styleWithCSS', false, 'false')
                        document.execCommand(cmd, false, val)
                      }}
                    >
                      <i className={icone} aria-hidden />
                    </button>
                  )
                  const CORES = ['#1f1f1f', '#b10202', '#753800', '#473821', '#11734b', '#0a53a8', '#5a3286', '#5f6368', '#ffffff']
                  return (
                    <>
                      {fmt('fas fa-bold', 'Negrito', 'bold')}
                      {fmt('fas fa-italic', 'Itálico', 'italic')}
                      {fmt('fas fa-underline', 'Sublinhado', 'underline')}
                      {fmt('fas fa-strikethrough', 'Tachado', 'strikeThrough')}
                      {/* título e listas — os mesmos do editor do detalhe (pedido 2026-09-16 00:32) */}
                      <button
                        type="button"
                        className="kb__sbtn"
                        disabled={!edicaoRica}
                        title={edicaoRica ? 'Título (liga/desliga)' : 'Título — edite uma célula de texto formatado'}
                        aria-label="Título"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const atual = String(document.queryCommandValue('formatBlock') || '').toLowerCase()
                          document.execCommand('formatBlock', false, atual === 'h3' ? 'P' : 'H3')
                        }}
                      >
                        <i className="fas fa-heading" aria-hidden />
                      </button>
                      {fmt('fas fa-list-ul', 'Lista com marcadores', 'insertUnorderedList')}
                      {fmt('fas fa-list-ol', 'Lista numerada', 'insertOrderedList')}
                      <span className="kb__sdrop-wrap">
                        <button
                          type="button"
                          className="kb__sbtn"
                          disabled={!edicaoRica}
                          title={edicaoRica ? 'Cor do texto' : 'Cor do texto — edite uma célula de texto formatado'}
                          aria-label="Cor do texto"
                          aria-haspopup="menu"
                          aria-expanded={corMenu}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setCorMenu((v) => !v)}
                        >
                          <i className="fas fa-font" aria-hidden />
                        </button>
                        {corMenu ? (
                          <>
                            <div className="wi-menu__backdrop" onMouseDown={(e) => e.preventDefault()} onClick={() => setCorMenu(false)} />
                            <div className="kb__paleta" role="menu" aria-label="Cor do texto">
                              {CORES.map((cor) => (
                                <button
                                  key={cor}
                                  type="button"
                                  className="kb__paleta-cor"
                                  style={{ background: cor }}
                                  title={cor}
                                  aria-label={`Cor ${cor}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    document.execCommand('styleWithCSS', false, 'true')
                                    document.execCommand('foreColor', false, cor)
                                    setCorMenu(false)
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        ) : null}
                      </span>
                    </>
                  )
                })()}
                {sep}
                {/* COR DE FUNDO e IMAGEM nos campos de texto formatado (SPEC-20260916-0048) */}
                <span className="kb__sdrop-wrap">
                  <button
                    type="button"
                    className="kb__sbtn"
                    disabled={!edicaoRica}
                    title={edicaoRica ? 'Cor de fundo do texto' : 'Cor de fundo — edite uma célula de texto formatado'}
                    aria-label="Cor de fundo"
                    aria-haspopup="menu"
                    aria-expanded={fundoMenu}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setFundoMenu((v) => !v)}
                  >
                    <i className="fas fa-fill-drip" aria-hidden />
                  </button>
                  {fundoMenu ? (
                    <>
                      <div className="wi-menu__backdrop" onMouseDown={(e) => e.preventDefault()} onClick={() => setFundoMenu(false)} />
                      <div className="kb__paleta" role="menu" aria-label="Cor de fundo">
                        {['transparent', '#fff2cc', '#d4edbc', '#bfe1f6', '#ffe5a0', '#ffcfc9', '#e6cff2', '#e6e6e6', '#d3e3fd'].map((cor) => (
                          <button
                            key={cor}
                            type="button"
                            className={`kb__paleta-cor${cor === 'transparent' ? ' kb__paleta-cor--nenhuma' : ''}`}
                            style={{ background: cor === 'transparent' ? '#fff' : cor }}
                            title={cor === 'transparent' ? 'Sem fundo' : cor}
                            aria-label={cor === 'transparent' ? 'Sem fundo' : `Fundo ${cor}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              // sem seleção, vale para o texto inteiro da célula
                              const sel = window.getSelection()
                              const ed = document.activeElement as HTMLElement | null
                              if (sel && sel.isCollapsed && ed?.isContentEditable) {
                                const r = document.createRange()
                                r.selectNodeContents(ed)
                                sel.removeAllRanges()
                                sel.addRange(r)
                              }
                              document.execCommand('styleWithCSS', false, 'true')
                              document.execCommand('hiliteColor', false, cor)
                              setFundoMenu(false)
                            }}
                          />
                        ))}
                      </div>
                    </>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="kb__sbtn"
                  disabled={!edicaoRica}
                  title={edicaoRica ? 'Inserir imagem no texto' : 'Inserir imagem — edite uma célula de texto formatado'}
                  aria-label="Inserir imagem"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const ed = document.activeElement as HTMLElement | null
                    if (!ed?.isContentEditable) return
                    editorDaImagemRef.current = ed
                    // a janela de arquivo tira o foco da célula: segura o blur até
                    // a janela fechar (o foco volta à janela do navegador)
                    blurSeguro = true
                    window.addEventListener(
                      'focus',
                      () => setTimeout(() => { blurSeguro = false; editorDaImagemRef.current?.focus() }, 80),
                      { once: true },
                    )
                    inputImagemRef.current?.click()
                  }}
                >
                  <i className="fas fa-image" aria-hidden />
                </button>
                <input
                  ref={inputImagemRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    const ed = editorDaImagemRef.current
                    if (!file || !ed) { blurSeguro = false; return }
                    try {
                      const url = await uploadWorkItemImage(file)
                      ed.focus()
                      document.execCommand('insertImage', false, url)
                    } catch (err) {
                      toast.push({ tone: 'danger', message: err instanceof Error ? err.message : 'Não foi possível inserir a imagem.' })
                    } finally {
                      blurSeguro = false
                    }
                  }}
                />
                {morto('fas fa-border-all', 'Bordas')}
                {morto('fas fa-object-group', 'Mesclar células')}
                {sep}
                {/* ALINHAMENTOS lado a lado (pedido 2026-09-16 00:45), sem menu */}
                {([
                  ['justifyLeft', 'fas fa-align-left', 'Alinhar à esquerda'],
                  ['justifyCenter', 'fas fa-align-center', 'Centralizar'],
                  ['justifyRight', 'fas fa-align-right', 'Alinhar à direita'],
                  ['justifyFull', 'fas fa-align-justify', 'Justificar'],
                ] as const).map(([cmd, icone, rotulo]) => (
                  <button
                    key={cmd}
                    type="button"
                    className="kb__sbtn kb__sbtn--sec"
                    disabled={!edicaoRica}
                    title={edicaoRica ? rotulo : `${rotulo} — edite uma célula de texto formatado`}
                    aria-label={rotulo}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => document.execCommand(cmd, false)}
                  >
                    <i className={icone} aria-hidden />
                  </button>
                ))}
                {morto('fas fa-align-center', 'Alinhamento vertical', 'kb__sbtn--sec')}
                {morto('fas fa-text-width', 'Quebra de texto', 'kb__sbtn--sec')}
                {/* rotação do texto, link e comentário saíram a pedido do usuário (2026-09-16 00:33) */}
                {sep}
                {morto('fas fa-chart-bar', 'Inserir gráfico', 'kb__sbtn--sec')}
                {/* FUNIL (SPEC-20260915-2245): liga/desliga os filtros nas colunas,
                    mostra os aplicados (com remover / limpar), lista os filtros
                    rápidos e cria um novo */}
                <span className="kb__sdrop-wrap">
                  <button
                    type="button"
                    className={`kb__sbtn${visaoAtiva || filtrosColuna.size > 0 ? ' kb__sbtn--on' : ''}`}
                    title="Filtros: nas colunas, aplicados e rápidos"
                    aria-label="Filtros"
                    aria-haspopup="menu"
                    aria-expanded={funilMenu}
                    onClick={() => setFunilMenu((v) => !v)}
                  >
                    <i className="fas fa-filter" aria-hidden />
                  </button>
                  {funilMenu
                    ? (() => {
                        const fechar = () => setFunilMenu(false)
                        const rapidos = ordenarVisoes(listaDeVisoes)
                        const rotuloColuna = (key: string) => colunasDisponiveis.find((c) => c.key === key)?.rotulo ?? key
                        return (
                          <>
                            <div className="wi-menu__backdrop" onClick={fechar} />
                            <div className="kb__smenu-tabela" role="menu" aria-label="Filtros">
                              <button type="button" className={`kb__smi${filtrosLigados ? ' kb__smi--on' : ''}`} onClick={() => setFiltrosLigados((v) => !v)}>
                                <span className="kb__smi-pre" aria-hidden><i className={filtrosLigados ? 'fas fa-check' : 'fas fa-filter'} /></span>
                                <span className="kb__smi-txt">
                                  {filtrosLigados ? 'Filtros nas colunas ligados' : 'Ligar filtros nas colunas'}
                                  <small>{filtrosLigados ? 'clique para esconder as setas dos cabeçalhos' : 'mostra a seta de filtro em cada cabeçalho'}</small>
                                </span>
                              </button>
                              {filtrosColuna.size > 0 ? (
                                <>
                                  <div className="kb__smenu-sep" />
                                  <div className="kb__smenu-titulo">Filtros aplicados</div>
                                  {[...filtrosColuna.entries()].map(([key, set]) => (
                                    <div key={key} className="kb__smi kb__smi--linha">
                                      <span className="kb__smi-pre" aria-hidden><i className="fas fa-filter" /></span>
                                      <span className="kb__smi-txt">
                                        {rotuloColuna(key)}
                                        <small>{set.size} {set.size === 1 ? 'valor' : 'valores'}</small>
                                      </span>
                                      <button
                                        type="button"
                                        className="kb__smi-x"
                                        title={`Remover o filtro de ${rotuloColuna(key)}`}
                                        aria-label={`Remover o filtro de ${rotuloColuna(key)}`}
                                        onClick={() => setFiltrosColuna((m) => { const n = new Map(m); n.delete(key); return n })}
                                      >
                                        <i className="fas fa-times" aria-hidden />
                                      </button>
                                    </div>
                                  ))}
                                  <button type="button" className="kb__smi" onClick={() => { setFiltrosColuna(new Map()); fechar() }}>
                                    <span className="kb__smi-pre" aria-hidden><i className="fas fa-times-circle" /></span>
                                    <span className="kb__smi-txt">Limpar todos os filtros de coluna</span>
                                  </button>
                                </>
                              ) : null}
                              <div className="kb__smenu-sep" />
                              <div className="kb__smenu-titulo">Visões</div>
                              {rapidos.length === 0 ? <div className="kb__smi kb__smi--vazio">Nenhuma visão ainda</div> : null}
                              {rapidos.map((f) => (
                                <button
                                  key={f.id}
                                  type="button"
                                  className={`kb__smi${activeCardFilterId === f.id ? ' kb__smi--on' : ''}`}
                                  onClick={() => { setActiveCardFilterId(activeCardFilterId === f.id ? null : f.id); fechar() }}
                                >
                                  <span className="kb__smi-pre" aria-hidden><i className={activeCardFilterId === f.id ? 'fas fa-check' : 'fas fa-layer-group'} /></span>
                                  <span className="kb__smi-txt">{f.name || 'Sem nome'}</span>
                                </button>
                              ))}
                              {activeCardFilterId ? (
                                <button type="button" className="kb__smi" onClick={() => { setActiveCardFilterId(null); fechar() }}>
                                  <span className="kb__smi-pre" aria-hidden><i className="fas fa-eraser" /></span>
                                  <span className="kb__smi-txt">Voltar para todos os itens</span>
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="kb__smi"
                                onClick={() => {
                                  setEditandoVisao('nova')
                                  fechar()
                                }}
                              >
                                <span className="kb__smi-pre" aria-hidden><i className="fas fa-plus" /></span>
                                <span className="kb__smi-txt">Nova visão</span>
                              </button>
                            </div>
                          </>
                        )
                      })()
                    : null}
                </span>
                {/* VISUALIZAÇÕES DE TABELA (SPEC-20260915-2223): o menu do Sheets
                    com o NOSSO agrupamento — Agrupar por / Subagrupar / Terceiro
                    nível em submenus; escolher agrupa na hora */}
                <span className="kb__sdrop-wrap">
                  <button
                    type="button"
                    className={`kb__sdrop kb__sdrop--tabela${agrupado ? ' kb__sbtn--on' : ''}`}
                    title="Visualizações de tabela: agrupar e colunas"
                    aria-label="Visualizações de tabela"
                    aria-haspopup="menu"
                    aria-expanded={tabelaMenu !== null}
                    onClick={() => setTabelaMenu((v) => (v ? null : 'root'))}
                  >
                    <IconeVisualizacoes />
                    <i className="fas fa-caret-down" aria-hidden />
                  </button>
                  {menuAgrupamento}
                </span>
                {/* COLUNAS na barra (pedido 2026-09-16 00:36): a lista portala o
                    seletor para cá (só o ícone); o botão "Colunas N" sai da barra
                    de fórmula */}
                {/* recolher/expandir TODAS as faixas — morava na barra de fórmula,
                    que saiu (2026-09-16 00:52) */}
                {agrupado
                  ? (() => {
                      const todasFechadas = bandas.length > 0 && bandas.every((b) => bandasFechadas.has(b.key))
                      return (
                        <button
                          type="button"
                          className="kb__sbtn"
                          title={todasFechadas ? 'Expandir todas as faixas' : 'Recolher todas as faixas'}
                          aria-label={todasFechadas ? 'Expandir todas as faixas' : 'Recolher todas as faixas'}
                          onClick={() => definirBandasFechadas(todasFechadas ? new Set() : new Set(bandas.map((b) => b.key)))}
                        >
                          <i className={todasFechadas ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} aria-hidden />
                        </button>
                      )
                    })()
                  : null}
                <span className="kb__barraslot kb__barraslot--sheets" ref={setSlotDaBarra} />
                {/* formatação privada/pública (SPEC-20260916-0102) */}
                {botaoFormato}
                {/* FORMATAR COMO TABELA (SPEC-20260916-0131): galeria Clara/Média/Escura × cor */}
                <span className="kb__sdrop-wrap">
                  <button
                    type="button"
                    className={`kb__sdrop kb__sdrop--galeria${estiloPlanilha ? ' kb__sbtn--on' : ''}`}
                    title="Formatar como Tabela: escolher um estilo de cores"
                    aria-haspopup="menu"
                    aria-expanded={galeriaMenu}
                    onClick={() => setGaleriaMenu((v) => !v)}
                  >
                    <i className="fas fa-table" aria-hidden />
                    <span>Estilo</span>
                    <i className="fas fa-caret-down" aria-hidden />
                  </button>
                  {galeriaMenu ? (
                    <>
                      <div className="wi-menu__backdrop" onClick={() => setGaleriaMenu(false)} />
                      <div className="kb__galeria" role="menu" aria-label="Estilos de tabela">
                        {TONS_DE_ESTILO.map((tom) => (
                          <div key={tom.k} className="kb__galeria-secao">
                            <div className="kb__galeria-titulo">{tom.r}</div>
                            <div className="kb__galeria-linha">
                              {CORES_DE_ESTILO.map((cor) => {
                                const idEstilo = `${tom.k}-${cor.k}`
                                const v = varsDoEstilo(idEstilo)
                                return (
                                  <button
                                    key={idEstilo}
                                    type="button"
                                    className={`kb__amostra${estiloPlanilha === idEstilo ? ' kb__amostra--on' : ''}`}
                                    title={`${tom.r} · ${cor.r}`}
                                    aria-label={`Estilo ${tom.r} ${cor.r}`}
                                    aria-pressed={estiloPlanilha === idEstilo}
                                    onClick={() => { setEstiloPlanilha(idEstilo); setGaleriaMenu(false) }}
                                  >
                                    <span className="kb__amostra-cab" style={{ background: v['--tbl-cab-bg'], borderColor: v['--tbl-borda'] }} />
                                    {[0, 1, 2, 3].map((i) => (
                                      <span key={i} className="kb__amostra-linha" style={{ background: i % 2 ? v['--tbl-faixa'] : v['--tbl-linha'], borderColor: v['--tbl-borda'] }} />
                                    ))}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                        <div className="kb__smenu-sep" />
                        <button type="button" className={`kb__smi${!estiloPlanilha ? ' kb__smi--on' : ''}`} onClick={() => { setEstiloPlanilha(''); setGaleriaMenu(false) }}>
                          <span className="kb__smi-pre" aria-hidden><i className={!estiloPlanilha ? 'fas fa-check' : 'fas fa-ban'} /></span>
                          <span className="kb__smi-txt">Sem estilo (padrão da planilha)</span>
                        </button>
                      </div>
                    </>
                  ) : null}
                </span>
                {/* Σ (Funções) saiu: o Font Awesome 5 não tem o glifo e o botão era morto */}
                {/* o botão do teclado ("Métodos de entrada") saiu a pedido do usuário (2026-09-16 00:10) */}
                <span style={{ flex: 1 }} />
                {/* ir direto para o QUADRO (ícone só) — pedido 2026-09-16 01:05 */}
                <button
                  type="button"
                  className="kb__sbtn kb__sbtn--quadro"
                  title="Ver como quadro"
                  aria-label="Ver como quadro"
                  onClick={() => trocarModo('quadro')}
                >
                  <i className="fas fa-columns" aria-hidden />
                </button>
                {telaCheia ? (
                  // só o ícone (pedido 2026-09-16 10:30: "tirar o texto, deixar só o ícone")
                  <button type="button" className="kb__svoltar" onClick={() => setTelaCheia(false)} title="Voltar ao Orbind (Esc)" aria-label="Voltar ao Orbind">
                    <i className="fas fa-compress" aria-hidden />
                  </button>
                ) : (
                  // dentro da lista (SPEC-20260916-0938): o mesmo lugar vira "Expandir"
                  <button type="button" className="kb__sbtn kb__sbtn--expandir" onClick={() => setTelaCheia(true)} title="Expandir para a tela toda" aria-label="Expandir para a tela toda">
                    <i className="fas fa-expand" aria-hidden />
                  </button>
                )}
              </>
            )
          })()}
        </div>
      ) : null}
      {/* ------------------------------------------------------------ header */}
      <div className="kb__head">
        {/* (a barra de fórmula da planilha saiu a pedido do usuário, 2026-09-16
            00:52: em planilha o kb__head inteiro fica escondido e o cabeçalho
            da tabela cola na barra do Sheets) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {titleEditing ? (
            <span className="kb__titleeditbox">
              <input
                className="kb__titleinput"
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur()
                  if (e.key === 'Escape') {
                    setTitleDraft(boardName)
                    setTitleEditing(false)
                  }
                }}
                onBlur={() => {
                  // clicar fora (ou Enter/check) salva
                  setTitleEditing(false)
                  const name = titleDraft.trim()
                  if (name !== '' && name !== boardName) saveTitle.mutate(name)
                }}
              />
              <button type="button" className="kb__titleok" aria-label="Confirmar título" title="Confirmar">
                <i className="fas fa-check" aria-hidden style={{ fontSize: 12 }} />
              </button>
            </span>
          ) : (
            // titulo e estrela na mesma linha: a estrela vem logo apos o texto
            <div className="kb__titlerow">
              <h1
                className="kb__title kb__title--clickable"
                title="Clique para renomear"
                onClick={() => {
                  // renomear direto no desenho vale tambem no modo de edicao
                  setTitleDraft(boardName)
                  setTitleEditing(true)
                }}
              >
                {boardName}
              </h1>
              {/* descricao do quadro num "i" DEPOIS do nome e ANTES do
                  favorito: o hint (tooltip) mostra a descricao — SO no quadro
                  final; na configuracao ela apenas se PREPARA (botao) */}
              {!editMode && boardData.board.descriptionTranslationKey ? (
                <i
                  className="fas fa-info-circle kb__descinfo"
                  title={labelOf(boardData.board.descriptionTranslationKey)}
                  aria-label={`Descrição: ${labelOf(boardData.board.descriptionTranslationKey)}`}
                  role="img"
                />
              ) : null}
              <button
                type="button"
                className={`kb__favbtn${isFavorite ? ' kb__favbtn--on' : ''}`}
                aria-label={isFavorite ? 'Remover dos favoritos' : 'Marcar como favorito'}
                title={isFavorite ? 'Remover dos favoritos' : 'Marcar como favorito'}
                onClick={() => toggleFavorite.mutate(!isFavorite)}
              >
                <i className={isFavorite ? 'fas fa-star' : 'far fa-star'} aria-hidden />
              </button>

              {editMode ? (
                <button
                  type="button"
                  className="kb__favbtn"
                  title="Descrição do quadro"
                  aria-label="Editar a descrição do quadro"
                  onClick={() => {
                    setDescricaoDraft(
                      boardData.board.descriptionTranslationKey ? labelOf(boardData.board.descriptionTranslationKey) : '',
                    )
                    setDescricaoOpen(true)
                  }}
                >
                  <i className="fas fa-align-left" aria-hidden />
                </button>
              ) : null}


              {/* busca dentro do quadro: realca quem casa, apaga o resto */}
              {!editMode ? (
                <div className={`kb__buscar${buscaAtiva ? ' kb__buscar--ativa' : ''}`}>
                  <i className="fas fa-search" aria-hidden />
                  <input
                    value={buscaCards}
                    placeholder="Localizar item…"
                    aria-label="Localizar item no quadro"
                    onChange={(e) => setBuscaCards(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation()
                        setBuscaCards('')
                      }
                    }}
                  />
                  {buscaAtiva ? (
                    <>
                      <span className="kb__buscar-n" title="Itens encontrados">
                        {totalAchados}
                      </span>
                      <button
                        type="button"
                        className="kb__buscar-x"
                        aria-label="Limpar a busca"
                        title="Limpar a busca (Esc)"
                        onClick={() => setBuscaCards('')}
                      >
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              {/* VISÕES (SPEC-20261103-1418): o combo que era dos filtros. Cada
                  visão leva filtro, colunas, ordenação e tipo; o ícone diz
                  quem a vê. Lápis para quem edita, lixeira só para o dono. */}
              {!editMode ? (
                <div className="ob-filterbar ob-filterbar--combo kb__cardfilters">
                  <FilterCombo
                    options={opcoesDoCombo(listaDeVisoes)}
                    activeId={visaoAtiva?.id ?? null}
                    allLabel="Todos os itens"
                    novoLabel="Nova visão"
                    icone="fas fa-layer-group"
                    rotuloDoItem="visão"
                    onActivate={setActiveCardFilterId}
                    onEdit={(opcao) => {
                      const v = listaDeVisoes.find((x) => x.id === opcao.id)
                      if (v) setEditandoVisao(v)
                    }}
                    onRemove={(opcao) => {
                      const v = listaDeVisoes.find((x) => x.id === opcao.id)
                      if (v) confirmarApagarVisao(v)
                    }}
                    onNew={() => setEditandoVisao('nova')}
                  />
                </div>
              ) : null}

              {/* FILTRO RÁPIDO (SPEC-20261104-1557-filtro-rapido-do-quadro): monta e aplica na hora,
                  sem salvar; o contador diz quantos critérios estão valendo */}
              {!editMode ? (
                <button
                  type="button"
                  className={`kb__frapido${filtroRapidoAtivo ? ' kb__frapido--on' : ''}`}
                  title={filtroRapidoAtivo ? 'Filtro rápido aplicado — clique para ajustar' : 'Filtro rápido (não é salvo)'}
                  aria-label="Filtro rápido"
                  aria-pressed={Boolean(filtroRapidoAtivo)}
                  onClick={() => {
                    // seletores diretos + critérios avançados (SPEC-20261104-1646)
                    const { rapidos, avancado } = separarFiltroRapido(filtroRapido)
                    setRapidosDraft(rapidos)
                    setFiltroRapidoDraft({ ...emptyFilter(filterFields, 'Filtro rápido'), criteria: avancado })
                    setAvancadoAberto(avancado.length > 0)
                    setFiltroRapidoAberto(true)
                  }}
                >
                  <i className="fas fa-filter" aria-hidden />
                  {filtroRapidoAtivo ? <span className="kb__frapido-n">{filtroRapidoAtivo.criteria.length}</span> : null}
                </button>
              ) : null}

              {/* PERÍODO (SPEC-20261104-1638-filtro-por-periodo-de-conclusao-e-criacao): combo de atalhos +
                  personalizado com início e fim opcionais, sobre conclusão ou criação */}
              {!editMode ? (
                <div className={`kb__periodo${periodo.preset ? ' kb__periodo--on' : ''}`} role="group" aria-label="Período">
                  {/* qual data: sempre visível, para trocar no próprio cabeçalho */}
                  <Select
                    value={periodo.campo}
                    options={CAMPOS_DE_PERIODO}
                    onChange={(v) => setPeriodo((p) => ({ ...p, campo: v as CampoDePeriodo }))}
                  />
                  <Select
                    value={periodo.preset}
                    options={PRESETS_DE_PERIODO}
                    onChange={(v) => setPeriodo((p) => ({ ...p, preset: v as PresetDePeriodo }))}
                  />
                  {periodo.preset === 'custom' ? (
                    <>
                      <DateInput
                        value={dataLocal(periodo.de)}
                        placeholder="início"
                        onChange={(d) => setPeriodo((p) => ({ ...p, de: d ? ymd(d) : '' }))}
                      />
                      <DateInput
                        value={dataLocal(periodo.ate)}
                        placeholder="fim"
                        onChange={(d) => setPeriodo((p) => ({ ...p, ate: d ? ymd(d) : '' }))}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
              {editandoVisao ? (
                <EditorDeVisao
                  quadroId={id}
                  visao={editandoVisao === 'nova' ? null : editandoVisao}
                  modos={modosDoAcesso}
                  modoAtual={modoVisao}
                  ondeVale="este quadro"
                  membros={(bootstrap.data?.members ?? []).map((m) => ({ value: m.id, label: m.name }))}
                  meuId={session.id}
                  filtroInicial={activeCardFilter ? filtroParaGuardar(activeCardFilter) : null}
                  colunas={{ catalogo: catalogoDaLista, atuais: colunasAtuaisDaLista }}
                  ordenacaoAtual={tabelaDaLista.ord}
                  renderFiltro={(filtro, mudar) => (
                    <FilterEditor
                      fields={filterFields}
                      value={{
                        id: 'visao',
                        name: 'visao',
                        join: filtro?.join === 'OR' ? 'OR' : 'AND',
                        criteria: Array.isArray(filtro?.criteria) ? (filtro.criteria as SavedFilter['criteria']) : [],
                      }}
                      onChange={(f) => mudar(filtroParaGuardar(f))}
                      loadValueOptions={loadCardValueOptions}
                    />
                  )}
                  onClose={() => setEditandoVisao(null)}
                  onApagada={(vid) => {
                    if (activeCardFilterId === vid) setActiveCardFilterId(null)
                    setEditandoVisao(null)
                  }}
                  onSalva={(v) => {
                    setActiveCardFilterId(v.id)
                    setEditandoVisao(null)
                  }}
                />
              ) : null}
              {idsNaoMapeados && (modoVisao === 'lista' || modoVisao === 'tempo') && !editMode ? (
                <span className="ob-filterchip ob-filterchip--on kb__sonaomapeados">
                  <i className="fas fa-exclamation-triangle" aria-hidden style={{ fontSize: 10 }} />
                  Só os não mapeados
                  <button
                    type="button"
                    aria-label="Mostrar todos os itens"
                    title="Mostrar todos os itens"
                    onClick={() => setSoNaoMapeados(false)}
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                </span>
              ) : null}
            </div>
          )}

          {/* AREAS do quadro na linha abaixo do titulo: chips + adicionar
              (uma ou mais; clique no chip remove) — SO no modo de configuracao */}
          {editMode ? (
          <div className="kb__arearow">
            {(() => {
              // chips do ESCOPO atual, cada um com o icone da categoria (ou o
              // icone do proprio item, quando o cadastro tem — tipos de item)
              const cfg = config.data
              const nosPortfolio = (portfolio.data?.nodes ?? []) as { id: string; name: string }[]
              const chips: { chave: string; grupo: string; icone?: string; iconeCatalogo?: string | null; rotulo: string; remover: () => void }[] = []
              for (const areaId of cfg?.areaIds ?? []) {
                const a = (bootstrap.data?.areas ?? []).find((x) => x.id === areaId)
                if (a) chips.push({ chave: `area-${areaId}`, grupo: 'Área', icone: 'fas fa-sitemap', rotulo: labelOf(a.nameTranslationKey), remover: () => saveEscopo.mutate({ areaIds: (cfg?.areaIds ?? []).filter((x) => x !== areaId) }) })
              }
              for (const tipoId of cfg?.workItemTypeIds ?? []) {
                const t2 = (bootstrap.data?.workItemTypes ?? []).find((x) => x.id === tipoId)
                if (t2) chips.push({ chave: `tipo-${tipoId}`, grupo: 'Tipos de item', iconeCatalogo: t2.icon, rotulo: labelOf(t2.nameTranslationKey), remover: () => saveEscopo.mutate({ workItemTypeIds: (cfg?.workItemTypeIds ?? []).filter((x) => x !== tipoId) }) })
              }
              for (const nodeId of cfg?.portfolioNodeIds ?? []) {
                const n = nosPortfolio.find((x) => x.id === nodeId)
                chips.push({ chave: `prod-${nodeId}`, grupo: 'Produtos/Serviços', icone: 'fas fa-cube', rotulo: n?.name ?? 'Produto', remover: () => saveEscopo.mutate({ portfolioNodeIds: (cfg?.portfolioNodeIds ?? []).filter((x) => x !== nodeId) }) })
              }
              // membros nao viram tag: vivem no popover Dono e membros (direita)
              for (const empresaId of cfg?.companyIds ?? []) {
                const c2 = (bootstrap.data?.companies ?? []).find((x) => x.id === empresaId)
                if (c2) chips.push({ chave: `cli-${empresaId}`, grupo: 'Empresas', icone: 'fas fa-building', rotulo: c2.name, remover: () => saveEscopo.mutate({ companyIds: (cfg?.companyIds ?? []).filter((x) => x !== empresaId) }) })
              }
              // fluxos NAO viram tag: ja aparecem no painel de etapas ao lado
              // (a categoria Fluxo continua no menu para vincular/desvincular)
              if (boardData.board.filter) {
                // contador de criterios do filtro avancado no proprio chip
                const avancado = parseBoardFilter(boardData.board.filter)
                chips.push({ chave: 'filtro', grupo: 'Filtro avançado', icone: 'fas fa-filter', rotulo: `Filtro avançado (${avancado?.criteria.length ?? 0})`, remover: () => saveEscopo.mutate({ filter: null }) })
              }
              // agrupado com LABEL por categoria: "Área:" tags, "Membros:" tags…
              // cada categoria tem a PROPRIA COR (tag e label no mesmo tom)
              const grupos = [...new Set(chips.map((c3) => c3.grupo))]
              return grupos.map((g) => {
                const cor = chips.find((c3) => c3.grupo === g)!.chave.split('-')[0]
                return (
                  <span key={g} className={`kb__filtergrupo kb__filtergrupo--${cor}`}>
                    <span className="kb__filterlabel">{g}:</span>
                    {chips
                      .filter((c3) => c3.grupo === g)
                      .map((chip) => (
                        <button
                          key={chip.chave}
                          type="button"
                          className={`kb__areachip kb__areachip--${cor}`}
                          title={`${chip.rotulo} — clique para remover`}
                          onClick={chip.remover}
                        >
                          {chip.iconeCatalogo !== undefined ? (
                            <CatalogIcon icon={chip.iconeCatalogo} fallback="fas fa-tasks" size={10} />
                          ) : (
                            <i className={chip.icone} aria-hidden style={{ fontSize: 10 }} />
                          )}
                          {chip.rotulo}
                          <i className="fas fa-times" aria-hidden style={{ fontSize: 9 }} />
                        </button>
                      ))}
                  </span>
                )
              })
            })()}
            <span className="kb__ownerwrap">
              <button
                type="button"
                className="kb__areachip kb__areachip--add"
                title="Adicionar filtro ao quadro"
                onClick={() => setFiltroMenu((v) => (v ? null : 'root'))}
              >
                <i className="fas fa-plus" aria-hidden style={{ fontSize: 9 }} />
                Adicionar filtro
              </button>
              {filtroMenu ? (
                <>
                  <div className="wi-menu__backdrop" onClick={() => setFiltroMenu(null)} />
                  <div className="kb__ownermenu">
                    {filtroMenu === 'root' ? (
                      <>
                        <div className="kb__menuhead">
                          <span className="kb__lanesec" style={{ margin: 0 }}>Adicionar filtro</span>
                          <button
                            type="button"
                            className="kb__gearclose"
                            aria-label="Fechar"
                            title="Fechar"
                            onClick={() => setFiltroMenu(null)}
                          >
                            <i className="fas fa-times" aria-hidden />
                          </button>
                        </div>
                        {([
                          { chave: 'area', rotulo: 'Área', icone: 'fas fa-sitemap' },
                          { chave: 'tipo', rotulo: 'Tipo de item', icone: 'fas fa-tasks' },
                          { chave: 'produto', rotulo: 'Produto/Serviço', icone: 'fas fa-cube' },
                          { chave: 'empresa', rotulo: 'Empresa', icone: 'fas fa-building' },
                          { chave: 'fluxo', rotulo: 'Fluxo', icone: 'fas fa-project-diagram' },
                          { chave: 'filtro', rotulo: 'Filtro avançado', icone: 'fas fa-filter' },
                        ] as const).map((opcao) => (
                          <button
                            key={opcao.chave}
                            type="button"
                            className="kb__menuitem"
                            onClick={() => {
                              if (opcao.chave === 'produto') { setProdutoDraft(config.data?.portfolioNodeIds ?? []); setFiltroModal('produto'); setFiltroMenu(null) }
                              else if (opcao.chave === 'filtro') { setFiltroDraft(parseBoardFilter(boardData.board.filter) ?? emptyFilter(CARD_FILTER_FIELDS, 'Filtro do quadro')); setFiltroModal('filtro'); setFiltroMenu(null) }
                              else {
                                // lista: carrega o rascunho — so aplica no Aplicar
                                const cfg2 = config.data
                                const atuais =
                                  opcao.chave === 'area' ? cfg2?.areaIds
                                  : opcao.chave === 'tipo' ? cfg2?.workItemTypeIds
                                  : opcao.chave === 'empresa' ? cfg2?.companyIds
                                  : cfg2?.flowIds
                                setListaDraft(atuais ?? [])
                                setFiltroMenu(opcao.chave)
                              }
                            }}
                          >
                            <i className={opcao.icone} aria-hidden style={{ width: 16, textAlign: 'center' }} />
                            {opcao.rotulo}
                            <i className="fas fa-chevron-right" aria-hidden style={{ marginLeft: 'auto', fontSize: 9, opacity: 0.5 }} />
                          </button>
                        ))}
                      </>
                    ) : (
                      <>
                        {/* Voltar a esquerda, Fechar a direita (pedido do usuario) */}
                        <div className="kb__menuhead">
                          <button type="button" className="kb__menuitem" style={{ padding: '4px 8px' }} onClick={() => setFiltroMenu('root')}>
                            <i className="fas fa-chevron-left" aria-hidden style={{ fontSize: 9 }} />
                            Voltar
                          </button>
                          <button
                            type="button"
                            className="kb__gearclose"
                            aria-label="Fechar"
                            title="Fechar"
                            onClick={() => setFiltroMenu(null)}
                          >
                            <i className="fas fa-times" aria-hidden />
                          </button>
                        </div>
                        {(() => {
                          // marca/desmarca so no RASCUNHO; aplica no botao do fim
                          const alternar = (idAlvo: string) =>
                            setListaDraft((atual) =>
                              atual.includes(idAlvo) ? atual.filter((x) => x !== idAlvo) : [...atual, idAlvo],
                            )
                          const linha = (chave: string, rotulo: string, iconeCatalogo?: string | null) => {
                            const marcada = listaDraft.includes(chave)
                            return (
                              <button key={chave} type="button" className={`kb__menuitem${marcada ? ' kb__menuitem--on' : ''}`} onClick={() => alternar(chave)}>
                                <i className={marcada ? 'fas fa-check-square' : 'far fa-square'} aria-hidden />
                                {iconeCatalogo !== undefined ? <CatalogIcon icon={iconeCatalogo} fallback="fas fa-tasks" size={10} /> : null}
                                {rotulo}
                              </button>
                            )
                          }
                          if (filtroMenu === 'area')
                            return (bootstrap.data?.areas ?? []).filter((a) => a.isActive).map((a) => linha(a.id, labelOf(a.nameTranslationKey)))
                          if (filtroMenu === 'tipo')
                            // com fluxo no quadro, so tipos que pertencem a um desses fluxos (2026-08-27)
                            return (bootstrap.data?.workItemTypes ?? [])
                              .filter(
                                (t2) =>
                                  t2.isActive &&
                                  (flowIdsDoQuadro.size === 0 || (t2.flowIds ?? []).some((f) => flowIdsDoQuadro.has(f))),
                              )
                              .map((t2) => linha(t2.id, labelOf(t2.nameTranslationKey), t2.icon))
                          if (filtroMenu === 'empresa')
                            return (bootstrap.data?.companies ?? []).map((c2) => linha(c2.id, c2.name))
                          if (filtroMenu === 'fluxo')
                            return (fluxos.data?.flows ?? []).filter((f) => f.isActive).map((f) => linha(f.id, f.name))
                          return null
                        })()}
                        <button
                          type="button"
                          className="kb__applybtn"
                          disabled={saveEscopo.isPending}
                          onClick={() => {
                            const campo =
                              filtroMenu === 'area' ? 'areaIds'
                              : filtroMenu === 'tipo' ? 'workItemTypeIds'
                              : filtroMenu === 'empresa' ? 'companyIds'
                              : 'flowIds'
                            saveEscopo.mutate({ [campo]: listaDraft }, { onSuccess: () => setFiltroMenu(null) })
                          }}
                        >
                          <i className="fas fa-check" aria-hidden style={{ fontSize: 10 }} />
                          Aplicar
                        </button>
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </span>
          </div>
          ) : null}
        </div>

        <div className="kb__actions">
          {/* Dono e membros (estilo Trello): pilha de avatares a direita;
              o popover mostra Dono e Membros — so o dono transfere a posse */}
          {(() => {
            const pessoas = bootstrap.data?.members ?? []
            // VARIOS donos (N:N); a coluna legada cobre quadros pre-migracao
            const ownerIds =
              (config.data?.ownerIds?.length ?? 0) > 0
                ? (config.data?.ownerIds ?? [])
                : boardData.board.ownerMemberId
                  ? [boardData.board.ownerMemberId]
                  : []
            const donos = ownerIds
              .map((oid) => pessoas.find((c3) => c3.id === oid))
              .filter((c3): c3 is NonNullable<typeof c3> => Boolean(c3))
            const membros = (config.data?.memberIds ?? [])
              .map((mid) => pessoas.find((c3) => c3.id === mid))
              .filter((c3): c3 is NonNullable<typeof c3> => Boolean(c3))
            const pilha = [...donos, ...membros.filter((m) => !ownerIds.includes(m.id))]
            const souDono = ownerIds.includes(session.id)
            const busca = membroBusca.trim().toLowerCase()
            const listados = pessoas
              .filter((c3) => c3.isActive)
              .filter((c3) => busca === '' || c3.name.toLowerCase().includes(busca))
            return (
              <span className="kb__ownerwrap">
                <button
                  type="button"
                  className="kb__avstack"
                  title="Compartilhar o quadro"
                  aria-label="Compartilhar o quadro"
                  // abre o diálogo no estilo do Google (SPEC-20260916-1527)
                  onClick={() => setCompartilharAberto(true)}
                >
                  {pilha.slice(0, 4).map((c3, i) => (
                    <span key={c3.id} className={`wi-avatar kb__avstack-av${ownerIds.includes(c3.id) ? ' kb__avstack-av--dono' : ''}`} style={{ zIndex: 10 - i }} title={`${c3.name}${ownerIds.includes(c3.id) ? ' (dono)' : ''}`}>
                      {initialsOf(c3.name)}
                    </span>
                  ))}
                  {pilha.length > 4 ? <span className="wi-avatar kb__avstack-av kb__avstack-mais">+{pilha.length - 4}</span> : null}
                  {pilha.length === 0 ? <i className="fas fa-user-plus" aria-hidden /> : null}
                </button>
                {compartilharAberto ? (
                  <CompartilharQuadro
                    open
                    onClose={() => setCompartilharAberto(false)}
                    boardId={id}
                    nome={boardName}
                    pessoas={pessoas}
                    meuId={session.id}
                    ownerIds={ownerIds}
                    pessoasDoQuadro={(config.data as { members?: PessoaDoQuadro[] } | undefined)?.members ?? []}
                    acessoGeral={{
                      generalAccess: (config.data?.board as { generalAccess?: string } | undefined)?.generalAccess === 'workspace' ? 'workspace' : 'restricted',
                      generalRole: (config.data?.board as { generalRole?: string } | undefined)?.generalRole === 'editor' ? 'editor' : 'viewer',
                    }}
                    souDono={souDono}
                    podeEditarPessoas={souDono || (podeGerenciarMembros && !souLeitor)}
                    onSalvarPessoas={(people, geral) => saveShare.mutateAsync({ people, ...(geral ?? {}) })}
                    onSalvarDonos={(ids) => saveDonos.mutateAsync(ids)}
                  />
                ) : null}
                {ownerMenuOpen ? (
                  <>
                    <div className="wi-menu__backdrop" onClick={() => setOwnerMenuOpen(false)} />
                    <div className="kb__ownermenu kb__membrospop">
                      {/* cabecalho no layout do Trello: titulo centralizado + X */}
                      <div className="kb__membroshead">
                        <span className="kb__membrostitulo">Membros do Quadro</span>
                        <button type="button" className="kb__gearclose kb__membrosx" aria-label="Fechar" onClick={() => setOwnerMenuOpen(false)}>
                          <i className="fas fa-times" aria-hidden />
                        </button>
                      </div>
                      {/* Redesenho 2026-08-31: DONOS primeiro, MEMBROS abaixo,
                          sempre visiveis. O "+" ao lado de cada fila abre a
                          LISTA DE ESCOLHA (pesquisa no topo, politica logo
                          abaixo, 10 nomes visiveis, o resto rola). Marcar poe
                          o certinho e a bolinha aparece na fila; desmarcar
                          tira. No modo dono, marcar um membro TRANSFERE para
                          dono; desmarcar devolve para membro. */}
                      <div className="kb__membrossecaohead">
                        <span className="kb__membrossecao">{donos.length > 1 ? 'Donos do quadro' : 'Dono do quadro'}</span>
                        {souDono ? (
                          <button
                            type="button"
                            className={`kb__membrosmais${membroPicker === 'dono' ? ' kb__membrosmais--on' : ''}`}
                            title="Adicionar donos"
                            aria-label="Adicionar donos"
                            aria-expanded={membroPicker === 'dono'}
                            onClick={() => {
                              setMembroBusca('')
                              setMembroPicker((m) => (m === 'dono' ? null : 'dono'))
                            }}
                          >
                            <i className="fas fa-plus" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                      <div className="kb__membrosfila">
                        {donos.length === 0 ? (
                          <span className="wi-avatar kb__membroav kb__membroav--dono">?</span>
                        ) : (
                          donos.map((d) => (
                            <span
                              key={d.id}
                              className="wi-avatar kb__membroav kb__membroav--dono"
                              title={`${d.name} (dono)${d.id === session.id ? ' — você' : ''}`}
                            >
                              {initialsOf(d.name)}
                            </span>
                          ))
                        )}
                      </div>

                      <div className="kb__membrossecaohead">
                        <span className="kb__membrossecao">Membros</span>
                        {podeGerenciarMembros ? (
                          <button
                            type="button"
                            className={`kb__membrosmais${membroPicker === 'membro' ? ' kb__membrosmais--on' : ''}`}
                            title="Adicionar membros"
                            aria-label="Adicionar membros"
                            aria-expanded={membroPicker === 'membro'}
                            onClick={() => {
                              setMembroBusca('')
                              setMembroPicker((m) => (m === 'membro' ? null : 'membro'))
                            }}
                          >
                            <i className="fas fa-plus" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                      <div className="kb__membrosfila">
                        {membros.filter((m) => !ownerIds.includes(m.id)).length === 0 ? (
                          <span className="kb__laneetapas-vazio">
                            {podeGerenciarMembros ? 'Use o + para adicionar pessoas.' : 'Nenhum membro.'}
                          </span>
                        ) : (
                          membros
                            .filter((m) => !ownerIds.includes(m.id))
                            .map((m) => (
                              <span key={m.id} className="wi-avatar kb__membroav" title={m.name}>
                                {initialsOf(m.name)}
                              </span>
                            ))
                        )}
                      </div>

                      {membroPicker ? (
                        <div className="kb__membropicker">
                          <input
                            ref={membroBuscaRef}
                            autoFocus
                            className="kb__membrosbusca"
                            value={membroBusca}
                            placeholder={membroPicker === 'dono' ? 'Pesquisar para adicionar donos' : 'Pesquisar membros'}
                            onChange={(e) => setMembroBusca(e.target.value)}
                          />
                          {/* politica de gestao de membros — so o DONO troca */}
                          {souDono ? (
                            <div className="kb__membrospolitica">
                              <span className="kb__membrospolibl">Quem adiciona membros ao quadro</span>
                              <div className="kb__membrospoliseg" role="group" aria-label="Quem adiciona membros">
                                <button
                                  type="button"
                                  className={gestaoMembros === 'members' ? 'kb__membrospoli--on' : ''}
                                  aria-pressed={gestaoMembros === 'members'}
                                  disabled={saveGestaoMembros.isPending}
                                  onClick={() => gestaoMembros !== 'members' && saveGestaoMembros.mutate('members')}
                                >
                                  Qualquer membro
                                </button>
                                <button
                                  type="button"
                                  className={gestaoMembros === 'owners' ? 'kb__membrospoli--on' : ''}
                                  aria-pressed={gestaoMembros === 'owners'}
                                  disabled={saveGestaoMembros.isPending}
                                  onClick={() => gestaoMembros !== 'owners' && saveGestaoMembros.mutate('owners')}
                                >
                                  Só donos
                                </button>
                              </div>
                            </div>
                          ) : null}
                          <div className="kb__membropicker-lista">
                            {listados.length === 0 ? (
                              <span className="kb__laneetapas-vazio">Ninguém com esse nome.</span>
                            ) : (
                              listados.map((c3) => {
                                const ehDono = ownerIds.includes(c3.id)
                                const ehMembro = (config.data?.memberIds ?? []).includes(c3.id)
                                const marcado = membroPicker === 'dono' ? ehDono : ehMembro
                                // dono nao se desmarca sozinho e o quadro nunca fica sem dono;
                                // no modo membro, dono nao vira "membro comum" por aqui
                                const podeMexer =
                                  membroPicker === 'dono'
                                    ? souDono && !(ehDono && (c3.id === session.id || donos.length <= 1))
                                    : podeGerenciarMembros && !ehDono
                                return (
                                  <button
                                    key={c3.id}
                                    type="button"
                                    className={`kb__membrolinha${marcado ? ' kb__membrolinha--on' : ''}`}
                                    disabled={!podeMexer}
                                    aria-pressed={marcado}
                                    title={
                                      membroPicker === 'dono'
                                        ? ehDono
                                          ? podeMexer
                                            ? `Desmarcar ${c3.name} — volta a ser membro`
                                            : c3.name
                                          : ehMembro
                                            ? `${c3.name} é membro — marcar transfere para dono`
                                            : `Marcar ${c3.name} como dono`
                                        : ehDono
                                          ? `${c3.name} já é dono do quadro`
                                          : marcado
                                            ? `Desmarcar ${c3.name} — sai do quadro`
                                            : `Marcar ${c3.name} — entra no quadro`
                                    }
                                    onClick={() => {
                                      const atuais = config.data?.memberIds ?? []
                                      if (membroPicker === 'dono') {
                                        if (ehDono) {
                                          // desmarcou: sai dos donos e VOLTA para membro
                                          saveDonos.mutate(ownerIds.filter((x) => x !== c3.id))
                                          if (!atuais.includes(c3.id)) saveMembros.mutate([...atuais, c3.id])
                                        } else {
                                          // marcou: vira dono; se era membro, e TRANSFERIDO
                                          saveDonos.mutate([...ownerIds, c3.id])
                                          if (atuais.includes(c3.id)) saveMembros.mutate(atuais.filter((x) => x !== c3.id))
                                        }
                                      } else {
                                        saveMembros.mutate(ehMembro ? atuais.filter((x) => x !== c3.id) : [...atuais, c3.id])
                                      }
                                    }}
                                  >
                                    <span className="wi-avatar kb__membroav">{initialsOf(c3.name)}</span>
                                    <span className="kb__membrolinha-nome">{c3.name}</span>
                                    {ehDono ? <span className="kb__voce">dono</span> : null}
                                    <i
                                      className={`fas fa-check kb__membrolinha-check${marcado ? ' kb__membrolinha-check--on' : ''}`}
                                      aria-hidden
                                    />
                                  </button>
                                )
                              })
                            )}
                          </div>
                          <button type="button" className="kb__membropicker-fechar" onClick={() => setMembroPicker(null)}>
                            Fechar
                          </button>
                        </div>
                      ) : null}
                      {/* Sair do quadro: so o MEMBRO (nao-dono) — perde a
                          visibilidade e volta pro explorer (2026-08-27) */}
                      {!souDono && (config.data?.memberIds ?? []).includes(session.id) ? (
                        <button
                          type="button"
                          className="kb__membrosair"
                          disabled={sairDoQuadro.isPending}
                          onClick={() =>
                            confirm.ask(
                              `Você sairá do quadro “${boardName}” e deixará de vê-lo. O dono pode adicioná-lo de volta.`,
                              () => sairDoQuadro.mutate(),
                            )
                          }
                        >
                          <i className="fas fa-arrow-right-from-bracket" aria-hidden />
                          Sair do quadro
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </span>
            )
          })()}
          {editMode ? (
            <>
              {/* itens visiveis por lane: define a altura da lane (o resto rola).
                  0 = automatico (ate o limite da area de trabalho) — 2026-08-27 */}
              <div className="kb__visiveis" title="Quantos itens ficam visíveis por lane antes de rolar (0 = automático)">
                <span className="kb__visiveislbl">Itens por lane</span>
                <button
                  type="button"
                  className="kb__visiveisbtn"
                  aria-label="Menos itens visíveis"
                  disabled={itensVisiveis <= 0 || saveVisiveis.isPending}
                  onClick={() => saveVisiveis.mutate(Math.max(0, itensVisiveis - 1))}
                >
                  <i className="fas fa-minus" aria-hidden />
                </button>
                <span className="kb__visiveisval">{itensVisiveis === 0 ? 'Auto' : itensVisiveis}</span>
                <button
                  type="button"
                  className="kb__visiveisbtn"
                  aria-label="Mais itens visíveis"
                  disabled={itensVisiveis >= 50 || saveVisiveis.isPending}
                  onClick={() => saveVisiveis.mutate(Math.min(50, itensVisiveis + 1))}
                >
                  <i className="fas fa-plus" aria-hidden />
                </button>
              </div>
              {/* os dados da criação do quadro, e a visibilidade dos novos
                  itens (SPEC-20261007-0934-visibilidade-padrao-do-quadro) */}
              <button
                type="button"
                className="kb__btn"
                title="Título, descrição, áreas, dono, tipos, fluxos e novos itens"
                onClick={openEditBoard}
              >
                <i className="fas fa-sliders-h" aria-hidden style={{ fontSize: 12 }} />
                Dados do quadro
              </button>
              <button
                type="button"
                className="kb__btn kb__btn--icon"
                aria-label="Alterar tela de fundo"
                title="Alterar tela de fundo"
                onClick={() => setBgOpen(true)}
              >
                <i className="fas fa-fill-drip" aria-hidden style={{ fontSize: 13 }} />
              </button>
              <button
                type="button"
                className="kb__btn kb__btn--danger"
                title="Excluir este quadro"
                onClick={() =>
                  confirm.ask(
                    `O quadro “${boardName}” e a sua configuração serão removidos. Os Itens de Trabalho continuam existindo.`,
                    () => excluirQuadro.mutate(),
                  )
                }
              >
                <i className="fas fa-trash" aria-hidden style={{ fontSize: 11 }} />
                Excluir quadro
              </button>
              <button type="button" className="kb__btn" onClick={() => setEditMode(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="kb__btn kb__btn--primary"
                onClick={() => saveLanes.mutate(draft)}
              >
                <i className="fas fa-check" aria-hidden style={{ fontSize: 10 }} />
                Salvar
              </button>
            </>
          ) : (
            <>
              {/* Agrupar por (swimlanes, estilo Jira/Plane): menu com sub-lista
                  de Tipo de Item; preferencia por usuario, guardada por quadro */}
              {(() => {
                const tipos = (bootstrap.data?.workItemTypes ?? []).filter((t) => t.isActive)
                const tipoAtual = agruparPor.startsWith('type:')
                  ? tipos.find((t) => `type:${t.code}` === agruparPor)
                  : undefined
                const raiz = [
                  { v: 'none', r: 'Sem agrupamento', i: 'fas fa-ban' },
                  { v: 'status', r: 'Status', i: 'fas fa-adjust' },
                  { v: 'etapa', r: 'Etapa do fluxo', i: 'fas fa-project-diagram' },
                  { v: 'tipoitem', r: 'Tipo do Item', i: 'fas fa-tasks' },
                  { v: 'member', r: 'Membro', i: 'fas fa-user' },
                  { v: 'responsavel', r: 'Responsável', i: 'fas fa-user-check' },
                  { v: 'area', r: 'Área', i: 'fas fa-sitemap' },
                  { v: 'company', r: 'Empresa', i: 'fas fa-building' },
                ] as const
                // o rótulo sai da MESMA lista do menu: opção nova aparece nos
                // dois lugares sem precisar lembrar de um ternário
                const rotulo = tipoAtual
                  ? `Item pai: ${labelOf(tipoAtual.nameTranslationKey)}`
                  : (raiz.find((o) => o.v === agruparPor)?.r ?? 'Sem agrupamento')
                return (
                  <>
                  {/* Quadro | Lista — a MESMA seleção de itens em duas leituras
                      (SPEC-20260914-2210). Fica antes do "Agrupar por" porque
                      o agrupamento vale nos dois modos. */}
                  {meuAcesso === 'ambos' ? (
                  <div className="kb__visao" role="group" aria-label="Modo de visualização do quadro">
                    {([
                      { v: 'quadro', r: 'Quadro', i: 'fas fa-columns' },
                      { v: 'lista', r: 'Lista', i: 'fas fa-list' },
                      { v: 'planilha', r: 'Planilha', i: 'fas fa-table' },
                      { v: 'tempo', r: 'Linha do tempo', i: 'fas fa-stream' },
                    ] as const).map((m) => (
                      <button
                        key={m.v}
                        type="button"
                        className={`kb__visaobtn${modoVisao === m.v ? ' kb__visaobtn--on' : ''}`}
                        aria-pressed={modoVisao === m.v}
                        title={`Ver como ${m.r.toLowerCase()}`}
                        onClick={() => trocarModo(m.v)}
                      >
                        <i className={m.i} aria-hidden />
                        <span>{m.r}</span>
                      </button>
                    ))}
                  </div>
                  ) : null}
                  {/* AGRUPAMENTO (SPEC-20260915-2234): um botão com o ícone de
                      visualizações do Sheets abre o menu de 3 níveis — o mesmo
                      da planilha, agora também no quadro e na lista normal */}
                  <div className="kb__groupby kb__sdrop-wrap">
                    <button
                      type="button"
                      className={`kb__groupbybtn${agrupado ? ' kb__groupbybtn--on' : ''}`}
                      title={`Agrupar: ${rotulo}`}
                      aria-label={`Agrupar por (atual: ${rotulo})`}
                      aria-haspopup="menu"
                      aria-expanded={tabelaMenu !== null}
                      onClick={() => setTabelaMenu((v) => (v ? null : 'root'))}
                    >
                      <IconeVisualizacoes />
                      {/*
                        Só o ícone e a seta (pedido do Cristian, 2026-10-02): o
                        rótulo mudava de largura conforme o agrupamento — "Sem
                        agrupamento" contra "Status" — e empurrava o resto do
                        cabeçalho a cada troca. Ele continua no `title` e no
                        rótulo acessível, para quem precisa saber o que está
                        valendo sem abrir o menu.
                      */}
                      <i className="fas fa-chevron-down" aria-hidden style={{ fontSize: 9, opacity: 0.6 }} />
                    </button>
                    {/* na planilha o menu mora na barra do Sheets: um só no DOM */}
                    {planilha ? null : menuAgrupamento}
                  </div>
                  </>
                )
              })()}
              {/* expandir/recolher TODAS as bandas de uma vez (2026-08-27) */}
              {agrupado
                ? (() => {
                    const todasFechadas = bandas.length > 0 && bandas.every((b) => bandasFechadas.has(b.key))
                    return (
                      <button
                        type="button"
                        className="kb__btn kb__btn--icon"
                        title={todasFechadas ? 'Expandir todas as faixas' : 'Recolher todas as faixas'}
                        aria-label={todasFechadas ? 'Expandir todas as faixas' : 'Recolher todas as faixas'}
                        onClick={() =>
                          definirBandasFechadas(todasFechadas ? new Set() : new Set(bandas.map((b) => b.key)))
                        }
                      >
                        <i
                          className={todasFechadas ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'}
                          aria-hidden
                        />
                      </button>
                    )
                  })()
                : null}
              {/* ícone + quantidade; o clique abre a lista com código e nome,
                  e o código leva à edição (SPEC-20261006-1825-itens-nao-mapeados-em-lista) */}
              {unmappedCount > 0 ? (
                <ItensNaoMapeados
                  boardId={id}
                  itens={unmapped.data?.items ?? []}
                  // quem só tem o Kanban ou só a Planilha não tem a Lista
                  onFiltrarNaLista={
                    meuAcesso === 'ambos'
                      ? () => {
                          setSoNaoMapeados(true)
                          setModoVisao('lista')
                        }
                      : undefined
                  }
                />
              ) : null}
              {souDonoQuadro ? (
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="kb__gearonly"
                    aria-label="Configurar quadro"
                    title="Configurar quadro"
                    // sem menu: a engrenagem entra DIRETO na configuracao do
                    // desenho — tudo do quadro se configura ali (pedido do
                    // usuario 2026-08-24); Alterar fundo vive nas acoes da
                    // configuracao e a tela "Editar quadro" saiu de cena.
                    // Config e exclusiva dos donos (2026-08-27).
                    onClick={startEdit}
                  >
                    <i className="fas fa-cog" aria-hidden />
                  </button>
                </div>
              ) : null}
              {/* slot da barra em tela cheia: a lista portala o seletor de
                  Colunas (e o aviso de ordem manual) para cá */}
              {planilha ? null : <span className="kb__barraslot" ref={setSlotDaBarra} />}
              {/* formatação privada/pública da lista (SPEC-20260916-0102) — só no modo Lista fora da planilha */}
              {modoVisao === 'planilha' && !planilha ? botaoFormato : null}
              {/* TELA CHEIA só na Lista (vira a planilha); o Kanban não tem o
                  botão (pedido 2026-09-16 02:21: "tira esse expandir tela do
                  kanban"). Em tela cheia ele fica para haver caminho de volta. */}
              {modoVisao === 'planilha' || telaCheia ? (
                <button
                  type="button"
                  className={`kb__gearonly kb__cheiabtn${telaCheia ? ' kb__cheiabtn--on' : ''}`}
                  aria-label={telaCheia ? 'Voltar à tela normal' : 'Expandir para a tela toda'}
                  aria-pressed={telaCheia}
                  title={telaCheia ? 'Tela normal (Esc)' : 'Tela toda'}
                  onClick={() => setTelaCheia((v) => !v)}
                >
                  <i className={telaCheia ? 'fas fa-compress' : 'fas fa-expand'} aria-hidden />
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------ MODO LISTA ---
          Os Itens de Trabalho DESTE quadro, na mesma tabela da funcionalidade
          de Itens (SPEC-20261003-1059). E a mesma tela montada aqui dentro, e
          nao uma copia: duas tabelas divergiriam na primeira coluna nova.

          Clicar no codigo leva para a pagina do item e sai do quadro — a tela
          de edicao continua sendo uma pagina, com a URL que notificacao, chat e
          e-mail ja apontam. */}
      {/* Lista e LINHA DO TEMPO: a MESMA tela da lista de Itens, recortada pelo
          quadro — um módulo só (SPEC-20261009-2112-linha-do-tempo-no-quadro) */}
      {(modoVisao === 'lista' || modoVisao === 'tempo') && !editMode ? (
        <div className="kb__listaitens">
          <WorkItemsPage
            key={modoVisao}
            kanbanBoardId={id}
            embutido
            modo={modoVisao === 'tempo' ? 'tempo' : 'tabela'}
            /* a busca e o filtro do CABEÇALHO valem nos três modos */
            controlesDoQuadro={{
              busca: buscaCards,
              filtros: [parseBoardFilter(boardData.board.filter), activeCardFilter, filtroRapidoAtivo, filtroDePeriodo].filter(
                (f): f is SavedFilter => f !== null,
              ),
              soOsIds: idsNaoMapeados,
              /* a VISÃO em uso (SPEC-20261103-1418): colunas e ordenação da
                 lista; mexer grava nela — se a pessoa pode editá-la */
              visao: visaoAtiva
                ? {
                    id: visaoAtiva.id,
                    colunas: visaoAtiva.config.colunas,
                    ordenacao: visaoAtiva.config.ordenacao,
                    alterar: visaoAtiva.canEdit
                      ? (config) =>
                          alterarVisao.mutate(
                            { id: visaoAtiva.id, config },
                            { onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar na visão.' }) },
                          )
                      : null,
                  }
                : null,
            }}
          />
        </div>
      ) : null}

      {/* -------------------------------------------------- MODO PLANILHA ---
          Mesma seleção de cartões do quadro, em tabela (SPEC-20260914-2210).
          Não vale no modo de EDIÇÃO: lá se desenha a lane, não se lê item. */}
      {modoVisao === 'planilha' && !editMode ? (
        <ListaDoQuadro
          cards={cardsVisiveis}
          colunas={colunasAtivas}
          todasAsColunas={colunasDisponiveis}
          onColunas={setColunasLista}
          onLargura={(chave, px) => setLargurasLista((s) => ({ ...s, [chave]: px }))}
          alturas={alturasLinha}
          onAltura={(cardId, px) => setAlturasLinha((s) => ({ ...s, [cardId]: px }))}
          onMoverLinha={moverLinha}
          bandasFechadas={bandasFechadas}
          onAlternarBanda={alternarBanda}
          edicao={{
            tipos: tiposDoQuadro.map((t) => ({ value: t.id, label: labelOf(t.nameTranslationKey), icon: t.icon ?? undefined })),
            prioridades: (bootstrap.data?.priorities ?? [])
              .filter((p) => p.isActive)
              .map((p) => ({ value: p.id, label: labelOf(p.nameTranslationKey) })),
            // quadro com lanes POR STATUS: só os status que têm lane (escolher
            // outro faria o item sumir). Quadro com lanes por ETAPA (statusId
            // nulo em todas): qualquer status ativo do catálogo — a transição
            // vai pela rota própria de status, e a lane (por etapa) não muda.
            statusComLane: (() => {
              const daLane = [...new Set(boardData.columns.map((c) => c.statusId).filter((s): s is string => Boolean(s)))]
              if (daLane.length > 0) return daLane.map((sid) => ({ value: sid, label: statusLabel(sid) }))
              return (bootstrap.data?.workItemStatuses ?? [])
                .filter((st) => st.isActive)
                .map((st) => ({ value: st.id, label: statusLabel(st.id) }))
            })(),
            membros: (bootstrap.data?.members ?? []).filter((m) => m.isActive).map((m) => ({ value: m.id, label: m.name })),
            camposCompl: new Map(camposComplFiltraveis.map((f) => [`cf:${f.key}`, f])),
            onEdicaoRica: setEdicaoRica,
            // ETAPA (SPEC-20260916-0120): mesma regra do composer — fluxos do TIPO
            // (dentro dos do quadro); tipo sem fluxo cai nos fluxos do quadro
            etapasParaNovo: (typeId) => {
              const tipo = tiposDoQuadro.find((t) => t.id === typeId)
              const fluxosDoTipo = ((tipo?.flowIds as string[] | undefined) ?? []).filter((f) => flowIdsDoQuadro.has(f))
              const fluxos = fluxosDoTipo.length > 0 ? fluxosDoTipo : [...flowIdsDoQuadro]
              const iniciais = fluxos.flatMap((f) => {
                const doFluxo = etapasDoQuadro.filter((s) => s.flowId === f)
                const marcadas = doFluxo.filter((s) => (s as { isInitial?: boolean }).isInitial)
                return marcadas.length > 0 ? marcadas : doFluxo.slice(0, 1)
              })
              return iniciais.map((s) => ({ value: s.flowStepId, label: `${s.flowName} · ${s.stepName}` }))
            },
            etapasParaItem: (typeId) => {
              const tipo = tiposDoQuadro.find((t) => t.id === typeId)
              const fluxosDoTipo = ((tipo?.flowIds as string[] | undefined) ?? []).filter((f) => flowIdsDoQuadro.has(f))
              const fluxos = new Set(fluxosDoTipo.length > 0 ? fluxosDoTipo : [...flowIdsDoQuadro])
              return etapasDoQuadro.filter((s) => fluxos.has(s.flowId)).map((s) => ({ value: s.flowStepId, label: `${s.flowName} · ${s.stepName}` }))
            },
            // trocar a etapa = mover para a lane daquela etapa (o servidor aplica a
            // etapa e valida a sequência do fluxo)
            mudarEtapa: async (cardId, flowStepId) => {
              const lane = boardData.columns.find((c) => (c.flowStepIds ?? []).includes(flowStepId))
              if (!lane) {
                toast.push({ tone: 'warning', message: 'Nenhuma lane deste quadro recebe essa etapa.' })
                throw new Error('sem lane')
              }
              await move.mutateAsync({ workItemId: cardId, targetColumnId: lane.id, beforeWorkItemId: null })
            },
            gravar: async (cardId, patch) => {
              try {
                const r = await api['work-items'][':id'].$patch({ param: { id: cardId }, query: {}, json: patch as never })
                if (!r.ok) throw new Error(((await r.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ?? 'Não foi possível gravar.')
                invalidate()
              } catch (e) {
                toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar.' })
                throw e
              }
            },
            gravarCompl: async (cardId, customValues) => {
              try {
                const r = await api['work-items'][':id'].$patch({ param: { id: cardId }, query: {}, json: { customValues } as never })
                if (!r.ok) throw new Error(((await r.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ?? 'Não foi possível gravar.')
                invalidate()
              } catch (e) {
                toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gravar.' })
                throw e
              }
            },
            // status no quadro É lane: mover para a primeira lane daquele status,
            // no fim dela (a regra de sequência do fluxo é do servidor)
            mudarStatus: async (cardId, statusId) => {
              const lane = boardData.columns.find((c) => c.statusId === statusId)
              if (lane) {
                await move.mutateAsync({ workItemId: cardId, targetColumnId: lane.id, beforeWorkItemId: null })
                return
              }
              // lanes por etapa: a transição de status tem rota própria (com as
              // regras de conclusão/reabertura); o item fica na lane em que está
              try {
                const r = await api['work-items'][':id'].status.$post({ param: { id: cardId }, json: { statusId } })
                if (!r.ok)
                  throw new Error(
                    ((await r.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ??
                      'Não foi possível mudar o status.',
                  )
                invalidate()
              } catch (e) {
                toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível mudar o status.' })
                throw e
              }
            },
          }}
          seletorAberto={seletorColunas}
          onSeletor={setSeletorColunas}
          slotDaBarra={planilha ? slotDaBarra : null}
          larguraCodigo={largurasLista.code ?? LARGURA_ROWNUM}
          alturaCabecalho={alturaCabecalho}
          onAlturaCabecalho={setAlturaCabecalho}
          estilo={estiloPlanilha}
          fonte={fontePlanilha}
          filtrosExtras={[
            ...(buscaCards.trim() ? [`Busca: "${buscaCards.trim()}"`] : []),
            ...(visaoAtiva ? [`Visão: ${visaoAtiva.name}`] : []),
          ]}
          bandas={
            agrupado
              ? // árvore de até 3 níveis: o filtro de cada faixa já compõe com o
                // do pai, então a folha filtra sozinha o conjunto certo
                bandas.map((b) => ({
                  ...b,
                  filhas: subBandasDe(b, subEfetivo, b.key).map((s) => ({
                    ...s,
                    filhas: subBandasDe(s, sub2Efetivo, b.key).map((n) => ({ ...n, filhas: [] })),
                  })),
                }))
              : null
          }
          ordem={ordemLista}
          onOrdenar={(col, desc) =>
            setOrdemLista((o) => ({ col, desc: desc ?? (o.col === col ? !o.desc : false) }))
          }
          planilha={planilha}
          somenteLeitura={souLeitor}
          nomeDaTabela={boardName}
          memoriaRolagem={`orbind.kb.scroll.${id}`}
          filtrosColuna={filtrosColuna}
          onFiltrosColuna={setFiltrosColuna}
          filtrosLigados={filtrosLigados}
          exportarRef={exportarRef}
          novaLinha={{
            // "+" abre a mesma tela de inclusão da lane (a primeira lane do quadro)
            aoAbrirTela: () => {
              const primeira = lanes[0]
              if (!primeira) { toast.push({ tone: 'warning', message: 'O quadro não tem lane para receber o item.' }); return }
              setComposerLane(primeira.key)
            },
            // linha preenchida: cria pelo mesmo caminho do composer e pina na primeira lane
            aoCriar: async (r) => {
              // lane = a que mapeia a etapa escolhida; sem etapa, a primeira
              const primeira = (r.flowStepId ? lanes.find((l) => (l.flowStepIds ?? []).includes(r.flowStepId!)) : undefined) ?? lanes[0]
              const statusId = primeira?.statusId ?? r.statusId ?? bootstrap.data?.workItemStatuses.find((s) => s.isActive)?.id
              if (!statusId || !r.workItemTypeId || !r.title?.trim()) throw new Error('Faltam dados para criar o item.')
              try {
                const res = await api['work-items'].$post({
                  json: {
                    title: r.title.trim(),
                    description: r.description ?? '',
                    workItemTypeId: r.workItemTypeId,
                    workItemStatusId: statusId,
                    workItemPriorityId: r.workItemPriorityId ?? bootstrap.data!.priorities.find((p) => p.code === 'MEDIUM')?.id ?? bootstrap.data!.priorities[0]!.id,
                    companyAreaId: config.data?.areaIds?.[0] ?? null,
                    companyId: config.data?.companyIds?.[0] ?? null,
                    portfolioNodeId: config.data?.portfolioNodeIds?.[0] ?? null,
                    assigneeMemberId: r.assigneeMemberId ?? null,
                    dueAt: r.dueAt ? new Date(r.dueAt) : null,
                    flowStepId: r.flowStepId ?? null,
                    kanbanBoardId: id,
                    expectedResult: r.expectedResult ?? '',
                    recurrence: 'never',
                    reminder: 'none',
                    customValues: r.customValues ?? {},
                  } as never,
                })
                if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ?? 'Falha ao criar o item.')
                const data = (await res.json()) as { item?: { id: string; code?: string } }
                const itemId = data.item?.id
                if (itemId) await fixarNaLane(itemId, primeira?.id)
                invalidate()
                toast.push({ tone: 'success', message: `Item ${data.item?.code ?? ''} criado.`.replace('  ', ' ') })
              } catch (e) {
                toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao criar o item.' })
                throw e
              }
            },
          }}
          buscaAtiva={buscaAtiva}
          casaBusca={casaBusca}
          selecionado={selectedCard}
          onSelecionar={setSelectedCard}
          onAbrir={(cardId) => navigate(`/kanbans/${id}/items/${cardId}`)}
          statusLabel={statusLabel}
          labelOf={labelOf}
          etapaDe={(card) => {
            const s = (config.data?.flowSteps ?? []).find((x) => x.flowStepId === card.flowStepId)
            return s ? { nome: s.stepName, cor: s.stepColor ?? null } : null
          }}
        />
      ) : null}

      {planilha ? (
        // ABAS DE PLANILHA no rodapé (SPEC-20260915-2149): "+" e "≡" cinza como
        // no Sheets; as abas são os modos do quadro — trocar de aba troca o modo
        <div className="kb__abas" role="tablist" aria-label="Abas da planilha">
          <button type="button" className="kb__abasbtn" disabled title="Adicionar página — não se aplica aqui" aria-label="Adicionar página">
            <i className="fas fa-plus" aria-hidden />
          </button>
          <button type="button" className="kb__abasbtn" disabled title="Todas as páginas — não se aplica aqui" aria-label="Todas as páginas">
            <i className="fas fa-bars" aria-hidden />
          </button>
          {([
            { v: 'quadro', r: 'Quadro' },
            { v: 'lista', r: 'Lista' },
            { v: 'planilha', r: 'Planilha' },
            { v: 'tempo', r: 'Linha do tempo' },
          ] as const).map((a) => (
            <button
              key={a.v}
              type="button"
              role="tab"
              aria-selected={modoVisao === a.v}
              className={`kb__aba${modoVisao === a.v ? ' kb__aba--on' : ''}`}
              onClick={() => trocarModo(a.v)}
            >
              {a.r}
              <i className="fas fa-caret-down" aria-hidden />
            </button>
          ))}
        </div>
      ) : null}

      {/* barra horizontal PROPRIA, fixa no pe da tela (ver o componente) */}
      {modoVisao === 'quadro' || editMode ? <BarraHorizontalFixa alvoRef={lanesRef} /> : null}

      {/* menu "Campos do card" (modo de edicao): UM so, num portal no body,
          ancorado no botao da lane que o abriu — fora do overflow das lanes */}
      {editMode && camposAnchor && camposPos
        ? createPortal(
            <div
              ref={camposPopRef}
              className="kb__ownermenu kb__camposmenu"
              role="dialog"
              aria-label="Campos do card"
              style={{
                left: camposPos.left,
                top: camposPos.top,
                bottom: camposPos.bottom,
                maxHeight: camposPos.maxH,
              }}
            >
              <div className="kb__menuhead">
                <span className="kb__lanesec" style={{ margin: 0 }}>
                  Campos do card
                  <span className="kb__gearcount">{camposEfetivos.length}</span>
                </span>
                <button type="button" className="kb__gearclose" aria-label="Fechar" onClick={() => setCamposAnchor(null)}>
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
              {CARD_CAMPOS.map((campo) => {
                const marcado = camposEfetivos.includes(campo.key)
                return (
                  <button
                    key={campo.key}
                    type="button"
                    className={`kb__menuitem${marcado ? ' kb__menuitem--on' : ''}`}
                    aria-pressed={marcado}
                    onClick={() => {
                      // rascunho local via REF: nem cliques na mesma
                      // renderizacao se atropelam
                      const base = camposDraftRef.current ?? camposEfetivos
                      const novos = base.includes(campo.key)
                        ? base.filter((x) => x !== campo.key)
                        : [...base, campo.key]
                      camposDraftRef.current = novos
                      setCamposDraft(novos)
                      saveEscopo.mutate({ cardFields: JSON.stringify(novos) })
                    }}
                  >
                    <i className={marcado ? 'fas fa-check-square' : 'far fa-square'} aria-hidden />
                    {campo.rotulo}
                  </button>
                )
              })}
            </div>,
            document.body,
          )
        : null}

      {/* ------------------------------------------------------------- lanes */}
      <div
        className={`kb__lanes${agrupado ? ' kb__lanes--grouped' : ''}`}
        hidden={modoVisao !== 'quadro' && !editMode}
        ref={lanesRef}
        /* onde a pessoa parou no QUADRO (2026-09-21); a planilha guarda no
           lugar dela, porque uma rola por colunas e a outra por linhas */
        onScroll={(e) =>
          guardarRolagemNoDisco(`orbind.kb.scrollquadro.${id}`, {
            top: e.currentTarget.scrollTop,
            left: e.currentTarget.scrollLeft,
          })
        }
        onMouseDown={(event) => {
          // pan so quando o clique nasce no fundo do quadro, nunca dentro de
          // uma lane, botao ou marcador
          const target = event.target as HTMLElement
          if (target.closest('.kb__lane, .kb__addlane, .kb__lanemark, button, input')) return
          if (!lanesRef.current) return
          panRef.current = { startX: event.clientX, scrollLeft: lanesRef.current.scrollLeft }
          document.body.style.cursor = 'grabbing'
          document.body.style.userSelect = 'none'
        }}
      >
        {/* PRIMEIRA COLUNA no modo de edicao: painel de etapas (acordeao por
            fluxo) — arrasta daqui para as lanes e de volta (pedido do usuario) */}
        {editMode && etapasDoQuadro.length > 0 ? (
          <section
            className={`kb__lane kb__poollane${etapaDropLane === 'pool' ? ' kb__lane--drop' : ''}`}
            onDragOver={(e) => {
              if (draggingEtapa && draggingEtapa.deKey !== 'pool') {
                e.preventDefault()
                setEtapaDropLane('pool')
                setEtapaDropIndex(null)
              }
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return
              setEtapaDropLane((c) => (c === 'pool' ? null : c))
            }}
            onDrop={(e) => {
              e.preventDefault()
              // ficha devolvida ao painel: sai da lane de origem
              if (draggingEtapa && draggingEtapa.deKey !== 'pool') {
                const { stepId, deKey } = draggingEtapa
                patchLane(deKey, {
                  flowStepIds: (draft.find((l) => l.key === deKey)?.flowStepIds ?? []).filter((x) => x !== stepId),
                })
              }
              setDraggingEtapa(null)
              setEtapaDropLane(null)
              setEtapaDropIndex(null)
            }}
          >
            <div className="kb__lanehead">
              <span className="kb__lanename">Fluxo/Etapas</span>
              <span style={{ flex: 1 }} />
              <span className="kbcfg__card-badge">{naoMapeadas.length}</span>
            </div>
            <div className="kb__poolbody">
              {fluxosDoPool.map((nomeFluxo) => {
                  const doFluxo = naoMapeadas.filter((e) => e.flowName === nomeFluxo)
                  const aberto = !fluxosFechados.has(nomeFluxo)
                  const fluxoId = etapasDoQuadro.find((e) => e.flowName === nomeFluxo)?.flowId
                  return (
                    <div key={nomeFluxo} className="kbcfg__fluxo">
                      <button
                        type="button"
                        className="kbcfg__fluxo-head"
                        aria-expanded={aberto}
                        onClick={() => alternarFluxo(nomeFluxo)}
                      >
                        <i className={aberto ? 'fas fa-chevron-down' : 'fas fa-chevron-right'} aria-hidden />
                        <span className="kbcfg__fluxo-nome">{nomeFluxo}</span>
                        <span className="kbcfg__card-badge">{doFluxo.length}</span>
                        {/* icone do FLUXO no lugar dos tres pontinhos (pedido
                            2026-08-25): e o botao que abre o desenho do fluxo;
                            fechar volta para o quadro */}
                        {fluxoId ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="kbcfg__fluxo-desenho"
                            title={`Abrir o fluxo ${nomeFluxo}`}
                            aria-label={`Abrir o fluxo ${nomeFluxo}`}
                            onClick={(ev) => {
                              ev.stopPropagation()
                              // guarda o rascunho da configuracao: a volta reabre igual
                              sessionStorage.setItem(chaveRascunho, JSON.stringify(draft))
                              navigate(`/admin/flows?desenhar=${fluxoId}&voltar=${encodeURIComponent(`/kanbans/${id}`)}`)
                            }}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.stopPropagation()
                                sessionStorage.setItem(chaveRascunho, JSON.stringify(draft))
                                navigate(`/admin/flows?desenhar=${fluxoId}&voltar=${encodeURIComponent(`/kanbans/${id}`)}`)
                              }
                            }}
                          >
                            <i className="fas fa-project-diagram" aria-hidden />
                          </span>
                        ) : null}
                      </button>
                      {aberto ? (
                        <div className="kbcfg__fluxo-etapas">
                          {doFluxo.map((e) => (
                            <div
                              key={e.flowStepId}
                              className="kbcfg__ficha"
                              title={`${e.flowName} · ${e.stepName} — arraste para uma lane`}
                              draggable
                              onDragStart={(ev) => {
                                ev.stopPropagation()
                                ev.dataTransfer.effectAllowed = 'move'
                                setDraggingEtapa({ stepId: e.flowStepId, deKey: 'pool' })
                              }}
                              onDragEnd={() => {
                                setDraggingEtapa(null)
                                setEtapaDropLane(null)
                                setEtapaDropIndex(null)
                              }}
                            >
                              <i className="fas fa-grip-vertical kbcfg__ficha-grip" aria-hidden />
                              <span
                                className="kbcfg__ficha-dot"
                                style={{ background: e.stepColor ?? 'var(--ob-primary)' }}
                                aria-hidden
                              />
                              <span className="kbcfg__ficha-nome">{e.stepName}</span>
                            </div>
                          ))}
                          {doFluxo.length === 0 ? (
                            <p className="kb__laneetapas-vazio">Todas as etapas estão em lanes.</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
            </div>
          </section>
        ) : null}

        {(() => {
          const renderLane = (lane: DraftLane, index: number, bandFilter?: (c: BoardCard) => boolean) => {
          const cards = cardsOf(lane).filter((c) => (bandFilter ? bandFilter(c) : true))
          // a lane mostra as ETAPAS (nao o status): cor e nome vem do fluxo;
          // a ORDEM segue lane.flowStepIds (o arraste define e persiste)
          const laneSteps = lane.flowStepIds
            .map((stepId) => (config.data?.flowSteps ?? []).find((s) => s.flowStepId === stepId))
            .filter((s): s is NonNullable<typeof s> => Boolean(s))
          const temFluxos = (config.data?.flowSteps?.length ?? 0) > 0
          const stepColor = laneSteps[0]?.stepColor ?? 'var(--ob-primary)'
          // WIP nao aparece no quadro: so bloqueia (drop e composer) ao exceder
          const wipFull = lane.wipLimit !== null && cards.length >= lane.wipLimit
          return (
            <div key={lane.key} style={{ display: 'flex', alignItems: 'stretch' }}>
              {/* marcador de destino do arraste de lane */}
              {editMode ? (
                <span
                  className={`kb__lanemark${laneDropIndex === index ? ' kb__lanemark--on' : ''}`}
                  onDragOver={(e) => {
                    if (!draggingLane) return
                    e.preventDefault()
                    setLaneDropIndex(index)
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (draggingLane) reorderLane(draggingLane, index)
                    setDraggingLane(null)
                    setLaneDropIndex(null)
                  }}
                />
              ) : null}

              <section
                className={`kb__lane${draggingLane === lane.key ? ' kb__lane--ghost' : ''}${armedLane === lane.key ? ' kb__lane--armed' : ''}${cardDropLane === lane.key || etapaDropLane === lane.key ? ' kb__lane--drop' : ''}`}
                draggable={editMode && armedLane === lane.key}
                onDragStart={(e) => {
                  if (!editMode || armedLane !== lane.key) return
                  setDraggingLane(lane.key)
                  e.dataTransfer.effectAllowed = 'move'
                  // suprime o desenho nativo: quem se move e o clone translucido
                  const empty = new Image()
                  empty.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
                  e.dataTransfer.setDragImage(empty, 0, 0)
                  const rect = e.currentTarget.getBoundingClientRect()
                  dragGhostOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
                  const clone = e.currentTarget.cloneNode(true) as HTMLElement
                  clone.classList.add('kb__laneghostfly')
                  clone.style.width = `${rect.width}px`
                  clone.style.height = `${rect.height}px`
                  clone.style.left = `${rect.left}px`
                  clone.style.top = `${rect.top}px`
                  // dentro do .ob-root para as variaveis de tema resolverem
                  ;(e.currentTarget.closest('.ob-root') ?? document.body).appendChild(clone)
                  dragGhostRef.current = clone
                }}
                onDragEnd={() => {
                  setDraggingLane(null)
                  setLaneDropIndex(null)
                  setArmedLane(null)
                  dragGhostRef.current?.remove()
                  dragGhostRef.current = null
                }}
                onMouseDown={(e) => {
                  // clicar e segurar em QUALQUER lugar da lane arma o arraste,
                  // menos nos controles interativos (renomear, botoes, combo)
                  if (!editMode) return
                  const target = e.target as HTMLElement
                  if (target.closest('input, button, .kb__gearpop, .ob-select, .kb__laneficha')) return
                  setArmedLane(lane.key)
                }}
                onMouseUp={() => setArmedLane(null)}
                onDragOver={(e) => {
                  if (draggingEtapa && editMode) {
                    e.preventDefault()
                    setEtapaDropLane(lane.key)
                    // fundo da lane (fora das fichas): insere no final
                    setEtapaDropIndex(laneSteps.length)
                  } else if (draggingCard) {
                    e.preventDefault()
                    setCardDropLane(lane.key)
                    // fundo da lane (fora dos cartoes): entra depois do ultimo
                    setCardDropIndex(cards.length)
                  } else if (draggingLane && editMode) {
                    e.preventDefault()
                    setLaneDropIndex(index)
                  }
                }}
                onDragLeave={(e) => {
                  // cruzar para um FILHO da lane tambem dispara dragleave — so
                  // limpa quando sai da lane de verdade (senao o marcador pisca
                  // e o drop falha)
                  if (e.currentTarget.contains(e.relatedTarget as Node)) return
                  setEtapaDropLane((c) => (c === lane.key ? null : c))
                  setEtapaDropIndex((i) => (etapaDropLane === lane.key ? null : i))
                  setCardDropLane((c) => {
                    if (c !== lane.key) return c
                    setCardDropIndex(null)
                    return null
                  })
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  // ficha de etapa solta na lane: entra NA POSICAO marcada
                  // (mesma lane = reordenar; outra lane = mudar de lane)
                  if (draggingEtapa && editMode) {
                    const { stepId, deKey } = draggingEtapa
                    // uma lane so aceita UMA etapa de cada fluxo — etapas do
                    // MESMO fluxo nao dividem lane (pedido 2026-08-25)
                    const fluxoDaEtapa = etapasDoQuadro.find((s) => s.flowStepId === stepId)?.flowId
                    const laneAlvo = draft.find((l) => l.key === lane.key)
                    const jaTemDoFluxo =
                      fluxoDaEtapa !== undefined &&
                      (laneAlvo?.flowStepIds ?? []).some(
                        (outra) => outra !== stepId && etapasDoQuadro.find((s) => s.flowStepId === outra)?.flowId === fluxoDaEtapa,
                      )
                    if (jaTemDoFluxo) {
                      toast.push({
                        tone: 'danger',
                        message: 'Esta lane já tem uma etapa deste fluxo — uma lane só pode ter uma etapa de cada fluxo.',
                      })
                      setDraggingEtapa(null)
                      setEtapaDropLane(null)
                      setEtapaDropIndex(null)
                      return
                    }
                    const indice = etapaDropIndex
                    setDraft((atual) =>
                      atual.map((l) => {
                        let ids = l.flowStepIds
                        const posAntiga = l.key === deKey ? ids.indexOf(stepId) : -1
                        if (l.key === deKey) ids = ids.filter((x) => x !== stepId)
                        if (l.key === lane.key && !ids.includes(stepId)) {
                          let alvoIdx = indice ?? ids.length
                          // removeu antes do ponto de insercao na MESMA lane
                          if (posAntiga !== -1 && posAntiga < alvoIdx) alvoIdx -= 1
                          alvoIdx = Math.max(0, Math.min(ids.length, alvoIdx))
                          ids = [...ids.slice(0, alvoIdx), stepId, ...ids.slice(alvoIdx)]
                        }
                        return ids === l.flowStepIds ? l : { ...l, flowStepIds: ids }
                      }),
                    )
                    setDraggingEtapa(null)
                    setEtapaDropLane(null)
                    setEtapaDropIndex(null)
                    return
                  }
                  const dropIndex = cardDropLane === lane.key ? cardDropIndex : null
                  setCardDropLane(null)
                  setCardDropIndex(null)
                  if (draggingCard && !editMode && lane.id) {
                    move.mutate({
                      workItemId: draggingCard,
                      targetColumnId: lane.id,
                      beforeWorkItemId: dropIndex === null ? null : (cards[dropIndex]?.id ?? null),
                    })
                    setDraggingCard(null)
                  } else if (draggingLane && editMode) {
                    reorderLane(draggingLane, index)
                    setDraggingLane(null)
                    setLaneDropIndex(null)
                  }
                }}
              >
                {/* header da lane */}
                <div className="kb__lanehead">
                  {editMode ? (
                    <>
                      <i className="fas fa-grip-vertical kb__grip" title="Arraste para mover a lane" aria-hidden />
                      <span className="kb__lanedot" style={{ background: stepColor }} />
                      <input
                        className="kb__laneinput"
                        value={lane.name}
                        placeholder="Nome da lane"
                        title="Clique para renomear"
                        onChange={(e) => patchLane(lane.key, { name: e.target.value })}
                      />
                      <button
                        type="button"
                        className="kb__trashbtn"
                        aria-label="Excluir lane"
                        title="Excluir lane"
                        onClick={() =>
                          confirm.ask(`A lane "${lane.name}" será removida do quadro (os itens continuam existindo).`, () =>
                            setDraft((current) => current.filter((l) => l.key !== lane.key)),
                          )
                        }
                      >
                        <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="kb__lanename">{lane.name}</span>
                      <span style={{ flex: 1 }} />
                      {/* contagem so quando ha cartoes, sem circulo, no canto */}
                      {cards.length > 0 ? <span className="kb__lanecount">{cards.length}</span> : null}
                    </>
                  )}
                </div>

                {/* etapas da lane, sob o titulo (modo edicao): MESMA ficha do
                    painel de configuracao, empilhadas — a lane cresce conforme
                    precisar (pedido do usuario 2026-08-24) */}
                {editMode ? (
                  <div className="kb__laneetapas">
                    <span className="kb__lanesec">Etapas</span>
                    {laneSteps.length > 0 ? (
                      <>
                        {laneSteps.map((s, idx) => (
                          <div key={s.flowStepId}>
                            {/* marcador de ONDE a ficha entra (pedido do usuario) */}
                            {etapaDropLane === lane.key && etapaDropIndex === idx ? (
                              <div className="kb__lanemarketapa" aria-hidden />
                            ) : null}
                            <div
                              className="kbcfg__ficha kb__laneficha"
                              title={`${s.flowName} · ${s.stepName} — arraste para outra lane ou clique para configurar`}
                              draggable
                              onDragStart={(e) => {
                                // ficha viaja entre lanes direto no quadro (pedido
                                // do usuario); nao arma o arraste da lane inteira
                                e.stopPropagation()
                                e.dataTransfer.effectAllowed = 'move'
                                setDraggingEtapa({ stepId: s.flowStepId, deKey: lane.key })
                              }}
                              onDragOver={(e) => {
                                if (!draggingEtapa) return
                                e.preventDefault()
                                e.stopPropagation()
                                const r = e.currentTarget.getBoundingClientRect()
                                setEtapaDropLane(lane.key)
                                setEtapaDropIndex(idx + (e.clientY > r.top + r.height / 2 ? 1 : 0))
                              }}
                              onDragEnd={() => {
                                setDraggingEtapa(null)
                                setEtapaDropLane(null)
                                setEtapaDropIndex(null)
                              }}
                            >
                              <i className="fas fa-grip-vertical kbcfg__ficha-grip" aria-hidden />
                              <span
                                className="kbcfg__ficha-dot"
                                style={{ background: s.stepColor ?? 'var(--ob-primary)' }}
                                aria-hidden
                              />
                              <span className="kbcfg__ficha-nome">{s.stepName}</span>
                            </div>
                          </div>
                        ))}
                        {etapaDropLane === lane.key && etapaDropIndex === laneSteps.length ? (
                          <div className="kb__lanemarketapa" aria-hidden />
                        ) : null}
                      </>
                    ) : (
                      <>
                        {etapaDropLane === lane.key ? <div className="kb__lanemarketapa" aria-hidden /> : null}
                        <p className="kb__laneetapas-vazio">
                          {temFluxos ? 'Arraste etapas do painel ao lado.' : 'O quadro não tem fluxos vinculados.'}
                        </p>
                      </>
                    )}
                  </div>
                ) : null}

                {/* STATUS AO ENTRAR NA LANE (2026-09-01): com a etapa da lane
                    ja definindo o status, a lane so MOSTRA (travado); sem
                    status na etapa — ou sem etapa — escolhe aqui, e o card que
                    entrar assume o status. */}
                {editMode ? (
                  <div className="kb__laneetapas">
                    <span className="kb__lanesec">Status ao entrar</span>
                    {(() => {
                      const daEtapa =
                        laneSteps
                          .map(
                            (ls) =>
                              (config.data?.flowSteps ?? []).find((f) => f.flowStepId === ls.flowStepId)
                                ?.onEnterStatusId ?? null,
                          )
                          .find((v): v is string => v !== null) ?? null
                      if (daEtapa) {
                        const st = (bootstrap.data?.workItemStatuses ?? []).find((x) => x.id === daEtapa)
                        return (
                          <div
                            className="kb__lanestatus--travado"
                            title="Definido na configuração da etapa — para mudar, edite a etapa no desenho do fluxo"
                          >
                            <span
                              aria-hidden
                              style={{
                                width: 9,
                                height: 9,
                                borderRadius: 9999,
                                background: st ? TONE_COLOR[st.tone] : 'var(--ob-primary)',
                                flex: 'none',
                              }}
                            />
                            <span>{statusLabel(daEtapa) ?? 'Status da etapa'}</span>
                            <i className="fas fa-lock" aria-hidden />
                          </div>
                        )
                      }
                      return (
                        <Select
                          value={lane.statusId ?? ''}
                          placeholder="Não muda o status"
                          options={[
                            { value: '', label: 'Não muda o status' },
                            ...(bootstrap.data?.workItemStatuses ?? [])
                              .filter((st) => st.isActive)
                              .map((st) => ({ value: st.id, label: statusLabel(st.id) ?? st.code })),
                          ]}
                          onChange={(v) => patchLane(lane.key, { statusId: v || null })}
                        />
                      )
                    })()}
                  </div>
                ) : null}

                {/* cartoes — no modo de edicao entra um cartao de EXEMPLO com
                    dados ficticios mostrando como as informacoes ficam
                    dispostas (pedido do usuario); nunca o conteudo real */}
                {editMode ? (
                  <div className="kb__cards">
                    <span className="kb__lanesec" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Modelo do card
                      {/* icone no CANTO DIREITO; o menu (um so, do quadro)
                          abre num portal ancorado neste botao — ver
                          camposAnchor */}
                      <span className="kb__ownerwrap" style={{ textTransform: 'none', letterSpacing: 'normal', marginLeft: 'auto' }}>
                        <button
                          type="button"
                          className="kb__camposbtn"
                          title="Escolher os campos mostrados no card"
                          aria-label="Escolher os campos mostrados no card"
                          aria-haspopup="dialog"
                          onClick={(e) => {
                            const btn = e.currentTarget
                            // o mesmo botao de novo fecha; outra lane troca a ancora
                            if (camposAnchor === btn) {
                              setCamposAnchor(null)
                              return
                            }
                            camposDraftRef.current = camposDoCard
                            setCamposDraft(camposDoCard)
                            setCamposAnchor(btn)
                          }}
                        >
                          <i className="fas fa-sliders-h" aria-hidden />
                        </button>
                      </span>
                    </span>
                    <article className="kb__card kb__cardexemplo" aria-hidden>
                      {temCampo('type') || temCampo('code') ? (
                        <div className="kb__cardtop">
                          {temCampo('type') ? (
                            <span className="kb__typechip">
                              <i className="fas fa-tasks kb__typeicon" aria-hidden style={{ fontSize: 8.5 }} />
                              Tarefa
                            </span>
                          ) : null}
                          <span style={{ flex: 1 }} />
                          {temCampo('code') ? <span className="kb__cardcode">WI-0001</span> : null}
                        </div>
                      ) : null}
                      <p className="kb__cardtitle">Exemplo de Item de Trabalho</p>
                      {temCampo('description') ? (
                        <p className="kb__carddesc">Trecho da descrição do item, em até duas linhas, para dar contexto.</p>
                      ) : null}
                      <div className="kb__badges">
                        {temCampo('due') ? (
                          <span className="kb__badge" title="Prazo">
                            <i className="far fa-clock" aria-hidden />
                            30 ago
                          </span>
                        ) : null}
                        {temCampo('start') ? (
                          <span className="kb__badge" title="Início">
                            <i className="fas fa-play" aria-hidden style={{ fontSize: 8 }} />
                            25 ago
                          </span>
                        ) : null}
                        {temCampo('completed') ? (
                          <span className="kb__badge kb__badge--done" title="Concluído em">
                            <i className="fas fa-flag-checkered" aria-hidden />
                            31 ago
                          </span>
                        ) : null}
                        {temCampo('area') ? (
                          <span className="kb__badge" title="Área">
                            <i className="fas fa-sitemap" aria-hidden />
                            Operações
                          </span>
                        ) : null}
                        {temCampo('produto') ? (
                          <span className="kb__badge" title="Produto/Serviço">
                            <i className="fas fa-cube" aria-hidden />
                            Portal Web
                          </span>
                        ) : null}
                        {temCampo('parent') ? (
                          <span className="kb__badge" title="É subitem">
                            <i className="fas fa-level-up-alt" aria-hidden />
                          </span>
                        ) : null}
                        {temCampo('recurrence') ? (
                          <span className="kb__badge" title="Recorrência">
                            <i className="fas fa-sync-alt" aria-hidden />
                          </span>
                        ) : null}
                        {temCampo('reminder') ? (
                          <span className="kb__badge" title="Lembrete">
                            <i className="far fa-bell" aria-hidden />
                          </span>
                        ) : null}
                        {temCampo('expectedResult') ? (
                          <span className="kb__badge" title="Tem resultado esperado">
                            <i className="fas fa-bullseye" aria-hidden />
                          </span>
                        ) : null}
                        {temCampo('criteria') ? (
                          <span className="kb__badge" title="Critérios de aceite">
                            <i className="far fa-check-square" aria-hidden />
                            2/3
                          </span>
                        ) : null}
                        {temCampo('children') ? (
                          <span className="kb__badge" title="Itens filhos">
                            <i className="fas fa-stream" aria-hidden />
                            1/2
                          </span>
                        ) : null}
                        {temCampo('company') ? (
                          <span className="kb__badge" title="Empresa">
                            <i className="fas fa-building" aria-hidden />
                            ACME
                          </span>
                        ) : null}
                        <span style={{ flex: 1 }} />
                        {temCampo('etapa') ? (
                          <span className="kb__priochip" style={{ color: 'var(--ob-primary)', borderColor: 'var(--ob-primary)' }} title="Etapa do fluxo">
                            Análise Técnica
                          </span>
                        ) : null}
                        {temCampo('status') ? (
                          <span className="kb__priochip" style={{ color: 'var(--ob-primary)', borderColor: 'var(--ob-primary)' }}>
                            Em andamento
                          </span>
                        ) : null}
                        {temCampo('priority') ? (
                          <span
                            className="kb__priochip"
                            style={{ color: 'var(--ob-warning-deep)', borderColor: 'var(--ob-warning)' }}
                          >
                            Média
                          </span>
                        ) : null}
                        {temCampo('assignee') ? <span className="wi-avatar" title="Responsável">MS</span> : null}
                      </div>
                    </article>
                  </div>
                ) : null}
                {/* total de itens: ULTIMA informacao da lane (modo de edicao) */}
                {editMode ? (
                  <div className="kb__lanewip">
                    <span className="kb__lanesec" style={{ width: '100%' }}>Total de itens</span>
                    <button
                      type="button"
                      className="kb__wipbtn"
                      aria-label="Diminuir o total de itens"
                      onClick={() => patchLane(lane.key, { wipLimit: Math.max(1, (lane.wipLimit ?? 6) - 1) })}
                    >
                      <i className="fas fa-minus" aria-hidden style={{ fontSize: 8 }} />
                    </button>
                    <span className="kb__wipvalue" style={{ minWidth: 62 }}>
                      {lane.wipLimit === null ? 'Sem limite' : lane.wipLimit}
                    </span>
                    <button
                      type="button"
                      className="kb__wipbtn"
                      aria-label="Aumentar o total de itens"
                      onClick={() => patchLane(lane.key, { wipLimit: (lane.wipLimit ?? 0) + 1 })}
                    >
                      <i className="fas fa-plus" aria-hidden style={{ fontSize: 8 }} />
                    </button>
                    {lane.wipLimit !== null ? (
                      <button
                        type="button"
                        className="kb__wipclear"
                        title="Remover o limite de itens"
                        onClick={() => patchLane(lane.key, { wipLimit: null })}
                      >
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {editMode ? null : (
                <div
                  className="kb__cards"
                  data-lane-cards={lane.key}
                  // altura: a do USUARIO (alca) > config do quadro > 6 cards
                  style={
                    laneAlturas[lane.key]
                      ? { height: laneAlturas[lane.key], maxHeight: 'none' }
                      : itensVisiveis > 0
                        ? { maxHeight: `calc(${itensVisiveis} * var(--kb-card-slot))` }
                        : { maxHeight: 'calc(6 * var(--kb-card-slot))' }
                  }
                >
                  {cards.map((card, cardIndex) => (
                    <article
                      key={card.id}
                      className={`kb__card${buscaAtiva ? (casaBusca(card) ? ' kb__card--achado' : ' kb__card--apagado') : ''}${selectedCard === card.id ? ' kb__card--selected' : ''}${
                        cardDropLane === lane.key && cardDropIndex === cardIndex ? ' kb__card--markabove' : ''
                      }${
                        cardDropLane === lane.key && cardIndex === cards.length - 1 && cardDropIndex === cards.length
                          ? ' kb__card--markbelow'
                          : ''
                      }`}
                      draggable={!editMode && !souLeitor}
                      title="Um clique seleciona · dois cliques abrem o detalhe"
                      onDragStart={() => setDraggingCard(card.id)}
                      onDragEnd={() => {
                        setDraggingCard(null)
                        setCardDropLane(null)
                        setCardDropIndex(null)
                      }}
                      onDragOver={(e) => {
                        if (!draggingCard || editMode) return
                        e.preventDefault()
                        e.stopPropagation()
                        // metade de cima = entra ANTES deste cartao; de baixo = DEPOIS
                        const rect = e.currentTarget.getBoundingClientRect()
                        setCardDropLane(lane.key)
                        setCardDropIndex(e.clientY < rect.top + rect.height / 2 ? cardIndex : cardIndex + 1)
                      }}
                      // 1 clique MARCA (borda azul) e a marca so troca quando
                      // OUTRO item do quadro for clicado (pedido 2026-08-25)
                      onClick={() => setSelectedCard(card.id)}
                      // abre o detalhe DENTRO de Quadros: o explorer continua
                      // no quadro e o voltar retorna com o cartao selecionado
                      onDoubleClick={() => navigate(`/kanbans/${id}/items/${card.id}`)}
                    >
                      {/* campos do card conforme a configuracao do quadro */}
                      <div className="kb__cardtop">
                        {temCampo('type') ? (
                          <span className="kb__typechip">
                            <CatalogIcon icon={card.type.icon} fallback="fas fa-tasks" size={8.5} className="kb__typeicon" />
                            {labelOf(card.type.nameKey)}
                          </span>
                        ) : null}
                        <span style={{ flex: 1 }} />
                        {temCampo('code') ? <span className="kb__cardcode">{card.code}</span> : null}
                        {/* selos de plugin: sem selo não renderiza nada, nem
                            espaço — o card fica igual em quadro sem plugin */}
                        <SelosDoCard selos={selosDePlugin.get(card.id)} />
                        {/* VER FLUXO no topo do card, depois do codigo e antes
                            do expandir (pedido 2026-08-25) */}
                        {card.flowStepId ? (
                          <button
                            type="button"
                            className="kb__cardexpand"
                            title="Ver o fluxo com a etapa em que o item está"
                            onClick={(e) => {
                              e.stopPropagation()
                              // abre o MESMO visualizador do painel Fluxo/Etapas
                              // (zoom/pan), com a etapa do item destacada.
                              // O fluxo real vem do card (pode ser de OUTRO fluxo
                              // que o do quadro, quando esta numa etapa comum);
                              // fallback no config do quadro por retrocompat.
                              const flowId =
                                card.flowId ??
                                (config.data?.flowSteps ?? []).find((s) => s.flowStepId === card.flowStepId)?.flowId
                              if (flowId) {
                                navigate(
                                  `/admin/flows?desenhar=${flowId}&destacar=${card.flowStepId}&codigo=${encodeURIComponent(card.code)}&voltar=${encodeURIComponent(`/kanbans/${id}`)}`,
                                )
                              }
                            }}
                          >
                            <i className="fas fa-route" aria-hidden />
                            <span className="ob-sr-only">Ver fluxo</span>
                          </button>
                        ) : null}
                        {/* expandir para a TELA do item — canto superior
                            direito, a direita do codigo (pedido 2026-08-25) */}
                        <button
                          type="button"
                          className="kb__cardexpand"
                          title="Abrir o item em tela completa"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/kanbans/${id}/items/${card.id}`)
                          }}
                        >
                          <i className="fas fa-external-link-alt" aria-hidden />
                        </button>
                      </div>
                      <p className="kb__cardtitle">{card.title}</p>

                      {/* descricao como CAMPO de texto: trecho em 2 linhas —
                          da para um card ser so Titulo + Descricao */}
                      {temCampo('description') && card.descriptionExcerpt?.trim() ? (
                        <p className="kb__carddesc">{card.descriptionExcerpt.replace(/<[^>]+>/g, ' ').trim()}</p>
                      ) : null}

                      {/* Selos no padrao do Trello: indicador curto, o conteudo
                          fica no detalhe. So aparece o que o item realmente
                          tem — cartao sem nada disso nao ganha a faixa. */}
                      <div className="kb__badges">
                        {temCampo('due') && card.dueAt ? (
                          <span
                            className={`kb__badge${new Date(card.dueAt) < new Date() ? ' kb__badge--late' : ''}`}
                            title={`Prazo: ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(card.dueAt))}`}
                          >
                            <i className="far fa-clock" aria-hidden />
                            {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' })
                              .format(new Date(card.dueAt))
                              .replace('.', '')}
                          </span>
                        ) : null}

                        {temCampo('start') && card.startAt ? (
                          <span className="kb__badge" title={`Início: ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(card.startAt))}`}>
                            <i className="fas fa-play" aria-hidden style={{ fontSize: 8 }} />
                            {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(card.startAt)).replace('.', '')}
                          </span>
                        ) : null}

                        {temCampo('completed') && card.completedAt ? (
                          <span className="kb__badge kb__badge--done" title={`Concluído em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(card.completedAt))}`}>
                            <i className="fas fa-flag-checkered" aria-hidden />
                            {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(card.completedAt)).replace('.', '')}
                          </span>
                        ) : null}

                        {temCampo('area') && card.companyAreaId ? (
                          <span className="kb__badge" title="Área">
                            <i className="fas fa-sitemap" aria-hidden />
                            {labelOf((bootstrap.data?.areas ?? []).find((a) => a.id === card.companyAreaId)?.nameTranslationKey ?? '')}
                          </span>
                        ) : null}

                        {temCampo('produto') && card.portfolioNodeId ? (
                          <span className="kb__badge" title="Produto/Serviço">
                            <i className="fas fa-cube" aria-hidden />
                            {((portfolio.data?.nodes ?? []) as { id: string; name: string }[]).find((n) => n.id === card.portfolioNodeId)?.name ?? 'Produto'}
                          </span>
                        ) : null}

                        {temCampo('parent') && card.parentWorkItemId ? (
                          <span className="kb__badge" title="É subitem de outro Item de Trabalho">
                            <i className="fas fa-level-up-alt" aria-hidden />
                            <span className="ob-sr-only">Subitem</span>
                          </span>
                        ) : null}

                        {temCampo('recurrence') && card.recurrence && card.recurrence !== 'never' ? (
                          <span className="kb__badge" title={`Recorrência: ${card.recurrence}`}>
                            <i className="fas fa-sync-alt" aria-hidden />
                            <span className="ob-sr-only">Recorrente</span>
                          </span>
                        ) : null}

                        {temCampo('reminder') && card.reminder && card.reminder !== 'none' ? (
                          <span className="kb__badge" title="Tem lembrete">
                            <i className="far fa-bell" aria-hidden />
                            <span className="ob-sr-only">Lembrete</span>
                          </span>
                        ) : null}

                        {temCampo('expectedResult') && card.hasExpectedResult ? (
                          <span className="kb__badge" title="Tem resultado esperado">
                            <i className="fas fa-bullseye" aria-hidden />
                            <span className="ob-sr-only">Tem resultado esperado</span>
                          </span>
                        ) : null}

                        {temCampo('criteria') && card.criteria ? (
                          <span
                            className={`kb__badge${card.criteria.done === card.criteria.total ? ' kb__badge--done' : ''}`}
                            title={`Critérios de aceite: ${card.criteria.done} de ${card.criteria.total} concluídos`}
                          >
                            <i className="far fa-check-square" aria-hidden />
                            {card.criteria.done}/{card.criteria.total}
                          </span>
                        ) : null}

                        {temCampo('children') && card.children ? (
                          <span
                            className={`kb__badge${card.children.done === card.children.total ? ' kb__badge--done' : ''}`}
                            title={`Filhos: ${card.children.done} de ${card.children.total} concluídos`}
                          >
                            <i className="fas fa-stream" aria-hidden />
                            {card.children.done}/{card.children.total}
                          </span>
                        ) : null}


                        {temCampo('company') && card.company ? (
                          <span className="kb__badge" title={`Empresa: ${card.company.name}`}>
                            <i className="fas fa-building" aria-hidden />
                            {card.company.name}
                          </span>
                        ) : null}

                        <span style={{ flex: 1 }} />

                        {temCampo('etapa') && card.flowStepId ? (
                          (() => {
                            const etapa = (config.data?.flowSteps ?? []).find((s) => s.flowStepId === card.flowStepId)
                            const cor = etapa?.stepColor ?? 'var(--ob-primary)'
                            return (
                              <span className="kb__priochip" style={{ color: cor, borderColor: cor }} title={etapa ? `Etapa: ${etapa.flowName} · ${etapa.stepName}` : 'Etapa do fluxo'}>
                                {etapa?.stepName ?? 'Etapa'}
                              </span>
                            )
                          })()
                        ) : null}

                        {temCampo('status') ? (
                          <span
                            className="kb__priochip"
                            style={{ color: TONE_COLOR[card.status.tone], borderColor: TONE_COLOR[card.status.tone] }}
                            title="Status do item"
                          >
                            {statusLabel(card.statusId)}
                          </span>
                        ) : null}

                        {temCampo('priority') ? (
                          <span className="kb__priochip" style={{ color: TONE_COLOR[card.priority.tone], borderColor: TONE_COLOR[card.priority.tone] }}>
                            {labelOf(card.priority.nameKey)}
                          </span>
                        ) : null}

                        {temCampo('assignee') && card.assignee?.name ? (
                          <span className="wi-avatar" title={card.assignee.name}>
                            {initialsOf(card.assignee.name)}
                          </span>
                        ) : null}

                        {temCampo('members') && (card.members?.length ?? 0) > 0 ? (
                          <span className="kb__cardmembros" title={(card.members ?? []).map((m) => m.name).join(', ')}>
                            {(card.members ?? []).slice(0, 4).map((m) => (
                              <span key={m.id} className="wi-avatar kb__cardmembro" title={m.name}>
                                {initialsOf(m.name)}
                              </span>
                            ))}
                            {(card.members?.length ?? 0) > 4 ? (
                              <span className="wi-avatar kb__cardmembro kb__cardmembro--mais">
                                +{(card.members?.length ?? 0) - 4}
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </div>
                    </article>
                  ))}
                  {/* lane VAZIA sendo alvo do arraste: mostra a linha azul de
                      insercao entre o titulo e o "Adicionar item" (as lanes com
                      cards usam o marcador ::before/::after do proprio cartao) */}
                  {draggingCard && cardDropLane === lane.key && cards.length === 0 ? (
                    <div className="kb__droplinha" aria-hidden />
                  ) : null}
                </div>
                )}
                {/* alca de ALTURA da lane (2026-08-31): arrasta para baixo/cima
                    e a medida fica lembrada para ESTE usuario; dois cliques
                    voltam ao padrao de 6 cards. O ajuste vai direto no DOM
                    durante o arraste e so vira estado no soltar. */}
                {!editMode && cards.length > 0 ? (
                  <span
                    className="kb__laneresize"
                    role="separator"
                    aria-label="Arraste para ajustar a altura da lane"
                    title="Arraste para ajustar a altura · dois cliques voltam ao padrão"
                    onDoubleClick={() => salvarLaneAltura(lane.key, null)}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      try {
                        e.currentTarget.setPointerCapture(e.pointerId)
                      } catch {
                        /* evento sintetico sem pointer ativo */
                      }
                      const alvo = e.currentTarget
                        .closest('.kb__lane')
                        ?.querySelector<HTMLElement>(`[data-lane-cards="${lane.key}"]`)
                      const alt = alvo?.getBoundingClientRect().height ?? 0
                      laneResize.current = { laneKey: lane.key, y: e.clientY, alt, atual: alt, rolado: 0 }
                      laneResizeUltimoY.current = e.clientY
                      // AUTO-SCROLL (2026-09-01): ponteiro parado no pe do
                      // quadro rola sozinho, e a altura soma o tanto rolado —
                      // a borda da lane continua acompanhando o mouse
                      laneResizeTimer.current = window.setInterval(() => {
                        const r = laneResize.current
                        const rolagem = lanesRef.current
                        if (!r || !rolagem) return
                        const borda = rolagem.getBoundingClientRect()
                        if (laneResizeUltimoY.current > borda.bottom - 48) {
                          const antes = rolagem.scrollTop
                          rolagem.scrollTop += 16
                          r.rolado += rolagem.scrollTop - antes
                        }
                        const nova = Math.max(64, Math.round(r.alt + (laneResizeUltimoY.current - r.y) + r.rolado))
                        if (nova === r.atual) return
                        r.atual = nova
                        for (const el of document.querySelectorAll<HTMLElement>(`[data-lane-cards="${r.laneKey}"]`)) {
                          el.style.height = `${nova}px`
                          el.style.maxHeight = 'none'
                        }
                      }, 16)
                    }}
                    onPointerMove={(e) => {
                      const r = laneResize.current
                      if (!r || r.laneKey !== lane.key) return
                      laneResizeUltimoY.current = e.clientY
                      const nova = Math.max(64, Math.round(r.alt + (e.clientY - r.y) + r.rolado))
                      r.atual = nova
                      // todas as instancias da lane (agrupado repete a lane por banda)
                      for (const el of document.querySelectorAll<HTMLElement>(`[data-lane-cards="${lane.key}"]`)) {
                        el.style.height = `${nova}px`
                        el.style.maxHeight = 'none'
                      }
                    }}
                    onPointerUp={(e) => {
                      const r = laneResize.current
                      if (!r) return
                      try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                      } catch {
                        /* pointer ja solto */
                      }
                      if (laneResizeTimer.current !== null) {
                        window.clearInterval(laneResizeTimer.current)
                        laneResizeTimer.current = null
                      }
                      laneResize.current = null
                      if (r.atual !== r.alt) salvarLaneAltura(r.laneKey, r.atual)
                    }}
                  >
                    <i className="fas fa-grip-lines" aria-hidden />
                  </span>
                ) : null}
                {/*
                  Adicionar item FIXO no rodapé da lane (fora do scroll dos
                  cards). Abre o CARTÃO DE INCLUSÃO RÁPIDA — só o título,
                  dentro da lane (SPEC-20261007-1633-inclusao-rapida-na-lane); o ícone dele leva à
                  inclusão completa, em branco ou por template.
                */}
                {!editMode && !souLeitor && rapido?.laneKey === lane.key ? (
                  <div className="kb__rapido">
                    <textarea
                      className="kb__rapido-titulo"
                      autoFocus
                      rows={2}
                      maxLength={300}
                      placeholder="Título do item"
                      aria-label="Título do novo Item de Trabalho"
                      value={rapido.titulo}
                      disabled={criarRapido.isPending}
                      onChange={(e) => setRapido({ laneKey: lane.key, titulo: e.target.value })}
                      onKeyDown={(e) => {
                        // Enter cria; Shift+Enter quebra a linha; Esc fecha
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          salvarRapido()
                        }
                        if (e.key === 'Escape') {
                          e.stopPropagation()
                          setRapido(null)
                        }
                      }}
                    />
                    <div className="kb__rapido-acoes">
                      <Button
                        size="sm"
                        variant="primary"
                        loading={criarRapido.isPending}
                        disabled={rapido.titulo.trim() === ''}
                        onClick={salvarRapido}
                      >
                        Adicionar
                      </Button>
                      <button
                        type="button"
                        className="kb__rapido-btn"
                        aria-label="Cancelar"
                        title="Cancelar (Esc)"
                        onClick={() => setRapido(null)}
                      >
                        <i className="fas fa-times" aria-hidden />
                      </button>
                      <span style={{ flex: 1 }} />
                      <button
                        type="button"
                        className="kb__rapido-btn"
                        aria-label="Inclusão completa: em branco ou a partir de um template"
                        title="Inclusão completa: em branco ou a partir de um template"
                        disabled={criarRapido.isPending}
                        onClick={() => abrirInclusaoCompleta(lane.key)}
                      >
                        <i className="fas fa-expand-alt" aria-hidden />
                      </button>
                    </div>
                  </div>
                ) : !editMode && !souLeitor ? (
                  <button
                    type="button"
                    className="kb__quickadd kb__quickadd--footer"
                    onClick={() => setRapido({ laneKey: lane.key, titulo: '' })}
                  >
                    <i className="fas fa-plus" aria-hidden style={{ fontSize: 10 }} />
                    Adicionar item
                  </button>
                ) : null}
              </section>
            </div>
          )
          }
          if (!agrupado) return lanes.map((lane, index) => renderLane(lane, index))
          return bandas.map((banda) => {
            const fechada = bandasFechadas.has(banda.key)
            // Na banda "por tipo" a cabeca E um item: clicar no NOME abre o
            // item (volta pra esta tela ao fechar, com o agrupamento igual, que
            // e por usuario). A setinha sempre recolhe/expande (2026-08-27).
            const bandaEhItem = agruparPor.startsWith('type:') && banda.key !== '__sem__'
            /*
             * Os subgrupos só são calculados com a banda ABERTA: fechada, nada
             * deles aparece, e partir os cartões à toa custa caro em quadro
             * grande.
             */
            const subsDaBanda = fechada ? [] : subBandasDe(banda, subEfetivo, banda.key)
            const chavesDosSubs = subsDaBanda.map((s) => `${banda.key}::${s.key}`)
            const subsTodosFechados =
              chavesDosSubs.length > 0 && chavesDosSubs.every((k) => bandasFechadas.has(k))
            return (
              <div className="kb__band" key={banda.key}>
                <div className="kb__bandhead">
                  <button
                    type="button"
                    className="kb__bandtoggle"
                    onClick={() => alternarBanda(banda.key)}
                    aria-expanded={!fechada}
                    aria-label={fechada ? 'Expandir' : 'Recolher'}
                    title={fechada ? 'Expandir' : 'Recolher'}
                  >
                    <i className={`fas fa-chevron-${fechada ? 'right' : 'down'}`} aria-hidden />
                  </button>
                  {banda.icon ? <i className={banda.icon} aria-hidden style={{ opacity: 0.7 }} /> : null}
                  {bandaEhItem ? (
                    <button
                      type="button"
                      className="kb__bandnome kb__bandnome--link"
                      onClick={() => navigate(`/kanbans/${id}/items/${banda.key}`)}
                      title="Abrir o item para editar"
                    >
                      {banda.label}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="kb__bandnome"
                      onClick={() => alternarBanda(banda.key)}
                    >
                      {banda.label}
                    </button>
                  )}
                  <span className="kb__bandcount">{banda.count}</span>
                  {chavesDosSubs.length > 0 ? (
                    <button
                      type="button"
                      className="kb__bandfilhos"
                      onClick={() => alternarFilhos(chavesDosSubs)}
                      aria-expanded={!subsTodosFechados}
                      aria-label={subsTodosFechados ? 'Expandir os subgrupos' : 'Recolher os subgrupos'}
                      title={
                        subsTodosFechados
                          ? `Expandir os ${chavesDosSubs.length} subgrupos`
                          : `Recolher os ${chavesDosSubs.length} subgrupos`
                      }
                    >
                      <i
                        className={`fas fa-angle-double-${subsTodosFechados ? 'down' : 'up'}`}
                        aria-hidden
                      />
                    </button>
                  ) : null}
                </div>
                {fechada ? null : (
                  (() => {
                    // dentro da banda do item, um nível por CHECKLIST de origem
                    const subs = subsDaBanda
                    if (subs.length === 0) {
                      return (
                        <div className="kb__bandlanes">
                          {lanes.map((lane, index) => renderLane(lane, index, banda.filter))}
                        </div>
                      )
                    }
                    return subs.map((sub) => {
                      const chaveSub = `${banda.key}::${sub.key}`
                      const subFechada = bandasFechadas.has(chaveSub)
                      // mesma economia do nível de cima: só calcula aberto
                      const netosDoSub = subFechada ? [] : subBandasDe(sub, sub2Efetivo, banda.key)
                      const chavesDosNetos = netosDoSub.map((n) => `${chaveSub}::${n.key}`)
                      const netosTodosFechados =
                        chavesDosNetos.length > 0 && chavesDosNetos.every((k) => bandasFechadas.has(k))
                      return (
                        <div className="kb__subband" key={chaveSub}>
                          <div className="kb__subbandhead">
                            <button
                              type="button"
                              className="kb__bandtoggle"
                              onClick={() => alternarBanda(chaveSub)}
                              aria-expanded={!subFechada}
                              aria-label={subFechada ? 'Expandir' : 'Recolher'}
                              title={subFechada ? 'Expandir' : 'Recolher'}
                            >
                              <i className={`fas fa-chevron-${subFechada ? 'right' : 'down'}`} aria-hidden />
                            </button>
                            {sub.icon ? <i className={sub.icon} aria-hidden style={{ opacity: 0.7 }} /> : null}
                            {/* código do item DONO quando o checklist é de um
                                descendente: sem ele, "Bugs" e "Bugs" na mesma
                                banda não dizem de quem são */}
                            {sub.dono ? (
                              <button
                                type="button"
                                className="kb__bandcodigo"
                                title={`Abrir ${sub.dono.code}, o item dono deste checklist`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/kanbans/${id}/items/${sub.dono!.id}`)
                                }}
                              >
                                {sub.dono.code}
                              </button>
                            ) : null}
                            <button type="button" className="kb__subbandnome" onClick={() => alternarBanda(chaveSub)}>
                              {sub.dono ? `: ${sub.label}` : sub.label}
                            </button>
                            <span className="kb__bandcount">{sub.count}</span>
                            {chavesDosNetos.length > 0 ? (
                              <button
                                type="button"
                                className="kb__bandfilhos"
                                onClick={() => alternarFilhos(chavesDosNetos)}
                                aria-expanded={!netosTodosFechados}
                                aria-label={netosTodosFechados ? 'Expandir os subgrupos' : 'Recolher os subgrupos'}
                                title={
                                  netosTodosFechados
                                    ? `Expandir os ${chavesDosNetos.length} subgrupos`
                                    : `Recolher os ${chavesDosNetos.length} subgrupos`
                                }
                              >
                                <i
                                  className={`fas fa-angle-double-${netosTodosFechados ? 'down' : 'up'}`}
                                  aria-hidden
                                />
                              </button>
                            ) : null}
                          </div>
                          {subFechada
                            ? null
                            : (() => {
                                // TERCEIRO nível: separa o que está dentro da
                                // sub-faixa (2026-08-31)
                                const netos = netosDoSub
                                if (netos.length === 0) {
                                  return (
                                    <div className="kb__bandlanes">
                                      {lanes.map((lane, index) => renderLane(lane, index, sub.filter))}
                                    </div>
                                  )
                                }
                                return netos.map((neto) => {
                                  const chaveNeto = `${chaveSub}::${neto.key}`
                                  const netoFechado = bandasFechadas.has(chaveNeto)
                                  return (
                                    <div className="kb__subband kb__subband--n3" key={chaveNeto}>
                                      <div className="kb__subbandhead">
                                        <button
                                          type="button"
                                          className="kb__bandtoggle"
                                          onClick={() => alternarBanda(chaveNeto)}
                                          aria-expanded={!netoFechado}
                                          aria-label={netoFechado ? 'Expandir' : 'Recolher'}
                                          title={netoFechado ? 'Expandir' : 'Recolher'}
                                        >
                                          <i
                                            className={`fas fa-chevron-${netoFechado ? 'right' : 'down'}`}
                                            aria-hidden
                                          />
                                        </button>
                                        {neto.icon ? (
                                          <i className={neto.icon} aria-hidden style={{ opacity: 0.7 }} />
                                        ) : null}
                                        {/* mesma regra do nível de cima: em cadeia */}
                                        {neto.dono ? (
                                          <button
                                            type="button"
                                            className="kb__bandcodigo"
                                            title={`Abrir ${neto.dono.code}, o item dono deste checklist`}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              navigate(`/kanbans/${id}/items/${neto.dono!.id}`)
                                            }}
                                          >
                                            {neto.dono.code}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="kb__subbandnome"
                                          onClick={() => alternarBanda(chaveNeto)}
                                        >
                                          {neto.dono ? `: ${neto.label}` : neto.label}
                                        </button>
                                        <span className="kb__bandcount">{neto.count}</span>
                                      </div>
                                      {netoFechado ? null : (
                                        <div className="kb__bandlanes">
                                          {lanes.map((lane, index) => renderLane(lane, index, neto.filter))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })
                              })()}
                        </div>
                      )
                    })
                  })()
                )}
              </div>
            )
          })
        })()}

        {/* marcador final + adicionar lane */}
        {editMode ? (
          <>
            <span
              className={`kb__lanemark${laneDropIndex === lanes.length ? ' kb__lanemark--on' : ''}`}
              onDragOver={(e) => {
                if (!draggingLane) return
                e.preventDefault()
                setLaneDropIndex(lanes.length)
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (draggingLane) reorderLane(draggingLane, lanes.length)
                setDraggingLane(null)
                setLaneDropIndex(null)
              }}
            />
            <button
              type="button"
              className="kb__addlane"
              onClick={() =>
                setDraft((current) => [
                  ...current,
                  {
                    key: `new-${current.length}-${Math.random().toString(36).slice(2, 8)}`,
                    name: 'Nova lane',
                    statusId: null,
                    wipLimit: null,
                    // etapas sao opcionais: nasce sem nenhuma; escolhe na engrenagem
                    flowStepIds: [],
                  },
                ])
              }
            >
              <i className="fas fa-plus" aria-hidden style={{ fontSize: 11 }} />
              Adicionar lane
            </button>
          </>
        ) : souDonoQuadro && !agrupado ? (
          /* lista rápida, como no Trello (SPEC-20261027-1920-adicionar-lista-rapida-no-quadro):
             só o título; status, etapas e WIP seguem na engrenagem */
          <NovaListaRapida
            onCriar={async (nome) => {
              try {
                const r = await api.kanbans[':id'].lanes.rapida.$post({ param: { id }, json: { name: nome } })
                if (!r.ok) throw new Error()
                invalidate()
              } catch (erro) {
                toast.push({
                  tone: 'danger',
                  message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível criar a lista.',
                })
                throw erro
              }
            }}
          />
        ) : null}
      </div>

      {/* ------------------------- modal "Alterar tela de fundo" (Trello) --- */}
      <Modal
        open={bgOpen}
        onClose={() => setBgOpen(false)}
        title="Alterar tela de fundo"
        footer={
          <>
            <Button variant="ghost" onClick={() => saveAppearance.mutate({ backgroundColor: null, backgroundImageUrl: null })}>
              Remover fundo
            </Button>
            <Button onClick={() => setBgOpen(false)}>Concluir</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <span className="kb__bgsection">Cores</span>
            <div className="kb__bggrid">
              {KB_BG_COLORS.map((color) => {
                const on = boardData.board.backgroundColor === color && !boardData.board.backgroundImageUrl
                return (
                  <button
                    key={color}
                    type="button"
                    className={`kb__bgtile${on ? ' kb__bgtile--on' : ''}`}
                    style={{ background: color }}
                    aria-label={`Cor de fundo ${color}`}
                    onClick={() => saveAppearance.mutate({ backgroundColor: color, backgroundImageUrl: null })}
                  >
                    {on ? <i className="fas fa-check" aria-hidden /> : null}
                  </button>
                )
              })}
              {KB_BG_GRADIENTS.map((gradient) => {
                const on = boardData.board.backgroundImageUrl === gradient
                return (
                  <button
                    key={gradient}
                    type="button"
                    className={`kb__bgtile${on ? ' kb__bgtile--on' : ''}`}
                    style={{ backgroundImage: gradient }}
                    aria-label="Gradiente de fundo"
                    onClick={() => saveAppearance.mutate({ backgroundColor: null, backgroundImageUrl: gradient })}
                  >
                    {on ? <i className="fas fa-check" aria-hidden /> : null}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <span className="kb__bgsection">Personalizar</span>
            <div className="kb__bggrid">
              <label className={`kb__bgtile kb__bgtile--add${uploading ? ' kb__bgtile--busy' : ''}`} title="Enviar imagem">
                <i className={uploading ? 'fas fa-spinner fa-spin' : 'fas fa-plus'} aria-hidden />
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) uploadBackground(file)
                    e.target.value = ''
                  }}
                />
              </label>
              {(backgrounds.data?.images ?? []).map((image) => {
                const path = `/public/kanban-bg/${image.id}`
                const on = boardData.board.backgroundImageUrl === path
                return (
                  <button
                    key={image.id}
                    type="button"
                    className={`kb__bgtile${on ? ' kb__bgtile--on' : ''}`}
                    style={{ backgroundImage: `url("${resolveBgUrl(path)}")` }}
                    title={image.name}
                    aria-label={`Usar a imagem ${image.name}`}
                    onClick={() => saveAppearance.mutate({ backgroundColor: null, backgroundImageUrl: path })}
                  >
                    {on ? <i className="fas fa-check" aria-hidden /> : null}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </Modal>

      {/* -------- modais do "Adicionar filtro" (produto / filtro fixo) */}
      <Modal
        open={filtroModal === 'produto'}
        onClose={() => setFiltroModal(null)}
        title="Produtos e serviços do quadro"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFiltroModal(null)}>
              Cancelar
            </Button>
            <Button
              loading={saveEscopo.isPending}
              onClick={() => saveEscopo.mutate({ portfolioNodeIds: produtoDraft }, { onSuccess: () => setFiltroModal(null) })}
            >
              Aplicar
            </Button>
          </>
        }
      >
        <ScopePicker
          placeholder="Buscar produto ou serviço…"
          nodes={((portfolio.data?.nodes ?? []) as {
            id: string
            parentId: string | null
            name: string
            level: string
            depth: number
          }[]).map((node) => ({
            id: node.id,
            label: node.name,
            meta: labelOf(`portfolio_level.${node.level.toLowerCase()}`),
            icon: 'fas fa-cube',
            parentId: node.parentId,
            depth: node.depth,
            selectable: true,
          }))}
          value={produtoDraft}
          onChange={setProdutoDraft}
          emptyMessage="Nenhum produto/serviço escolhido: o quadro considera itens de todo o portfólio."
        />
      </Modal>

      {/* Filtro AVANCADO: sempre aplicado ao quadro (o antigo filtro fixo) */}
      <Modal
        open={filtroModal === 'filtro'}
        onClose={() => setFiltroModal(null)}
        title="Filtro avançado do quadro"
        footer={
          <>
            {boardData.board.filter ? (
              <Button variant="ghost" onClick={() => saveEscopo.mutate({ filter: null }, { onSuccess: () => setFiltroModal(null) })}>
                Remover filtro
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setFiltroModal(null)}>
              Cancelar
            </Button>
            <Button
              loading={saveEscopo.isPending}
              disabled={filtroDraft === null}
              onClick={() =>
                filtroDraft &&
                saveEscopo.mutate({ filter: JSON.stringify(filtroDraft) }, { onSuccess: () => setFiltroModal(null) })
              }
            >
              Aplicar
            </Button>
          </>
        }
      >
        <p className="kbform__filter-desc">Filtro fixo do quadro — opcional. Sem critérios, todos os itens aparecem.</p>
        {filtroDraft ? (
          <FilterEditor
            fields={CARD_FILTER_FIELDS}
            value={filtroDraft}
            onChange={setFiltroDraft}
            loadValueOptions={loadCardValueOptions}
          />
        ) : null}
      </Modal>

      {/* FILTRO RÁPIDO (SPEC-20261104-1557-filtro-rapido-do-quadro): só nesta sessão, não grava em lugar nenhum */}
      <Modal
        open={filtroRapidoAberto}
        onClose={() => setFiltroRapidoAberto(false)}
        title="Filtro rápido"
        footer={
          <>
            {filtroRapidoAtivo ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setFiltroRapido(null)
                  setFiltroRapidoAberto(false)
                }}
              >
                Limpar filtro
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setFiltroRapidoAberto(false)}>
              Cancelar
            </Button>
            <Button
              disabled={filtroRapidoDraft === null}
              onClick={() => {
                if (filtroRapidoDraft) setFiltroRapido(juntarFiltroRapido(filtroRapidoDraft, rapidosDraft, filtroRapidoDraft.criteria))
                setFiltroRapidoAberto(false)
              }}
            >
              Aplicar
            </Button>
          </>
        }
      >
        <p className="kbform__filter-desc">
          Vale por cima da visão escolhida e só nesta sessão — não é salvo. Para guardar um filtro, crie uma visão.
        </p>
        {/* campos padrão como seletores diretos (SPEC-20261104-1646): vazio = todos */}
        <div className="kb__frapido-grade">
          {camposRapidosDe(filterFields).map((f) => (
            <label key={f.key} className="kb__frapido-campo">
              <span>{f.key === 'etapa' ? 'Etapa (Fluxo/Etapa)' : f.label}</span>
              <TagSelect
                value={rapidosDraft[f.key] ?? []}
                options={loadCardValueOptions(f.key, '')}
                placeholder="Todos"
                emptyLabel="Nenhuma opção"
                onChange={(vals) => setRapidosDraft((r) => ({ ...r, [f.key]: vals }))}
              />
            </label>
          ))}
        </div>
        <button
          type="button"
          className="kb__frapido-avancado"
          aria-expanded={avancadoAberto}
          onClick={() => setAvancadoAberto((v) => !v)}
        >
          <i className={`fas fa-chevron-${avancadoAberto ? 'down' : 'right'}`} aria-hidden />
          Avançado
          {filtroRapidoDraft && filtroRapidoDraft.criteria.length > 0 ? (
            <span className="kb__frapido-n">{filtroRapidoDraft.criteria.length}</span>
          ) : null}
          <small>escolha os campos e monte os critérios</small>
        </button>
        {avancadoAberto && filtroRapidoDraft ? (
          <FilterEditor
            fields={filterFields}
            value={filtroRapidoDraft}
            onChange={setFiltroRapidoDraft}
            loadValueOptions={loadCardValueOptions}
          />
        ) : null}
      </Modal>

      {/* Adicionar item na lane: MODAL flutuante (pedido 2026-08-25), com a
          opcao de abrir a INCLUSAO COMPLETA do item */}
      {(() => {
        const laneDoComposer = composerLane !== null ? (lanes.find((l) => l.key === composerLane) ?? null) : null
        const cartoesNaLane = laneDoComposer
          ? (boardData.columns.find((c) => c.id === laneDoComposer.id)?.cards.length ?? 0)
          : 0
        const wipCheio = laneDoComposer?.wipLimit != null && cartoesNaLane >= laneDoComposer.wipLimit
        const pedeEtapa = laneDoComposer ? etapasAPerguntar(laneDoComposer) : null
        // Fluxos da LANE (pelas etapas mapeadas nela). Quando a lane tem fluxo,
        // o composer so oferece os tipos DESSE fluxo (2026-08-28) e a etapa sai
        // desses fluxos; sem fluxo na lane, cai no filtro do quadro.
        const fluxosDaLane = new Set(
          (laneDoComposer?.flowStepIds ?? [])
            .map((sid) => etapasDoQuadro.find((s) => s.flowStepId === sid)?.flowId)
            .filter((f): f is string => Boolean(f)),
        )
        const fluxosDeFiltro = fluxosDaLane.size > 0 ? fluxosDaLane : flowIdsDoQuadro
        // tipos que o composer aceita: os do quadro E, se a lane tem fluxo, so os
        // desse fluxo. Tipo SEM nenhum vinculo de fluxo e neutro e entra sempre —
        // senao, workspaces sem vinculos tipo<->fluxo ficariam com a lista VAZIA.
        const tiposDoComposer = tiposDoQuadro.filter(
          (t) =>
            fluxosDaLane.size === 0 ||
            (t.flowIds ?? []).length === 0 ||
            (t.flowIds ?? []).some((f) => fluxosDaLane.has(f)),
        )
        // TIPO com fluxo(s): a etapa vem do TIPO — a lista mostra a etapa INICIAL
        // de cada fluxo do tipo (dentre os fluxos da lane/quadro). Um fluxo => ja
        // seta e mostra so ela; varios => escolhe uma.
        const tipoSel = tiposDoComposer.find((t) => t.id === composer.typeId)
        const fluxosDoTipo = ((tipoSel?.flowIds as string[] | undefined) ?? []).filter((f) => fluxosDeFiltro.has(f))
        const etapasIniciaisDoTipo = fluxosDoTipo.flatMap((f) => {
          const doFluxo = etapasDoQuadro.filter((s) => s.flowId === f)
          // INÍCIO DE FLUXO marcado no desenho (2026-09-02): com etapas
          // marcadas, TODAS elas podem ser a primeira; sem nenhuma, vale a
          // primeira do fluxo, como antes.
          const marcadas = doFluxo.filter((s) => (s as { isInitial?: boolean }).isInitial)
          return marcadas.length > 0 ? marcadas : doFluxo.slice(0, 1)
        })
        const usaEtapaDoTipo = etapasIniciaisDoTipo.length > 0
        const opcoesEtapa = usaEtapaDoTipo ? etapasIniciaisDoTipo : (pedeEtapa ?? [])
        // precisa ESCOLHER quando ha mais de uma opcao; com uma so, ja vem setada
        const precisaEscolherEtapa = opcoesEtapa.length > 1
        return (
          <Modal
            open={laneDoComposer !== null}
            resizable
            onClose={() => setComposerLane(null)}
            title={`Adicionar item${laneDoComposer ? ` — ${laneDoComposer.name}` : ''}`}
            headerExtra={
              <IconButton
                icon="fas fa-external-link-alt"
                label="Expandir para a inclusão completa do item"
                onClick={() => {
                  if (!laneDoComposer) return
                  if (wipCheio) {
                    toast.push({ tone: 'danger', message: 'Limite de WIP excedido nesta lane.' })
                    return
                  }
                  // exige escolher a etapa (varias opcoes) antes de expandir
                  if (precisaEscolherEtapa && composer.flowStepId === '') {
                    toast.push({ tone: 'warning', message: 'Escolha a etapa do fluxo antes de expandir.' })
                    return
                  }
                  const statusId = laneDoComposer.statusId ?? bootstrap.data?.workItemStatuses.find((s) => s.isActive)?.id
                  if (statusId) createItem.mutate({ statusId, laneId: laneDoComposer.id, abrirDetalhe: true })
                }}
              />
            }
            footer={
              <>
                <Button variant="ghost" onClick={() => setComposerLane(null)}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  loading={createItem.isPending}
                  disabled={
                    composer.title.trim() === '' ||
                    composer.typeId === '' ||
                    (precisaEscolherEtapa && composer.flowStepId === '')
                  }
                  onClick={() => {
                    if (!laneDoComposer) return
                    if (wipCheio) {
                      toast.push({ tone: 'danger', message: 'Limite de WIP excedido nesta lane.' })
                      return
                    }
                    // lane sem status: o item nasce com o status inicial do catalogo
                    const statusId = laneDoComposer.statusId ?? bootstrap.data?.workItemStatuses.find((s) => s.isActive)?.id
                    // finalizar a inclusao ABRE o item para editar; fechar o
                    // item volta para o quadro (pedido 2026-09-01)
                    if (statusId) createItem.mutate({ statusId, laneId: laneDoComposer.id, abrirDetalhe: true })
                  }}
                >
                  Salvar
                </Button>
              </>
            }
          >
            <FieldBlock label="Tipo de item">
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  value={composer.typeId}
                  placeholder="Tipo de item"
                  // SO os tipos aceitos: do quadro e, se a lane tem fluxo, so os desse fluxo
                  options={tiposDoComposer.map((x) => ({ value: x.id, label: labelOf(x.nameTranslationKey), icon: x.icon ?? undefined }))}
                  onChange={(v) => {
                    // tipo com UM fluxo ja seta a etapa inicial; com varios/nenhum limpa
                    const tipo = tiposDoComposer.find((t) => t.id === v)
                    const fluxos = ((tipo?.flowIds as string[] | undefined) ?? []).filter((fx) => fluxosDeFiltro.has(fx))
                    const iniciais = fluxos
                      .map((fx) => etapasDoQuadro.find((s) => s.flowId === fx))
                      .filter((s): s is NonNullable<typeof s> => Boolean(s))
                    const etapaAuto = iniciais.length === 1 ? iniciais[0]!.flowStepId : ''
                    setComposer((f) => ({ ...f, typeId: v, flowStepId: etapaAuto }))
                  }}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Título">
              {({ id: fieldId }) => (
                <Input
                  id={fieldId}
                  autoFocus
                  placeholder="Título do item"
                  value={composer.title}
                  onChange={(e) => setComposer((f) => ({ ...f, title: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição">
              {({ id: fieldId }) => (
                <Textarea
                  id={fieldId}
                  rows={3}
                  placeholder="Descrição"
                  value={composer.description}
                  onChange={(e) => setComposer((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>

            {/*
              Visibilidade: a do QUADRO (SPEC-20261007-0934-visibilidade-padrao-do-quadro). Aqui só se
              mostra — "novos itens devem sempre seguir a configuração definida
              no quadro"; quem muda é o dono, em Dados do quadro.
            */}
            <FieldBlock label="Visibilidade">
              {() => (
                <div className="kb-visib-fixa" role="note">
                  <i className={novosItensPrivados ? 'fas fa-lock' : 'fas fa-globe'} aria-hidden />
                  <span>
                    <strong>{novosItensPrivados ? 'Privado' : 'Público'}</strong>
                    <small>
                      {novosItensPrivados
                        ? 'Só o responsável, os membros e o administrador veem. '
                        : 'Todos do workspace veem. '}
                      {novosItensExclusivos ? 'Aparece somente neste quadro. ' : ''}
                      Definido pelo quadro.
                    </small>
                  </span>
                </div>
              )}
            </FieldBlock>

            {/* Etapa: quando o TIPO tem fluxo(s), as opcoes sao a etapa INICIAL
                de cada fluxo do tipo (um fluxo = ja setada, mostra so ela; varios
                = escolhe). Sem fluxo no tipo, cai na regra da lane. */}
            {opcoesEtapa.length > 0 ? (
              <FieldBlock
                label="Etapa do fluxo"
                hint={usaEtapaDoTipo && opcoesEtapa.length === 1 ? 'Definida pelo fluxo do tipo.' : undefined}
              >
                {({ id: fieldId }) => (
                  <Select
                    id={fieldId}
                    value={composer.flowStepId || (opcoesEtapa.length === 1 ? opcoesEtapa[0]!.flowStepId : '')}
                    placeholder="Etapa do fluxo"
                    disabled={opcoesEtapa.length === 1}
                    options={opcoesEtapa.map((s) => ({ value: s.flowStepId, label: `${s.flowName} · ${s.stepName}` }))}
                    onChange={(v) => setComposer((f) => ({ ...f, flowStepId: v }))}
                  />
                )}
              </FieldBlock>
            ) : null}
          </Modal>
        )
      })()}

      {/* modal da descricao do quadro (configuracao, botao ao lado do titulo) */}
      <Modal
        open={descricaoOpen}
        onClose={() => setDescricaoOpen(false)}
        title="Descrição do quadro"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDescricaoOpen(false)}>
              Cancelar
            </Button>
            <Button
              loading={saveEscopo.isPending}
              onClick={() =>
                saveEscopo.mutate(
                  { description: descricaoDraft.trim() === '' ? null : descricaoDraft.trim() },
                  { onSuccess: () => setDescricaoOpen(false) },
                )
              }
            >
              Salvar
            </Button>
          </>
        }
      >
        <FieldBlock label="Descrição" hint="Aparece no “i” ao lado do nome do quadro.">
          {({ id: fieldId }) => (
            <Textarea
              id={fieldId}
              rows={4}
              maxLength={200}
              placeholder="Para que serve este quadro?"
              // amplia na horizontal E na vertical (pedido do usuario)
              style={{ resize: 'both', minWidth: '100%' }}
              value={descricaoDraft}
              onChange={(e) => setDescricaoDraft(e.target.value)}
            />
          )}
        </FieldBlock>
      </Modal>

      {/* Em branco x template, ANTES do composer (pedido 2026-08-30) */}
      {escolhendoParaLane !== null ? (
        <EscolherTemplateModal
          // a RAIZ nasce já na etapa da lane clicada: sem isso ela vinha na
          // etapa do template e o pino era recusado por `flow_sequence_blocked`
          flowStepId={lanes.find((l) => l.key === escolhendoParaLane)?.flowStepIds?.[0] ?? null}
          kanbanBoardId={id}
          onFechar={() => setEscolhendoParaLane(null)}
          onEmBranco={() => {
            const lane = escolhendoParaLane
            setEscolhendoParaLane(null)
            // em branco: nasce com os padrões do quadro (e o título já
            // digitado no cartão) e abre a tela completa do item
            if (lane) criarRapido.mutate({ laneKey: lane, titulo: rapido?.titulo ?? '', abrirDetalhe: true })
          }}
          onAplicado={(rootId) => {
            const laneId = lanes.find((l) => l.key === escolhendoParaLane)?.id
            setEscolhendoParaLane(null)
            setRapido(null)
            // fixa na lane e ABRE o item criado (pedido 2026-08-30). O `finally`
            // garante a navegação mesmo se o pino falhar — o item já existe, e
            // deixar o usuário na lista sem saber onde ele foi parar é pior.
            void fixarNaLane(rootId, laneId).finally(() => navigate(`/kanbans/${id}/items/${rootId}`))
          }}
        />
      ) : null}

      {confirm.element}
    </div>
  )
}

/**
 * MODO LISTA do quadro (SPEC-20260914-2210 · colunas SPEC-20260914-2231).
 *
 * A MESMA seleção de cartões do quadro, em tabela. Recebe tudo pronto —
 * cartões já filtrados, bandas já montadas, colunas já resolvidas — porque a
 * regra de o-que-aparece é do quadro: duas fontes de verdade fariam a lista e
 * as colunas divergirem no primeiro filtro novo.
 */
/**
 * Barra da lista (ordem manual + Colunas). No normal fica no canto da área da
 * lista; em tela cheia (SPEC-20260915-2116) é portalada para o slot da barra
 * de ferramentas do quadro — o estado e o menu continuam os mesmos.
 */
/** Ícone de "visualizações de tabela" do Sheets: uma grade com outra folha atrás (SPEC-20260915-2234). */
function IconeVisualizacoes() {
  return (
    <svg className="kb__icovis" viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
      <path d="M6.5 2.5H17.5V13.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="2.2" y="6.2" width="11.6" height="11.6" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M2.2 10.1H13.8M2.2 14H13.8M6.1 6.2V17.8M9.9 6.2V17.8" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

/**
 * EDITOR RICO na célula (SPEC-20260915-2250): o mesmo `RichTextEditor` do
 * detalhe, já em edição, numa caixa sobre a linha. Salvar grava; Cancelar/Esc
 * fecha. Avisa a página enquanto está aberto — a barra do Sheets liga os
 * botões de formatação e age no editor por `document.execCommand`.
 */
function EditorRico({
  valor,
  onSalvar,
  onFechar,
  onAtivo,
}: {
  valor: string
  /** HTML final e para onde o cursor vai depois (Ctrl+Enter/↓ desce, ↑ sobe, Tab avança, clique fora fica). */
  onSalvar: (html: string, depois: 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica') => void
  onFechar: () => void
  onAtivo?: (ativo: boolean) => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const inicial = useRef(valor.startsWith('<!--rte:doc-->') ? valor.slice('<!--rte:doc-->'.length) : valor)
  const fechado = useRef(false)
  useEffect(() => {
    onAtivo?.(true)
    const el = ref.current
    if (el) {
      // sem a caixa do editor (pedido 2026-09-16 00:23): a própria célula é
      // editável; o HTML entra direto e o cursor vai para o fim
      el.innerHTML = inicial.current
      el.focus()
      const sel = window.getSelection()
      if (sel) {
        const r = document.createRange()
        r.selectNodeContents(el)
        r.collapse(false)
        sel.removeAllRanges()
        sel.addRange(r)
      }
    }
    return () => onAtivo?.(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const html = () => {
    const el = ref.current
    if (!el) return inicial.current
    // célula "vazia" (só <br>/<p></p>) grava vazio de verdade
    const texto = (el.textContent ?? '').trim()
    return texto === '' && !el.querySelector('img') ? '' : el.innerHTML
  }
  const gravar = (depois: 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica') => {
    if (fechado.current) return
    fechado.current = true
    const novo = html()
    if (novo === inicial.current) onFechar()
    else onSalvar(novo, depois)
  }
  return (
    <div
      ref={ref}
      className="kbl__editor kbl__editor--celula"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Texto formatado — Enter quebra a linha; Ctrl+Enter, Tab ou setas para cima/baixo gravam e saem; Esc cancela"
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          e.preventDefault()
          fechado.current = true
          onFechar()
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          // Ctrl+Enter grava e sai (pedido 2026-09-16 01:20: "o enter quebra
          // linha, e o control enter sai da edição, ou as setas, ou tab")
          e.preventDefault()
          gravar('baixo')
        } else if (e.key === 'Enter') {
          e.preventDefault()
          document.execCommand('insertLineBreak')
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          // setas ↑↓ gravam e vão para a linha de cima/baixo; ←→ seguem
          // movendo o cursor dentro do texto
          e.preventDefault()
          gravar(e.key === 'ArrowDown' ? 'baixo' : 'cima')
        } else if (e.key === 'Tab') {
          e.preventDefault()
          gravar(e.shiftKey ? 'esquerda' : 'direita')
        }
      }}
      onBlur={() => {
        if (blurSeguro) return // janela de imagem aberta: o foco volta
        gravar('fica')
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    />
  )
}

/**
 * LINHA NOVA da planilha (SPEC-20260916-0042): id do cartão-rascunho que
 * ocupa a última linha. Preencher qualquer célula dele entra no modo "novo
 * item"; Enter/✓ criam o item; ✕ cancela.
 */
const NOVO_ID = '__novo__'
/**
 * Enquanto a janela de escolher imagem está aberta, o contenteditable da
 * célula perde o foco — sem esta trava ele gravaria e fecharia antes de a
 * imagem entrar (SPEC-20260916-0048).
 */
let blurSeguro = false
type Rascunho = {
  title?: string
  workItemTypeId?: string | null
  flowStepId?: string | null
  statusId?: string | null
  workItemPriorityId?: string | null
  assigneeMemberId?: string | null
  dueAt?: string | null
  description?: string
  expectedResult?: string
  customValues?: Record<string, unknown>
}

/**
 * ESTILOS DE TABELA (SPEC-20260916-0131) — a galeria "Formatar como Tabela"
 * do Excel: três tons (Clara/Média/Escura) × cores. Cada estilo vira um
 * conjunto de variáveis CSS na raiz da planilha; sem estilo, a planilha fica
 * com as cores padrão do Sheets.
 */
const TONS_DE_ESTILO = [
  { k: 'clara', r: 'Clara' },
  { k: 'media', r: 'Média' },
  { k: 'escura', r: 'Escura' },
] as const
const CORES_DE_ESTILO = [
  { k: 'cinza', r: 'Cinza', c: '#5f6368' },
  { k: 'azul', r: 'Azul', c: '#4472c4' },
  { k: 'laranja', r: 'Laranja', c: '#ed7d31' },
  { k: 'verde', r: 'Verde', c: '#70ad47' },
  { k: 'amarelo', r: 'Amarelo', c: '#d9a300' },
  { k: 'roxo', r: 'Roxo', c: '#7030a0' },
  { k: 'ciano', r: 'Ciano', c: '#2a9db5' },
  { k: 'vermelho', r: 'Vermelho', c: '#c0392b' },
] as const
/** Mistura `hex` com branco (p<0 = com preto): p = quanto da cor fica. */
const misturar = (hex: string, p: number, comPreto = false) => {
  const n = parseInt(hex.slice(1), 16)
  const base = comPreto ? 0 : 255
  const canal = (c: number) => Math.round(c * p + base * (1 - p))
  const r = canal((n >> 16) & 255), g = canal((n >> 8) & 255), b = canal(n & 255)
  return `rgb(${r}, ${g}, ${b})`
}
type VarsDeEstilo = Record<'--tbl-cab-bg' | '--tbl-cab-fg' | '--tbl-faixa' | '--tbl-linha' | '--tbl-borda' | '--tbl-grupo-bg' | '--tbl-grupo-fg' | '--tbl-trilho', string>
const varsDoEstilo = (idEstilo: string): Partial<VarsDeEstilo> => {
  if (!idEstilo) return {}
  const [tom, corK] = idEstilo.split('-')
  const cor = CORES_DE_ESTILO.find((c) => c.k === corK)?.c
  if (!cor) return {}
  const escuro = misturar(cor, 0.65, true)
  if (tom === 'clara')
    return { '--tbl-cab-bg': '#ffffff', '--tbl-cab-fg': escuro, '--tbl-faixa': misturar(cor, 0.12), '--tbl-linha': '#ffffff', '--tbl-borda': misturar(cor, 0.45), '--tbl-grupo-bg': misturar(cor, 0.22), '--tbl-grupo-fg': escuro, '--tbl-trilho': cor }
  if (tom === 'media')
    return { '--tbl-cab-bg': cor, '--tbl-cab-fg': '#ffffff', '--tbl-faixa': misturar(cor, 0.18), '--tbl-linha': '#ffffff', '--tbl-borda': misturar(cor, 0.4), '--tbl-grupo-bg': misturar(cor, 0.32), '--tbl-grupo-fg': escuro, '--tbl-trilho': escuro }
  return { '--tbl-cab-bg': escuro, '--tbl-cab-fg': '#ffffff', '--tbl-faixa': misturar(cor, 0.5), '--tbl-linha': misturar(cor, 0.28), '--tbl-borda': '#ffffff', '--tbl-grupo-bg': misturar(cor, 0.8), '--tbl-grupo-fg': '#ffffff', '--tbl-trilho': '#ffffff' }
}

/** Largura do cabeçalho de linha (código do item) na cara de planilha. */
const LARGURA_ROWNUM = 112
/** Altura mínima de uma linha redimensionada (SPEC-20260915-2214). */
const ALTURA_MIN_LINHA = 24

/**
 * LISTA SUSPENSA da planilha (SPEC-20260915-2149): a célula de lista abre,
 * com um clique, as opções logo abaixo dela, na largura da própria coluna —
 * a atual em negrito, o lápis cinza no pé (as opções vêm do catálogo) e
 * clicar fora fecha. É o chip de lista suspensa do Sheets.
 */
/**
 * ÍCONE de Colunas (SPEC-20260916-0936): retângulo contornado dividido em três
 * colunas — o desenho que o usuário mandou ("utilizar esse ícone para
 * colunas"); o FA5 não tem esse glifo, então é um SVG inline.
 */
function IconeColunas() {
  return (
    <svg className="kbl__colicone" viewBox="0 0 16 16" width="16" height="16" aria-hidden focusable="false">
      <rect x="1.5" y="3" width="13" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <line x1="5.9" y1="3" x2="5.9" y2="13" stroke="currentColor" strokeWidth="1.4" />
      <line x1="10.1" y1="3" x2="10.1" y2="13" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

type DepoisDaCelula = 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica'
function ListaSuspensa({
  atual,
  opcoes,
  proxima,
  onEscolher,
  onFechar,
}: {
  atual: string | null
  opcoes: readonly { value: string; label: string }[]
  /** para onde o cursor vai depois de escolher com Enter (linha nova: direita; item existente: baixo) */
  proxima: 'baixo' | 'direita'
  onEscolher: (value: string, depois: DepoisDaCelula) => void
  onFechar: () => void
}) {
  const rotulo = opcoes.find((o) => o.value === (atual ?? ''))?.label ?? ''
  // TECLADO (SPEC-20260916-0149): a lista nasce com a opção atual realçada;
  // ↑↓ percorrem, Enter escolhe e vai para a próxima célula, Tab escolhe e
  // vai para o lado, Esc fecha (pedido 2026-09-16 01:50: "um enter e rolar a
  // seta deve ir selecionando o item da lista e ao pressionar enter já
  // seleciona e vai para próxima célula")
  const [realce, setRealce] = useState(() => Math.max(0, opcoes.findIndex((o) => o.value === (atual ?? ''))))
  const listaRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    listaRef.current?.focus()
  }, [])
  useEffect(() => {
    const el = listaRef.current?.querySelectorAll<HTMLElement>('.kbl__suspensa-op')[realce]
    el?.scrollIntoView({ block: 'nearest' })
  }, [realce])
  const escolherRealce = (depois: DepoisDaCelula) => {
    const o = opcoes[realce]
    if (o) onEscolher(o.value, depois)
    else onFechar()
  }
  return (
    <span
      className="kbl__editor kbl__suspensa-cel"
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') { e.preventDefault(); onFechar() }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setRealce((i) => (opcoes.length ? (i + 1) % opcoes.length : 0)) }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setRealce((i) => (opcoes.length ? (i - 1 + opcoes.length) % opcoes.length : 0)) }
        else if (e.key === 'Enter') { e.preventDefault(); escolherRealce(proxima) }
        else if (e.key === 'Tab') { e.preventDefault(); escolherRealce(e.shiftKey ? 'esquerda' : 'direita') }
        else if (e.key === 'Home') { e.preventDefault(); setRealce(0) }
        else if (e.key === 'End') { e.preventDefault(); setRealce(Math.max(0, opcoes.length - 1)) }
      }}
    >
      <span className="kbl__suspensa-atual">{rotulo}</span>
      {/* o pano de fundo fica DENTRO da célula (não no body): a célula em edição
          é um contexto de empilhamento próprio e um pano no body cobria a
          lista — o clique real caía no pano e fechava sem escolher
          (pedido 2026-09-16 02:30, "não consigo selecionar o item nas listas") */}
      <div className="wi-menu__backdrop kbl__suspensa-fora" onClick={onFechar} />
      <div ref={listaRef} className="kbl__suspensa" role="listbox" aria-label="Opções" tabIndex={0} aria-activedescendant={opcoes[realce] ? `kbl-op-${realce}` : undefined}>
        {opcoes.map((o, i) => (
          <button
            key={o.value}
            id={`kbl-op-${i}`}
            type="button"
            role="option"
            tabIndex={-1}
            aria-selected={o.value === (atual ?? '')}
            className={`kbl__suspensa-op${o.value === (atual ?? '') ? ' kbl__suspensa-op--on' : ''}${i === realce ? ' kbl__suspensa-op--realce' : ''}`}
            onMouseEnter={() => setRealce(i)}
            onClick={() => onEscolher(o.value, 'fica')}
          >
            {o.label}
          </button>
        ))}
        {opcoes.length === 0 ? <p className="kbl__suspensa-vazia">Sem opções</p> : null}
        <span className="kbl__suspensa-pe">
          <button type="button" disabled title="As opções vêm do catálogo" aria-label="Editar opções">
            <i className="fas fa-pencil-alt" aria-hidden />
          </button>
        </span>
      </div>
    </span>
  )
}

function BarraDaLista({ slot, children }: { slot: HTMLElement | null; children: ReactNode }) {
  if (slot) return createPortal(<span className="kbl__barra kbl__barra--portal">{children}</span>, slot)
  return <div className="kbl__barra">{children}</div>
}

/** O que `EditorDaCelula` precisa da lista — passado por prop para o componente não remontar a cada render. */
type CtxEditorDaCelula<C extends KbListaCard> = {
  edicao: EdicaoDaGrade
  gravarRascunho: (patch: Rascunho) => void
  depoisDeConfirmar: (como: 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica') => void
  rolagemRef: React.MutableRefObject<HTMLDivElement | null>
  planilha: boolean
  _c?: C
}
type CtxMenuFiltro<C extends KbListaCard> = {
  cards: C[]
  textoDaCelula: (c: C, col: ColunaDaLista) => string
  filtrosColuna: Map<string, Set<string>>
  setFiltrosColuna: (f: Map<string, Set<string>> | ((m: Map<string, Set<string>>) => Map<string, Set<string>>)) => void
  setFiltroAberto: (v: string | null) => void
  ordenavel: (col: ColunaDaLista) => boolean
  onOrdenar: (col: ColunaLista, desc?: boolean) => void
}

function EditorDaCelula<C extends KbListaCard>({ card, col, inicial, ctx }: { card: C; col: ColunaDaLista; inicial?: string; ctx: CtxEditorDaCelula<C> }) {
  const { edicao, gravarRascunho, depoisDeConfirmar, rolagemRef, planilha } = ctx
  const [ocupado, setOcupado] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // valor dos editores de TEXTO (título, complementar texto/número) — um só
  // hook no topo, fora dos ramos, para a ordem dos hooks não depender da coluna
  const atualCompl = col.compl ? complDoCartao(card)[col.compl.key] : undefined
  const [texto, setTexto] = useState(
    inicial ?? (col.key === 'title' ? card.title : atualCompl == null ? '' : String(atualCompl)),
  )
  // na LINHA NOVA os gravadores escrevem no rascunho, não no servidor
  const ehNovo = card.id === NOVO_ID
  const gravarCampo = (patch: Record<string, unknown>): Promise<unknown> =>
    ehNovo ? Promise.resolve(gravarRascunho(patch as Rascunho)) : edicao.gravar(card.id, patch)
  const gravarComplCampo = (cv: Record<string, unknown>): Promise<unknown> =>
    ehNovo ? Promise.resolve(gravarRascunho({ customValues: cv })) : edicao.gravarCompl(card.id, cv)
  const mudarStatusCampo = (sid: string): Promise<unknown> =>
    ehNovo ? Promise.resolve(gravarRascunho({ statusId: sid })) : edicao.mudarStatus(card.id, sid)
  // Enter na lista suspensa: na linha nova segue para a DIREITA (preenchendo a
  // linha); num item existente desce, como no Sheets (SPEC-20260916-0149)
  const proxima: 'baixo' | 'direita' = ehNovo ? 'direita' : 'baixo'
  useEffect(() => {
    inputRef.current?.focus()
    if (inputRef.current && !inicial) inputRef.current.select()
    // Select/DateInput: abre o popover sozinho, para não pedir um 2º clique
    // (a lista suspensa da planilha não tem gatilho: já nasce aberta)
    const gatilho = inputRef.current
      ? null
      : (rolagemRef.current?.querySelector('.kbl__cel--edit .ob-select__trigger, .kbl__cel--edit .ob-date__trigger') as HTMLButtonElement | null)
    gatilho?.focus()
    gatilho?.click()
  }, [])

  const salvar = async (fn: () => Promise<unknown>, depois: 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica') => {
    setOcupado(true)
    try {
      await fn()
      depoisDeConfirmar(depois)
    } catch {
      // o toast do gravador já avisou; a célula volta ao valor de antes
      setOcupado(false)
      depoisDeConfirmar('fica')
    }
  }
  const teclasDoTexto = (e: React.KeyboardEvent<HTMLInputElement>, confirmar: () => Promise<unknown>) => {
    if (e.key === 'Enter') { e.preventDefault(); void salvar(confirmar, 'baixo') }
    else if (e.key === 'Tab') { e.preventDefault(); void salvar(confirmar, e.shiftKey ? 'esquerda' : 'direita') }
    else if (e.key === 'Escape') { e.preventDefault(); depoisDeConfirmar('fica') }
    e.stopPropagation()
  }

  // ---- complementar
  if (col.compl) {
    const def = edicao.camposCompl.get(col.key)
    const cv = { ...complDoCartao(card) }
    const atual = cv[col.compl.key]
    const gravarCompl = (valor: unknown) => gravarComplCampo({ ...cv, [col.compl!.key]: valor })
    if (!def) return <span className="kbl__vago">—</span>
    if (def.type === 'richtext') {
      return (
        <EditorRico
          valor={typeof atual === 'string' ? atual : ''}
          onSalvar={(html, depois) => void salvar(() => gravarCompl(html), depois)}
          onFechar={() => depoisDeConfirmar('fica')}
          onAtivo={edicao.onEdicaoRica}
        />
      )
    }
    if (def.type === 'text' || def.type === 'number') {
      const confirmar = () => gravarCompl(def.type === 'number' ? (texto === '' ? null : Number(texto)) : texto)
      return (
        <input
          ref={inputRef}
          type={def.type === 'number' ? 'number' : 'text'}
          className="kbl__editor"
          value={texto}
          disabled={ocupado}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => teclasDoTexto(e, confirmar)}
          onBlur={() => void salvar(confirmar, 'fica')}
        />
      )
    }
    if (def.type === 'date' || def.type === 'datetime') {
      return (
        <span className="kbl__editor kbl__editor--pop" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); depoisDeConfirmar('fica') } }}>
          <DateInput
            value={typeof atual === 'string' && atual ? new Date(atual) : null}
            onChange={(d) => void salvar(() => gravarCompl(d ? d.toISOString() : null), 'fica')}
          />
        </span>
      )
    }
    if (def.type === 'boolean') {
      return (
        <span className="kbl__editor">
          <Checkbox checked={atual === true} label="Sim" onChange={(e) => void salvar(() => gravarCompl(e.target.checked), 'fica')} />
        </span>
      )
    }
    if (def.type === 'select') {
      if (planilha) {
        return (
          <ListaSuspensa
            atual={typeof atual === 'string' ? atual : ''}
            opcoes={[{ value: '', label: '—' }, ...(def.config.options ?? [])]}
            proxima={proxima}
            onEscolher={(val, depois) => void salvar(() => gravarCompl(val || null), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={typeof atual === 'string' ? atual : ''}
            placeholder="—"
            options={[{ value: '', label: '—' }, ...(def.config.options ?? [])]}
            onChange={(val) => void salvar(() => gravarCompl(val || null), 'fica')}
          />
        </span>
      )
    }
    if (def.type === 'multiselect') {
      return (
        <span className="kbl__editor kbl__editor--pop">
          <TagSelect
            value={Array.isArray(atual) ? (atual as string[]) : []}
            options={def.config.options ?? []}
            emptyLabel="Nenhuma opção cadastrada"
            placeholder="Escolha…"
            onChange={(vals) => void salvar(() => gravarCompl(vals), 'fica')}
          />
        </span>
      )
    }
    return <span className="kbl__vago">—</span>
  }

  // ---- fixos
  // guarda final (SPEC-20260916-1948): sem o HTML inteiro, nada de editor —
  // gravaria vazio por cima do conteúdo do item
  if (celulaPesada(card, col)) return <span className="kbl__suave">{tiraHtml(htmlDaCelula(card, col.key as 'description' | 'expectedResult'))}</span>
  switch (col.key) {
    case 'description':
    case 'expectedResult': {
      const campo = col.key
      return (
        <EditorRico
          valor={(campo === 'description' ? card.description : card.expectedResult) ?? ''}
          onSalvar={(html, depois) => void salvar(() => gravarCampo({ [campo]: html }), depois)}
          onFechar={() => depoisDeConfirmar('fica')}
          onAtivo={edicao.onEdicaoRica}
        />
      )
    }
    case 'title': {
      const confirmar = () =>
        texto.trim() && texto !== card.title ? gravarCampo({ title: texto.trim() }) : Promise.resolve()
      return (
        <input
          ref={inputRef}
          className="kbl__editor"
          value={texto}
          disabled={ocupado}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => teclasDoTexto(e, confirmar)}
          onBlur={() => void salvar(confirmar, 'fica')}
        />
      )
    }
    case 'type':
      if (planilha) {
        return (
          <ListaSuspensa
            atual={card.workItemTypeId ?? null}
            opcoes={edicao.tipos}
            proxima={proxima}
            onEscolher={(id, depois) => void salvar(() => gravarCampo({ workItemTypeId: id }), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={card.workItemTypeId ?? null}
            options={edicao.tipos}
            onChange={(id) => void salvar(() => gravarCampo({ workItemTypeId: id }), 'fica')}
          />
        </span>
      )
    case 'priority':
      if (planilha) {
        return (
          <ListaSuspensa
            atual={card.workItemPriorityId ?? null}
            opcoes={edicao.prioridades}
            proxima={proxima}
            onEscolher={(id, depois) => void salvar(() => gravarCampo({ workItemPriorityId: id }), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={card.workItemPriorityId ?? null}
            options={edicao.prioridades}
            onChange={(id) => void salvar(() => gravarCampo({ workItemPriorityId: id }), 'fica')}
          />
        </span>
      )
    case 'etapa': {
      // ETAPA (SPEC-20260916-0120): novo = etapas iniciais do tipo; existente =
      // etapas dos fluxos do tipo (o servidor valida a sequência ao mover)
      const opcoes = ehNovo ? edicao.etapasParaNovo(card.workItemTypeId ?? null) : edicao.etapasParaItem(card.workItemTypeId ?? null)
      const gravarEtapa = (id: string) => (ehNovo ? Promise.resolve(gravarRascunho({ flowStepId: id })) : edicao.mudarEtapa(card.id, id))
      if (planilha) {
        return (
          <ListaSuspensa
            atual={card.flowStepId ?? null}
            opcoes={opcoes}
            proxima={proxima}
            onEscolher={(id, depois) => void salvar(() => gravarEtapa(id), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={card.flowStepId ?? null}
            options={opcoes}
            placeholder="Etapa"
            onChange={(id) => void salvar(() => gravarEtapa(id), 'fica')}
          />
        </span>
      )
    }
    case 'status':
      if (planilha) {
        return (
          <ListaSuspensa
            atual={card.statusId}
            opcoes={edicao.statusComLane}
            proxima={proxima}
            onEscolher={(id, depois) => void salvar(() => mudarStatusCampo(id), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={card.statusId}
            options={edicao.statusComLane}
            placeholder="Status"
            onChange={(id) => void salvar(() => mudarStatusCampo(id), 'fica')}
          />
        </span>
      )
    case 'assignee':
      if (planilha) {
        return (
          <ListaSuspensa
            atual={card.assigneeMemberId ?? ''}
            opcoes={[{ value: '', label: 'Sem responsável' }, ...edicao.membros]}
            proxima={proxima}
            onEscolher={(id, depois) => void salvar(() => gravarCampo({ assigneeMemberId: id || null }), depois)}
            onFechar={() => depoisDeConfirmar('fica')}
          />
        )
      }
      return (
        <span className="kbl__editor kbl__editor--pop">
          <Select
            value={card.assigneeMemberId ?? ''}
            placeholder="Sem responsável"
            options={[{ value: '', label: 'Sem responsável' }, ...edicao.membros]}
            onChange={(id) => void salvar(() => gravarCampo({ assigneeMemberId: id || null }), 'fica')}
          />
        </span>
      )
    case 'due':
      return (
        <span className="kbl__editor kbl__editor--pop" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); depoisDeConfirmar('fica') } }}>
          <DateInput
            value={card.dueAt ? new Date(card.dueAt) : null}
            onChange={(d) => void salvar(() => gravarCampo({ dueAt: d ? d.toISOString() : null }), 'fica')}
          />
        </span>
      )
    default:
      return null
  }
}

function MenuFiltroColuna<C extends KbListaCard>({ col, ctx }: { col: ColunaDaLista; ctx: CtxMenuFiltro<C> }) {
  const { cards, textoDaCelula, filtrosColuna, setFiltrosColuna, setFiltroAberto, ordenavel, onOrdenar } = ctx
  const valores = [...new Set(cards.map((c) => textoDaCelula(c, col)))].sort((a, b) => a.localeCompare(b, 'pt-BR'))
  const [busca, setBusca] = useState('')
  const [marcados, setMarcados] = useState<Set<string>>(() => new Set(filtrosColuna.get(col.key) ?? valores))
  const visiveis = valores.filter((v) => v.toLowerCase().includes(busca.trim().toLowerCase()))
  const fechar = () => setFiltroAberto(null)
  const aplicar = () => {
    setFiltrosColuna((m) => {
      const novo = new Map(m)
      // tudo marcado = sem filtro (o funil some)
      if (marcados.size >= valores.length) novo.delete(col.key)
      else novo.set(col.key, new Set(marcados))
      return novo
    })
    fechar()
  }
  const limparFiltro = () => {
    setFiltrosColuna((m) => {
      const novo = new Map(m)
      novo.delete(col.key)
      return novo
    })
    fechar()
  }
  return (
    <>
      <div className="wi-menu__backdrop" onClick={fechar} />
      <div
        className="kbl__filtromenu"
        role="dialog"
        aria-label={`Filtrar ${col.rotulo}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Escape') fechar()
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') aplicar()
        }}
      >
        {ordenavel(col) ? (
          <>
            <button type="button" className="kbl__filtroitem" onClick={() => { onOrdenar(col.key as ColunaLista, false); fechar() }}>
              <i className="fas fa-sort-alpha-down" aria-hidden /> Ordenar de A a Z
            </button>
            <button type="button" className="kbl__filtroitem" onClick={() => { onOrdenar(col.key as ColunaLista, true); fechar() }}>
              <i className="fas fa-sort-alpha-down-alt" aria-hidden /> Ordenar de Z a A
            </button>
            <div className="kbl__filtrosep" />
          </>
        ) : null}
        {filtrosColuna.has(col.key) ? (
          <>
            <button type="button" className="kbl__filtroitem" onClick={limparFiltro}>
              <i className="fas fa-times-circle" aria-hidden /> Limpar filtro de “{col.rotulo}”
            </button>
            <div className="kbl__filtrosep" />
          </>
        ) : null}
        <div className="kbl__filtrobusca">
          <i className="fas fa-search" aria-hidden />
          <input value={busca} placeholder="Pesquisar" aria-label="Pesquisar valores" autoFocus onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="kbl__filtroacoes">
          <button type="button" onClick={() => setMarcados(new Set(valores))}>Selecionar tudo</button>
          <span aria-hidden>·</span>
          <button type="button" onClick={() => setMarcados(new Set())}>Limpar</button>
        </div>
        <div className="kbl__filtrolista">
          {visiveis.map((v) => (
            <label key={v} className="kbl__filtroval">
              <input
                type="checkbox"
                checked={marcados.has(v)}
                onChange={() =>
                  setMarcados((s) => {
                    const n = new Set(s)
                    if (n.has(v)) n.delete(v)
                    else n.add(v)
                    return n
                  })
                }
              />
              <span>{v === '' ? '(Vazias)' : v}</span>
            </label>
          ))}
          {visiveis.length === 0 ? <p className="kbl__filtronada">Nenhum valor corresponde.</p> : null}
        </div>
        <div className="kbl__filtrope">
          <button type="button" className="kb__btn" onClick={fechar}>Cancelar</button>
          <button type="button" className="kb__btn kb__btn--primary" onClick={aplicar}>OK</button>
        </div>
      </div>
    </>
  )
}

function ListaDoQuadro<C extends KbListaCard>({
  cards,
  bandas,
  colunas,
  todasAsColunas,
  onColunas,
  onLargura,
  alturas,
  onAltura,
  larguraCodigo = LARGURA_ROWNUM,
  alturaCabecalho = 0,
  onAlturaCabecalho,
  estilo = '',
  fonte = 13,
  filtrosExtras = [],
  onMoverLinha,
  bandasFechadas,
  onAlternarBanda,
  seletorAberto,
  onSeletor,
  slotDaBarra,
  planilha = false,
  slotDoNome,
  nomeDaTabela,
  memoriaRolagem,
  filtrosColuna,
  onFiltrosColuna,
  filtrosLigados = true,
  exportarRef,
  novaLinha: novaLinhaProp,
  somenteLeitura = false,
  ordem,
  onOrdenar,
  buscaAtiva,
  casaBusca,
  selecionado,
  onSelecionar,
  onAbrir,
  statusLabel,
  labelOf,
  etapaDe,
  edicao,
}: {
  cards: C[]
  /** Em tela cheia: elemento da barra de ferramentas que recebe a barra da lista (portal). */
  slotDaBarra?: HTMLElement | null
  /**
   * CARA DE PLANILHA (SPEC-20260915-2149): cabeçalho de linha com o código,
   * chip da tabela, barra de fórmula (no `slotDoNome`), faixas azuis.
   */
  planilha?: boolean
  slotDoNome?: HTMLElement | null
  nomeDaTabela?: string
  /** Chave (sessionStorage) onde a rolagem da lista é lembrada — voltar do item devolve a mesma posição. */
  memoriaRolagem?: string
  /** Filtros por coluna (coluna → textos permitidos) e as setas nos cabeçalhos ligadas/desligadas. */
  filtrosColuna: Map<string, Set<string>>
  onFiltrosColuna: (f: Map<string, Set<string>>) => void
  filtrosLigados?: boolean
  /**
   * EXPORTAR PARA O EXCEL (SPEC-20260916-0035): a lista preenche com a função
   * que gera o .xlsx do que está visível; a barra do Sheets chama.
   */
  exportarRef?: React.MutableRefObject<((nomeDoArquivo: string) => Promise<{ linhas: number; colunas: string[] }>) | null>
  /**
   * LINHA NOVA no fim da planilha (SPEC-20260916-0042): "+" abre a tela de
   * inclusão; preencher uma célula cria o item na própria linha.
   */
  novaLinha?: { aoAbrirTela: () => void; aoCriar: (rascunho: Rascunho) => Promise<void> }
  /** LEITOR (SPEC-20260916-1527): nada de editar célula nem linha nova. */
  somenteLeitura?: boolean
  /**
   * Seções quando "Agrupar por" está ativo; null = lista única. Cada faixa pode
   * ter filhas (sub-agrupamento e terceiro nível) — o `filter` da filha já
   * compõe com o do pai.
   */
  bandas: BandaDaLista<C>[] | null
  /** Colunas visíveis, na ordem escolhida. */
  colunas: ColunaDaLista[]
  /** Tudo que PODE virar coluna (fixas + complementares) — alimenta o seletor. */
  todasAsColunas: ColunaDaLista[]
  onColunas: (chaves: string[]) => void
  /** Largura nova de uma coluna, em px, ao fim do arraste da borda. */
  onLargura: (chave: string, px: number) => void
  /** Altura de cada linha (item→px); ausente = padrão. Arrastar a borda inferior grava aqui. */
  alturas: Record<string, number>
  onAltura: (cardId: string, px: number) => void
  /** Largura do cabeçalho de linha (coluna do código) na planilha — gravada como largura da coluna `code` (SPEC-20260916-0943). */
  larguraCodigo?: number
  /** Altura do cabeçalho da planilha em px; 0 = padrão. Arrastar a borda inferior do canto grava. */
  alturaCabecalho?: number
  onAlturaCabecalho?: (px: number) => void
  /** Estilo de tabela e tamanho da letra da planilha — a exportação reproduz (SPEC-20260916-0947). */
  estilo?: string
  fonte?: number
  /** Filtros aplicados fora da grade (busca, filtro rápido), em texto, para a aba "Filtros" do .xlsx. */
  filtrosExtras?: string[]
  /** Item arrastado pela alça soltou sobre `alvoId` (null = fim do grupo). */
  onMoverLinha: (arrastadoId: string, alvoId: string | null, bandaAlvo: string | null) => void
  /** Grupos recolhidos — o MESMO estado das bandas do quadro. */
  bandasFechadas: Set<string>
  onAlternarBanda: (key: string) => void
  seletorAberto: boolean
  onSeletor: (aberto: boolean) => void
  ordem: { col: ColunaLista | 'manual'; desc: boolean }
  /** Sem `desc` alterna (clique no cabeçalho); com `desc` fixa a direção (menu de filtro). */
  onOrdenar: (col: ColunaLista, desc?: boolean) => void
  buscaAtiva: boolean
  casaBusca: (c: C) => boolean
  selecionado: string | null
  onSelecionar: (id: string) => void
  onAbrir: (id: string) => void
  statusLabel: (statusId: string | null) => string
  labelOf: (key: string) => string
  /** Etapa do fluxo do cartão, com a cor do passo para o ponto da célula. */
  etapaDe: (c: C) => { nome: string; cor: string | null } | null
  /** Catálogos e gravação para a GRADE editável (SPEC-20260915-2048). */
  edicao: EdicaoDaGrade
}) {
  /** Arraste do cabeçalho para reordenar; vive em ref para não redesenhar. */
  const arrasto = useRef<string | null>(null)
  const [sobre, setSobre] = useState<string | null>(null)
  const tabelaRef = useRef<HTMLTableElement | null>(null)
  /** Linha sendo arrastada pela alça, e a linha sob o ponteiro. */
  const linhaArrastada = useRef<string | null>(null)
  const [alvoLinha, setAlvoLinha] = useState<string | null>(null)
  /** Redimensionando qual coluna — só para pintar a alça durante o gesto. */
  const [redimensionando, setRedimensionando] = useState<string | null>(null)

  const rolagemRef = useRef<HTMLDivElement | null>(null)
  /**
   * MEMÓRIA DA ROLAGEM (SPEC-20260915-2231): ao montar com itens, devolve a
   * posição guardada; a cada rolagem, guarda. Abrir um item e voltar encontra
   * a lista no mesmo ponto.
   *
   * Desde 2026-09-21 ela DURA: vivia em `sessionStorage` e morria junto com a
   * aba, então voltar no dia seguinte começava do topo.
   */
  const rolagemRestaurada = useRef(false)
  useLayoutEffect(() => {
    if (rolagemRestaurada.current || !memoriaRolagem || cards.length === 0) return
    const el = rolagemRef.current
    if (!el) return
    rolagemRestaurada.current = true
    const lido = lerRolagem(memoriaRolagem)
    if (lido) {
      el.scrollTop = lido.top
      el.scrollLeft = lido.left
    }
  }, [memoriaRolagem, cards.length])
  // ao desmontar (abrir um item, trocar de modo) guarda a posição de qualquer
  // jeito — não depende de o último evento de rolagem ter chegado. Layout
  // effect: a limpeza roda com o nó AINDA no DOM (scrollTop válido); o ref
  // espelho sobrevive ao React zerar `rolagemRef` antes das limpezas.
  const elRolagem = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(
    () => () => {
      if (elRolagem.current) guardarRolagem(elRolagem.current)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const guardarRolagem = (el: HTMLDivElement) => {
    if (!memoriaRolagem) return
    guardarRolagemNoDisco(memoriaRolagem, { top: el.scrollTop, left: el.scrollLeft })
  }

  /**
   * GRADE (SPEC-20260915-2048): uma célula ATIVA (cursor), movida por teclado
   * ou clique, e no máximo UMA em edição. As duas são `{cardId, col}` — chave
   * de coluna, não índice, para sobreviver a reordenar/esconder colunas.
   */
  // `linha` = chave da LINHA (grupo + item): com agrupamento em níveis o mesmo
  // item aparece em mais de um grupo, e só o id marcaria todas as cópias
  // (SPEC-20260915-2229)
  const [cursor, setCursor] = useState<{ cardId: string; col: string; linha: string } | null>(null)
  const [editando, setEditando] = useState<{ cardId: string; col: string; linha: string; inicial?: string } | null>(null)
  /** Clicar FORA da grade (e fora de menus/editores) desmarca a célula. */
  useEffect(() => {
    if (!cursor) return
    const fora = (e: PointerEvent) => {
      const alvo = e.target as HTMLElement | null
      if (!alvo) return
      if (alvo.closest('.kbl__tabela, .kbl__filtromenu, .kbl__colmenu, .kbl__suspensa, .kbl__suspensa-fora, .ob-select__list, .ob-date__popover, .ob-tagselect__list, [role="dialog"], .wi-menu__backdrop, .kb__ownermenu')) return
      setCursor(null)
    }
    document.addEventListener('pointerdown', fora, true)
    return () => document.removeEventListener('pointerdown', fora, true)
  }, [cursor])
  // leitor não tem linha nova (SPEC-20260916-1527)
  const novaLinha = somenteLeitura ? undefined : novaLinhaProp
  /** Colunas que aceitam edição na célula. Código e Etapa ficam só leitura. */
  const editavel = (col: ColunaDaLista) => {
    if (somenteLeitura) return false
    if (col.compl) return ['text', 'number', 'date', 'datetime', 'boolean', 'select', 'multiselect', 'richtext'].includes(col.compl.type)
    return ['title', 'type', 'status', 'etapa', 'assignee', 'due', 'priority', 'description', 'expectedResult'].includes(col.key)
  }
  /** Colunas de TEXTO FORMATADO: editor rico na célula, barra do Sheets viva (SPEC-20260915-2250). */
  const temTextoRico = (col: ColunaDaLista) =>
    col.compl ? col.compl.type === 'richtext' : (COLUNAS_RICAS as readonly string[]).includes(col.key)
  /** Ordem visual das linhas (para as setas ↑↓): recalculada a cada render. */
  const linhasVisiveis = useRef<{ linha: string; cardId: string }[]>([])

  /**
   * RASCUNHO da linha nova (SPEC-20260916-0042). `null` = linha em repouso
   * (só o "+"). O ref espelha o estado para a conclusão ler o valor recém
   * gravado no mesmo tique da tecla Enter.
   */
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const rascunhoRef = useRef<Rascunho | null>(null)
  const toast = useToast()
  const gravarRascunho = (patch: Rascunho) => {
    // enquanto a linha está sendo gravada (ou acabou de ser), o blur do editor
    // que fecha ainda tenta gravar o título — e reabria um rascunho fantasma
    if (criandoRef.current) return
    const novo = { ...(rascunhoRef.current ?? {}), ...patch }
    rascunhoRef.current = novo
    setRascunho(novo)
  }
  const ativarRascunho = () => {
    if (!rascunhoRef.current) gravarRascunho({})
  }
  const cancelarRascunho = () => {
    rascunhoRef.current = null
    setRascunho(null)
    setEditando(null)
  }
  const [criando, setCriando] = useState(false)
  /**
   * Trava SÍNCRONA contra gravar a mesma linha duas vezes (bug 2026-09-16
   * 03:30: "criou 3 registros iguais") — o estado `criando` só muda no
   * próximo render, e Enter + blur + ✓ chegavam no mesmo tique.
   */
  const criandoRef = useRef(false)
  /** Enter/✓: cria o item se Item, Tipo (e Etapa, quando há escolha) estão preenchidos; senão avisa e leva o cursor ao que falta. */
  const concluirRascunho = async () => {
    const r = rascunhoRef.current
    if (!r || !novaLinha || criandoRef.current) return
    // etapa: uma só opção já vale; mais de uma exige escolha (SPEC-20260916-0120)
    const etapas = edicao.etapasParaNovo(r.workItemTypeId ?? null)
    if (!r.flowStepId && etapas.length === 1) r.flowStepId = etapas[0]!.value
    const falta = !r.title?.trim() ? 'title' : !r.workItemTypeId ? 'type' : etapas.length > 1 && !r.flowStepId ? 'etapa' : null
    if (falta) {
      setCursor({ cardId: NOVO_ID, col: falta, linha: `nova::${NOVO_ID}` })
      toast.push({
        tone: 'warning',
        message:
          falta === 'title'
            ? 'Informe o título do item para gravar a linha.'
            : falta === 'type'
              ? 'Escolha o tipo do item para gravar a linha.'
              : 'Escolha a etapa em que o item começa para gravar a linha.',
      })
      return
    }
    criandoRef.current = true
    setCriando(true)
    try {
      await novaLinha.aoCriar(r)
      // linha gravada: a próxima linha nova já nasce ativa, com o cursor no título
      rascunhoRef.current = null
      setRascunho(null)
      setEditando(null)
      setCursor({ cardId: NOVO_ID, col: 'title', linha: `nova::${NOVO_ID}` })
      rolagemRef.current?.focus()
    } catch {
      /* a página já avisou */
    } finally {
      setCriando(false)
      // solta a trava só depois de o React fechar o editor (o blur dele chega
      // no commit seguinte); antes disso o blur reabria o rascunho
      setTimeout(() => {
        criandoRef.current = false
      }, 150)
    }
  }
  /** Cartão-rascunho que ocupa a linha nova — o que `leitura()` e os editores leem. */
  const cardNovo = useMemo(
    () =>
      ({
        id: NOVO_ID,
        code: '+',
        title: rascunho?.title ?? '',
        type: { icon: null, nameKey: '' },
        statusId: rascunho?.statusId ?? null,
        status: null,
        assignee: null,
        dueAt: rascunho?.dueAt ?? null,
        priority: { nameKey: '' },
        flowStepId: rascunho?.flowStepId ?? null,
        workItemTypeId: rascunho?.workItemTypeId ?? null,
        workItemPriorityId: rascunho?.workItemPriorityId ?? null,
        assigneeMemberId: rascunho?.assigneeMemberId ?? null,
        customValues: rascunho?.customValues ?? {},
        description: rascunho?.description ?? '',
        expectedResult: rascunho?.expectedResult ?? '',
      }) as unknown as C,
    [rascunho],
  )
  /** Na cara de planilha o código vira cabeçalho de linha: sai da grade. */
  const colsVis = planilha ? colunas.filter((c) => c.key !== 'code') : colunas
  /**
   * FILTRO POR COLUNA estilo Excel (SPEC-20260915-2149): coluna → conjunto de
   * textos permitidos. Coluna sem entrada não filtra. Compõe com os filtros
   * do quadro (a lista já chega filtrada) e vale agrupado, porque é aplicado
   * dentro de `ordenar`, por onde passam a lista plana e cada faixa.
   */
  // (o estado vem da página: `filtrosColuna`/`onFiltrosColuna` — SPEC-20260915-2245)
  const setFiltrosColuna = (f: Map<string, Set<string>> | ((m: Map<string, Set<string>>) => Map<string, Set<string>>)) =>
    onFiltrosColuna(typeof f === 'function' ? f(filtrosColuna) : f)
  const [filtroAberto, setFiltroAberto] = useState<string | null>(null)

  /** Para onde o cursor iria `dl` linhas e `dc` colunas adiante (sem mover). */
  const alvoDoCursor = (dl: number, dc: number) => {
    const linhas = linhasVisiveis.current
    const cols = colsVis.map((c) => c.key)
    const atual = cursor ?? { linha: linhas[0]?.linha ?? '', col: cols[0] ?? '' }
    const li = Math.min(Math.max(0, linhas.findIndex((l) => l.linha === atual.linha) + dl), linhas.length - 1)
    const ci = Math.min(Math.max(0, cols.indexOf(atual.col) + dc), cols.length - 1)
    const alvo = linhas[li]
    const col = cols[ci]
    return alvo && col ? { cardId: alvo.cardId, col, linha: alvo.linha } : null
  }
  /** Move o cursor `dl` linhas e `dc` colunas, sem sair da grade. */
  const moverCursor = (dl: number, dc: number) => {
    const alvo = alvoDoCursor(dl, dc)
    if (!alvo) return
    const col = alvo.col
    setCursor(alvo)
    if (alvo.cardId !== NOVO_ID) onSelecionar(alvo.cardId)
    // LINHA NOVA em inclusão: Tab/setas já entram em edição na célula seguinte
    // (pedido 2026-09-16 02:25)
    if (alvo.cardId === NOVO_ID && rascunhoRef.current && dl === 0) {
      const def = colunas.find((c) => c.key === col)
      if (def && editavel(def)) setEditando({ cardId: NOVO_ID, col, linha: alvo.linha })
    }
    // mantém a célula ativa à vista
    rolagemRef.current
      ?.querySelector(`[data-cel="${CSS.escape(alvo.linha)}::${CSS.escape(col)}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
  const iniciarEdicao = (inicial?: string) => {
    if (!cursor) return
    const col = colunas.find((c) => c.key === cursor.col)
    if (!col || !editavel(col)) return
    // célula rica de item pesado: o HTML não veio inteiro — edita pela tela
    // do item, nunca na linha (SPEC-20260916-1948)
    const cardDoCursor = cards.find((c) => c.id === cursor.cardId)
    if (cardDoCursor && celulaPesada(cardDoCursor, col)) {
      onAbrir(cardDoCursor.id)
      return
    }
    // editar uma célula da linha nova liga o rascunho (o "+" vira ✓/✕)
    if (cursor.cardId === NOVO_ID) ativarRascunho()
    setEditando({ cardId: cursor.cardId, col: cursor.col, linha: cursor.linha, inicial })
  }
  /** Depois de confirmar: desce (Enter) ou avança (Tab) — como na planilha. */
  const depoisDeConfirmar = (como: 'baixo' | 'cima' | 'direita' | 'esquerda' | 'fica') => {
    setEditando(null)
    rolagemRef.current?.focus()
    // na LINHA NOVA, Enter = gravar a linha (cria o item) e ir para a próxima
    if (como === 'baixo' && cursor?.cardId === NOVO_ID) {
      void concluirRascunho()
      return
    }
    if (como === 'baixo') moverCursor(1, 0)
    else if (como === 'cima') moverCursor(-1, 0)
    else if (como === 'direita') moverCursor(0, 1)
    else if (como === 'esquerda') moverCursor(0, -1)
  }

  /**
   * Teclado da grade — só quando NÃO há célula em edição (o editor trata as
   * próprias teclas). Setas movem; Tab avança; Home/End vão às pontas da
   * linha; Enter/F2 editam; uma letra começa a edição já com a letra;
   * Ctrl+Enter abre o item.
   */
  const teclaDaGrade = (e: React.KeyboardEvent) => {
    if (editando) return
    const alvo = e.target as HTMLElement
    // tecla dentro de um controle da barra (busca, seletor) não é da grade
    if (alvo.closest('input, textarea, [contenteditable], .kbl__colmenu, .kbl__barra, .kbl__filtromenu')) return
    const cols = colsVis.map((c) => c.key)
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); moverCursor(1, 0); return
      case 'ArrowUp': e.preventDefault(); moverCursor(-1, 0); return
      case 'ArrowRight': e.preventDefault(); moverCursor(0, 1); return
      case 'ArrowLeft': e.preventDefault(); moverCursor(0, -1); return
      case 'Tab': e.preventDefault(); moverCursor(0, e.shiftKey ? -1 : 1); return
      case 'Home': e.preventDefault(); moverCursor(0, -cols.length); return
      case 'End': e.preventDefault(); moverCursor(0, cols.length); return
      case 'Enter':
        e.preventDefault()
        if (e.ctrlKey || e.metaKey) { if (cursor && cursor.cardId !== NOVO_ID) onAbrir(cursor.cardId); return }
        iniciarEdicao()
        return
      case 'F2': e.preventDefault(); iniciarEdicao(); return
      // sem cursor o Esc segue para quem estiver por fora (sair da tela cheia)
      case 'Escape': if (cursor) { e.preventDefault(); setCursor(null) } return
    }
    // tecla imprimível numa célula de texto: começa a editar substituindo
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && cursor) {
      const col = colunas.find((c) => c.key === cursor.col)
      const textual = col && (col.key === 'title' || (col.compl && ['text', 'number'].includes(col.compl.type)))
      if (textual) { e.preventDefault(); iniciarEdicao(e.key) }
    }
  }

  /**
   * EDITOR de uma célula: o controle do tipo, já com foco, gravando ao
   * confirmar. Complementares reusam a leitura de tipo de `CampoInput`
   * (campos-shared): texto/número → Input; data → DateInput; sim/não →
   * Checkbox; escolha → Select; múltipla → TagSelect.
   */
  /** Tudo que o editor da célula precisa da lista (componente de módulo: não remonta a cada render). */
  const ctxEditor: CtxEditorDaCelula<C> = { edicao, gravarRascunho, depoisDeConfirmar, rolagemRef, planilha }

  /**
   * ÂNCORA AO RECOLHER (SPEC-20260915-0207). Recolher encolhe o conteúdo; com a
   * lista rolada, o navegador grampeia o `scrollTop` no novo máximo e tudo
   * desliza — a faixa clicada some de debaixo do cursor e o clique seguinte
   * acerta outra. Guardamos onde ela estava e devolvemos a rolagem a esse
   * ponto. `useLayoutEffect` porque a correção tem de valer antes da pintura:
   * com rAF o usuário veria o solavanco.
   */
  const ancora = useRef<{ chave: string; top: number } | null>(null)

  /** Alterna guardando a posição de tela da faixa, para ela não fugir. */
  const alternarAncorado = (chave: string, el: HTMLElement) => {
    ancora.current = { chave, top: el.getBoundingClientRect().top }
    onAlternarBanda(chave)
  }

  /**
   * FOLGA NO FIM (SPEC-20260915-0207). A âncora acima só consegue trabalhar se
   * houver para onde rolar: quando o conteúdo encolhe abaixo da rolagem atual,
   * o navegador grampeia o `scrollTop` e NADA devolve a faixa ao lugar. Uma
   * folga no fim — meia janela — absorve o encolhimento de um grupo recolhido,
   * e some quando a lista cabe inteira, para não inventar barra de rolagem.
   */
  /**
   * FOLGA + ÂNCORA, no MESMO efeito e direto no DOM (SPEC-20260915-0207).
   *
   * Recolher encolhe o conteúdo; com a lista rolada, o navegador grampeia o
   * `scrollTop` no novo máximo e tudo desliza — a faixa clicada some de debaixo
   * do cursor e o clique seguinte acerta outra. A folga no fim CRESCE exatamente
   * o que a tabela perdeu, mantendo a altura rolável constante: sem encolhimento,
   * sem clamp. Depois disso a âncora devolve a faixa ao pixel em que estava.
   *
   * Os dois passos precisam do mesmo frame e nesta ordem — por isso o padding vai
   * direto no elemento em vez de passar por estado, que só chegaria ao DOM no
   * passe seguinte, quando o clamp já teria acontecido.
   */
  const alturaMax = useRef(0)
  /**
   * Assinatura da SELEÇÃO — em primitivos. `cards`/`bandas` são recriados a cada
   * render do pai: usá-los como dependência zeraria o máximo o tempo todo, e a
   * folga nunca chegaria a existir.
   */
  const assinatura = `${cards.length}|${colunas.length}|${(bandas ?? []).map((b) => b.key).join(',')}`
  useLayoutEffect(() => {
    alturaMax.current = 0
  }, [assinatura])
  useLayoutEffect(() => {
    const caixa = rolagemRef.current
    const tabela = tabelaRef.current
    if (!caixa || !tabela) return

    // 1) folga que compensa o encolhimento (limitada a uma tela, para não deixar
    //    um vazio enorme com quase tudo recolhido)
    const atual = tabela.offsetHeight
    alturaMax.current = Math.max(alturaMax.current, atual)
    const encolheu = alturaMax.current - atual
    const nova = alturaMax.current > caixa.clientHeight ? Math.min(encolheu, caixa.clientHeight) : 0
    caixa.style.paddingBottom = `${nova}px`

    // 2) com espaço garantido, devolve a faixa clicada ao lugar
    const a = ancora.current
    if (!a) return
    ancora.current = null
    const el = caixa.querySelector(`[data-faixa="${CSS.escape(a.chave)}"]`)
    if (!el) return
    const delta = el.getBoundingClientRect().top - a.top
    if (delta) caixa.scrollTop += delta
  }, [assinatura, bandasFechadas])

  /**
   * Arrastar DENTRO das linhas rola a lista nos dois eixos (SPEC-20260914-2307).
   * Só entra em modo de rolagem depois de LIMIAR_PAN px: abaixo disso o gesto
   * ainda é um clique, e selecionar/abrir o item continua valendo.
   */
  const iniciarPan = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    const alvo = e.target as HTMLElement
    // controles têm gesto próprio — a alça de largura, o botão de ordenar, links
    // ...nem uma célula em edição: arrastar dentro do editor é selecionar texto
    if (alvo.closest('button, a, input, .kbl__medida, .kbl__medida-linha, thead, .kbl__cel--edit, .kbl__editor')) return
    const caixa = rolagemRef.current
    if (!caixa) return
    // nada para rolar: não sequestrar o gesto
    if (caixa.scrollWidth <= caixa.clientWidth && caixa.scrollHeight <= caixa.clientHeight) return

    const x0 = e.clientX
    const y0 = e.clientY
    const esq0 = caixa.scrollLeft
    const topo0 = caixa.scrollTop
    let virouPan = false

    const mover = (ev: PointerEvent) => {
      const dx = ev.clientX - x0
      const dy = ev.clientY - y0
      if (!virouPan) {
        if (Math.hypot(dx, dy) < LIMIAR_PAN) return
        virouPan = true
        caixa.classList.add('kbl__rolagem--puxando')
        // some com a seleção de texto que o arraste teria começado
        window.getSelection()?.removeAllRanges()
      }
      ev.preventDefault()
      caixa.scrollLeft = esq0 - dx
      caixa.scrollTop = topo0 - dy
    }
    const soltar = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', mover)
      window.removeEventListener('pointerup', soltar)
      window.removeEventListener('pointercancel', soltar)
      caixa.classList.remove('kbl__rolagem--puxando')
      // arrastou de verdade: o clique que vem a seguir não deve selecionar
      if (virouPan) {
        const comer = (c: Event) => {
          c.stopPropagation()
          c.preventDefault()
        }
        window.addEventListener('click', comer, { capture: true, once: true })
        // se o clique não vier (ponteiro fora), desarma na volta do frame
        setTimeout(() => window.removeEventListener('click', comer, { capture: true }), 0)
        ev.preventDefault()
      }
    }
    window.addEventListener('pointermove', mover, { passive: false })
    window.addEventListener('pointerup', soltar)
    window.addEventListener('pointercancel', soltar)
  }

  /**
   * TUDO SELECIONADO (SPEC-20260916-0943): clicar no canto superior esquerdo
   * marca todas as colunas e linhas; enquanto está marcado, arrastar a borda
   * de UMA coluna dá a largura a todas e a de UMA linha dá a altura a todas
   * (como no Sheets). Mover o cursor para uma célula desmarca.
   */
  const [tudo, setTudo] = useState(false)
  useEffect(() => {
    if (cursor) setTudo(false)
  }, [cursor])
  const aplicarLarguraEmTudo = (px: number) => {
    onLargura('code', px)
    for (const c of colunas) if (c.key !== 'code') onLargura(c.key, px)
  }
  const aplicarAlturaEmTudo = (px: number) => {
    for (const c of cards) onAltura(c.id, px)
  }

  /**
   * Redimensionar pela borda do cabeçalho. Durante o gesto a largura vai DIRETO
   * no `<col>`: passar por estado a cada pixel redesenharia a tabela inteira e
   * o arraste engasgaria. O estado (e o storage) só recebem no fim.
   */
  const iniciarRedimensionar = (e: React.PointerEvent, col: ColunaDaLista, indice: number) => {
    e.preventDefault()
    e.stopPropagation() // o clique é da alça, não do botão de ordenar
    const alca = e.currentTarget as HTMLElement
    const th = alca.closest('th')
    const elCol = tabelaRef.current?.querySelectorAll('colgroup col')[indice] as HTMLElement | undefined
    const x0 = e.clientX
    const w0 = th?.getBoundingClientRect().width ?? col.largura
    let atual = w0

    // a alça para a propagação (acima): o gesto de mudar a coluna de lugar não começa aqui
    setRedimensionando(col.key)
    // captura para o gesto seguir valendo com o ponteiro fora da alça; onde
    // não houver, o arraste ainda funciona dentro dela
    try {
      alca.setPointerCapture(e.pointerId)
    } catch {
      /* ponteiro já liberado */
    }

    const mover = (ev: PointerEvent) => {
      atual = Math.max(LARGURA_MIN_COLUNA, Math.round(w0 + (ev.clientX - x0)))
      if (elCol) elCol.style.width = `${atual}px`
    }
    const soltar = () => {
      alca.removeEventListener('pointermove', mover)
      alca.removeEventListener('pointerup', soltar)
      alca.removeEventListener('pointercancel', soltar)
      setRedimensionando(null)
      if (atual !== w0) {
        if (tudo) aplicarLarguraEmTudo(atual)
        else onLargura(col.key, atual)
      }
    }
    alca.addEventListener('pointermove', mover)
    alca.addEventListener('pointerup', soltar)
    alca.addEventListener('pointercancel', soltar)
  }

  /**
   * Redimensionar a ALTURA da linha pela borda inferior do cabeçalho de linha
   * (SPEC-20260915-2214). Mesmo desenho da largura: durante o gesto a altura
   * vai direto na variável CSS da `<tr>`; o estado só recebe no fim.
   */
  const iniciarRedimensionarLinha = (e: React.PointerEvent, card: C) => {
    e.preventDefault()
    e.stopPropagation() // não é clique na célula, nem pan, nem arraste da alça
    const alca = e.currentTarget as HTMLElement
    const tr = alca.closest('tr') as HTMLTableRowElement | null
    const y0 = e.clientY
    const h0 = tr?.getBoundingClientRect().height ?? ALTURA_MIN_LINHA
    let atual = h0
    try {
      alca.setPointerCapture(e.pointerId)
    } catch {
      /* ponteiro já liberado */
    }
    tr?.classList.add('kbl__row--medindo')
    const mover = (ev: PointerEvent) => {
      atual = Math.max(ALTURA_MIN_LINHA, Math.round(h0 + (ev.clientY - y0)))
      tr?.style.setProperty('--kbl-h', `${atual}px`)
    }
    const soltar = () => {
      alca.removeEventListener('pointermove', mover)
      alca.removeEventListener('pointerup', soltar)
      alca.removeEventListener('pointercancel', soltar)
      tr?.classList.remove('kbl__row--medindo')
      if (atual !== h0) {
        if (tudo) aplicarAlturaEmTudo(atual)
        else onAltura(card.id, atual)
      }
    }
    alca.addEventListener('pointermove', mover)
    alca.addEventListener('pointerup', soltar)
    alca.addEventListener('pointercancel', soltar)
  }
  /**
   * Altura do CABEÇALHO (SPEC-20260916-0943): pela borda inferior do canto;
   * durante o gesto vai na variável da `<tr>` do thead, o estado recebe no fim.
   */
  const iniciarRedimensionarCabecalho = (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const alca = e.currentTarget as HTMLElement
    const tr = alca.closest('tr') as HTMLTableRowElement | null
    const y0 = e.clientY
    const h0 = tr?.getBoundingClientRect().height ?? ALTURA_MIN_LINHA
    let atual = h0
    try {
      alca.setPointerCapture(e.pointerId)
    } catch {
      /* ponteiro já liberado */
    }
    const mover = (ev: PointerEvent) => {
      atual = Math.max(ALTURA_MIN_LINHA, Math.round(h0 + (ev.clientY - y0)))
      tr?.style.setProperty('--kbl-cab-h', `${atual}px`)
    }
    const soltar = () => {
      alca.removeEventListener('pointermove', mover)
      alca.removeEventListener('pointerup', soltar)
      alca.removeEventListener('pointercancel', soltar)
      if (atual !== h0) onAlturaCabecalho?.(atual)
    }
    alca.addEventListener('pointermove', mover)
    alca.addEventListener('pointerup', soltar)
    alca.addEventListener('pointercancel', soltar)
  }
  /** Alça da altura: na borda inferior do cabeçalho de linha (ou da primeira célula). */
  const medidaLinha = (card: C) => (
    <span
      className="kbl__medida-linha"
      role="separator"
      aria-orientation="horizontal"
      aria-label={`Ajustar a altura da linha ${card.code}`}
      title="Arraste para ajustar a altura"
      onPointerDown={(e) => iniciarRedimensionarLinha(e, card)}
      onClick={(e) => e.stopPropagation()}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
    />
  )

  const valorCompl = (card: C, col: ColunaDaLista): string => {
    const bruto = ((card as { customValues?: Record<string, unknown> }).customValues ?? {})[col.compl!.key]
    if (bruto === null || bruto === undefined || bruto === '') return '—'
    const tipo = col.compl!.type
    if (tipo === 'boolean') return bruto ? 'Sim' : 'Não'
    // anexo (SPEC-20261104-1542): o valor é o objeto do arquivo; mostra o nome
    if (tipo === 'file') return ehAnexoDeCampo(bruto) ? bruto.name : '—'
    if (tipo === 'date' || tipo === 'datetime') {
      const d = new Date(String(bruto))
      return Number.isNaN(d.getTime())
        ? String(bruto)
        : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d)
    }
    if (Array.isArray(bruto)) return bruto.map(String).join(', ')
    return String(bruto)
  }

  /** Valor de ordenação de cada coluna FIXA. Data crua para ordenar de verdade. */
  const chave = (c: C, col: ColunaLista): string | number => {
    switch (col) {
      case 'code': return c.code
      case 'title': return c.title.toLowerCase()
      case 'type': return labelOf(c.type.nameKey).toLowerCase()
      case 'status': return statusLabel(c.statusId).toLowerCase()
      case 'etapa': return (etapaDe(c)?.nome ?? '').toLowerCase()
      case 'assignee': return (c.assignee?.name ?? '').toLowerCase()
      // sem prazo vai para o fim em ordem crescente, não para o começo
      case 'due': return c.dueAt ? new Date(c.dueAt).getTime() : Number.MAX_SAFE_INTEGER
      case 'priority': return labelOf(c.priority.nameKey).toLowerCase()
      case 'description': return tiraHtml(htmlDaCelula(c, 'description')).toLowerCase()
      case 'expectedResult': return tiraHtml(htmlDaCelula(c, 'expectedResult')).toLowerCase()
    }
  }
  const ordenar = (lista: C[]) =>
    // manual = a ordem em que o quadro entregou (lane, depois sortOrder): é a
    // ordem que o arraste da alça edita, então não pode ser reordenada aqui
    ordem.col === 'manual'
      ? lista.filter(passaNosFiltros)
      : [...lista.filter(passaNosFiltros)].sort((a, b) => {
          const va = chave(a, ordem.col as ColunaLista)
          const vb = chave(b, ordem.col as ColunaLista)
          const r =
            typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'pt-BR')
          return ordem.desc ? -r : r
        })

  const fmtPrazo = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(new Date(iso)) : '—'
  const atrasado = (c: C) =>
    Boolean(c.dueAt) && new Date(c.dueAt!).getTime() < Date.now() && !c.status?.isTerminal

  /**
   * Texto puro de uma célula — o que o filtro de coluna lista e compara, e o
   * que a barra de fórmula mostra. Sem chip, sem ícone: só o que se lê.
   */
  const textoDaCelula = (c: C, col: ColunaDaLista): string => {
    if (col.compl) return col.compl.type === 'richtext' ? tiraHtml(valorCompl(c, col)) || '—' : valorCompl(c, col)
    switch (col.key) {
      case 'description': return tiraHtml(htmlDaCelula(c, 'description')) || '—'
      case 'expectedResult': return tiraHtml(htmlDaCelula(c, 'expectedResult')) || '—'
      case 'code': return c.code
      case 'title': return c.title
      case 'type': return labelOf(c.type.nameKey)
      case 'status': return statusLabel(c.statusId)
      case 'etapa': return etapaDe(c)?.nome ?? '—'
      case 'assignee': return c.assignee?.name ?? 'Sem responsável'
      case 'due':
        return c.dueAt
          ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(c.dueAt))
          : '—'
      case 'priority': return labelOf(c.priority.nameKey)
      default: return ''
    }
  }
  const colunaPorChave = (key: string) => colunas.find((c) => c.key === key) ?? todasAsColunas.find((c) => c.key === key)
  const passaNosFiltros = (c: C) => {
    for (const [key, permitidos] of filtrosColuna) {
      const col = colunaPorChave(key)
      if (col && !permitidos.has(textoDaCelula(c, col))) return false
    }
    return true
  }

  /** Conteúdo de uma célula, por coluna. */
  /**
   * Conteúdo de uma célula, por coluna. Os campos de ESTADO (status, prioridade,
   * tipo, etapa) saem em chips com a mesma linguagem dos cartões do quadro —
   * por tom, com ícone ou com a cor do passo — para se lerem de relance
   * (SPEC-20260915-1810). Cor nunca vai sozinha: o texto está sempre junto.
   */
  /** O que a célula mostra em LEITURA — o chip de cada campo. */
  /** Leitura da LINHA NOVA: o rascunho pelos catálogos da grade, ou um convite em cinza. */
  const leituraRascunho = (col: ColunaDaLista): React.ReactNode => {
    const r = rascunho ?? {}
    const vago = (txt: string) => <span className="kbl__vago kbl__vago--novo">{txt}</span>
    if (col.compl) {
      const v = complDoCartao(cardNovo)[col.compl.key]
      if (v === null || v === undefined || v === '') return vago('')
      const txt = col.compl.type === 'richtext' ? tiraHtml(String(v)) : ehAnexoDeCampo(v) ? v.name : Array.isArray(v) ? v.map(String).join(', ') : v === true ? 'Sim' : v === false ? 'Não' : String(v)
      return <span className="kbl__suave">{txt}</span>
    }
    switch (col.key) {
      case 'title': return r.title ? <span>{r.title}</span> : vago(rascunho ? 'Título do item (obrigatório)' : 'Novo item…')
      case 'type': { const t = edicao.tipos.find((x) => x.value === r.workItemTypeId); return t ? <span className="kbl__cel-txt">{t.label}</span> : vago(rascunho ? 'Tipo (obrigatório)' : '') }
      case 'status': return r.statusId ? <span className="kbl__pill" data-tone="neutral">{statusLabel(r.statusId)}</span> : vago('')
      case 'etapa': {
        const ops = edicao.etapasParaNovo(r.workItemTypeId ?? null)
        const e = ops.find((o) => o.value === r.flowStepId)
        return e ? <span className="kbl__cel-txt">{e.label}</span> : vago(rascunho && ops.length > 1 ? 'Etapa (obrigatória)' : '')
      }
      case 'assignee': { const m = edicao.membros.find((x) => x.value === r.assigneeMemberId); return m ? <span className="kbl__cel-txt">{m.label}</span> : vago('') }
      case 'due': return r.dueAt ? <span className="kbl__prazo">{fmtPrazo(r.dueAt)}</span> : vago('')
      case 'priority': { const p = edicao.prioridades.find((x) => x.value === r.workItemPriorityId); return p ? <span className="kbl__prio" data-tone="neutral"><span className="kbl__cel-txt">{p.label}</span></span> : vago('') }
      case 'description': return r.description ? <span className="kbl__suave">{tiraHtml(r.description)}</span> : vago('')
      case 'expectedResult': return r.expectedResult ? <span className="kbl__suave">{tiraHtml(r.expectedResult)}</span> : vago('')
      default: return vago('')
    }
  }

  const leitura = (card: C, col: ColunaDaLista): React.ReactNode => {
    if (card.id === NOVO_ID) return leituraRascunho(col)
    if (col.compl) {
      const v = col.compl.type === 'richtext' ? tiraHtml(valorCompl(card, col)) || '—' : valorCompl(card, col)
      return v === '—' ? <span className="kbl__vago">—</span> : <span className="kbl__suave">{v}</span>
    }
    switch (col.key) {
      case 'description':
      case 'expectedResult': {
        const previa = tiraHtml(htmlDaCelula(card, col.key))
        const pesada = celulaPesada(card, col)
        return previa ? (
          <span className="kbl__suave" title={pesada ? `${previa}\n(conteúdo grande — edita pela tela do item)` : previa}>
            {pesada ? <i className="fas fa-external-link-alt kbl__pesada" aria-hidden /> : null}
            {previa}
          </span>
        ) : (
          <span className="kbl__vago">—</span>
        )
      }
      case 'code': return card.code
      case 'title': return card.title
      case 'type':
        return (
          <span className="kbl__tipo">
            <span className="kbl__tipoico" aria-hidden>
              <CatalogIcon icon={card.type.icon} fallback="fas fa-tasks" size={10} />
            </span>
            <span className="kbl__cel-txt">{labelOf(card.type.nameKey)}</span>
          </span>
        )
      case 'status': {
        const soft = TONE_SOFT[card.status?.tone ?? 'neutral'] ?? TONE_SOFT.neutral!
        return (
          <span className="kbl__pill" data-tone={card.status?.tone ?? 'neutral'} style={{ background: soft.bg, color: soft.fg }}>
            {statusLabel(card.statusId)}
          </span>
        )
      }
      case 'etapa': {
        const etapa = etapaDe(card)
        return etapa ? (
          <span className="kbl__etapa">
            <span className="kbl__ponto" style={{ background: etapa.cor ?? 'var(--ob-primary)' }} aria-hidden />
            <span className="kbl__cel-txt">{etapa.nome}</span>
          </span>
        ) : (
          <span className="kbl__vago">—</span>
        )
      }
      case 'assignee':
        return card.assignee ? (
          <span className="kbl__pessoa">
            <span className="wi-avatar" aria-hidden>{initialsOf(card.assignee.name)}</span>
            <span className="kbl__cel-txt">{card.assignee.name}</span>
          </span>
        ) : (
          <span className="kbl__vago">Sem responsável</span>
        )
      case 'due':
        return card.dueAt ? (
          <span className={`kbl__prazo${atrasado(card) ? ' kbl__prazo--atrasado' : ''}`}>
            {atrasado(card) ? <i className="far fa-clock" aria-hidden /> : null}
            {fmtPrazo(card.dueAt)}
          </span>
        ) : (
          <span className="kbl__vago">—</span>
        )
      case 'priority':
        return (
          <span className="kbl__prio" data-tone={card.priority?.tone ?? 'neutral'}>
            <span className="kbl__ponto" style={{ background: TONE_COLOR[card.priority?.tone ?? 'neutral'] }} aria-hidden />
            <span className="kbl__cel-txt">{labelOf(card.priority.nameKey)}</span>
          </span>
        )
      default: return '—'
    }
  }

  /**
   * Célula da GRADE (SPEC-20260915-2048): em leitura mostra o chip; com o
   * cursor em cima ganha o contorno; em edição troca o chip pelo controle do
   * tipo. Clique põe o cursor (e seleciona a linha); duplo clique edita; o
   * Código abre o item com um clique — o duplo clique deixou de abrir.
   */
  /** Colunas de LISTA (escolha única): na planilha abrem a lista suspensa com um clique e levam a setinha. */
  const temLista = (col: ColunaDaLista) =>
    col.compl ? col.compl.type === 'select' : ['type', 'priority', 'status', 'etapa', 'assignee'].includes(col.key)

  const celula = (card: C, col: ColunaDaLista, chaveLinha: string) => {
    const ativa = cursor?.linha === chaveLinha && cursor.col === col.key
    const emEdicao = editando?.linha === chaveLinha && editando.col === col.key
    const podeEditar = editavel(col)
    const classe = [
      'kbl__cel',
      col.key === 'code' ? 'kbl__code' : col.key === 'title' ? 'kbl__titulo' : '',
      col.compl && !emEdicao ? 'kbl__suave' : '',
      ativa ? 'kbl__cel--ativa' : '',
      emEdicao ? 'kbl__cel--edit' : '',
      podeEditar ? 'kbl__cel--editavel' : '',
      temLista(col) ? 'kbl__cel--lista' : '',
    ]
      .filter(Boolean)
      .join(' ')
    const titulo =
      col.key === 'code'
        ? 'Abrir o item'
        : col.key === 'title'
          ? card.title
          : col.key === 'due' && atrasado(card)
            ? 'Prazo vencido'
            : undefined
    return (
      <td
        key={col.key}
        className={classe}
        title={titulo}
        data-col={col.key}
        data-cel={`${chaveLinha}::${col.key}`}
        onClick={(e) => {
          if (emEdicao) return
          setCursor({ cardId: card.id, col: col.key, linha: chaveLinha })
          if (col.key === 'code') {
            e.stopPropagation()
            onAbrir(card.id)
          }
        }}
        onDoubleClick={(e) => {
          e.preventDefault()
          if (!podeEditar) return
          // item pesado: a célula rica abre o item (SPEC-20260916-1948)
          if (celulaPesada(card, col)) {
            onAbrir(card.id)
            return
          }
          if (card.id === NOVO_ID) ativarRascunho()
          setEditando({ cardId: card.id, col: col.key, linha: chaveLinha })
        }}
      >
        {emEdicao ? <EditorDaCelula card={card} col={col} inicial={editando?.inicial} ctx={ctxEditor} /> : leitura(card, col)}
        {/* SETINHA da célula de lista (planilha): só ela — ou o duplo clique —
            abre a lista; o clique simples na célula apenas seleciona (pedido
            2026-09-15 22:22) */}
        {planilha && podeEditar && temLista(col) && !emEdicao ? (
          <button
            type="button"
            className="kbl__seta"
            aria-label={`Abrir a lista de ${col.rotulo}`}
            title="Abrir a lista"
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              setCursor({ cardId: card.id, col: col.key, linha: chaveLinha })
              if (card.id === NOVO_ID) ativarRascunho()
              else onSelecionar(card.id)
              setEditando({ cardId: card.id, col: col.key, linha: chaveLinha })
            }}
          />
        ) : null}
      </td>
    )
  }

  /** A alça de arraste da linha: mora na primeira célula (ou no cabeçalho de linha, na planilha). */
  const alca = (card: C) => (
    <span
      className="kbl__alca"
      role="button"
      tabIndex={-1}
      aria-label={`Arrastar ${card.title} para outra posição`}
      title="Arraste para mudar a posição"
      draggable
      onDragStart={(e) => {
        linhaArrastada.current = card.id
        e.dataTransfer.effectAllowed = 'move'
        // sem isto o Firefox não inicia o arraste
        e.dataTransfer.setData('text/plain', card.id)
      }}
      onDragEnd={() => {
        linhaArrastada.current = null
        setAlvoLinha(null)
      }}
      // a alça tem gesto próprio: não deve virar rolagem nem seleção
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <i className="fas fa-grip-vertical" aria-hidden />
    </span>
  )

  const linha = (card: C, i: number, bandaKey: string | null, chaveGrupo: string | null) => {
    // chave da LINHA: grupo composto + item — única mesmo quando o item se
    // repete em mais de um grupo (agrupamento por membro, por exemplo)
    const chaveLinha = `${chaveGrupo ?? ''}::${card.id}`
    // ordem visual das linhas, para as setas ↑↓ da grade
    linhasVisiveis.current.push({ linha: chaveLinha, cardId: card.id })
    if (card.id === NOVO_ID) {
      // LINHA NOVA (SPEC-20260916-0042): sem arraste; o cabeçalho de linha é
      // o "+" (abre a tela) ou ✓/✕ enquanto há rascunho
      return (
        <tr key={chaveLinha} className={`kbl__row kbl__row--nova${rascunho ? ' kbl__row--nova-ativa' : ''}`}>
          <td className={`kbl__rownum kbl__rownum--novo${cursor?.linha === chaveLinha ? ' kbl__rownum--ativa' : ''}`}>
            {rascunho ? (
              <span className="kbl__novo-acoes">
                <button type="button" className="kbl__novo-ok" title="Gravar a linha (Enter)" aria-label="Gravar a linha" disabled={criando} onClick={(e) => { e.stopPropagation(); void concluirRascunho() }}>
                  <i className={criando ? 'fas fa-spinner fa-spin' : 'fas fa-check'} aria-hidden />
                </button>
                <button type="button" className="kbl__novo-x" title="Cancelar a linha" aria-label="Cancelar a linha" disabled={criando} onClick={(e) => { e.stopPropagation(); cancelarRascunho() }}>
                  <i className="fas fa-times" aria-hidden />
                </button>
              </span>
            ) : (
              <button type="button" className="kbl__novo-mais" title="Adicionar item (abre a tela de inclusão)" aria-label="Adicionar item" onClick={(e) => { e.stopPropagation(); novaLinha?.aoAbrirTela() }}>
                <i className="fas fa-plus" aria-hidden />
              </button>
            )}
          </td>
          {colsVis.map((col) => celula(card, col, chaveLinha))}
        </tr>
      )
    }
    return (
    <tr
      key={chaveLinha}
      className={`kbl__row${i % 2 === 1 ? ' kbl__row--zebra' : ''}${selecionado === card.id ? ' kbl__row--on' : ''}${
        buscaAtiva ? (casaBusca(card) ? ' kbl__row--achado' : ' kbl__row--apagado') : ''
      }${alvoLinha === card.id ? ' kbl__row--alvo' : ''}`}
      // altura ajustada pela pessoa: vai na variável que as células leem
      style={alturas[card.id] ? ({ '--kbl-h': `${alturas[card.id]}px` } as React.CSSProperties) : undefined}
      onClick={() => onSelecionar(card.id)}
      onDragOver={(e) => {
        if (!linhaArrastada.current || linhaArrastada.current === card.id) return
        e.preventDefault()
        setAlvoLinha(card.id)
      }}
      onDragLeave={() => setAlvoLinha((a) => (a === card.id ? null : a))}
      onDrop={(e) => {
        e.preventDefault()
        const de = linhaArrastada.current
        linhaArrastada.current = null
        setAlvoLinha(null)
        if (de && de !== card.id) onMoverLinha(de, card.id, bandaKey)
      }}
    >
      {planilha ? (
        // CABEÇALHO DE LINHA da planilha: o código do item, congelado à
        // esquerda; clique abre o item (como a coluna Código fazia)
        <td
          className={`kbl__rownum kbl__cel-alca${cursor?.linha === chaveLinha ? ' kbl__rownum--ativa' : ''}`}
          title="Abrir o item"
          onClick={(e) => {
            e.stopPropagation()
            onSelecionar(card.id)
            onAbrir(card.id)
          }}
        >
          {alca(card)}
          {card.code}
          {medidaLinha(card)}
        </td>
      ) : null}
      {colsVis.map((col, iCol) => {
        const td = celula(card, col, chaveLinha)
        // a alça mora na PRIMEIRA célula: é o "marcador no início da linha"
        // (na planilha ela já foi para o cabeçalho de linha)
        if (iCol !== 0 || planilha) return td
        // a célula da grade traz handlers (cursor, edição): eles seguem junto
        const { children: miolo, className: classeCel, ...restoCel } = td.props as {
          children: React.ReactNode
          className?: string
          [k: string]: unknown
        }
        return (
          <td key={col.key} {...restoCel} className={`${classeCel ?? ''} kbl__cel-alca`.trim()}>
            {alca(card)}
            {miolo}
            {medidaLinha(card)}
          </td>
        )
      })}
    </tr>
  )
  }

  /** Só as fixas ordenam: o complementar não tem comparação definida aqui. */
  const ordenavel = (col: ColunaDaLista) => !col.compl

  /**
   * MENU DE FILTRO DA COLUNA (estilo Excel): ordenar, pesquisar, marcar os
   * valores e aplicar no OK. O rascunho vive aqui; só o OK grava no estado da
   * lista. Componente próprio para ter estado local sem inflar a lista.
   */
  const ctxMenuFiltro: CtxMenuFiltro<C> = { cards, textoDaCelula, filtrosColuna, setFiltrosColuna, setFiltroAberto, ordenavel, onOrdenar }

  const cabecalho = (
    <thead>
      <tr style={alturaCabecalho ? ({ '--kbl-cab-h': `${alturaCabecalho}px` } as React.CSSProperties) : undefined}>
        {planilha ? (
          // canto congelado, vazio como no Sheets (o chip com o nome do quadro
          // saiu a pedido do usuário em 2026-09-15 22:30). Clicar marca TUDO;
          // a borda direita ajusta a largura do cabeçalho de linha e a borda
          // inferior a altura do cabeçalho (SPEC-20260916-0943)
          <th
            className={`kbl__canto${tudo ? ' kbl__canto--on' : ''}`}
            aria-label={nomeDaTabela ?? 'Tabela'}
            title={tudo ? 'Desmarcar tudo' : 'Selecionar tudo (colunas e linhas)'}
            role="button"
            aria-pressed={tudo}
            onClick={() => { setCursor(null); setTudo((v) => !v) }}
          >
            <span
              className="kbl__medida"
              role="separator"
              aria-orientation="vertical"
              aria-label="Ajustar a largura do cabeçalho de linha"
              title="Arraste para ajustar a largura"
              onPointerDown={(e) => iniciarRedimensionar(e, { key: 'code', rotulo: 'Código', largura: larguraCodigo } as ColunaDaLista, 0)}
              onClick={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            />
            <span
              className="kbl__medida-linha"
              role="separator"
              aria-orientation="horizontal"
              aria-label="Ajustar a altura do cabeçalho"
              title="Arraste para ajustar a altura do cabeçalho"
              onPointerDown={iniciarRedimensionarCabecalho}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
            />
          </th>
        ) : null}
        {colsVis.map((c, iCol) => (
          <th
            key={c.key}
            className={`${sobre === c.key ? 'kbl__th-alvo' : ''}${redimensionando === c.key ? ' kbl__th-medindo' : ''}${
              cursor?.col === c.key ? ' kbl__th--ativa' : ''
            }${filtroAberto === c.key ? ' kbl__th--menu' : ''}`.trim() || undefined}
            aria-sort={ordem.col === c.key ? (ordem.desc ? 'descending' : 'ascending') : 'none'}
            data-coluna={c.key}
            /* mudar de lugar arrastando, por ponteiro (SPEC-20261103-1217): o
               arraste nativo do navegador parou de funcionar aqui */
            onPointerDown={(e) =>
              iniciarArrasteDeColuna(e, {
                chave: c.key,
                caixa: e.currentTarget.closest('tr'),
                onInicio: (k) => {
                  arrasto.current = k
                },
                onSobre: setSobre,
                onFim: () => {
                  arrasto.current = null
                  setSobre(null)
                },
                onSoltar: (destino) => {
                  // a ordem gravada inclui o Código mesmo quando ele está escondido na planilha
                  const chaves = colunas.map((x) => x.key)
                  const deLa = chaves.indexOf(c.key)
                  const praLa = chaves.indexOf(destino)
                  if (deLa < 0 || praLa < 0) return
                  const ordemNova = chaves.filter((k) => k !== c.key)
                  // descendo, fica depois do destino; subindo, antes — como na lista de Itens
                  ordemNova.splice(praLa, 0, c.key)
                  onColunas(ordemNova)
                },
              })
            }
          >
            <button
              type="button"
              className="kbl__th"
              onClick={() => ordenavel(c) && onOrdenar(c.key as ColunaLista)}
              title={ordenavel(c) ? `Ordenar por ${c.rotulo} · arraste para mover a coluna` : `${c.rotulo} · arraste para mover a coluna`}
            >
              {c.rotulo}
              {ordenavel(c) ? (
                <i
                  className={`fas fa-sort${ordem.col === c.key ? (ordem.desc ? '-down' : '-up') : ''} kbl__sort${
                    ordem.col === c.key ? ' kbl__sort--on' : ''
                  }`}
                  aria-hidden
                />
              ) : null}
            </button>
            {/* FILTRO DA COLUNA (estilo Excel): a seta abre o menu; o funil
                marca coluna filtrada. Some quando os filtros estão desligados
                pelo funil da barra (a coluna filtrada continua mostrando) */}
            {filtrosLigados || filtrosColuna.has(c.key) ? (
            <button
              type="button"
              className={`kbl__thfiltro${filtrosColuna.has(c.key) ? ' kbl__thfiltro--on' : ''}`}
              aria-label={filtrosColuna.has(c.key) ? `Filtro aplicado em ${c.rotulo} — alterar` : `Filtrar ${c.rotulo}`}
              title={filtrosColuna.has(c.key) ? 'Filtro aplicado — clique para alterar' : 'Filtrar esta coluna'}
              aria-expanded={filtroAberto === c.key}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                setFiltroAberto((v) => (v === c.key ? null : c.key))
              }}
            >
              <i className={filtrosColuna.has(c.key) ? 'fas fa-filter' : 'fas fa-caret-down'} aria-hidden />
            </button>
            ) : null}
            {filtroAberto === c.key ? <MenuFiltroColuna col={c} ctx={ctxMenuFiltro} /> : null}
            {/* alça na borda: arrastar aqui muda a largura, não a ordem */}
            <span
              className="kbl__medida"
              role="separator"
              aria-orientation="vertical"
              aria-label={`Ajustar a largura da coluna ${c.rotulo}`}
              title="Arraste para ajustar a largura"
              // na planilha o colgroup tem o <col> do cabeçalho de linha na frente
              onPointerDown={(e) => iniciarRedimensionar(e, c, iCol + (planilha ? 1 : 0))}
              onClick={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            />
          </th>
        ))}
      </tr>
    </thead>
  )

  /**
   * Desenha uma faixa e o que vem abaixo dela: as sub-faixas, se houver, ou os
   * itens quando é folha. A CHAVE de recolhimento é composta (`pai::filha`),
   * a mesma que o quadro usa — por isso recolher vale nos dois modos.
   */
  const secoesDaBanda = (b: BandaDaLista<C>, chave: string, nivel: number): React.ReactNode[] => {
    const doGrupo = ordenar(cards.filter(b.filter)).filter((c) => !buscaAtiva || casaBusca(c))
    if (doGrupo.length === 0) return []
    const fechada = bandasFechadas.has(chave)
    const alvo = alvoLinha === `banda:${chave}`

    const faixa = (
      <tr
        key={`faixa:${chave}`}
        data-faixa={chave}
        className={`kbl__secao kbl__secao--n${nivel}${fechada ? ' kbl__secao--fechada' : ''}${
          alvo ? ' kbl__secao--alvo' : ''
        }`}
        onDragOver={(e) => {
          if (!linhaArrastada.current) return
          e.preventDefault()
          setAlvoLinha(`banda:${chave}`)
        }}
        onDragLeave={() => setAlvoLinha((a) => (a === `banda:${chave}` ? null : a))}
        onDrop={(e) => {
          e.preventDefault()
          const de = linhaArrastada.current
          linhaArrastada.current = null
          setAlvoLinha(null)
          // soltar no cabeçalho = topo daquele grupo
          if (de) onMoverLinha(de, doGrupo[0]?.id ?? null, b.key)
        }}
      >
        <td colSpan={colsVis.length + (planilha ? 1 : 0)}>
          {/* a faixa inteira é o controle de recolher — alvo grande */}
          <button
            type="button"
            className="kbl__secaobtn"
            style={{ paddingLeft: 12 + nivel * 18 }}
            aria-expanded={!fechada}
            title={fechada ? `Expandir ${b.label}` : `Recolher ${b.label}`}
            onClick={(e) => alternarAncorado(chave, e.currentTarget)}
          >
            {/* o miolo é sticky à esquerda: rolando a tabela para o lado, o nome
                do grupo continua visível em vez de sumir com a primeira coluna */}
            <span className="kbl__secaoin">
              <i className={`fas fa-chevron-${fechada ? 'right' : 'down'} kbl__secaoseta`} aria-hidden />
              {b.icon ? <i className={b.icon} aria-hidden /> : null}
              {b.label}
              {/* a contagem é do grupo TODO, recolhido ou não */}
              <span className="kbl__contagem">{doGrupo.length}</span>
            </span>
          </button>
        </td>
      </tr>
    )

    if (fechada) return [faixa]
    // com filhas, quem mostra os itens são elas; sem filhas, esta faixa é folha
    const abaixo = b.filhas.length
      ? b.filhas.flatMap((f) => secoesDaBanda(f, `${chave}::${f.key}`, nivel + 1))
      : doGrupo.map((c, i) => linha(c, i, b.key, chave))
    return [faixa, ...abaixo]
  }

  /**
   * EXPORTAR PARA O EXCEL (SPEC-20260916-0035): exatamente o que a grade mostra
   * — colunas visíveis na ordem da tela, linhas que passaram pelos filtros
   * (quadro, coluna, busca) e grupos recolhidos de fora; faixas viram linhas
   * de título. A biblioteca entra sob demanda.
   */
  if (exportarRef) {
    exportarRef.current = async (nomeDoArquivo: string) => {
      const ExcelJS = (await import('exceljs')).default ?? (await import('exceljs'))
      const colunasExport: ColunaDaLista[] = planilha
        ? [{ key: 'code', rotulo: 'Código', largura: larguraCodigo } as ColunaDaLista, ...colsVis]
        : colsVis
      // `zebra` = a mesma alternância da tela (índice ímpar dentro do grupo)
      type LinhaExport = { tipo: 'faixa'; rotulo: string; nivel: number } | { tipo: 'item'; card: C; zebra: boolean }
      const saida: LinhaExport[] = []
      const visivel = (c: C) => !buscaAtiva || casaBusca(c)
      const empilhar = (b: BandaDaLista<C>, chave: string, nivel: number) => {
        const doGrupo = ordenar(cards.filter(b.filter)).filter(visivel)
        if (doGrupo.length === 0) return
        saida.push({ tipo: 'faixa', rotulo: `${b.label} (${doGrupo.length})`, nivel })
        if (bandasFechadas.has(chave)) return
        if (b.filhas.length) b.filhas.forEach((f) => empilhar(f, `${chave}::${f.key}`, nivel + 1))
        else doGrupo.forEach((c, i) => saida.push({ tipo: 'item', card: c, zebra: i % 2 === 1 }))
      }
      if (bandas) bandas.forEach((b) => empilhar(b, b.key, 0))
      else ordenar(cards).filter(visivel).forEach((c, i) => saida.push({ tipo: 'item', card: c, zebra: i % 2 === 1 }))

      const valor = (c: C, col: ColunaDaLista): string | number | Date | null => {
        if (col.key === 'due') return c.dueAt ? new Date(c.dueAt) : null
        if (col.compl) {
          const bruto = complDoCartao(c)[col.compl.key]
          if (bruto === null || bruto === undefined || bruto === '') return null
          if (col.compl.type === 'number' && typeof bruto === 'number') return bruto
          if ((col.compl.type === 'date' || col.compl.type === 'datetime') && typeof bruto === 'string') {
            const d = new Date(bruto)
            return Number.isNaN(d.getTime()) ? String(bruto) : d
          }
          if (col.compl.type === 'boolean') return bruto === true ? 'Sim' : 'Não'
        }
        const t = textoDaCelula(c, col)
        return t === '—' ? null : t
      }

      // LAYOUT IDÊNTICO À TELA (SPEC-20260916-0947): as cores do estilo de tabela
      // ativo (ou as padrão da planilha), larguras, alturas e letra viram
      // formatação das células; px→pt = ×0,75
      const argb = (cor: string): string => {
        const m = cor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
        const hex = m ? m.slice(1, 4).map((x) => Number(x).toString(16).padStart(2, '0')).join('') : cor.replace('#', '')
        return `FF${hex.toUpperCase()}`
      }
      /** clareia uma cor ARGB misturando com branco (p = quanto da cor fica) — o 2º nível de faixa */
      const clarear = (cor: string, p: number) => {
        const n = parseInt(cor.slice(2), 16)
        const canal = (c: number) => Math.round(c * p + 255 * (1 - p)).toString(16).padStart(2, '0')
        return `FF${canal((n >> 16) & 255)}${canal((n >> 8) & 255)}${canal(n & 255)}`.toUpperCase()
      }
      const v = varsDoEstilo(planilha ? estilo : '')
      const grupoBg = argb(v['--tbl-grupo-bg'] ?? '#e8f0fe')
      const cores = {
        cabBg: argb(v['--tbl-cab-bg'] ?? '#f8f9fa'),
        cabFg: argb(v['--tbl-cab-fg'] ?? '#444746'),
        linha: argb(v['--tbl-linha'] ?? '#ffffff'),
        faixa: argb(v['--tbl-faixa'] ?? (planilha ? '#cfe2f3' : '#ffffff')),
        borda: argb(v['--tbl-borda'] ?? '#e1e3e1'),
        grupoBg,
        grupoBg2: v['--tbl-grupo-bg'] ? clarear(grupoBg, 0.6) : 'FFF3F7FE',
        grupoFg: argb(v['--tbl-grupo-fg'] ?? '#174ea6'),
        texto: 'FF1F1F1F',
      }
      const pt = (px: number) => Math.round(px * 0.75 * 10) / 10
      const tamanhoLetra = Math.max(8, pt(fonte))
      const alturaPadrao = planilha ? 30 : 44
      const wb = new ExcelJS.Workbook()
      wb.creator = 'Orbind'
      const ws = wb.addWorksheet('Itens', { views: [{ state: 'frozen', xSplit: planilha ? 1 : 0, ySplit: 1 }] })
      ws.columns = colunasExport.map((col) => ({ header: col.rotulo, key: col.key, width: Math.max(6, Math.round(col.largura / 7)) }))
      const borda = { style: 'thin' as const, color: { argb: cores.borda } }
      const bordas = { top: borda, left: borda, bottom: borda, right: borda }
      const fundo = (cor: string) => ({ type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: cor } })
      const cab = ws.getRow(1)
      cab.font = { name: 'Arial', size: tamanhoLetra, bold: true, color: { argb: cores.cabFg } }
      cab.fill = fundo(cores.cabBg)
      cab.alignment = { vertical: 'middle', horizontal: 'center' }
      cab.height = pt(alturaCabecalho || 24)
      cab.eachCell((cell) => { cell.border = bordas })
      for (const l of saida) {
        if (l.tipo === 'faixa') {
          const r = ws.addRow([`${'    '.repeat(l.nivel)}${l.rotulo}`])
          ws.mergeCells(r.number, 1, r.number, colunasExport.length)
          r.font = { name: 'Arial', size: tamanhoLetra, bold: true, color: { argb: cores.grupoFg } }
          r.fill = fundo(l.nivel === 0 ? cores.grupoBg : cores.grupoBg2)
          r.height = pt(32)
          r.alignment = { vertical: 'middle' }
          r.eachCell((cell) => { cell.border = bordas })
          continue
        }
        const r = ws.addRow(colunasExport.map((col) => valor(l.card, col)))
        r.height = pt(alturas[l.card.id] ?? alturaPadrao)
        r.eachCell({ includeEmpty: true }, (cell, n) => {
          cell.border = bordas
          const col = colunasExport[n - 1]
          // o cabeçalho de linha (código) tem a cara do cabeçalho, como na tela
          const ehCodigo = planilha && col?.key === 'code'
          cell.fill = fundo(ehCodigo ? cores.cabBg : l.zebra ? cores.faixa : cores.linha)
          cell.font = { name: 'Arial', size: tamanhoLetra, bold: ehCodigo, color: { argb: ehCodigo ? cores.cabFg : cores.texto } }
          if (col && (col.key === 'due' || col.compl?.type === 'date')) cell.numFmt = 'dd/mm/yyyy'
          if (col?.compl?.type === 'datetime') cell.numFmt = 'dd/mm/yyyy hh:mm'
          cell.alignment = { vertical: 'middle', horizontal: ehCodigo ? 'center' : 'left', wrapText: false }
        })
      }
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: colunasExport.length } }
      // ABA "FILTROS": o que estava aplicado quando a exportação saiu
      const wf = wb.addWorksheet('Filtros')
      wf.columns = [{ header: 'Filtro', key: 'f', width: 28 }, { header: 'Valor', key: 'v', width: 60 }]
      wf.getRow(1).font = { name: 'Arial', bold: true, color: { argb: cores.cabFg } }
      wf.getRow(1).fill = fundo(cores.cabBg)
      const linhasFiltro: [string, string][] = []
      for (const [chave, valores] of filtrosColuna) {
        const col = todasAsColunas.find((c) => c.key === chave)
        linhasFiltro.push([`Coluna: ${col?.rotulo ?? chave}`, [...valores].join(', ')])
      }
      for (const extra of filtrosExtras) {
        const i = extra.indexOf(':')
        linhasFiltro.push(i > 0 ? [extra.slice(0, i), extra.slice(i + 1).trim()] : [extra, ''])
      }
      if (linhasFiltro.length === 0) linhasFiltro.push(['Nenhum filtro aplicado', ''])
      for (const lf of linhasFiltro) wf.addRow(lf)
      const buffer = await wb.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomeDoArquivo
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      return { linhas: saida.length, colunas: colunasExport.map((c) => c.rotulo) }
    }
  }

  // a lista de linhas visíveis é reconstruída a cada render pelo `linha()`
  linhasVisiveis.current = []
  // célula ativa, para a barra de fórmula da planilha
  const cardAtivo = cursor ? cards.find((c) => c.id === cursor.cardId) : undefined
  const colAtiva = cursor ? colunas.find((c) => c.key === cursor.col) : undefined
  // com filtro de coluna, pode não sobrar nada — a grade avisa e oferece limpar
  const sobrouAlgo = cards.length === 0 || cards.some(passaNosFiltros)

  const marcadas = new Set(colunas.map((c) => c.key))
  const alternarColuna = (key: string) => {
    if (key === 'title') return // a coluna que identifica a linha não sai
    const nova = marcadas.has(key)
      ? colunas.map((c) => c.key).filter((k) => k !== key)
      : [...colunas.map((c) => c.key), key]
    if (nova.length === 0) return // nunca ficar sem coluna nenhuma
    onColunas(nova)
  }

  return (
    <div className={`kbl${planilha ? ' kbl--planilha' : ''}`}>
      {/* BARRA DE FÓRMULA da planilha (SPEC-20260915-2149): caixa de nome com a
          célula ativa como "Coluna · WI-0000", o fx e o valor — vai por portal
          para o começo da barra do quadro */}
      {planilha && slotDoNome
        ? createPortal(
            <div className="kbl__formula" aria-live="polite">
              <span className="kbl__nome" title="Célula ativa">
                <span>{cardAtivo && colAtiva ? `${colAtiva.rotulo} · ${cardAtivo.code}` : '—'}</span>
                <i className="fas fa-caret-down" aria-hidden />
              </span>
              <span className="kbl__fx" aria-hidden>fx</span>
              <span className="kbl__valor">{cardAtivo && colAtiva ? textoDaCelula(cardAtivo, colAtiva) : ''}</span>
            </div>,
            slotDoNome,
          )
        : null}
      {/* Seletor de colunas, no canto da área da lista — em tela cheia vai
          para a barra de ferramentas do quadro (portal, mesmo estado) */}
      <BarraDaLista slot={slotDaBarra ?? null}>
        {/* Na ordem manual nenhuma coluna mostra seta: sem este aviso a lista
            pareceria embaralhada sem motivo (SPEC-20260914-2307). */}
        {ordem.col === 'manual' ? (
          <span className="kbl__ordemmanual">
            <i className="fas fa-grip-vertical" aria-hidden />
            Ordem manual
            <button
              type="button"
              className="kbl__ordemsair"
              title="Voltar a ordenar por Código"
              aria-label="Voltar a ordenar por Código"
              onClick={() => onOrdenar('code')}
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </span>
        ) : null}
        <div className="kbl__colsel">
          <button
            type="button"
            className="kbl__colbtn"
            aria-expanded={seletorAberto}
            title="Escolher as colunas da lista"
            onClick={() => onSeletor(!seletorAberto)}
          >
            <IconeColunas />
            <span>Colunas</span>
            <span className="kbl__colcount">{colunas.length}</span>
          </button>
          {seletorAberto ? (
            <>
              <div className="wi-menu__backdrop" onClick={() => onSeletor(false)} />
              <div className="kbl__colmenu" role="menu" aria-label="Colunas da lista">
                <p className="kbl__colhint">Marque o que aparece. Arraste o cabeçalho para mudar a ordem.</p>
                {todasAsColunas.filter((c) => !c.compl).map((c) => (
                  <label key={c.key} className={`kbl__colitem${c.key === 'title' ? ' kbl__colitem--fixa' : ''}`}>
                    <input
                      type="checkbox"
                      checked={marcadas.has(c.key)}
                      disabled={c.key === 'title'}
                      onChange={() => alternarColuna(c.key)}
                    />
                    {c.rotulo}
                    {c.key === 'title' ? <span className="kbl__colfixa">sempre</span> : null}
                  </label>
                ))}
                {todasAsColunas.some((c) => c.compl) ? (
                  <>
                    <span className="kbl__colsec">Campos complementares</span>
                    {todasAsColunas.filter((c) => c.compl).map((c) => (
                      <label key={c.key} className="kbl__colitem">
                        <input type="checkbox" checked={marcadas.has(c.key)} onChange={() => alternarColuna(c.key)} />
                        {c.rotulo}
                      </label>
                    ))}
                  </>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </BarraDaLista>

      {cards.length === 0 ? (
        <p className="kbl__vazio">Nenhum item para mostrar com os filtros atuais.</p>
      ) : (
        <div
          className="kbl__rolagem kbl__rolagem--grade"
          ref={(el) => {
            rolagemRef.current = el
            if (el) elRolagem.current = el
          }}
          onScroll={(e) => guardarRolagem(e.currentTarget)}
          onPointerDown={iniciarPan}
          // a GRADE recebe foco e teclado aqui: setas, Tab, Enter, F2, Esc
          tabIndex={0}
          onKeyDown={teclaDaGrade}
          aria-label="Grade de itens — setas movem, Enter edita, Ctrl+Enter abre"
        >
        <table
          className={`kbl__tabela${tudo ? ' kbl__tabela--tudo' : ''}`}
          ref={tabelaRef}
          // largura = EXATAMENTE a soma das colunas (SPEC-20260915-2214): alargar
          // uma não encolhe as outras; passou do espaço, o contêiner rola
          style={{ width: colsVis.reduce((s, c) => s + c.largura, 0) + (planilha ? larguraCodigo : 0) }}
        >
          <colgroup>
            {planilha ? <col style={{ width: larguraCodigo }} /> : null}
            {colsVis.map((c) => (
              <col key={c.key} style={{ width: c.largura }} />
            ))}
          </colgroup>
          {cabecalho}
          {!sobrouAlgo ? (
            <tbody>
              <tr className="kbl__semfiltro">
                <td colSpan={colsVis.length + (planilha ? 1 : 0)}>
                  Nenhum item passa pelos filtros de coluna.
                  <button type="button" className="kb__btn" onClick={() => setFiltrosColuna(new Map())}>
                    <i className="fas fa-times-circle" aria-hidden /> Limpar filtros de coluna
                  </button>
                </td>
              </tr>
            </tbody>
          ) : bandas ? (
            // agrupado: uma seção por banda, na ordem que o quadro já definiu,
            // descendo até o terceiro nível (SPEC-20260915-0141)
            bandas.map((b) => <tbody key={b.key}>{secoesDaBanda(b, b.key, 0)}</tbody>)
          ) : (
            <tbody>
              {ordenar(cards)
                .filter((c) => !buscaAtiva || casaBusca(c))
                .map((c, i) => linha(c, i, null, null))}
            </tbody>
          )}
          {/* LINHA NOVA no fim (SPEC-20260916-0042), só na planilha */}
          {planilha && novaLinha ? <tbody className="kbl__novo">{linha(cardNovo, 0, null, 'nova')}</tbody> : null}
        </table>
        </div>
      )}
    </div>
  )
}
